// ============================================
// COMMON REQUESTS DATABASE
// Comprehensive autocomplete entries for prior auth requests
// ============================================

export interface CommonRequest {
  name: string;
  category: string;
  aliases?: string[];
  contrastApplicable?: boolean; // For imaging studies
}

export const COMMON_REQUESTS: CommonRequest[] = [
  // ============================================
  // MEDICATIONS
  // ============================================

  // Biologics
  {
    name: "Humira (adalimumab)",
    category: "Medication",
    aliases: ["adalimumab", "humira", "ada"],
  },
  {
    name: "Enbrel (etanercept)",
    category: "Medication",
    aliases: ["etanercept", "enbrel"],
  },
  {
    name: "Remicade (infliximab)",
    category: "Medication",
    aliases: ["infliximab", "remicade", "ifx"],
  },
  {
    name: "Stelara (ustekinumab)",
    category: "Medication",
    aliases: ["ustekinumab", "stelara"],
  },
  {
    name: "Dupixent (dupilumab)",
    category: "Medication",
    aliases: ["dupilumab", "dupixent"],
  },
  {
    name: "Skyrizi (risankizumab)",
    category: "Medication",
    aliases: ["risankizumab", "skyrizi"],
  },
  {
    name: "Rinvoq (upadacitinib)",
    category: "Medication",
    aliases: ["upadacitinib", "rinvoq"],
  },
  {
    name: "Cosentyx (secukinumab)",
    category: "Medication",
    aliases: ["secukinumab", "cosentyx"],
  },
  {
    name: "Taltz (ixekizumab)",
    category: "Medication",
    aliases: ["ixekizumab", "taltz"],
  },
  {
    name: "Tremfya (guselkumab)",
    category: "Medication",
    aliases: ["guselkumab", "tremfya"],
  },

  // GLP-1s
  {
    name: "Ozempic (semaglutide)",
    category: "Medication",
    aliases: ["semaglutide", "ozempic"],
  },
  { name: "Wegovy (semaglutide)", category: "Medication", aliases: ["wegovy"] },
  {
    name: "Mounjaro (tirzepatide)",
    category: "Medication",
    aliases: ["tirzepatide", "mounjaro"],
  },
  {
    name: "Zepbound (tirzepatide)",
    category: "Medication",
    aliases: ["zepbound"],
  },
  {
    name: "Trulicity (dulaglutide)",
    category: "Medication",
    aliases: ["dulaglutide", "trulicity"],
  },
  {
    name: "Victoza (liraglutide)",
    category: "Medication",
    aliases: ["liraglutide", "victoza"],
  },
  {
    name: "Saxenda (liraglutide)",
    category: "Medication",
    aliases: ["saxenda"],
  },
  {
    name: "Rybelsus (semaglutide)",
    category: "Medication",
    aliases: ["rybelsus", "oral semaglutide"],
  },

  // Specialty / High-Cost
  {
    name: "Keytruda (pembrolizumab)",
    category: "Medication",
    aliases: ["pembrolizumab", "keytruda"],
  },
  {
    name: "Opdivo (nivolumab)",
    category: "Medication",
    aliases: ["nivolumab", "opdivo"],
  },
  {
    name: "Ibrance (palbociclib)",
    category: "Medication",
    aliases: ["palbociclib", "ibrance"],
  },
  {
    name: "Xarelto (rivaroxaban)",
    category: "Medication",
    aliases: ["rivaroxaban", "xarelto"],
  },
  {
    name: "Eliquis (apixaban)",
    category: "Medication",
    aliases: ["apixaban", "eliquis"],
  },
  {
    name: "Entresto (sacubitril/valsartan)",
    category: "Medication",
    aliases: ["sacubitril", "entresto"],
  },
  {
    name: "Jardiance (empagliflozin)",
    category: "Medication",
    aliases: ["empagliflozin", "jardiance"],
  },
  {
    name: "Farxiga (dapagliflozin)",
    category: "Medication",
    aliases: ["dapagliflozin", "farxiga"],
  },
  {
    name: "Otezla (apremilast)",
    category: "Medication",
    aliases: ["apremilast", "otezla"],
  },
  {
    name: "Xeljanz (tofacitinib)",
    category: "Medication",
    aliases: ["tofacitinib", "xeljanz"],
  },
  {
    name: "Ocrevus (ocrelizumab)",
    category: "Medication",
    aliases: ["ocrelizumab", "ocrevus"],
  },
  {
    name: "Tysabri (natalizumab)",
    category: "Medication",
    aliases: ["natalizumab", "tysabri"],
  },
  {
    name: "Tecfidera (dimethyl fumarate)",
    category: "Medication",
    aliases: ["dimethyl fumarate", "tecfidera"],
  },
  {
    name: "Vyvanse (lisdexamfetamine)",
    category: "Medication",
    aliases: ["lisdexamfetamine", "vyvanse"],
  },
  {
    name: "Qelbree (viloxazine)",
    category: "Medication",
    aliases: ["viloxazine", "qelbree"],
  },
  {
    name: "Auvelity (bupropion/dextromethorphan)",
    category: "Medication",
    aliases: ["auvelity"],
  },
  {
    name: "Spravato (esketamine)",
    category: "Medication",
    aliases: ["esketamine", "spravato"],
  },

  // ============================================
  // IMAGING
  // ============================================

  // MRI (contrast applicable)
  {
    name: "MRI Brain",
    category: "Imaging",
    aliases: ["brain mri", "head mri", "mri head"],
    contrastApplicable: true,
  },
  {
    name: "MRI Cervical Spine",
    category: "Imaging",
    aliases: ["neck mri", "c-spine mri", "mri c-spine", "mri neck"],
    contrastApplicable: true,
  },
  {
    name: "MRI Lumbar Spine",
    category: "Imaging",
    aliases: [
      "lower back mri",
      "l-spine mri",
      "mri l-spine",
      "mri lower back",
      "lumbar mri",
    ],
    contrastApplicable: true,
  },
  {
    name: "MRI Thoracic Spine",
    category: "Imaging",
    aliases: ["t-spine mri", "mid back mri", "mri t-spine"],
    contrastApplicable: true,
  },
  {
    name: "MRI Shoulder",
    category: "Imaging",
    aliases: ["shoulder mri"],
    contrastApplicable: true,
  },
  {
    name: "MRI Knee",
    category: "Imaging",
    aliases: ["knee mri"],
    contrastApplicable: true,
  },
  {
    name: "MRI Hip",
    category: "Imaging",
    aliases: ["hip mri"],
    contrastApplicable: true,
  },
  {
    name: "MRI Pelvis",
    category: "Imaging",
    aliases: ["pelvic mri", "mri pelvis"],
    contrastApplicable: true,
  },
  {
    name: "MRI Abdomen",
    category: "Imaging",
    aliases: ["abdominal mri", "mri abdomen", "mrcp"],
    contrastApplicable: true,
  },
  {
    name: "MRI Ankle",
    category: "Imaging",
    aliases: ["ankle mri", "foot mri"],
    contrastApplicable: true,
  },
  {
    name: "MRI Wrist",
    category: "Imaging",
    aliases: ["wrist mri", "hand mri"],
    contrastApplicable: true,
  },
  {
    name: "MRI Elbow",
    category: "Imaging",
    aliases: ["elbow mri"],
    contrastApplicable: true,
  },
  {
    name: "Cardiac MRI",
    category: "Imaging",
    aliases: ["heart mri", "cmr", "cardiac mr"],
    contrastApplicable: true,
  },
  {
    name: "MRA Head",
    category: "Imaging",
    aliases: ["mra brain", "intracranial mra"],
    contrastApplicable: true,
  },
  {
    name: "MRA Neck",
    category: "Imaging",
    aliases: ["carotid mra", "mra carotid"],
    contrastApplicable: true,
  },
  {
    name: "Breast MRI",
    category: "Imaging",
    aliases: ["mri breast", "bilateral breast mri"],
    contrastApplicable: true,
  },

  // CT (contrast applicable)
  {
    name: "CT Head",
    category: "Imaging",
    aliases: ["head ct", "brain ct", "ct brain"],
    contrastApplicable: true,
  },
  {
    name: "CT Chest",
    category: "Imaging",
    aliases: ["chest ct", "ct thorax", "lung ct"],
    contrastApplicable: true,
  },
  {
    name: "CT Abdomen/Pelvis",
    category: "Imaging",
    aliases: ["ct abd pelvis", "abdominal ct", "ct abdomen", "ct pelvis"],
    contrastApplicable: true,
  },
  {
    name: "CT Angiography Chest",
    category: "Imaging",
    aliases: ["cta chest", "pe protocol", "pulmonary embolism ct"],
    contrastApplicable: true,
  },
  {
    name: "CT Angiography Head/Neck",
    category: "Imaging",
    aliases: ["cta head", "cta neck", "cta head and neck"],
    contrastApplicable: true,
  },
  {
    name: "CT Coronary Angiography",
    category: "Imaging",
    aliases: ["ccta", "coronary cta", "cardiac ct"],
    contrastApplicable: true,
  },
  {
    name: "CT Spine",
    category: "Imaging",
    aliases: [
      "cervical ct",
      "lumbar ct",
      "thoracic ct",
      "ct c-spine",
      "ct l-spine",
    ],
    contrastApplicable: true,
  },
  {
    name: "CT Sinus",
    category: "Imaging",
    aliases: ["sinus ct", "ct sinuses"],
    contrastApplicable: false,
  },
  {
    name: "CT Urogram",
    category: "Imaging",
    aliases: ["ctu", "ct urography"],
    contrastApplicable: true,
  },

  // PET
  {
    name: "PET/CT",
    category: "Imaging",
    aliases: ["pet scan", "pet ct", "fdg pet", "oncologic pet"],
  },
  {
    name: "PET Brain",
    category: "Imaging",
    aliases: ["brain pet", "amyloid pet", "fdg brain pet"],
  },
  {
    name: "Cardiac PET",
    category: "Imaging",
    aliases: ["myocardial perfusion pet", "cardiac viability pet"],
  },

  // Ultrasound
  {
    name: "Echocardiogram",
    category: "Imaging",
    aliases: ["echo", "tte", "transthoracic echo", "cardiac ultrasound"],
  },
  {
    name: "Transesophageal Echo",
    category: "Imaging",
    aliases: ["tee", "transesophageal echocardiogram"],
  },
  {
    name: "Stress Echocardiogram",
    category: "Imaging",
    aliases: ["stress echo", "dobutamine stress echo"],
  },
  {
    name: "Carotid Ultrasound",
    category: "Imaging",
    aliases: ["carotid doppler", "carotid duplex"],
  },
  {
    name: "Abdominal Ultrasound",
    category: "Imaging",
    aliases: ["abd us", "abdominal us", "liver ultrasound"],
  },
  {
    name: "Pelvic Ultrasound",
    category: "Imaging",
    aliases: ["pelvic us", "transvaginal ultrasound", "tvus"],
  },
  {
    name: "Renal Ultrasound",
    category: "Imaging",
    aliases: ["kidney ultrasound", "renal us"],
  },
  {
    name: "Thyroid Ultrasound",
    category: "Imaging",
    aliases: ["thyroid us", "neck ultrasound"],
  },
  {
    name: "Venous Duplex",
    category: "Imaging",
    aliases: [
      "dvt ultrasound",
      "leg doppler",
      "venous doppler",
      "lower extremity duplex",
    ],
  },
  {
    name: "Arterial Duplex",
    category: "Imaging",
    aliases: ["arterial doppler", "abi", "lower extremity arterial"],
  },

  // Nuclear Medicine
  {
    name: "Nuclear Stress Test",
    category: "Imaging",
    aliases: [
      "myocardial perfusion imaging",
      "mpi",
      "nuclear cardiac stress",
      "lexiscan",
      "adenosine stress",
    ],
  },
  {
    name: "HIDA Scan",
    category: "Imaging",
    aliases: ["hepatobiliary scan", "gallbladder scan"],
  },
  {
    name: "Bone Scan",
    category: "Imaging",
    aliases: ["bone scintigraphy", "skeletal scintigraphy"],
  },
  {
    name: "VQ Scan",
    category: "Imaging",
    aliases: ["ventilation perfusion scan", "lung scan", "v/q scan"],
  },
  {
    name: "Thyroid Scan",
    category: "Imaging",
    aliases: ["thyroid uptake", "i-123 scan", "radioactive iodine uptake"],
  },
  {
    name: "MUGA Scan",
    category: "Imaging",
    aliases: ["multigated acquisition scan", "radionuclide ventriculography"],
  },

  // ============================================
  // PROCEDURES - SPINE & PAIN MEDICINE
  // ============================================

  {
    name: "Epidural Steroid Injection",
    category: "Procedure",
    aliases: [
      "esi",
      "epidural",
      "steroid injection",
      "caudal injection",
      "interlaminar esi",
      "transforaminal esi",
      "tfesi",
    ],
  },
  {
    name: "Lumbar Laminectomy",
    category: "Procedure",
    aliases: ["laminectomy", "decompression", "lumbar decompression"],
  },
  {
    name: "Cervical Laminectomy",
    category: "Procedure",
    aliases: ["cervical decompression", "c-spine laminectomy"],
  },
  {
    name: "Spinal Fusion",
    category: "Procedure",
    aliases: [
      "fusion",
      "lumbar fusion",
      "cervical fusion",
      "alif",
      "plif",
      "tlif",
      "acdf",
      "posterior fusion",
    ],
  },
  {
    name: "Anterior Cervical Discectomy and Fusion",
    category: "Procedure",
    aliases: ["acdf", "anterior cervical fusion"],
  },
  {
    name: "Discectomy",
    category: "Procedure",
    aliases: [
      "microdiscectomy",
      "disc surgery",
      "diskectomy",
      "lumbar discectomy",
    ],
  },
  {
    name: "Facet Joint Injection",
    category: "Procedure",
    aliases: ["facet block", "facet injection", "zygapophyseal injection"],
  },
  {
    name: "Medial Branch Block",
    category: "Procedure",
    aliases: ["mbb", "diagnostic block", "medial branch nerve block"],
  },
  {
    name: "Radiofrequency Ablation",
    category: "Procedure",
    aliases: [
      "rfa",
      "rhizotomy",
      "nerve ablation",
      "radiofrequency neurotomy",
      "genicular rfa",
      "si joint rfa",
    ],
  },
  {
    name: "Spinal Cord Stimulator",
    category: "Procedure",
    aliases: [
      "scs",
      "dorsal column stimulator",
      "spinal cord stim",
      "scs trial",
      "scs implant",
    ],
  },
  {
    name: "Spinal Cord Stimulator Trial",
    category: "Procedure",
    aliases: ["scs trial", "stim trial", "neuromodulation trial"],
  },
  {
    name: "Kyphoplasty",
    category: "Procedure",
    aliases: ["balloon kyphoplasty"],
  },
  {
    name: "Vertebroplasty",
    category: "Procedure",
    aliases: ["vertebral augmentation"],
  },
  {
    name: "Sacroiliac Joint Injection",
    category: "Procedure",
    aliases: ["si joint injection", "sij injection", "sacroiliac injection"],
  },
  {
    name: "SI Joint Fusion",
    category: "Procedure",
    aliases: ["sacroiliac fusion", "sij fusion", "si fusion"],
  },
  {
    name: "Intrathecal Pump",
    category: "Procedure",
    aliases: [
      "pain pump",
      "baclofen pump",
      "morphine pump",
      "intrathecal drug delivery",
    ],
  },
  {
    name: "Peripheral Nerve Stimulator",
    category: "Procedure",
    aliases: ["pns", "peripheral nerve stim"],
  },
  {
    name: "Dorsal Root Ganglion Stimulator",
    category: "Procedure",
    aliases: ["drg", "drg stimulator"],
  },
  {
    name: "Trigger Point Injection",
    category: "Procedure",
    aliases: ["tpi", "muscle injection"],
  },
  {
    name: "Stellate Ganglion Block",
    category: "Procedure",
    aliases: ["sgb", "cervical sympathetic block"],
  },
  {
    name: "Celiac Plexus Block",
    category: "Procedure",
    aliases: ["celiac block", "splanchnic nerve block"],
  },
  {
    name: "Lumbar Sympathetic Block",
    category: "Procedure",
    aliases: ["sympathetic block", "lsb"],
  },
  {
    name: "Occipital Nerve Block",
    category: "Procedure",
    aliases: ["greater occipital nerve block", "gon block"],
  },
  {
    name: "Genicular Nerve Block",
    category: "Procedure",
    aliases: ["genicular block", "knee nerve block", "genicular nerve ablation"],
  },
  {
    name: "Genicular Nerve Radiofrequency Ablation",
    category: "Procedure",
    aliases: [
      "genicular rfa",
      "cooled radiofrequency knee",
      "coolief",
      "knee rfa",
      "genicular nerve ablation",
    ],
  },
  {
    name: "Cooled Radiofrequency Ablation",
    category: "Procedure",
    aliases: [
      "cooled rfa",
      "coolief",
      "cooled rf",
      "cooled radiofrequency neurotomy",
    ],
  },
  {
    name: "MILD Procedure",
    category: "Procedure",
    aliases: [
      "minimally invasive lumbar decompression",
      "mild lumbar",
      "percutaneous lumbar decompression",
      "mild stenosis",
    ],
  },
  {
    name: "Interspinous Spacer",
    category: "Procedure",
    aliases: [
      "vertiflex",
      "superion",
      "interspinous process decompression",
      "ipd",
      "indirect decompression spacer",
    ],
  },
  {
    name: "Percutaneous Disc Decompression",
    category: "Procedure",
    aliases: [
      "nucleoplasty",
      "disc fx",
      "percutaneous discectomy",
      "intradiscal procedure",
    ],
  },
  {
    name: "Viscosupplementation",
    category: "Procedure",
    aliases: [
      "hyaluronic acid injection",
      "synvisc",
      "gel-one",
      "euflexxa",
      "supartz",
      "knee gel injection",
      "joint lubrication injection",
    ],
  },
  {
    name: "Platelet-Rich Plasma Injection",
    category: "Procedure",
    aliases: ["prp", "prp injection", "platelet rich plasma", "regenerative injection"],
  },
  {
    name: "Prolotherapy",
    category: "Procedure",
    aliases: ["dextrose prolotherapy", "regenerative injection therapy", "sclerotherapy joint"],
  },
  {
    name: "Joint Injection",
    category: "Procedure",
    aliases: [
      "knee injection",
      "hip injection",
      "shoulder injection",
      "intra-articular injection",
      "corticosteroid injection joint",
    ],
  },
  {
    name: "Bursa Injection",
    category: "Procedure",
    aliases: [
      "trochanteric bursa injection",
      "subacromial bursa injection",
      "olecranon bursa injection",
      "pes anserine bursa injection",
      "bursitis injection",
    ],
  },
  {
    name: "Piriformis Injection",
    category: "Procedure",
    aliases: ["piriformis block", "piriformis muscle injection", "piriformis syndrome injection"],
  },
  {
    name: "Suprascapular Nerve Block",
    category: "Procedure",
    aliases: ["suprascapular block", "shoulder nerve block", "ssn block"],
  },
  {
    name: "Intercostal Nerve Block",
    category: "Procedure",
    aliases: ["intercostal block", "rib nerve block", "chest wall block"],
  },
  {
    name: "Superior Hypogastric Plexus Block",
    category: "Procedure",
    aliases: ["hypogastric block", "pelvic pain block", "shpb"],
  },
  {
    name: "Ganglion Impar Block",
    category: "Procedure",
    aliases: ["ganglion of walther block", "coccygeal block", "perineal pain block"],
  },
  {
    name: "Transforaminal Epidural Steroid Injection",
    category: "Procedure",
    aliases: ["tfesi", "transforaminal esi", "selective nerve root block", "snrb"],
  },
  {
    name: "Caudal Epidural Injection",
    category: "Procedure",
    aliases: ["caudal block", "caudal esi", "caudal steroid injection"],
  },
  {
    name: "Intrathecal Pump Trial",
    category: "Procedure",
    aliases: ["pain pump trial", "intrathecal trial", "it pump trial", "ziconotide trial"],
  },
  {
    name: "Spinal Cord Stimulator Revision",
    category: "Procedure",
    aliases: ["scs revision", "lead revision", "stim revision", "generator replacement"],
  },
  {
    name: "Peripheral Nerve Block",
    category: "Procedure",
    aliases: ["regional nerve block", "nerve block", "targeted nerve block"],
  },
  {
    name: "Botox for Pain",
    category: "Procedure",
    aliases: [
      "botulinum toxin pain",
      "botox myofascial pain",
      "botox cervical dystonia",
      "botox spasticity",
    ],
  },
  {
    name: "Ketamine Infusion Pain",
    category: "Procedure",
    aliases: [
      "ketamine infusion",
      "iv ketamine pain",
      "ketamine chronic pain",
      "low dose ketamine infusion",
    ],
  },
  {
    name: "Lidocaine Infusion",
    category: "Procedure",
    aliases: ["iv lidocaine", "lidocaine drip", "intravenous lidocaine pain"],
  },
  {
    name: "Fluoroscopy-Guided Injection",
    category: "Procedure",
    aliases: [
      "fluoroscopic guidance",
      "fluoro guided injection",
      "c-arm guided injection",
    ],
  },
  {
    name: "Ultrasound-Guided Injection",
    category: "Procedure",
    aliases: [
      "us guided injection",
      "ultrasound guided nerve block",
      "sono guided injection",
    ],
  },

  // ============================================
  // PROCEDURES / IMAGING - PRIMARY CARE SCREENINGS
  // ============================================

  {
    name: "DEXA Scan",
    category: "Imaging",
    aliases: [
      "bone density scan",
      "bone density test",
      "dxa scan",
      "dexa",
      "osteoporosis screening",
    ],
  },
  {
    name: "Screening Mammogram",
    category: "Imaging",
    aliases: [
      "mammogram",
      "mammography",
      "breast cancer screening",
      "annual mammogram",
    ],
  },
  {
    name: "Diagnostic Mammogram",
    category: "Imaging",
    aliases: [
      "diagnostic mammo",
      "breast imaging diagnostic",
      "mammogram with ultrasound",
    ],
  },
  {
    name: "Low-Dose CT Lung Cancer Screening",
    category: "Imaging",
    aliases: [
      "ldct",
      "lung cancer screening",
      "low dose ct chest",
      "lung screening ct",
    ],
  },
  {
    name: "Ambulatory Blood Pressure Monitor",
    category: "Procedure",
    aliases: [
      "abpm",
      "24 hour blood pressure",
      "ambulatory bp monitor",
    ],
  },
  {
    name: "Holter Monitor",
    category: "Procedure",
    aliases: [
      "holter",
      "24 hour heart monitor",
      "48 hour holter",
      "continuous ecg monitor",
    ],
  },

  // ============================================
  // MEDICATIONS - WEIGHT MANAGEMENT
  // ============================================

  {
    name: "Contrave (naltrexone/bupropion)",
    category: "Medication",
    aliases: ["contrave", "naltrexone bupropion", "weight loss medication"],
  },
  {
    name: "Qsymia (phentermine/topiramate)",
    category: "Medication",
    aliases: ["qsymia", "phentermine topiramate", "weight loss combination"],
  },
  {
    name: "Phentermine",
    category: "Medication",
    aliases: ["adipex", "lomaira", "phentermine weight loss", "appetite suppressant"],
  },
  {
    name: "Orlistat (Xenical)",
    category: "Medication",
    aliases: ["xenical", "alli", "orlistat", "fat absorption blocker"],
  },
  {
    name: "Plenity",
    category: "Medication",
    aliases: ["plenity", "cellulose citric acid hydrogel", "weight management device"],
  },

  // ============================================
  // MEDICATIONS - HORMONE REPLACEMENT (MENOPAUSE)
  // ============================================

  {
    name: "Estradiol Oral",
    category: "Medication",
    aliases: ["estrace", "oral estrogen", "estradiol tablets", "hrt oral"],
  },
  {
    name: "Estradiol Patch",
    category: "Medication",
    aliases: [
      "vivelle-dot",
      "climara",
      "estradiol transdermal",
      "estrogen patch",
      "hrt patch",
    ],
  },
  {
    name: "Vaginal Estrogen",
    category: "Medication",
    aliases: [
      "premarin cream",
      "estrace cream",
      "vagifem",
      "imvexxy",
      "vaginal estradiol",
      "vaginal atrophy treatment",
    ],
  },
  {
    name: "Veozah (fezolinetant)",
    category: "Medication",
    aliases: ["fezolinetant", "veozah", "hot flash treatment", "nk3 antagonist menopause"],
  },
  {
    name: "Osphena (ospemifene)",
    category: "Medication",
    aliases: ["ospemifene", "osphena", "dyspareunia treatment"],
  },
  {
    name: "Progesterone (Prometrium)",
    category: "Medication",
    aliases: [
      "prometrium",
      "micronized progesterone",
      "progesterone hrt",
      "bioidentical progesterone",
    ],
  },
  {
    name: "Bijuva (estradiol/progesterone)",
    category: "Medication",
    aliases: ["bijuva", "combination hrt", "estradiol progesterone capsule"],
  },

  // ============================================
  // MEDICATIONS - THYROID / COMMON FM ENDOCRINE
  // ============================================

  {
    name: "Armour Thyroid",
    category: "Medication",
    aliases: ["desiccated thyroid", "armour thyroid", "np thyroid", "natural thyroid"],
  },
  {
    name: "Tirosint (levothyroxine gel cap)",
    category: "Medication",
    aliases: ["tirosint", "levothyroxine gelcap", "levothyroxine liquid"],
  },

  // ============================================
  // PROCEDURES - ORTHOPEDICS
  // ============================================

  {
    name: "Total Knee Replacement",
    category: "Procedure",
    aliases: ["tkr", "tka", "knee replacement", "total knee arthroplasty"],
  },
  {
    name: "Partial Knee Replacement",
    category: "Procedure",
    aliases: ["uka", "unicompartmental knee", "partial knee"],
  },
  {
    name: "Knee Revision Surgery",
    category: "Procedure",
    aliases: ["revision tka", "knee revision", "revision knee replacement"],
  },
  {
    name: "Total Hip Replacement",
    category: "Procedure",
    aliases: ["thr", "tha", "hip replacement", "total hip arthroplasty"],
  },
  {
    name: "Hip Revision Surgery",
    category: "Procedure",
    aliases: ["revision tha", "hip revision", "revision hip replacement"],
  },
  {
    name: "Hip Resurfacing",
    category: "Procedure",
    aliases: ["birmingham hip", "hip resurfacing arthroplasty"],
  },
  {
    name: "Total Shoulder Replacement",
    category: "Procedure",
    aliases: ["tsr", "tsa", "shoulder replacement", "shoulder arthroplasty"],
  },
  {
    name: "Reverse Total Shoulder Replacement",
    category: "Procedure",
    aliases: ["reverse tsa", "rtsa", "reverse shoulder"],
  },
  {
    name: "Total Ankle Replacement",
    category: "Procedure",
    aliases: ["tar", "ankle arthroplasty", "ankle replacement"],
  },
  {
    name: "ACL Reconstruction",
    category: "Procedure",
    aliases: [
      "acl repair",
      "acl surgery",
      "aclr",
      "anterior cruciate ligament",
    ],
  },
  {
    name: "PCL Reconstruction",
    category: "Procedure",
    aliases: ["pcl repair", "posterior cruciate ligament"],
  },
  {
    name: "Rotator Cuff Repair",
    category: "Procedure",
    aliases: ["shoulder repair", "rct repair", "rotator cuff surgery"],
  },
  {
    name: "Bankart Repair",
    category: "Procedure",
    aliases: ["shoulder stabilization", "labral repair shoulder"],
  },
  {
    name: "Carpal Tunnel Release",
    category: "Procedure",
    aliases: ["ctr", "carpal tunnel surgery", "carpal tunnel decompression"],
  },
  {
    name: "Cubital Tunnel Release",
    category: "Procedure",
    aliases: ["ulnar nerve release", "ulnar nerve decompression"],
  },
  {
    name: "Knee Arthroscopy",
    category: "Procedure",
    aliases: ["knee scope", "arthroscopic knee surgery"],
  },
  {
    name: "Shoulder Arthroscopy",
    category: "Procedure",
    aliases: ["shoulder scope", "arthroscopic shoulder surgery"],
  },
  {
    name: "Hip Arthroscopy",
    category: "Procedure",
    aliases: ["hip scope", "arthroscopic hip surgery", "fai surgery"],
  },
  {
    name: "Ankle Arthroscopy",
    category: "Procedure",
    aliases: ["ankle scope"],
  },
  {
    name: "Meniscectomy",
    category: "Procedure",
    aliases: ["meniscus surgery", "meniscus repair", "partial meniscectomy"],
  },
  {
    name: "Meniscus Repair",
    category: "Procedure",
    aliases: ["meniscal repair"],
  },
  {
    name: "Labral Repair",
    category: "Procedure",
    aliases: ["labrum repair", "slap repair", "hip labral repair"],
  },
  {
    name: "Achilles Tendon Repair",
    category: "Procedure",
    aliases: ["achilles repair", "achilles surgery"],
  },
  {
    name: "Bunionectomy",
    category: "Procedure",
    aliases: ["bunion surgery", "hallux valgus correction"],
  },
  {
    name: "Hammer Toe Correction",
    category: "Procedure",
    aliases: ["hammertoe surgery", "toe straightening"],
  },
  {
    name: "Plantar Fascia Release",
    category: "Procedure",
    aliases: ["plantar fasciitis surgery", "plantar fasciotomy"],
  },
  {
    name: "Osteotomy",
    category: "Procedure",
    aliases: [
      "tibial osteotomy",
      "hto",
      "high tibial osteotomy",
      "femoral osteotomy",
    ],
  },
  {
    name: "ORIF",
    category: "Procedure",
    aliases: [
      "open reduction internal fixation",
      "fracture surgery",
      "fracture fixation",
    ],
  },
  {
    name: "Hardware Removal",
    category: "Procedure",
    aliases: ["plate removal", "screw removal", "implant removal"],
  },

  // ============================================
  // PROCEDURES - GENERAL SURGERY
  // ============================================

  // Gallbladder & Biliary
  {
    name: "Cholecystectomy",
    category: "Procedure",
    aliases: [
      "gallbladder removal",
      "lap chole",
      "laparoscopic cholecystectomy",
      "open cholecystectomy",
    ],
  },
  {
    name: "Intraoperative Cholangiogram",
    category: "Procedure",
    aliases: ["ioc", "bile duct imaging"],
  },
  {
    name: "Common Bile Duct Exploration",
    category: "Procedure",
    aliases: ["cbde", "bile duct exploration", "choledochotomy"],
  },
  {
    name: "Cholecystostomy Tube",
    category: "Procedure",
    aliases: [
      "cholecystostomy",
      "gallbladder drain",
      "percutaneous cholecystostomy",
    ],
  },

  // Appendix
  {
    name: "Appendectomy",
    category: "Procedure",
    aliases: [
      "appendix removal",
      "lap appy",
      "laparoscopic appendectomy",
      "open appendectomy",
    ],
  },
  {
    name: "Interval Appendectomy",
    category: "Procedure",
    aliases: ["delayed appendectomy"],
  },

  // Hernia - Inguinal/Femoral
  {
    name: "Inguinal Hernia Repair",
    category: "Procedure",
    aliases: ["inguinal herniorrhaphy", "groin hernia repair"],
  },
  {
    name: "Open Inguinal Hernia Repair",
    category: "Procedure",
    aliases: ["lichtenstein repair", "open hernia", "mesh hernia repair"],
  },
  {
    name: "Laparoscopic Inguinal Hernia Repair",
    category: "Procedure",
    aliases: [
      "lap hernia",
      "tep",
      "tapp",
      "laparoscopic tep",
      "laparoscopic tapp",
    ],
  },
  {
    name: "Robotic Inguinal Hernia Repair",
    category: "Procedure",
    aliases: ["robotic hernia", "robotic tapp"],
  },
  {
    name: "Femoral Hernia Repair",
    category: "Procedure",
    aliases: ["femoral herniorrhaphy"],
  },
  {
    name: "Bilateral Inguinal Hernia Repair",
    category: "Procedure",
    aliases: ["bilateral hernia", "double hernia repair"],
  },
  {
    name: "Recurrent Hernia Repair",
    category: "Procedure",
    aliases: ["hernia revision", "redo hernia"],
  },

  // Hernia - Ventral/Abdominal Wall
  {
    name: "Ventral Hernia Repair",
    category: "Procedure",
    aliases: ["abdominal wall hernia", "incisional hernia repair"],
  },
  {
    name: "Umbilical Hernia Repair",
    category: "Procedure",
    aliases: ["umbilical herniorrhaphy", "belly button hernia"],
  },
  {
    name: "Incisional Hernia Repair",
    category: "Procedure",
    aliases: ["incisional herniorrhaphy", "scar hernia repair"],
  },
  {
    name: "Laparoscopic Ventral Hernia Repair",
    category: "Procedure",
    aliases: ["lap ventral hernia", "lvhr"],
  },
  {
    name: "Robotic Ventral Hernia Repair",
    category: "Procedure",
    aliases: ["robotic ventral hernia"],
  },
  {
    name: "Component Separation",
    category: "Procedure",
    aliases: [
      "abdominal wall reconstruction",
      "tar",
      "transversus abdominis release",
    ],
  },
  {
    name: "Parastomal Hernia Repair",
    category: "Procedure",
    aliases: ["ostomy hernia repair"],
  },
  {
    name: "Spigelian Hernia Repair",
    category: "Procedure",
    aliases: ["lateral ventral hernia"],
  },
  {
    name: "Epigastric Hernia Repair",
    category: "Procedure",
    aliases: ["epigastric herniorrhaphy"],
  },

  // Hernia - Hiatal/Diaphragmatic
  {
    name: "Hiatal Hernia Repair",
    category: "Procedure",
    aliases: ["hiatus hernia repair", "diaphragmatic hernia repair"],
  },
  {
    name: "Paraesophageal Hernia Repair",
    category: "Procedure",
    aliases: ["peh repair", "giant hiatal hernia"],
  },

  // Colorectal
  {
    name: "Colectomy",
    category: "Procedure",
    aliases: ["colon resection", "colon removal"],
  },
  {
    name: "Right Hemicolectomy",
    category: "Procedure",
    aliases: ["right colectomy", "ileocecectomy"],
  },
  {
    name: "Left Hemicolectomy",
    category: "Procedure",
    aliases: ["left colectomy"],
  },
  {
    name: "Sigmoid Colectomy",
    category: "Procedure",
    aliases: ["sigmoid resection", "sigmoidectomy"],
  },
  {
    name: "Total Colectomy",
    category: "Procedure",
    aliases: ["total abdominal colectomy", "tac"],
  },
  {
    name: "Proctocolectomy",
    category: "Procedure",
    aliases: ["total proctocolectomy", "colectomy with proctectomy"],
  },
  {
    name: "Low Anterior Resection",
    category: "Procedure",
    aliases: ["lar", "anterior resection", "rectal resection"],
  },
  {
    name: "Abdominoperineal Resection",
    category: "Procedure",
    aliases: ["apr", "miles procedure", "ap resection"],
  },
  {
    name: "Transanal Excision",
    category: "Procedure",
    aliases: [
      "local excision rectum",
      "transanal minimally invasive surgery",
      "tamis",
    ],
  },
  {
    name: "Hartmann Procedure",
    category: "Procedure",
    aliases: ["hartmann's procedure", "sigmoid colectomy with colostomy"],
  },
  {
    name: "Small Bowel Resection",
    category: "Procedure",
    aliases: ["bowel resection", "enterectomy", "small intestine resection"],
  },
  {
    name: "Ileocecal Resection",
    category: "Procedure",
    aliases: ["ileocecectomy"],
  },
  {
    name: "Strictureplasty",
    category: "Procedure",
    aliases: ["stricture repair", "crohn's strictureplasty"],
  },
  {
    name: "Bowel Anastomosis",
    category: "Procedure",
    aliases: ["intestinal anastomosis", "bowel reconnection"],
  },

  // Ostomy procedures
  {
    name: "Colostomy",
    category: "Procedure",
    aliases: ["colostomy creation", "end colostomy", "loop colostomy"],
  },
  {
    name: "Ileostomy",
    category: "Procedure",
    aliases: ["ileostomy creation", "end ileostomy", "loop ileostomy"],
  },
  {
    name: "Colostomy Reversal",
    category: "Procedure",
    aliases: ["colostomy takedown", "colostomy closure", "hartmann reversal"],
  },
  {
    name: "Ileostomy Reversal",
    category: "Procedure",
    aliases: ["ileostomy takedown", "ileostomy closure"],
  },
  {
    name: "Ostomy Revision",
    category: "Procedure",
    aliases: ["stoma revision", "ostomy relocation"],
  },
  {
    name: "J-Pouch Creation",
    category: "Procedure",
    aliases: [
      "ileal pouch anal anastomosis",
      "ipaa",
      "j-pouch",
      "restorative proctocolectomy",
    ],
  },

  // Foregut/Esophageal
  {
    name: "Nissen Fundoplication",
    category: "Procedure",
    aliases: [
      "fundoplication",
      "anti-reflux surgery",
      "gerd surgery",
      "lap nissen",
      "360 wrap",
    ],
  },
  {
    name: "Toupet Fundoplication",
    category: "Procedure",
    aliases: ["partial fundoplication", "270 wrap", "posterior fundoplication"],
  },
  {
    name: "Dor Fundoplication",
    category: "Procedure",
    aliases: ["anterior fundoplication"],
  },
  {
    name: "LINX Procedure",
    category: "Procedure",
    aliases: ["linx device", "magnetic sphincter augmentation", "msa"],
  },
  {
    name: "Heller Myotomy",
    category: "Procedure",
    aliases: ["esophageal myotomy", "achalasia surgery", "lap heller"],
  },
  {
    name: "POEM Procedure",
    category: "Procedure",
    aliases: ["peroral endoscopic myotomy", "poem"],
  },
  {
    name: "Esophagectomy",
    category: "Procedure",
    aliases: [
      "esophagus surgery",
      "esophageal resection",
      "ivor lewis",
      "mckeown esophagectomy",
      "transhiatal esophagectomy",
    ],
  },
  {
    name: "Gastrectomy",
    category: "Procedure",
    aliases: ["stomach removal", "gastric resection"],
  },
  {
    name: "Partial Gastrectomy",
    category: "Procedure",
    aliases: ["subtotal gastrectomy", "distal gastrectomy", "antrectomy"],
  },
  {
    name: "Total Gastrectomy",
    category: "Procedure",
    aliases: ["complete stomach removal"],
  },
  {
    name: "Gastric Wedge Resection",
    category: "Procedure",
    aliases: ["wedge gastrectomy", "gist resection"],
  },
  {
    name: "Pyloroplasty",
    category: "Procedure",
    aliases: ["pyloric surgery", "gastric outlet surgery"],
  },
  {
    name: "Vagotomy",
    category: "Procedure",
    aliases: ["truncal vagotomy", "selective vagotomy"],
  },
  {
    name: "Gastrojejunostomy",
    category: "Procedure",
    aliases: ["gastric bypass anastomosis", "gj"],
  },

  // Bariatric
  {
    name: "Bariatric Surgery",
    category: "Procedure",
    aliases: ["weight loss surgery", "metabolic surgery"],
  },
  {
    name: "Gastric Sleeve",
    category: "Procedure",
    aliases: ["sleeve gastrectomy", "vsg", "vertical sleeve gastrectomy"],
  },
  {
    name: "Gastric Bypass",
    category: "Procedure",
    aliases: ["rygb", "roux-en-y gastric bypass", "rny"],
  },
  {
    name: "Duodenal Switch",
    category: "Procedure",
    aliases: ["bpd-ds", "biliopancreatic diversion", "sadi-s", "sips"],
  },
  {
    name: "Lap Band",
    category: "Procedure",
    aliases: [
      "gastric band",
      "laparoscopic adjustable gastric banding",
      "lagb",
    ],
  },
  {
    name: "Lap Band Removal",
    category: "Procedure",
    aliases: ["gastric band removal", "band explant"],
  },
  {
    name: "Bariatric Revision Surgery",
    category: "Procedure",
    aliases: ["revision bariatric", "sleeve to bypass", "band to sleeve"],
  },
  {
    name: "Gastric Balloon",
    category: "Procedure",
    aliases: ["intragastric balloon", "orbera", "obalon"],
  },
  {
    name: "Endoscopic Sleeve Gastroplasty",
    category: "Procedure",
    aliases: ["esg", "accordion procedure", "endoscopic sleeve"],
  },

  // Hepatobiliary/Pancreas
  {
    name: "Liver Resection",
    category: "Procedure",
    aliases: ["hepatectomy", "partial hepatectomy", "liver surgery"],
  },
  {
    name: "Right Hepatectomy",
    category: "Procedure",
    aliases: ["right liver resection", "right lobectomy liver"],
  },
  {
    name: "Left Hepatectomy",
    category: "Procedure",
    aliases: ["left liver resection", "left lobectomy liver"],
  },
  {
    name: "Liver Segmentectomy",
    category: "Procedure",
    aliases: ["hepatic segmentectomy", "segment resection"],
  },
  {
    name: "Liver Ablation",
    category: "Procedure",
    aliases: ["hepatic ablation", "rfa liver", "microwave ablation liver"],
  },
  {
    name: "Pancreatectomy",
    category: "Procedure",
    aliases: ["pancreas surgery"],
  },
  {
    name: "Whipple Procedure",
    category: "Procedure",
    aliases: ["pancreaticoduodenectomy", "whipple"],
  },
  {
    name: "Distal Pancreatectomy",
    category: "Procedure",
    aliases: ["left pancreatectomy", "tail of pancreas resection"],
  },
  {
    name: "Total Pancreatectomy",
    category: "Procedure",
    aliases: ["complete pancreas removal"],
  },
  {
    name: "Pancreatic Debridement",
    category: "Procedure",
    aliases: ["necrosectomy", "pancreatic necrosectomy"],
  },
  {
    name: "Pancreatic Cyst Drainage",
    category: "Procedure",
    aliases: ["pseudocyst drainage", "cystgastrostomy"],
  },

  // Spleen/Adrenal
  {
    name: "Splenectomy",
    category: "Procedure",
    aliases: ["spleen removal", "lap splenectomy", "open splenectomy"],
  },
  {
    name: "Partial Splenectomy",
    category: "Procedure",
    aliases: ["spleen-sparing surgery"],
  },
  {
    name: "Adrenalectomy",
    category: "Procedure",
    aliases: ["adrenal gland removal", "lap adrenalectomy"],
  },
  {
    name: "Bilateral Adrenalectomy",
    category: "Procedure",
    aliases: ["double adrenalectomy"],
  },

  // Acute Care/Trauma
  {
    name: "Exploratory Laparotomy",
    category: "Procedure",
    aliases: ["ex lap", "exploratory surgery", "diagnostic laparotomy"],
  },
  {
    name: "Damage Control Laparotomy",
    category: "Procedure",
    aliases: ["damage control surgery", "dcl", "abbreviated laparotomy"],
  },
  {
    name: "Trauma Laparotomy",
    category: "Procedure",
    aliases: ["emergency laparotomy"],
  },
  {
    name: "Bowel Obstruction Surgery",
    category: "Procedure",
    aliases: ["sbo surgery", "lysis of adhesions", "adhesiolysis"],
  },
  {
    name: "Perforated Ulcer Repair",
    category: "Procedure",
    aliases: ["ulcer repair", "graham patch", "omental patch"],
  },
  {
    name: "Diverticulitis Surgery",
    category: "Procedure",
    aliases: ["sigmoid resection for diverticulitis"],
  },
  {
    name: "Mesenteric Ischemia Surgery",
    category: "Procedure",
    aliases: ["bowel ischemia surgery", "dead bowel resection"],
  },
  {
    name: "Feeding Jejunostomy",
    category: "Procedure",
    aliases: ["j-tube", "jejunostomy tube", "surgical j-tube"],
  },
  {
    name: "Gastrostomy Tube Placement",
    category: "Procedure",
    aliases: ["g-tube surgical", "open g-tube", "stamm gastrostomy"],
  },

  // Soft Tissue/Wound
  {
    name: "Incision and Drainage",
    category: "Procedure",
    aliases: ["i&d", "abscess drainage", "abscess incision"],
  },
  {
    name: "Wound Debridement",
    category: "Procedure",
    aliases: ["debridement", "surgical debridement", "wound excision"],
  },
  {
    name: "Wound VAC Placement",
    category: "Procedure",
    aliases: ["negative pressure wound therapy", "npwt placement"],
  },
  {
    name: "Skin Graft",
    category: "Procedure",
    aliases: ["stsg", "split thickness skin graft", "skin grafting"],
  },
  {
    name: "Lipoma Excision",
    category: "Procedure",
    aliases: ["lipoma removal", "fatty tumor removal"],
  },
  {
    name: "Sebaceous Cyst Excision",
    category: "Procedure",
    aliases: ["cyst removal", "epidermal cyst excision"],
  },
  {
    name: "Pilonidal Cyst Excision",
    category: "Procedure",
    aliases: ["pilonidal cystectomy", "pilonidal surgery"],
  },
  {
    name: "Hidradenitis Surgery",
    category: "Procedure",
    aliases: ["hidradenitis excision", "hs surgery"],
  },

  // ============================================
  // PROCEDURES - TRANSPLANT SURGERY
  // ============================================

  // Kidney Transplant
  {
    name: "Kidney Transplant",
    category: "Procedure",
    aliases: ["renal transplant", "kidney transplantation"],
  },
  {
    name: "Deceased Donor Kidney Transplant",
    category: "Procedure",
    aliases: ["ddkt", "cadaveric kidney transplant"],
  },
  {
    name: "Living Donor Kidney Transplant",
    category: "Procedure",
    aliases: [
      "ldkt",
      "living related kidney transplant",
      "living unrelated kidney transplant",
    ],
  },
  {
    name: "Pediatric Kidney Transplant",
    category: "Procedure",
    aliases: ["pediatric renal transplant"],
  },
  {
    name: "Kidney Retransplant",
    category: "Procedure",
    aliases: ["repeat kidney transplant", "second kidney transplant"],
  },
  {
    name: "Dual Kidney Transplant",
    category: "Procedure",
    aliases: ["dual renal transplant", "en bloc kidney transplant"],
  },
  {
    name: "Robotic Kidney Transplant",
    category: "Procedure",
    aliases: ["robotic renal transplant"],
  },
  {
    name: "Donor Nephrectomy",
    category: "Procedure",
    aliases: ["living donor nephrectomy", "donor kidney removal"],
  },
  {
    name: "Laparoscopic Donor Nephrectomy",
    category: "Procedure",
    aliases: ["lap donor nephrectomy", "minimally invasive donor nephrectomy"],
  },
  {
    name: "Robotic Donor Nephrectomy",
    category: "Procedure",
    aliases: ["robotic living donor nephrectomy"],
  },
  {
    name: "Hand-Assisted Donor Nephrectomy",
    category: "Procedure",
    aliases: ["haldn", "hand assisted laparoscopic donor nephrectomy"],
  },
  {
    name: "Kidney Transplant Nephrectomy",
    category: "Procedure",
    aliases: [
      "transplant nephrectomy",
      "allograft nephrectomy",
      "failed transplant removal",
    ],
  },
  {
    name: "Kidney Transplant Biopsy",
    category: "Procedure",
    aliases: ["renal allograft biopsy", "transplant kidney biopsy"],
  },
  {
    name: "Kidney Transplant Ureteral Reimplant",
    category: "Procedure",
    aliases: ["transplant ureteroneocystostomy revision"],
  },
  {
    name: "Transplant Renal Artery Stenosis Intervention",
    category: "Procedure",
    aliases: ["tras angioplasty", "transplant renal artery stent"],
  },
  {
    name: "Kidney Paired Donation",
    category: "Procedure",
    aliases: ["paired exchange", "kidney swap"],
  },
  {
    name: "ABO Incompatible Kidney Transplant",
    category: "Procedure",
    aliases: ["aboi transplant", "blood type incompatible transplant"],
  },
  {
    name: "Desensitization Protocol",
    category: "Procedure",
    aliases: ["antibody removal", "plasmapheresis desensitization"],
  },

  // Liver Transplant
  {
    name: "Liver Transplant",
    category: "Procedure",
    aliases: [
      "hepatic transplant",
      "liver transplantation",
      "olt",
      "orthotopic liver transplant",
    ],
  },
  {
    name: "Deceased Donor Liver Transplant",
    category: "Procedure",
    aliases: ["ddlt", "cadaveric liver transplant"],
  },
  {
    name: "Living Donor Liver Transplant",
    category: "Procedure",
    aliases: ["ldlt", "living donor hepatectomy"],
  },
  {
    name: "Split Liver Transplant",
    category: "Procedure",
    aliases: ["split graft", "liver splitting"],
  },
  {
    name: "Pediatric Liver Transplant",
    category: "Procedure",
    aliases: ["peds liver transplant"],
  },
  {
    name: "Liver Retransplant",
    category: "Procedure",
    aliases: ["repeat liver transplant", "redo liver transplant"],
  },
  {
    name: "Auxiliary Liver Transplant",
    category: "Procedure",
    aliases: ["auxiliary partial orthotopic liver transplant", "apolt"],
  },
  {
    name: "Domino Liver Transplant",
    category: "Procedure",
    aliases: ["domino procedure", "sequential liver transplant"],
  },
  {
    name: "Living Donor Right Hepatectomy",
    category: "Procedure",
    aliases: ["right lobe donation", "donor right hepatectomy"],
  },
  {
    name: "Living Donor Left Hepatectomy",
    category: "Procedure",
    aliases: ["left lobe donation", "donor left hepatectomy"],
  },
  {
    name: "Living Donor Left Lateral Segment",
    category: "Procedure",
    aliases: ["left lateral sectionectomy donor", "segments 2 and 3 donation"],
  },
  {
    name: "Liver Transplant Biopsy",
    category: "Procedure",
    aliases: ["hepatic allograft biopsy", "post-transplant liver biopsy"],
  },
  {
    name: "Biliary Complication Repair",
    category: "Procedure",
    aliases: [
      "transplant biliary stricture repair",
      "biliary leak repair transplant",
    ],
  },
  {
    name: "Hepatic Artery Thrombosis Treatment",
    category: "Procedure",
    aliases: ["hat treatment", "transplant hepatic artery revision"],
  },
  {
    name: "Portal Vein Thrombosis Treatment Transplant",
    category: "Procedure",
    aliases: ["pvt treatment transplant"],
  },
  {
    name: "ERCP Post Liver Transplant",
    category: "Procedure",
    aliases: ["transplant ercp", "biliary intervention post olt"],
  },
  {
    name: "Machine Perfusion Liver",
    category: "Procedure",
    aliases: [
      "normothermic machine perfusion",
      "hypothermic machine perfusion liver",
      "nmp",
      "hmp",
    ],
  },

  // Pancreas Transplant
  {
    name: "Pancreas Transplant",
    category: "Procedure",
    aliases: ["pancreatic transplant", "pancreas transplantation"],
  },
  {
    name: "Pancreas Transplant Alone",
    category: "Procedure",
    aliases: ["pta", "solitary pancreas transplant"],
  },
  {
    name: "Simultaneous Pancreas Kidney Transplant",
    category: "Procedure",
    aliases: ["spk", "kidney pancreas transplant", "combined pancreas kidney"],
  },
  {
    name: "Pancreas After Kidney Transplant",
    category: "Procedure",
    aliases: ["pak", "sequential pancreas transplant"],
  },
  {
    name: "Islet Cell Transplant",
    category: "Procedure",
    aliases: ["islet transplantation", "pancreatic islet infusion"],
  },
  {
    name: "Islet Autotransplant",
    category: "Procedure",
    aliases: [
      "auto islet transplant",
      "tpiat",
      "total pancreatectomy with islet autotransplant",
    ],
  },
  {
    name: "Pancreas Transplant Biopsy",
    category: "Procedure",
    aliases: ["pancreatic allograft biopsy"],
  },
  {
    name: "Pancreas Transplant Thrombectomy",
    category: "Procedure",
    aliases: [
      "transplant pancreatectomy",
      "failed pancreas transplant removal",
    ],
  },

  // Intestinal Transplant
  {
    name: "Intestinal Transplant",
    category: "Procedure",
    aliases: [
      "small bowel transplant",
      "intestine transplant",
      "isolated intestinal transplant",
    ],
  },
  {
    name: "Combined Liver Intestine Transplant",
    category: "Procedure",
    aliases: ["liver small bowel transplant"],
  },
  {
    name: "Multivisceral Transplant",
    category: "Procedure",
    aliases: ["multi-organ transplant", "cluster transplant", "mvt"],
  },
  {
    name: "Modified Multivisceral Transplant",
    category: "Procedure",
    aliases: ["modified mvt", "stomach sparing multivisceral"],
  },
  {
    name: "Intestinal Transplant Biopsy",
    category: "Procedure",
    aliases: ["allograft enteroscopy with biopsy", "small bowel graft biopsy"],
  },
  {
    name: "Intestinal Graft Enterectomy",
    category: "Procedure",
    aliases: ["failed intestinal transplant removal"],
  },

  // Heart Transplant (supplementing cardiology)
  {
    name: "Heterotopic Heart Transplant",
    category: "Procedure",
    aliases: ["piggyback heart transplant"],
  },
  {
    name: "Pediatric Heart Transplant",
    category: "Procedure",
    aliases: ["peds cardiac transplant"],
  },
  {
    name: "Heart Retransplant",
    category: "Procedure",
    aliases: ["repeat heart transplant", "cardiac retransplantation"],
  },
  {
    name: "DCD Heart Transplant",
    category: "Procedure",
    aliases: ["donation after circulatory death heart"],
  },
  {
    name: "Ex Vivo Heart Perfusion",
    category: "Procedure",
    aliases: ["ocs heart", "heart in a box", "transmedics heart"],
  },

  // Lung Transplant (supplementing pulmonology)
  {
    name: "Lobar Lung Transplant",
    category: "Procedure",
    aliases: ["lobar transplant", "lobe transplant"],
  },
  {
    name: "Lung Retransplant",
    category: "Procedure",
    aliases: ["repeat lung transplant", "pulmonary retransplantation"],
  },
  {
    name: "DCD Lung Transplant",
    category: "Procedure",
    aliases: ["donation after circulatory death lung"],
  },
  {
    name: "Lung Transplant Biopsy",
    category: "Procedure",
    aliases: ["transbronchial biopsy surveillance", "allograft lung biopsy"],
  },

  // Composite Tissue / Vascularized Composite Allotransplant
  {
    name: "Hand Transplant",
    category: "Procedure",
    aliases: ["upper extremity transplant", "vca hand"],
  },
  {
    name: "Face Transplant",
    category: "Procedure",
    aliases: ["facial transplant", "vca face"],
  },
  {
    name: "Uterus Transplant",
    category: "Procedure",
    aliases: ["uterine transplant", "womb transplant"],
  },
  {
    name: "Penile Transplant",
    category: "Procedure",
    aliases: ["penis transplant", "vca penis"],
  },
  {
    name: "Abdominal Wall Transplant",
    category: "Procedure",
    aliases: ["vca abdominal wall"],
  },
  {
    name: "Laryngeal Transplant",
    category: "Procedure",
    aliases: ["larynx transplant", "voice box transplant"],
  },

  // Donor Procurement / Organ Recovery
  {
    name: "Organ Procurement",
    category: "Procedure",
    aliases: ["organ recovery", "organ retrieval"],
  },
  {
    name: "Multi-Organ Procurement",
    category: "Procedure",
    aliases: ["multi-organ recovery", "thoracic abdominal procurement"],
  },
  {
    name: "DCD Organ Procurement",
    category: "Procedure",
    aliases: ["donation after circulatory death recovery", "dcd recovery"],
  },
  {
    name: "Kidney Procurement",
    category: "Procedure",
    aliases: ["kidney recovery", "renal procurement"],
  },
  {
    name: "Liver Procurement",
    category: "Procedure",
    aliases: ["liver recovery", "hepatic procurement"],
  },
  {
    name: "Pancreas Procurement",
    category: "Procedure",
    aliases: ["pancreas recovery"],
  },
  {
    name: "Heart Procurement",
    category: "Procedure",
    aliases: ["heart recovery", "cardiac procurement"],
  },
  {
    name: "Lung Procurement",
    category: "Procedure",
    aliases: ["lung recovery", "pulmonary procurement"],
  },
  {
    name: "Intestine Procurement",
    category: "Procedure",
    aliases: ["small bowel recovery", "intestinal procurement"],
  },
  {
    name: "Back Table Preparation",
    category: "Procedure",
    aliases: ["bench surgery", "organ preparation"],
  },

  // Transplant Vascular Access & Procedures
  {
    name: "Transplant Arterial Anastomosis",
    category: "Procedure",
    aliases: ["arterial reconstruction transplant"],
  },
  {
    name: "Transplant Venous Anastomosis",
    category: "Procedure",
    aliases: ["venous reconstruction transplant"],
  },
  {
    name: "Transplant Vascular Thrombectomy",
    category: "Procedure",
    aliases: ["graft thrombectomy"],
  },
  {
    name: "Transplant Vascular Revision",
    category: "Procedure",
    aliases: ["vascular complication repair transplant"],
  },
  {
    name: "IVC Extension Graft",
    category: "Procedure",
    aliases: ["caval extension", "piggyback technique"],
  },
  {
    name: "Portal Vein Reconstruction",
    category: "Procedure",
    aliases: ["portal vein jump graft", "portal vein thrombosis bypass"],
  },

  // Post-Transplant Complications & Procedures
  {
    name: "Transplant Wound Exploration",
    category: "Procedure",
    aliases: ["transplant re-exploration", "graft exploration"],
  },
  {
    name: "Transplant Lymphocele Drainage",
    category: "Procedure",
    aliases: ["lymphocele window", "lymphocele marsupialization"],
  },
  {
    name: "Transplant Urinoma Drainage",
    category: "Procedure",
    aliases: ["urine leak repair"],
  },
  {
    name: "Transplant Hematoma Evacuation",
    category: "Procedure",
    aliases: ["perigraft hematoma drainage"],
  },
  {
    name: "Transplant Abscess Drainage",
    category: "Procedure",
    aliases: ["perigraft abscess i&d"],
  },
  {
    name: "Incisional Hernia Repair Post Transplant",
    category: "Procedure",
    aliases: ["transplant hernia repair"],
  },

  // Transplant Immunology Procedures
  {
    name: "Plasmapheresis",
    category: "Procedure",
    aliases: ["plasma exchange", "plex", "tpe"],
  },
  {
    name: "Donor Specific Antibody Removal",
    category: "Procedure",
    aliases: ["dsa removal", "antibody mediated rejection treatment"],
  },
  {
    name: "Immunoadsorption",
    category: "Procedure",
    aliases: ["protein a column", "extracorporeal immunoadsorption"],
  },
  {
    name: "Photopheresis",
    category: "Procedure",
    aliases: ["extracorporeal photopheresis", "ecp"],
  },
  {
    name: "Thymoglobulin Infusion",
    category: "Procedure",
    aliases: ["atg infusion", "rabbit antithymocyte globulin"],
  },
  {
    name: "Rituximab Infusion Transplant",
    category: "Procedure",
    aliases: ["anti-cd20 desensitization"],
  },
  {
    name: "IVIG Infusion Transplant",
    category: "Procedure",
    aliases: ["intravenous immunoglobulin transplant"],
  },

  // ============================================
  // PROCEDURES - PEDIATRIC SURGERY
  // ============================================

  // Neonatal GI Emergencies
  {
    name: "Pyloromyotomy",
    category: "Procedure",
    aliases: [
      "pyloric stenosis surgery",
      "ramstedt pyloromyotomy",
      "fredet-ramstedt",
    ],
  },
  {
    name: "Laparoscopic Pyloromyotomy",
    category: "Procedure",
    aliases: ["lap pyloromyotomy", "minimally invasive pyloromyotomy"],
  },
  {
    name: "Open Pyloromyotomy",
    category: "Procedure",
    aliases: ["open pyloric stenosis repair"],
  },
  {
    name: "Ladd Procedure",
    category: "Procedure",
    aliases: ["ladds procedure", "malrotation surgery", "malrotation repair"],
  },
  {
    name: "Laparoscopic Ladd Procedure",
    category: "Procedure",
    aliases: ["lap ladd", "minimally invasive malrotation repair"],
  },
  {
    name: "Midgut Volvulus Surgery",
    category: "Procedure",
    aliases: ["volvulus detorsion", "volvulus reduction"],
  },
  {
    name: "Duodenal Atresia Repair",
    category: "Procedure",
    aliases: [
      "duodenoduodenostomy",
      "diamond anastomosis",
      "duodenal web excision",
    ],
  },
  {
    name: "Jejunal Atresia Repair",
    category: "Procedure",
    aliases: ["jejunal anastomosis", "small bowel atresia repair"],
  },
  {
    name: "Ileal Atresia Repair",
    category: "Procedure",
    aliases: ["ileal anastomosis"],
  },
  {
    name: "Colonic Atresia Repair",
    category: "Procedure",
    aliases: ["colon atresia surgery"],
  },
  {
    name: "Meconium Ileus Surgery",
    category: "Procedure",
    aliases: ["meconium ileus enterotomy", "meconium ileus resection"],
  },
  {
    name: "Necrotizing Enterocolitis Surgery",
    category: "Procedure",
    aliases: ["nec surgery", "nec laparotomy", "nec resection"],
  },
  {
    name: "NEC Drain Placement",
    category: "Procedure",
    aliases: ["peritoneal drain nec", "primary peritoneal drainage"],
  },
  {
    name: "Intestinal Perforation Repair Neonatal",
    category: "Procedure",
    aliases: ["spontaneous intestinal perforation", "sip surgery"],
  },
  {
    name: "Intussusception Reduction",
    category: "Procedure",
    aliases: ["air enema reduction", "contrast enema reduction"],
  },
  {
    name: "Operative Intussusception Reduction",
    category: "Procedure",
    aliases: ["surgical intussusception", "manual reduction intussusception"],
  },
  {
    name: "Intussusception Resection",
    category: "Procedure",
    aliases: ["bowel resection intussusception"],
  },
  {
    name: "Meckel Diverticulectomy",
    category: "Procedure",
    aliases: ["meckel diverticulum excision", "meckels surgery"],
  },
  {
    name: "Intestinal Duplication Cyst Excision",
    category: "Procedure",
    aliases: ["enteric duplication cyst", "alimentary duplication"],
  },

  // Abdominal Wall Defects
  {
    name: "Gastroschisis Repair",
    category: "Procedure",
    aliases: ["gastroschisis closure", "gastroschisis surgery"],
  },
  {
    name: "Primary Gastroschisis Closure",
    category: "Procedure",
    aliases: ["primary closure gastroschisis"],
  },
  {
    name: "Silo Placement Gastroschisis",
    category: "Procedure",
    aliases: ["spring loaded silo", "staged gastroschisis closure"],
  },
  {
    name: "Sutureless Gastroschisis Closure",
    category: "Procedure",
    aliases: ["plastic closure", "umbilical cord closure"],
  },
  {
    name: "Omphalocele Repair",
    category: "Procedure",
    aliases: ["omphalocele closure", "exomphalos repair"],
  },
  {
    name: "Primary Omphalocele Closure",
    category: "Procedure",
    aliases: ["primary closure omphalocele"],
  },
  {
    name: "Staged Omphalocele Closure",
    category: "Procedure",
    aliases: ["silo omphalocele", "delayed omphalocele closure"],
  },
  {
    name: "Paint and Wait Omphalocele",
    category: "Procedure",
    aliases: ["escharotic omphalocele", "conservative omphalocele management"],
  },
  {
    name: "Umbilical Hernia Repair Pediatric",
    category: "Procedure",
    aliases: ["pediatric umbilical hernia", "peds umbilical hernia"],
  },
  {
    name: "Epigastric Hernia Repair Pediatric",
    category: "Procedure",
    aliases: ["pediatric epigastric hernia"],
  },
  {
    name: "Inguinal Hernia Repair Pediatric",
    category: "Procedure",
    aliases: ["pediatric inguinal hernia", "peds hernia repair"],
  },
  {
    name: "Laparoscopic Inguinal Hernia Pediatric",
    category: "Procedure",
    aliases: ["pils", "laparoscopic peds hernia"],
  },
  {
    name: "Hydrocele Repair Pediatric",
    category: "Procedure",
    aliases: [
      "patent processus vaginalis ligation",
      "communicating hydrocele repair",
    ],
  },
  {
    name: "Prune Belly Syndrome Surgery",
    category: "Procedure",
    aliases: [
      "eagle-barrett syndrome surgery",
      "abdominal wall reconstruction pbs",
    ],
  },
  {
    name: "Bladder Exstrophy Repair",
    category: "Procedure",
    aliases: ["exstrophy closure", "complete primary repair exstrophy"],
  },
  {
    name: "Cloacal Exstrophy Repair",
    category: "Procedure",
    aliases: ["omphalocele exstrophy imperforate anus spinal defects"],
  },

  // Congenital Diaphragmatic Hernia
  {
    name: "CDH Repair",
    category: "Procedure",
    aliases: [
      "congenital diaphragmatic hernia repair",
      "bochdalek hernia repair",
    ],
  },
  {
    name: "Open CDH Repair",
    category: "Procedure",
    aliases: ["open diaphragmatic hernia repair", "subcostal cdh repair"],
  },
  {
    name: "Thoracoscopic CDH Repair",
    category: "Procedure",
    aliases: ["minimally invasive cdh", "thoracoscopic diaphragmatic hernia"],
  },
  {
    name: "CDH Patch Repair",
    category: "Procedure",
    aliases: ["gore-tex patch cdh", "diaphragm patch"],
  },
  {
    name: "Morgagni Hernia Repair",
    category: "Procedure",
    aliases: ["anterior diaphragmatic hernia repair"],
  },
  {
    name: "Eventration of Diaphragm Repair",
    category: "Procedure",
    aliases: ["diaphragm plication pediatric"],
  },
  {
    name: "ECMO for CDH",
    category: "Procedure",
    aliases: ["ecmo cdh", "extracorporeal support cdh"],
  },

  // Esophageal Atresia / Tracheoesophageal Fistula
  {
    name: "TEF Repair",
    category: "Procedure",
    aliases: ["tracheoesophageal fistula repair", "tef ligation"],
  },
  {
    name: "Esophageal Atresia Repair",
    category: "Procedure",
    aliases: ["ea repair", "esophageal anastomosis"],
  },
  {
    name: "EA/TEF Repair",
    category: "Procedure",
    aliases: [
      "esophageal atresia tracheoesophageal fistula repair",
      "gross type c repair",
    ],
  },
  {
    name: "Thoracoscopic TEF Repair",
    category: "Procedure",
    aliases: ["minimally invasive tef repair"],
  },
  {
    name: "Open TEF Repair",
    category: "Procedure",
    aliases: ["thoracotomy tef repair", "extrapleural tef repair"],
  },
  {
    name: "Long Gap Esophageal Atresia Repair",
    category: "Procedure",
    aliases: [
      "foker process",
      "delayed primary anastomosis",
      "esophageal lengthening",
    ],
  },
  {
    name: "Esophageal Replacement",
    category: "Procedure",
    aliases: [
      "gastric pull-up",
      "colon interposition",
      "jejunal interposition",
    ],
  },
  {
    name: "Gastric Transposition",
    category: "Procedure",
    aliases: ["gastric pull-up esophageal replacement"],
  },
  {
    name: "Colonic Interposition",
    category: "Procedure",
    aliases: ["colon interposition esophagus"],
  },
  {
    name: "H-Type TEF Repair",
    category: "Procedure",
    aliases: ["isolated tef repair", "h-fistula repair"],
  },
  {
    name: "Recurrent TEF Repair",
    category: "Procedure",
    aliases: ["redo tef repair", "recurrent fistula repair"],
  },
  {
    name: "Esophageal Stricture Dilation Pediatric",
    category: "Procedure",
    aliases: ["anastomotic stricture dilation", "peds esophageal dilation"],
  },

  // Biliary Atresia & Choledochal Cyst
  {
    name: "Kasai Portoenterostomy",
    category: "Procedure",
    aliases: [
      "kasai procedure",
      "hepatoportoenterostomy",
      "biliary atresia surgery",
    ],
  },
  {
    name: "Revision Kasai",
    category: "Procedure",
    aliases: ["redo portoenterostomy", "revision portoenterostomy"],
  },
  {
    name: "Choledochal Cyst Excision",
    category: "Procedure",
    aliases: ["choledochal cyst resection", "hepaticojejunostomy"],
  },
  {
    name: "Laparoscopic Choledochal Cyst Excision",
    category: "Procedure",
    aliases: ["lap choledochal cyst"],
  },
  {
    name: "Gallbladder Dyskinesia Surgery Pediatric",
    category: "Procedure",
    aliases: ["pediatric cholecystectomy"],
  },

  // Hirschsprung Disease
  {
    name: "Hirschsprung Pull-Through",
    category: "Procedure",
    aliases: ["hirschsprung surgery", "pull-through procedure"],
  },
  {
    name: "Swenson Pull-Through",
    category: "Procedure",
    aliases: ["swenson procedure"],
  },
  {
    name: "Duhamel Pull-Through",
    category: "Procedure",
    aliases: ["duhamel procedure", "retrorectal pull-through"],
  },
  {
    name: "Soave Pull-Through",
    category: "Procedure",
    aliases: ["soave procedure", "endorectal pull-through"],
  },
  {
    name: "Transanal Pull-Through",
    category: "Procedure",
    aliases: ["transanal endorectal pull-through", "de la torre procedure"],
  },
  {
    name: "Laparoscopic Pull-Through",
    category: "Procedure",
    aliases: ["laparoscopic assisted pull-through"],
  },
  {
    name: "One-Stage Pull-Through",
    category: "Procedure",
    aliases: ["primary pull-through"],
  },
  {
    name: "Staged Pull-Through",
    category: "Procedure",
    aliases: ["leveling colostomy", "staged hirschsprung repair"],
  },
  {
    name: "Hirschsprung Colostomy",
    category: "Procedure",
    aliases: ["leveling colostomy hirschsprung"],
  },
  {
    name: "Hirschsprung Colostomy Closure",
    category: "Procedure",
    aliases: ["colostomy takedown hirschsprung"],
  },
  {
    name: "Total Colonic Hirschsprung Surgery",
    category: "Procedure",
    aliases: ["total colonic aganglionosis", "martin procedure"],
  },
  {
    name: "Rectal Biopsy Hirschsprung",
    category: "Procedure",
    aliases: ["suction rectal biopsy", "full thickness rectal biopsy"],
  },
  {
    name: "Redo Pull-Through",
    category: "Procedure",
    aliases: ["revision pull-through", "failed pull-through repair"],
  },

  // Anorectal Malformations
  {
    name: "PSARP",
    category: "Procedure",
    aliases: [
      "posterior sagittal anorectoplasty",
      "pena procedure",
      "imperforate anus repair",
    ],
  },
  {
    name: "Laparoscopic Assisted PSARP",
    category: "Procedure",
    aliases: ["laparoscopic anorectoplasty", "larsarp"],
  },
  {
    name: "ASARP",
    category: "Procedure",
    aliases: ["anterior sagittal anorectoplasty"],
  },
  {
    name: "Perineal Anoplasty",
    category: "Procedure",
    aliases: ["cutback anoplasty", "low imperforate anus repair"],
  },
  {
    name: "Anoplasty for Vestibular Fistula",
    category: "Procedure",
    aliases: ["vestibular fistula repair"],
  },
  {
    name: "Colostomy for Imperforate Anus",
    category: "Procedure",
    aliases: ["divided sigmoid colostomy", "arm colostomy"],
  },
  {
    name: "Colostomy Closure ARM",
    category: "Procedure",
    aliases: ["stoma closure arm"],
  },
  {
    name: "Cloaca Repair",
    category: "Procedure",
    aliases: ["cloacal malformation repair", "urogenital mobilization"],
  },
  {
    name: "Total Urogenital Mobilization",
    category: "Procedure",
    aliases: ["tum", "posterior sagittal approach cloaca"],
  },
  {
    name: "Bowel Management Program",
    category: "Procedure",
    aliases: ["bmp", "fecal incontinence management"],
  },
  {
    name: "ACE Procedure Pediatric",
    category: "Procedure",
    aliases: ["antegrade continence enema", "malone procedure"],
  },
  {
    name: "Anal Stenosis Dilation Pediatric",
    category: "Procedure",
    aliases: ["anal dilation", "hegar dilation"],
  },

  // Congenital Lung Lesions
  {
    name: "CPAM Resection",
    category: "Procedure",
    aliases: [
      "ccam resection",
      "congenital pulmonary airway malformation",
      "cystic adenomatoid malformation",
    ],
  },
  {
    name: "Pulmonary Sequestration Resection",
    category: "Procedure",
    aliases: ["sequestration excision", "bps resection"],
  },
  {
    name: "Congenital Lobar Emphysema Surgery",
    category: "Procedure",
    aliases: ["cle lobectomy", "congenital lobar overinflation"],
  },
  {
    name: "Bronchogenic Cyst Excision",
    category: "Procedure",
    aliases: ["bronchogenic cyst resection"],
  },
  {
    name: "Thoracoscopic Lung Resection Pediatric",
    category: "Procedure",
    aliases: ["vats lobectomy pediatric", "thoracoscopic cpam"],
  },
  {
    name: "Congenital Lung Lesion Lobectomy",
    category: "Procedure",
    aliases: ["pediatric lobectomy"],
  },

  // Pediatric Oncology Surgery
  {
    name: "Wilms Tumor Nephrectomy",
    category: "Procedure",
    aliases: ["wilms tumor resection", "nephroblastoma surgery"],
  },
  {
    name: "Bilateral Wilms Tumor Surgery",
    category: "Procedure",
    aliases: ["bilateral nephroblastoma", "nephron sparing wilms"],
  },
  {
    name: "Neuroblastoma Resection",
    category: "Procedure",
    aliases: ["neuroblastoma surgery", "adrenal neuroblastoma excision"],
  },
  {
    name: "Hepatoblastoma Resection",
    category: "Procedure",
    aliases: ["hepatoblastoma surgery", "pediatric liver tumor resection"],
  },
  {
    name: "Teratoma Excision",
    category: "Procedure",
    aliases: [
      "sacrococcygeal teratoma resection",
      "sct excision",
      "germ cell tumor excision",
    ],
  },
  {
    name: "Ovarian Tumor Excision Pediatric",
    category: "Procedure",
    aliases: ["pediatric ovarian cystectomy", "pediatric oophorectomy"],
  },
  {
    name: "Testicular Tumor Excision Pediatric",
    category: "Procedure",
    aliases: ["pediatric orchiectomy", "testicular teratoma"],
  },
  {
    name: "Rhabdomyosarcoma Resection",
    category: "Procedure",
    aliases: ["rhabdo resection", "rms surgery"],
  },
  {
    name: "Lymphoma Biopsy Pediatric",
    category: "Procedure",
    aliases: ["pediatric lymph node biopsy"],
  },
  {
    name: "Central Line Placement Pediatric Oncology",
    category: "Procedure",
    aliases: ["port placement peds", "broviac placement", "hickman placement"],
  },
  {
    name: "Tumor Biopsy Pediatric",
    category: "Procedure",
    aliases: ["pediatric mass biopsy"],
  },

  // Pediatric Foregut & Motility
  {
    name: "Fundoplication Pediatric",
    category: "Procedure",
    aliases: ["nissen pediatric", "pediatric reflux surgery"],
  },
  {
    name: "Laparoscopic Fundoplication Pediatric",
    category: "Procedure",
    aliases: ["lap nissen pediatric"],
  },
  {
    name: "Gastrostomy Tube Pediatric",
    category: "Procedure",
    aliases: ["g-tube pediatric", "peg tube pediatric", "stamm gastrostomy"],
  },
  {
    name: "Laparoscopic Gastrostomy Pediatric",
    category: "Procedure",
    aliases: ["lap g-tube", "laparoscopic stamm"],
  },
  {
    name: "Gastrojejunostomy Tube Pediatric",
    category: "Procedure",
    aliases: ["gj tube pediatric", "transgastric jejunostomy"],
  },
  {
    name: "Gastrostomy Tube Change Pediatric",
    category: "Procedure",
    aliases: ["g-tube change peds", "mickey button change"],
  },
  {
    name: "Pyloroplasty Pediatric",
    category: "Procedure",
    aliases: ["heineke-mikulicz pediatric"],
  },
  {
    name: "Gastric Emptying Procedure Pediatric",
    category: "Procedure",
    aliases: ["pyloroplasty with fundoplication"],
  },

  // Pediatric Spleen & Vascular
  {
    name: "Splenectomy Pediatric",
    category: "Procedure",
    aliases: ["pediatric splenectomy", "laparoscopic splenectomy pediatric"],
  },
  {
    name: "Partial Splenectomy Pediatric",
    category: "Procedure",
    aliases: ["spleen preserving surgery"],
  },
  {
    name: "Splenic Cyst Excision Pediatric",
    category: "Procedure",
    aliases: ["pediatric splenic cyst"],
  },
  {
    name: "Kasabach-Merritt Syndrome Surgery",
    category: "Procedure",
    aliases: ["hemangioma resection"],
  },
  {
    name: "Vascular Malformation Excision Pediatric",
    category: "Procedure",
    aliases: [
      "lymphatic malformation excision",
      "venous malformation excision",
    ],
  },
  {
    name: "Lymphatic Malformation Sclerotherapy",
    category: "Procedure",
    aliases: ["cystic hygroma sclerotherapy", "ok-432 injection"],
  },

  // Other Pediatric General Surgery
  {
    name: "Appendectomy Pediatric",
    category: "Procedure",
    aliases: ["pediatric appendectomy", "peds appy"],
  },
  {
    name: "Laparoscopic Appendectomy Pediatric",
    category: "Procedure",
    aliases: ["lap appy pediatric"],
  },
  {
    name: "Perforated Appendicitis Surgery Pediatric",
    category: "Procedure",
    aliases: ["complicated appendicitis peds"],
  },
  {
    name: "Interval Appendectomy Pediatric",
    category: "Procedure",
    aliases: ["delayed appendectomy peds"],
  },
  {
    name: "Cholecystectomy Pediatric",
    category: "Procedure",
    aliases: ["pediatric cholecystectomy", "peds lap chole"],
  },
  {
    name: "Ovarian Torsion Surgery Pediatric",
    category: "Procedure",
    aliases: ["pediatric ovarian detorsion", "oophoropexy pediatric"],
  },
  {
    name: "Testicular Torsion Surgery Pediatric",
    category: "Procedure",
    aliases: ["pediatric testicular detorsion", "orchiopexy torsion"],
  },
  {
    name: "Inguinal Exploration Pediatric",
    category: "Procedure",
    aliases: ["groin exploration peds"],
  },
  {
    name: "Thyroglossal Duct Cyst Excision",
    category: "Procedure",
    aliases: ["sistrunk procedure", "tgdc excision"],
  },
  {
    name: "Branchial Cleft Cyst Excision",
    category: "Procedure",
    aliases: ["branchial cleft anomaly excision", "branchial fistula excision"],
  },
  {
    name: "Dermoid Cyst Excision Pediatric",
    category: "Procedure",
    aliases: ["pediatric dermoid", "nasal dermoid excision"],
  },
  {
    name: "Pilonidal Disease Surgery Pediatric",
    category: "Procedure",
    aliases: ["pediatric pilonidal cyst"],
  },
  {
    name: "Rectal Prolapse Surgery Pediatric",
    category: "Procedure",
    aliases: ["pediatric rectal prolapse", "injection sclerotherapy prolapse"],
  },
  {
    name: "Foreign Body Removal Pediatric",
    category: "Procedure",
    aliases: ["esophageal foreign body peds", "coin removal"],
  },
  {
    name: "Diagnostic Laparoscopy Pediatric",
    category: "Procedure",
    aliases: ["peds diagnostic lap"],
  },
  {
    name: "Exploratory Laparotomy Pediatric",
    category: "Procedure",
    aliases: ["peds ex lap"],
  },

  // Neonatal ECMO & Critical Care Procedures
  {
    name: "ECMO Cannulation Neonatal",
    category: "Procedure",
    aliases: ["neonatal ecmo", "ecmo for mash", "newborn ecmo"],
  },
  {
    name: "ECMO Decannulation Neonatal",
    category: "Procedure",
    aliases: ["neonatal ecmo removal"],
  },
  {
    name: "Carotid Reconstruction Post-ECMO",
    category: "Procedure",
    aliases: ["carotid repair ecmo"],
  },
  {
    name: "EXIT Procedure",
    category: "Procedure",
    aliases: ["ex utero intrapartum treatment", "exit to airway"],
  },

  // ============================================
  // PROCEDURES - BREAST SURGERY
  // ============================================

  // Mastectomy variants
  {
    name: "Mastectomy",
    category: "Procedure",
    aliases: ["breast removal", "simple mastectomy", "total mastectomy"],
  },
  {
    name: "Skin-Sparing Mastectomy",
    category: "Procedure",
    aliases: ["ssm", "skin sparing"],
  },
  {
    name: "Nipple-Sparing Mastectomy",
    category: "Procedure",
    aliases: ["nsm", "nipple sparing", "nipple-preserving mastectomy"],
  },
  {
    name: "Modified Radical Mastectomy",
    category: "Procedure",
    aliases: ["mrm", "radical mastectomy"],
  },
  {
    name: "Double Mastectomy",
    category: "Procedure",
    aliases: ["bilateral mastectomy", "bilateral simple mastectomy"],
  },
  {
    name: "Prophylactic Mastectomy",
    category: "Procedure",
    aliases: [
      "preventive mastectomy",
      "risk-reducing mastectomy",
      "brca mastectomy",
    ],
  },
  {
    name: "Completion Mastectomy",
    category: "Procedure",
    aliases: ["completion surgery"],
  },

  // Breast conserving surgery
  {
    name: "Lumpectomy",
    category: "Procedure",
    aliases: [
      "breast conserving surgery",
      "partial mastectomy",
      "wide local excision",
      "bcs",
      "segmental mastectomy",
    ],
  },
  {
    name: "Oncoplastic Lumpectomy",
    category: "Procedure",
    aliases: ["oncoplastic surgery", "oncoplastic breast surgery"],
  },
  {
    name: "Re-excision Lumpectomy",
    category: "Procedure",
    aliases: ["re-excision", "margin re-excision", "positive margin surgery"],
  },

  // Breast biopsy
  {
    name: "Breast Biopsy",
    category: "Procedure",
    aliases: ["breast tissue biopsy"],
  },
  {
    name: "Stereotactic Breast Biopsy",
    category: "Procedure",
    aliases: [
      "stereotactic biopsy",
      "mammogram-guided biopsy",
      "stereotactic core biopsy",
    ],
  },
  {
    name: "Ultrasound-Guided Breast Biopsy",
    category: "Procedure",
    aliases: [
      "us-guided biopsy",
      "ultrasound breast biopsy",
      "sono-guided biopsy",
    ],
  },
  {
    name: "MRI-Guided Breast Biopsy",
    category: "Procedure",
    aliases: ["mri breast biopsy", "mri-guided core biopsy"],
  },
  {
    name: "Excisional Breast Biopsy",
    category: "Procedure",
    aliases: ["surgical breast biopsy", "open biopsy breast"],
  },
  {
    name: "Wire Localization Biopsy",
    category: "Procedure",
    aliases: ["needle localization", "wire-loc", "wire localized excision"],
  },

  // Lymph node procedures
  {
    name: "Sentinel Lymph Node Biopsy",
    category: "Procedure",
    aliases: ["slnb", "sentinel node biopsy", "sentinel node"],
  },
  {
    name: "Axillary Lymph Node Dissection",
    category: "Procedure",
    aliases: [
      "alnd",
      "axillary dissection",
      "axillary clearance",
      "level i/ii dissection",
    ],
  },
  {
    name: "Targeted Axillary Dissection",
    category: "Procedure",
    aliases: ["tad", "targeted axillary"],
  },

  // Reconstruction - implant based
  {
    name: "Breast Reconstruction",
    category: "Procedure",
    aliases: ["breast implant reconstruction", "implant reconstruction"],
  },
  {
    name: "Tissue Expander Placement",
    category: "Procedure",
    aliases: [
      "expander placement",
      "tissue expander insertion",
      "te placement",
    ],
  },
  {
    name: "Tissue Expander Exchange",
    category: "Procedure",
    aliases: [
      "expander to implant",
      "te exchange",
      "second stage reconstruction",
    ],
  },
  {
    name: "Direct-to-Implant Reconstruction",
    category: "Procedure",
    aliases: [
      "dti reconstruction",
      "immediate implant reconstruction",
      "one-stage reconstruction",
    ],
  },
  {
    name: "Implant Exchange",
    category: "Procedure",
    aliases: [
      "implant revision",
      "implant replacement",
      "breast implant exchange",
    ],
  },
  {
    name: "Capsulectomy",
    category: "Procedure",
    aliases: [
      "capsule removal",
      "implant capsule removal",
      "en bloc capsulectomy",
    ],
  },
  {
    name: "Implant Removal",
    category: "Procedure",
    aliases: ["explant", "breast implant removal", "implant explantation"],
  },

  // Reconstruction - autologous/flap
  {
    name: "DIEP Flap Reconstruction",
    category: "Procedure",
    aliases: ["diep flap", "deep inferior epigastric perforator", "diep"],
  },
  {
    name: "TRAM Flap Reconstruction",
    category: "Procedure",
    aliases: [
      "tram flap",
      "transverse rectus abdominis",
      "pedicled tram",
      "free tram",
    ],
  },
  {
    name: "Latissimus Dorsi Flap",
    category: "Procedure",
    aliases: ["lat flap", "ld flap", "latissimus flap"],
  },
  {
    name: "SGAP Flap",
    category: "Procedure",
    aliases: ["superior gluteal artery perforator", "sgap reconstruction"],
  },
  {
    name: "TUG Flap",
    category: "Procedure",
    aliases: [
      "transverse upper gracilis",
      "tug reconstruction",
      "inner thigh flap",
    ],
  },
  {
    name: "Fat Grafting to Breast",
    category: "Procedure",
    aliases: [
      "breast fat transfer",
      "autologous fat transfer",
      "lipofilling breast",
      "fat injection breast",
    ],
  },

  // Revision and aesthetic
  {
    name: "Nipple Reconstruction",
    category: "Procedure",
    aliases: ["nipple creation", "nipple areola reconstruction"],
  },
  {
    name: "Areola Tattooing",
    category: "Procedure",
    aliases: [
      "nipple tattooing",
      "3d nipple tattoo",
      "areola micropigmentation",
    ],
  },
  {
    name: "Breast Revision Surgery",
    category: "Procedure",
    aliases: ["breast revision", "reconstruction revision"],
  },
  {
    name: "Symmetry Procedure",
    category: "Procedure",
    aliases: [
      "contralateral symmetry",
      "breast symmetry surgery",
      "balancing procedure",
    ],
  },
  {
    name: "Breast Reduction",
    category: "Procedure",
    aliases: ["reduction mammoplasty", "breast reduction surgery"],
  },
  {
    name: "Breast Lift",
    category: "Procedure",
    aliases: ["mastopexy", "breast lift surgery"],
  },
  {
    name: "Breast Augmentation",
    category: "Procedure",
    aliases: [
      "augmentation mammoplasty",
      "breast implants",
      "breast enlargement",
    ],
  },

  // Male breast
  {
    name: "Gynecomastia Surgery",
    category: "Procedure",
    aliases: [
      "male breast reduction",
      "gynecomastia excision",
      "male chest contouring",
    ],
  },

  // ============================================
  // PROCEDURES - UROLOGY
  // ============================================

  // Prostate - Cancer
  {
    name: "Prostatectomy",
    category: "Procedure",
    aliases: ["prostate removal", "prostate surgery"],
  },
  {
    name: "Radical Prostatectomy",
    category: "Procedure",
    aliases: ["radical prostate removal", "rp"],
  },
  {
    name: "Open Radical Prostatectomy",
    category: "Procedure",
    aliases: ["open prostatectomy", "retropubic prostatectomy"],
  },
  {
    name: "Laparoscopic Prostatectomy",
    category: "Procedure",
    aliases: ["lap prostatectomy", "laparoscopic radical prostatectomy"],
  },
  {
    name: "Robotic Prostatectomy",
    category: "Procedure",
    aliases: [
      "rarp",
      "robotic assisted radical prostatectomy",
      "davinci prostatectomy",
    ],
  },
  {
    name: "Nerve-Sparing Prostatectomy",
    category: "Procedure",
    aliases: ["nerve sparing", "potency sparing prostatectomy"],
  },
  {
    name: "Salvage Prostatectomy",
    category: "Procedure",
    aliases: ["salvage radical prostatectomy"],
  },
  {
    name: "Pelvic Lymph Node Dissection",
    category: "Procedure",
    aliases: ["plnd", "prostate lymphadenectomy"],
  },
  {
    name: "Prostate Biopsy",
    category: "Procedure",
    aliases: ["prostate bx", "trus biopsy", "transrectal ultrasound biopsy"],
  },
  {
    name: "MRI Fusion Prostate Biopsy",
    category: "Procedure",
    aliases: [
      "fusion biopsy",
      "targeted prostate biopsy",
      "mri targeted biopsy",
    ],
  },
  {
    name: "Transperineal Prostate Biopsy",
    category: "Procedure",
    aliases: ["tp biopsy", "perineal biopsy"],
  },
  {
    name: "Prostate Focal Therapy",
    category: "Procedure",
    aliases: ["focal therapy prostate", "hemiablation"],
  },
  {
    name: "Prostate Cryotherapy",
    category: "Procedure",
    aliases: ["prostate cryoablation", "cryo prostate"],
  },
  {
    name: "Prostate HIFU",
    category: "Procedure",
    aliases: ["high intensity focused ultrasound prostate", "hifu ablation"],
  },

  // Prostate - BPH
  {
    name: "TURP",
    category: "Procedure",
    aliases: ["transurethral resection of prostate", "prostate resection"],
  },
  {
    name: "Bipolar TURP",
    category: "Procedure",
    aliases: ["plasmakinetic turp", "bipolar resection"],
  },
  {
    name: "Greenlight Laser",
    category: "Procedure",
    aliases: [
      "pvp",
      "photoselective vaporization",
      "laser prostate",
      "greenlight pvp",
    ],
  },
  {
    name: "Holmium Laser Enucleation",
    category: "Procedure",
    aliases: ["holep", "holmium laser prostatectomy"],
  },
  {
    name: "Thulium Laser Enucleation",
    category: "Procedure",
    aliases: ["thulep", "thulium laser prostatectomy"],
  },
  {
    name: "UroLift",
    category: "Procedure",
    aliases: ["prostatic urethral lift", "pul"],
  },
  {
    name: "Rezum",
    category: "Procedure",
    aliases: [
      "water vapor therapy",
      "steam treatment prostate",
      "convective water vapor",
    ],
  },
  {
    name: "iTind",
    category: "Procedure",
    aliases: ["temporarily implanted nitinol device"],
  },
  {
    name: "Aquablation",
    category: "Procedure",
    aliases: ["aquabeam", "robotic waterjet ablation"],
  },
  {
    name: "Simple Prostatectomy",
    category: "Procedure",
    aliases: [
      "open simple prostatectomy",
      "suprapubic prostatectomy",
      "robotic simple prostatectomy",
    ],
  },
  {
    name: "TUIP",
    category: "Procedure",
    aliases: ["transurethral incision of prostate", "bladder neck incision"],
  },
  {
    name: "Prostate Artery Embolization",
    category: "Procedure",
    aliases: ["pae", "prostatic artery embolization"],
  },

  // Kidney - Oncology
  { name: "Nephrectomy", category: "Procedure", aliases: ["kidney removal"] },
  {
    name: "Radical Nephrectomy",
    category: "Procedure",
    aliases: ["complete kidney removal", "radical kidney removal"],
  },
  {
    name: "Open Radical Nephrectomy",
    category: "Procedure",
    aliases: ["open nephrectomy"],
  },
  {
    name: "Laparoscopic Radical Nephrectomy",
    category: "Procedure",
    aliases: ["lap nephrectomy", "laparoscopic kidney removal"],
  },
  {
    name: "Robotic Radical Nephrectomy",
    category: "Procedure",
    aliases: ["robotic nephrectomy", "robotic kidney removal"],
  },
  {
    name: "Partial Nephrectomy",
    category: "Procedure",
    aliases: ["nephron-sparing surgery", "kidney-sparing surgery"],
  },
  {
    name: "Robotic Partial Nephrectomy",
    category: "Procedure",
    aliases: ["robotic partial", "robotic nephron sparing"],
  },
  {
    name: "Laparoscopic Partial Nephrectomy",
    category: "Procedure",
    aliases: ["lap partial nephrectomy"],
  },
  {
    name: "Nephroureterectomy",
    category: "Procedure",
    aliases: ["kidney and ureter removal", "radical nephroureterectomy"],
  },
  {
    name: "Renal Mass Biopsy",
    category: "Procedure",
    aliases: ["kidney biopsy", "kidney mass biopsy", "renal biopsy"],
  },
  {
    name: "Renal Cryoablation",
    category: "Procedure",
    aliases: [
      "kidney cryotherapy",
      "renal cryo",
      "percutaneous renal cryoablation",
    ],
  },
  {
    name: "Renal Radiofrequency Ablation",
    category: "Procedure",
    aliases: ["renal rfa", "kidney rfa", "kidney ablation"],
  },
  {
    name: "Retroperitoneal Lymph Node Dissection",
    category: "Procedure",
    aliases: ["rplnd", "retroperitoneal dissection"],
  },
  {
    name: "Adrenalectomy",
    category: "Procedure",
    aliases: [
      "adrenal gland removal",
      "laparoscopic adrenalectomy",
      "robotic adrenalectomy",
    ],
  },

  // Kidney - Stones
  {
    name: "Ureteroscopy",
    category: "Procedure",
    aliases: ["urs", "ureteral scope", "ureteroscopic stone removal"],
  },
  {
    name: "Flexible Ureteroscopy",
    category: "Procedure",
    aliases: ["furs", "flexible urs", "rirs"],
  },
  {
    name: "Ureteroscopy with Laser Lithotripsy",
    category: "Procedure",
    aliases: ["urs laser", "holmium laser lithotripsy", "ureteroscopic laser"],
  },
  {
    name: "ESWL",
    category: "Procedure",
    aliases: [
      "extracorporeal shock wave lithotripsy",
      "shock wave lithotripsy",
      "lithotripsy",
    ],
  },
  {
    name: "PCNL",
    category: "Procedure",
    aliases: [
      "percutaneous nephrolithotomy",
      "perc nephro",
      "percutaneous stone removal",
    ],
  },
  {
    name: "Mini-PCNL",
    category: "Procedure",
    aliases: ["mini perc", "miniperc", "mini percutaneous nephrolithotomy"],
  },
  {
    name: "Ultra-Mini PCNL",
    category: "Procedure",
    aliases: ["ump", "ultra mini perc"],
  },
  { name: "Tubeless PCNL", category: "Procedure", aliases: ["tubeless perc"] },
  {
    name: "Anatrophic Nephrolithotomy",
    category: "Procedure",
    aliases: ["staghorn stone surgery", "open stone surgery"],
  },
  {
    name: "Ureteral Stent Placement",
    category: "Procedure",
    aliases: ["double j stent", "ureteral stent", "dj stent", "jj stent"],
  },
  {
    name: "Ureteral Stent Removal",
    category: "Procedure",
    aliases: ["stent removal", "stent pull"],
  },
  {
    name: "Nephrostomy Tube Placement",
    category: "Procedure",
    aliases: ["pcn", "percutaneous nephrostomy", "nephrostomy"],
  },
  {
    name: "Nephrostomy Tube Exchange",
    category: "Procedure",
    aliases: ["nephrostomy change", "pcn exchange"],
  },

  // Bladder - Oncology
  { name: "Cystectomy", category: "Procedure", aliases: ["bladder removal"] },
  {
    name: "Radical Cystectomy",
    category: "Procedure",
    aliases: ["complete bladder removal", "radical bladder removal"],
  },
  {
    name: "Robotic Radical Cystectomy",
    category: "Procedure",
    aliases: ["robotic cystectomy"],
  },
  {
    name: "Open Radical Cystectomy",
    category: "Procedure",
    aliases: ["open cystectomy"],
  },
  {
    name: "Partial Cystectomy",
    category: "Procedure",
    aliases: ["partial bladder removal", "segmental cystectomy"],
  },
  {
    name: "Cystoscopy",
    category: "Procedure",
    aliases: ["bladder scope", "cysto", "cystourethroscopy"],
  },
  {
    name: "Flexible Cystoscopy",
    category: "Procedure",
    aliases: ["flex cysto", "office cystoscopy"],
  },
  { name: "Rigid Cystoscopy", category: "Procedure", aliases: ["rigid cysto"] },
  {
    name: "Blue Light Cystoscopy",
    category: "Procedure",
    aliases: ["cysview", "fluorescence cystoscopy", "photodynamic cystoscopy"],
  },
  {
    name: "TURBT",
    category: "Procedure",
    aliases: [
      "transurethral resection of bladder tumor",
      "bladder tumor resection",
      "turb",
    ],
  },
  {
    name: "Blue Light TURBT",
    category: "Procedure",
    aliases: ["cysview turbt", "fluorescence turbt"],
  },
  {
    name: "Repeat TURBT",
    category: "Procedure",
    aliases: ["re-turbt", "restaging turbt"],
  },
  {
    name: "En Bloc TURBT",
    category: "Procedure",
    aliases: ["en bloc resection"],
  },
  {
    name: "BCG Instillation",
    category: "Procedure",
    aliases: ["bcg therapy", "intravesical bcg", "bladder bcg"],
  },
  {
    name: "Intravesical Chemotherapy",
    category: "Procedure",
    aliases: [
      "bladder chemo",
      "mitomycin instillation",
      "gemcitabine instillation",
    ],
  },
  {
    name: "Bladder Biopsy",
    category: "Procedure",
    aliases: ["cystoscopy with biopsy", "bladder tissue biopsy"],
  },

  // Urinary Diversion
  {
    name: "Ileal Conduit",
    category: "Procedure",
    aliases: ["urostomy", "ileal loop", "bricker"],
  },
  {
    name: "Neobladder",
    category: "Procedure",
    aliases: [
      "orthotopic neobladder",
      "studer neobladder",
      "bladder reconstruction",
    ],
  },
  {
    name: "Indiana Pouch",
    category: "Procedure",
    aliases: ["continent cutaneous diversion", "continent pouch"],
  },
  {
    name: "Continent Urinary Diversion",
    category: "Procedure",
    aliases: ["continent diversion"],
  },
  {
    name: "Urostomy Revision",
    category: "Procedure",
    aliases: ["ileal conduit revision", "stoma revision"],
  },

  // Bladder - Functional/Reconstruction
  {
    name: "Bladder Augmentation",
    category: "Procedure",
    aliases: ["augmentation cystoplasty", "enterocystoplasty"],
  },
  {
    name: "Bladder Neck Reconstruction",
    category: "Procedure",
    aliases: ["bnr", "bladder neck surgery"],
  },
  {
    name: "Bladder Diverticulectomy",
    category: "Procedure",
    aliases: ["diverticulum removal"],
  },
  {
    name: "Vesicovaginal Fistula Repair",
    category: "Procedure",
    aliases: ["vvf repair", "bladder vaginal fistula"],
  },
  {
    name: "Enterovesical Fistula Repair",
    category: "Procedure",
    aliases: ["colovesical fistula repair", "bladder bowel fistula"],
  },

  // Incontinence - Male
  {
    name: "Artificial Urinary Sphincter",
    category: "Procedure",
    aliases: ["aus", "artificial sphincter", "ams 800"],
  },
  {
    name: "AUS Revision",
    category: "Procedure",
    aliases: ["artificial sphincter revision", "aus replacement"],
  },
  {
    name: "Male Sling",
    category: "Procedure",
    aliases: ["male urethral sling", "advance sling", "virtue sling"],
  },
  {
    name: "ProACT Implant",
    category: "Procedure",
    aliases: ["adjustable continence therapy", "proact balloons"],
  },
  {
    name: "Bladder Neck Injection",
    category: "Procedure",
    aliases: ["bulking agent injection", "collagen injection bladder"],
  },

  // Overactive Bladder / Neurogenic
  {
    name: "Bladder Botox",
    category: "Procedure",
    aliases: [
      "botox bladder",
      "intravesical botox",
      "onabotulinumtoxin bladder",
      "detrusor botox",
    ],
  },
  {
    name: "InterStim",
    category: "Procedure",
    aliases: [
      "sacral neuromodulation",
      "sacral nerve stimulator",
      "sns",
      "medtronic interstim",
    ],
  },
  {
    name: "InterStim Revision",
    category: "Procedure",
    aliases: ["sacral neuromodulation revision", "interstim replacement"],
  },
  {
    name: "Percutaneous Tibial Nerve Stimulation",
    category: "Procedure",
    aliases: ["ptns", "tibial nerve stimulation", "urgent pc"],
  },
  {
    name: "Cystometrogram",
    category: "Procedure",
    aliases: ["cmg", "filling study"],
  },
  {
    name: "Urodynamic Study",
    category: "Procedure",
    aliases: ["urodynamics", "uds", "pressure flow study"],
  },
  {
    name: "Video Urodynamics",
    category: "Procedure",
    aliases: ["vuds", "fluoroscopic urodynamics"],
  },

  // Urethra
  {
    name: "Urethral Stricture Repair",
    category: "Procedure",
    aliases: ["urethroplasty", "stricture repair"],
  },
  {
    name: "Urethroplasty",
    category: "Procedure",
    aliases: ["urethral reconstruction"],
  },
  {
    name: "Buccal Mucosal Graft Urethroplasty",
    category: "Procedure",
    aliases: ["bmg urethroplasty", "buccal graft"],
  },
  {
    name: "Anastomotic Urethroplasty",
    category: "Procedure",
    aliases: ["excision and primary anastomosis", "epa urethroplasty"],
  },
  {
    name: "Staged Urethroplasty",
    category: "Procedure",
    aliases: ["two stage urethroplasty", "johanson urethroplasty"],
  },
  {
    name: "Direct Vision Internal Urethrotomy",
    category: "Procedure",
    aliases: ["dviu", "urethrotomy", "optical urethrotomy"],
  },
  {
    name: "Urethral Dilation",
    category: "Procedure",
    aliases: ["urethral dilatation", "meatal dilation"],
  },
  {
    name: "Meatotomy",
    category: "Procedure",
    aliases: ["meatal stenosis repair", "urethral meatus surgery"],
  },
  {
    name: "Meatoplasty",
    category: "Procedure",
    aliases: ["meatal reconstruction"],
  },
  {
    name: "Urethral Diverticulum Excision",
    category: "Procedure",
    aliases: ["ud excision", "female urethral diverticulum"],
  },

  // Ureter
  {
    name: "Ureterectomy",
    category: "Procedure",
    aliases: ["ureter removal", "distal ureterectomy"],
  },
  {
    name: "Ureteral Reimplantation",
    category: "Procedure",
    aliases: ["ureteroneocystostomy", "ureteral reimplant"],
  },
  {
    name: "Robotic Ureteral Reimplantation",
    category: "Procedure",
    aliases: ["robotic reimplant"],
  },
  {
    name: "Ureteroureterostomy",
    category: "Procedure",
    aliases: ["ureteral anastomosis", "ureter to ureter"],
  },
  {
    name: "Transureteroureterostomy",
    category: "Procedure",
    aliases: ["tuu", "cross ureter anastomosis"],
  },
  {
    name: "Boari Flap",
    category: "Procedure",
    aliases: ["bladder flap", "psoas hitch boari"],
  },
  {
    name: "Psoas Hitch",
    category: "Procedure",
    aliases: ["bladder psoas hitch"],
  },
  {
    name: "Ileal Ureter",
    category: "Procedure",
    aliases: ["ileal ureteral replacement", "ureteral substitution"],
  },
  {
    name: "Ureterolysis",
    category: "Procedure",
    aliases: ["ureteral lysis", "retroperitoneal fibrosis surgery"],
  },
  {
    name: "Ureteral Stricture Repair",
    category: "Procedure",
    aliases: ["ureteral reconstruction"],
  },
  {
    name: "Endopyelotomy",
    category: "Procedure",
    aliases: ["upj incision", "endoscopic pyeloplasty"],
  },
  {
    name: "Pyeloplasty",
    category: "Procedure",
    aliases: ["upj repair", "ureteropelvic junction repair", "anderson-hynes"],
  },
  {
    name: "Robotic Pyeloplasty",
    category: "Procedure",
    aliases: ["robotic upj repair"],
  },
  {
    name: "Laparoscopic Pyeloplasty",
    category: "Procedure",
    aliases: ["lap pyeloplasty"],
  },

  // Male Reproductive / Andrology
  {
    name: "Vasectomy",
    category: "Procedure",
    aliases: ["male sterilization", "vas ligation"],
  },
  {
    name: "No-Scalpel Vasectomy",
    category: "Procedure",
    aliases: ["nsv", "minimally invasive vasectomy"],
  },
  {
    name: "Vasectomy Reversal",
    category: "Procedure",
    aliases: ["vasovasostomy", "vv"],
  },
  {
    name: "Vasoepididymostomy",
    category: "Procedure",
    aliases: ["ve", "epididymal bypass"],
  },
  {
    name: "Testicular Sperm Extraction",
    category: "Procedure",
    aliases: ["tese", "testicular biopsy for sperm"],
  },
  {
    name: "Micro-TESE",
    category: "Procedure",
    aliases: ["microdissection tese", "microsurgical tese"],
  },
  {
    name: "Varicocelectomy",
    category: "Procedure",
    aliases: ["varicocele repair"],
  },
  {
    name: "Microscopic Varicocelectomy",
    category: "Procedure",
    aliases: ["micro varicocelectomy", "subinguinal varicocelectomy"],
  },
  {
    name: "Laparoscopic Varicocelectomy",
    category: "Procedure",
    aliases: ["lap varicocele repair"],
  },
  {
    name: "Varicocele Embolization",
    category: "Procedure",
    aliases: ["gonadal vein embolization"],
  },
  {
    name: "Spermatocelectomy",
    category: "Procedure",
    aliases: ["spermatocele removal", "epididymal cyst removal"],
  },
  {
    name: "Epididymectomy",
    category: "Procedure",
    aliases: ["epididymis removal"],
  },

  // Testis / Scrotum
  {
    name: "Orchiectomy",
    category: "Procedure",
    aliases: ["testis removal", "testicle removal", "orchidectomy"],
  },
  {
    name: "Radical Orchiectomy",
    category: "Procedure",
    aliases: ["inguinal orchiectomy", "testis cancer surgery"],
  },
  {
    name: "Simple Orchiectomy",
    category: "Procedure",
    aliases: ["scrotal orchiectomy", "bilateral orchiectomy"],
  },
  {
    name: "Orchiopexy",
    category: "Procedure",
    aliases: ["testis fixation", "undescended testicle surgery", "orchidopexy"],
  },
  {
    name: "Laparoscopic Orchiopexy",
    category: "Procedure",
    aliases: ["lap orchiopexy", "fowler-stephens"],
  },
  {
    name: "Testicular Detorsion",
    category: "Procedure",
    aliases: ["torsion repair", "testicular salvage"],
  },
  {
    name: "Testicular Prosthesis",
    category: "Procedure",
    aliases: ["testicular implant", "prosthetic testicle"],
  },
  {
    name: "Hydrocelectomy",
    category: "Procedure",
    aliases: ["hydrocele repair", "hydrocele excision"],
  },
  {
    name: "Hydrocele Aspiration",
    category: "Procedure",
    aliases: ["hydrocele drainage"],
  },
  {
    name: "Scrotal Exploration",
    category: "Procedure",
    aliases: ["scrotal surgery", "scrotal exploration surgery"],
  },
  {
    name: "Scrotoplasty",
    category: "Procedure",
    aliases: ["scrotal reconstruction", "scrotal reduction"],
  },
  {
    name: "Fournier Gangrene Debridement",
    category: "Procedure",
    aliases: ["fournier debridement", "necrotizing fasciitis scrotum"],
  },

  // Penis
  {
    name: "Circumcision",
    category: "Procedure",
    aliases: ["adult circumcision", "foreskin removal"],
  },
  {
    name: "Circumcision Revision",
    category: "Procedure",
    aliases: ["redo circumcision", "circumcision repair"],
  },
  {
    name: "Frenuloplasty",
    category: "Procedure",
    aliases: ["frenulum repair", "frenulum release"],
  },
  {
    name: "Penile Implant",
    category: "Procedure",
    aliases: ["penile prosthesis", "ipp", "inflatable penile prosthesis"],
  },
  {
    name: "Three-Piece Penile Implant",
    category: "Procedure",
    aliases: ["3 piece ipp", "inflatable prosthesis"],
  },
  {
    name: "Malleable Penile Implant",
    category: "Procedure",
    aliases: ["semi-rigid implant", "two piece implant"],
  },
  {
    name: "Penile Implant Revision",
    category: "Procedure",
    aliases: ["ipp revision", "prosthesis revision"],
  },
  {
    name: "Penile Implant Removal",
    category: "Procedure",
    aliases: ["explant", "prosthesis removal"],
  },
  {
    name: "Penile Plication",
    category: "Procedure",
    aliases: ["nesbit procedure", "peyronie plication"],
  },
  {
    name: "Penile Plaque Incision and Grafting",
    category: "Procedure",
    aliases: ["peyronie surgery", "plaque incision grafting", "pig procedure"],
  },
  {
    name: "Penile Fracture Repair",
    category: "Procedure",
    aliases: ["corporal rupture repair"],
  },
  {
    name: "Priapism Shunt",
    category: "Procedure",
    aliases: ["winter shunt", "al-ghorab shunt", "priapism surgery"],
  },
  {
    name: "Corporal Fibrosis Surgery",
    category: "Procedure",
    aliases: ["corporal reconstruction"],
  },
  {
    name: "Partial Penectomy",
    category: "Procedure",
    aliases: ["partial penis amputation", "penile cancer surgery"],
  },
  {
    name: "Total Penectomy",
    category: "Procedure",
    aliases: ["complete penectomy", "radical penectomy"],
  },
  {
    name: "Glansectomy",
    category: "Procedure",
    aliases: ["glans removal", "glans penis excision"],
  },
  {
    name: "Inguinal Lymph Node Dissection",
    category: "Procedure",
    aliases: ["ilnd", "groin dissection penile cancer"],
  },
  {
    name: "Phalloplasty",
    category: "Procedure",
    aliases: ["penile reconstruction"],
  },

  // Pediatric Urology
  {
    name: "Hypospadias Repair",
    category: "Procedure",
    aliases: ["hypospadias surgery", "tip repair", "tubularized incised plate"],
  },
  {
    name: "Chordee Repair",
    category: "Procedure",
    aliases: ["penile curvature correction pediatric"],
  },
  {
    name: "Ureteral Reimplantation Pediatric",
    category: "Procedure",
    aliases: ["cohen reimplant", "politano reimplant", "vur surgery"],
  },
  {
    name: "Deflux Injection",
    category: "Procedure",
    aliases: ["endoscopic vur treatment", "dextranomer injection"],
  },
  {
    name: "Pyeloplasty Pediatric",
    category: "Procedure",
    aliases: ["pediatric upj repair"],
  },
  {
    name: "Posterior Urethral Valve Ablation",
    category: "Procedure",
    aliases: ["puv ablation", "valve ablation"],
  },
  {
    name: "Malone Antegrade Continence Enema",
    category: "Procedure",
    aliases: ["mace procedure", "appendicostomy"],
  },
  {
    name: "Mitrofanoff Procedure",
    category: "Procedure",
    aliases: ["appendicovesicostomy", "catheterizable channel"],
  },

  // ============================================
  // PROCEDURES - GYNECOLOGY
  // ============================================

  // Hysterectomy - by approach
  {
    name: "Hysterectomy",
    category: "Procedure",
    aliases: ["uterus removal", "hysterectomy surgery"],
  },
  {
    name: "Total Abdominal Hysterectomy",
    category: "Procedure",
    aliases: ["tah", "open hysterectomy", "abdominal hysterectomy"],
  },
  {
    name: "Vaginal Hysterectomy",
    category: "Procedure",
    aliases: ["vag hyst", "tvh", "total vaginal hysterectomy"],
  },
  {
    name: "Laparoscopic Hysterectomy",
    category: "Procedure",
    aliases: [
      "tlh",
      "total laparoscopic hysterectomy",
      "lap hyst",
      "lavh",
      "laparoscopic assisted vaginal hysterectomy",
    ],
  },
  {
    name: "Robotic Hysterectomy",
    category: "Procedure",
    aliases: ["davinci hysterectomy", "robotic assisted hysterectomy", "rah"],
  },
  {
    name: "Supracervical Hysterectomy",
    category: "Procedure",
    aliases: [
      "subtotal hysterectomy",
      "lsh",
      "laparoscopic supracervical hysterectomy",
    ],
  },
  {
    name: "Radical Hysterectomy",
    category: "Procedure",
    aliases: [
      "type iii hysterectomy",
      "wertheim hysterectomy",
      "oncologic hysterectomy",
    ],
  },

  // Hysterectomy - with additional procedures
  {
    name: "Hysterectomy with BSO",
    category: "Procedure",
    aliases: ["tah bso", "hysterectomy bilateral salpingo-oophorectomy"],
  },
  {
    name: "Hysterectomy with Lymphadenectomy",
    category: "Procedure",
    aliases: ["hysterectomy with node dissection", "staging hysterectomy"],
  },

  // Fibroid procedures
  {
    name: "Myomectomy",
    category: "Procedure",
    aliases: [
      "fibroid removal",
      "uterine fibroid surgery",
      "fibroid resection",
    ],
  },
  {
    name: "Abdominal Myomectomy",
    category: "Procedure",
    aliases: ["open myomectomy"],
  },
  {
    name: "Laparoscopic Myomectomy",
    category: "Procedure",
    aliases: ["lap myomectomy", "minimally invasive myomectomy"],
  },
  {
    name: "Robotic Myomectomy",
    category: "Procedure",
    aliases: ["davinci myomectomy"],
  },
  {
    name: "Hysteroscopic Myomectomy",
    category: "Procedure",
    aliases: ["transcervical myomectomy", "submucosal fibroid resection"],
  },
  {
    name: "Uterine Fibroid Embolization",
    category: "Procedure",
    aliases: ["ufe", "uterine artery embolization", "uae"],
  },
  {
    name: "MRI-Guided Focused Ultrasound",
    category: "Procedure",
    aliases: ["mrgfus", "hifu fibroids", "focused ultrasound fibroids"],
  },
  {
    name: "Radiofrequency Ablation Fibroids",
    category: "Procedure",
    aliases: ["acessa", "lap rfa fibroids", "sonata"],
  },

  // Ovarian procedures
  {
    name: "Oophorectomy",
    category: "Procedure",
    aliases: ["ovary removal", "ovariectomy"],
  },
  {
    name: "Unilateral Oophorectomy",
    category: "Procedure",
    aliases: ["single ovary removal", "uso"],
  },
  {
    name: "Bilateral Oophorectomy",
    category: "Procedure",
    aliases: ["bso", "bilateral salpingo-oophorectomy", "both ovaries removal"],
  },
  {
    name: "Prophylactic Oophorectomy",
    category: "Procedure",
    aliases: ["risk-reducing oophorectomy", "rrso", "brca oophorectomy"],
  },
  {
    name: "Ovarian Cystectomy",
    category: "Procedure",
    aliases: ["ovarian cyst removal", "cyst excision ovary"],
  },
  {
    name: "Laparoscopic Ovarian Cystectomy",
    category: "Procedure",
    aliases: ["lap cystectomy", "lap ovarian cyst removal"],
  },
  {
    name: "Ovarian Drilling",
    category: "Procedure",
    aliases: ["laparoscopic ovarian drilling", "lod", "pcos drilling"],
  },
  {
    name: "Ovarian Transposition",
    category: "Procedure",
    aliases: ["oophoropexy", "ovarian preservation radiation"],
  },
  {
    name: "Ovarian Torsion Surgery",
    category: "Procedure",
    aliases: ["detorsion ovary", "ovarian salvage"],
  },

  // Fallopian tube procedures
  {
    name: "Salpingectomy",
    category: "Procedure",
    aliases: ["fallopian tube removal", "tube removal"],
  },
  {
    name: "Bilateral Salpingectomy",
    category: "Procedure",
    aliases: ["both tubes removal", "opportunistic salpingectomy"],
  },
  {
    name: "Salpingostomy",
    category: "Procedure",
    aliases: [
      "ectopic pregnancy surgery",
      "tubal pregnancy surgery",
      "linear salpingostomy",
    ],
  },
  {
    name: "Tubal Ligation",
    category: "Procedure",
    aliases: [
      "tubes tied",
      "bilateral tubal ligation",
      "btl",
      "tubal sterilization",
    ],
  },
  {
    name: "Laparoscopic Tubal Ligation",
    category: "Procedure",
    aliases: ["lap btl", "laparoscopic sterilization"],
  },
  {
    name: "Postpartum Tubal Ligation",
    category: "Procedure",
    aliases: ["postpartum btl", "mini-lap tubal"],
  },
  {
    name: "Tubal Reversal",
    category: "Procedure",
    aliases: [
      "tubal reanastomosis",
      "tubal ligation reversal",
      "sterilization reversal",
    ],
  },
  {
    name: "Essure Removal",
    category: "Procedure",
    aliases: ["essure explant", "essure device removal"],
  },
  {
    name: "Salpingo-Oophorectomy",
    category: "Procedure",
    aliases: ["tube and ovary removal", "so"],
  },

  // Cervical procedures
  {
    name: "Colposcopy",
    category: "Procedure",
    aliases: ["cervical exam", "colpo"],
  },
  {
    name: "Colposcopy with Biopsy",
    category: "Procedure",
    aliases: ["cervical biopsy", "colpo biopsy", "punch biopsy cervix"],
  },
  {
    name: "LEEP",
    category: "Procedure",
    aliases: ["loop electrosurgical excision", "cervical leep", "lletz"],
  },
  {
    name: "Cone Biopsy",
    category: "Procedure",
    aliases: ["cervical conization", "cold knife cone", "ckc"],
  },
  {
    name: "Laser Conization",
    category: "Procedure",
    aliases: ["laser cone", "cervical laser"],
  },
  {
    name: "Cervical Cerclage",
    category: "Procedure",
    aliases: [
      "cerclage",
      "cervical stitch",
      "shirodkar cerclage",
      "mcdonald cerclage",
    ],
  },
  {
    name: "Cerclage Removal",
    category: "Procedure",
    aliases: ["cerclage takedown", "stitch removal"],
  },
  {
    name: "Trachelectomy",
    category: "Procedure",
    aliases: [
      "cervicectomy",
      "radical trachelectomy",
      "fertility-sparing cervical cancer surgery",
    ],
  },
  {
    name: "Cervical Polypectomy",
    category: "Procedure",
    aliases: ["cervical polyp removal"],
  },
  {
    name: "Cervical Dilation",
    category: "Procedure",
    aliases: ["cervical dilatation", "mechanical dilation"],
  },
  {
    name: "Cryotherapy Cervix",
    category: "Procedure",
    aliases: ["cervical cryotherapy", "cryo cervix", "cervical freezing"],
  },

  // Endometrial/Uterine procedures
  {
    name: "D&C",
    category: "Procedure",
    aliases: ["dilation and curettage", "dnc", "d and c"],
  },
  {
    name: "Suction D&C",
    category: "Procedure",
    aliases: ["suction curettage", "vacuum aspiration", "mva"],
  },
  {
    name: "Hysteroscopy",
    category: "Procedure",
    aliases: ["uterine scope", "diagnostic hysteroscopy"],
  },
  {
    name: "Operative Hysteroscopy",
    category: "Procedure",
    aliases: ["surgical hysteroscopy", "hysteroscopic surgery"],
  },
  {
    name: "Hysteroscopic Polypectomy",
    category: "Procedure",
    aliases: ["endometrial polyp removal", "uterine polyp removal"],
  },
  {
    name: "Hysteroscopic Adhesiolysis",
    category: "Procedure",
    aliases: [
      "asherman surgery",
      "intrauterine adhesion lysis",
      "synechiae lysis",
    ],
  },
  {
    name: "Hysteroscopic Septum Resection",
    category: "Procedure",
    aliases: ["septoplasty uterine", "uterine septum removal", "metroplasty"],
  },
  {
    name: "Endometrial Ablation",
    category: "Procedure",
    aliases: [
      "uterine ablation",
      "novasure",
      "thermal ablation",
      "minerva ablation",
    ],
  },
  {
    name: "Endometrial Biopsy",
    category: "Procedure",
    aliases: ["emb", "pipelle biopsy", "uterine biopsy"],
  },
  {
    name: "IUD Insertion",
    category: "Procedure",
    aliases: [
      "iud placement",
      "mirena insertion",
      "paragard insertion",
      "intrauterine device",
    ],
  },
  {
    name: "IUD Removal",
    category: "Procedure",
    aliases: ["iud retrieval", "difficult iud removal"],
  },
  {
    name: "Hysteroscopic IUD Removal",
    category: "Procedure",
    aliases: ["embedded iud removal", "lost iud retrieval"],
  },

  // Pelvic floor / Prolapse
  {
    name: "Pelvic Floor Repair",
    category: "Procedure",
    aliases: ["prolapse repair", "pelvic reconstructive surgery"],
  },
  {
    name: "Anterior Colporrhaphy",
    category: "Procedure",
    aliases: [
      "anterior repair",
      "cystocele repair",
      "anterior vaginal wall repair",
    ],
  },
  {
    name: "Posterior Colporrhaphy",
    category: "Procedure",
    aliases: [
      "posterior repair",
      "rectocele repair",
      "posterior vaginal wall repair",
    ],
  },
  {
    name: "Colpocleisis",
    category: "Procedure",
    aliases: ["vaginal closure", "lefort colpocleisis", "obliterative surgery"],
  },
  {
    name: "Sacrocolpopexy",
    category: "Procedure",
    aliases: ["vaginal vault suspension", "sacral colpopexy"],
  },
  {
    name: "Laparoscopic Sacrocolpopexy",
    category: "Procedure",
    aliases: ["lap sacrocolpopexy", "minimally invasive sacrocolpopexy"],
  },
  {
    name: "Robotic Sacrocolpopexy",
    category: "Procedure",
    aliases: ["davinci sacrocolpopexy"],
  },
  {
    name: "Uterosacral Ligament Suspension",
    category: "Procedure",
    aliases: ["usls", "uterosacral suspension", "high uterosacral"],
  },
  {
    name: "Sacrospinous Ligament Fixation",
    category: "Procedure",
    aliases: ["sslf", "sacrospinous fixation", "sacrospinous suspension"],
  },
  {
    name: "McCall Culdoplasty",
    category: "Procedure",
    aliases: ["mccall procedure", "culdoplasty"],
  },
  {
    name: "Enterocele Repair",
    category: "Procedure",
    aliases: ["enterocele surgery"],
  },
  {
    name: "Vaginal Mesh Removal",
    category: "Procedure",
    aliases: ["mesh excision", "mesh explant", "transvaginal mesh removal"],
  },
  {
    name: "Perineorrhaphy",
    category: "Procedure",
    aliases: ["perineal repair", "perineoplasty"],
  },

  // Incontinence procedures
  {
    name: "Mid-Urethral Sling",
    category: "Procedure",
    aliases: ["bladder sling", "mus", "incontinence sling"],
  },
  {
    name: "TVT Sling",
    category: "Procedure",
    aliases: ["tension-free vaginal tape", "retropubic sling"],
  },
  {
    name: "TOT Sling",
    category: "Procedure",
    aliases: ["transobturator tape", "transobturator sling"],
  },
  {
    name: "Mini-Sling",
    category: "Procedure",
    aliases: ["single incision sling"],
  },
  {
    name: "Sling Revision",
    category: "Procedure",
    aliases: ["sling adjustment", "sling loosening"],
  },
  {
    name: "Sling Removal",
    category: "Procedure",
    aliases: ["sling excision", "mesh sling removal"],
  },
  {
    name: "Burch Colposuspension",
    category: "Procedure",
    aliases: ["burch procedure", "retropubic urethropexy"],
  },
  {
    name: "Urethral Bulking",
    category: "Procedure",
    aliases: ["periurethral injection", "bulkamid", "urethral injection"],
  },
  {
    name: "Botox Bladder",
    category: "Procedure",
    aliases: ["bladder botox", "detrusor botox", "onabotulinumtoxin bladder"],
  },

  // Fertility / Reproductive
  {
    name: "Diagnostic Laparoscopy",
    category: "Procedure",
    aliases: ["dx lap", "diagnostic lap", "infertility laparoscopy"],
  },
  {
    name: "Chromotubation",
    category: "Procedure",
    aliases: ["dye test", "tubal patency test"],
  },
  {
    name: "Ovarian Reserve Testing",
    category: "Procedure",
    aliases: ["amh testing", "antral follicle count"],
  },
  {
    name: "Egg Retrieval",
    category: "Procedure",
    aliases: ["oocyte retrieval", "ivf retrieval", "follicle aspiration"],
  },
  {
    name: "Embryo Transfer",
    category: "Procedure",
    aliases: ["et", "ivf transfer", "frozen embryo transfer", "fet"],
  },
  {
    name: "Intrauterine Insemination",
    category: "Procedure",
    aliases: ["iui", "artificial insemination"],
  },

  // Gynecologic oncology
  {
    name: "Pelvic Lymphadenectomy",
    category: "Procedure",
    aliases: ["pelvic node dissection", "pelvic lymph node sampling"],
  },
  {
    name: "Para-Aortic Lymphadenectomy",
    category: "Procedure",
    aliases: ["para-aortic node dissection", "paln"],
  },
  {
    name: "Sentinel Lymph Node Mapping",
    category: "Procedure",
    aliases: ["sentinel node gyn", "slnm", "icg mapping"],
  },
  {
    name: "Omentectomy",
    category: "Procedure",
    aliases: ["omental resection", "omental biopsy"],
  },
  {
    name: "Tumor Debulking",
    category: "Procedure",
    aliases: [
      "cytoreductive surgery",
      "debulking surgery",
      "optimal debulking",
    ],
  },
  {
    name: "Pelvic Exenteration",
    category: "Procedure",
    aliases: [
      "total pelvic exenteration",
      "anterior exenteration",
      "posterior exenteration",
    ],
  },
  {
    name: "Vulvectomy",
    category: "Procedure",
    aliases: ["vulvar excision", "radical vulvectomy", "partial vulvectomy"],
  },
  {
    name: "Skinning Vulvectomy",
    category: "Procedure",
    aliases: ["simple vulvectomy"],
  },
  {
    name: "Wide Local Excision Vulva",
    category: "Procedure",
    aliases: ["vulvar wle", "vulvar lesion excision"],
  },
  {
    name: "Inguinofemoral Lymphadenectomy",
    category: "Procedure",
    aliases: ["groin node dissection", "inguinal lymphadenectomy"],
  },
  {
    name: "Vaginectomy",
    category: "Procedure",
    aliases: ["vaginal excision", "partial vaginectomy"],
  },

  // Vulvar/Vaginal procedures
  {
    name: "Vulvar Biopsy",
    category: "Procedure",
    aliases: ["vulva biopsy", "punch biopsy vulva"],
  },
  {
    name: "Bartholin Cyst Drainage",
    category: "Procedure",
    aliases: ["bartholin abscess i&d", "bartholin marsupialization"],
  },
  {
    name: "Bartholin Gland Excision",
    category: "Procedure",
    aliases: ["bartholinectomy", "bartholin cyst excision"],
  },
  {
    name: "Vulvar Vestibulectomy",
    category: "Procedure",
    aliases: ["vestibulectomy", "vestibulodynia surgery"],
  },
  {
    name: "Labial Reduction",
    category: "Procedure",
    aliases: ["labiaplasty", "labia minora reduction"],
  },
  {
    name: "Clitoral Hood Reduction",
    category: "Procedure",
    aliases: ["hoodectomy", "clitoral unhooding"],
  },
  {
    name: "Vaginal Septum Excision",
    category: "Procedure",
    aliases: ["vaginal septum removal", "longitudinal septum"],
  },
  {
    name: "Hymenectomy",
    category: "Procedure",
    aliases: ["hymen surgery", "imperforate hymen repair"],
  },
  {
    name: "Vaginoplasty",
    category: "Procedure",
    aliases: ["vaginal reconstruction", "vaginal tightening"],
  },

  // Obstetric (commonly denied/prior auth)
  {
    name: "Cesarean Section",
    category: "Procedure",
    aliases: ["c-section", "cesarean delivery", "cs", "c section"],
  },
  {
    name: "Cesarean Hysterectomy",
    category: "Procedure",
    aliases: ["peripartum hysterectomy", "emergency hysterectomy"],
  },
  {
    name: "External Cephalic Version",
    category: "Procedure",
    aliases: ["ecv", "version", "breech version"],
  },

  // ============================================
  // PROCEDURES - CARDIOLOGY
  // ============================================

  // Diagnostic Catheterization
  {
    name: "Cardiac Catheterization",
    category: "Procedure",
    aliases: ["cath", "heart cath", "coronary angiography"],
  },
  {
    name: "Left Heart Catheterization",
    category: "Procedure",
    aliases: ["lhc", "left heart cath", "coronary angiogram"],
  },
  {
    name: "Right Heart Catheterization",
    category: "Procedure",
    aliases: ["rhc", "right heart cath", "swan ganz", "pa catheter"],
  },
  {
    name: "Combined Left and Right Heart Cath",
    category: "Procedure",
    aliases: ["combined cath", "bilateral heart cath"],
  },
  {
    name: "Coronary Angiography",
    category: "Procedure",
    aliases: ["coronary angiogram", "heart angiogram"],
  },
  {
    name: "Aortography",
    category: "Procedure",
    aliases: ["aortic angiogram", "aortogram"],
  },
  {
    name: "Ventriculography",
    category: "Procedure",
    aliases: ["lv gram", "left ventriculogram"],
  },
  {
    name: "Fractional Flow Reserve",
    category: "Procedure",
    aliases: [
      "ffr",
      "coronary pressure wire",
      "instantaneous wave-free ratio",
      "ifr",
    ],
  },
  {
    name: "Intravascular Ultrasound",
    category: "Procedure",
    aliases: ["ivus", "coronary ivus"],
  },
  {
    name: "Optical Coherence Tomography Coronary",
    category: "Procedure",
    aliases: ["oct coronary", "coronary oct"],
  },
  {
    name: "Coronary Flow Reserve",
    category: "Procedure",
    aliases: ["cfr", "doppler flow wire"],
  },
  {
    name: "Endomyocardial Biopsy",
    category: "Procedure",
    aliases: ["emb", "heart biopsy", "myocardial biopsy"],
  },

  // Percutaneous Coronary Intervention
  {
    name: "PCI",
    category: "Procedure",
    aliases: [
      "percutaneous coronary intervention",
      "angioplasty",
      "coronary stent",
    ],
  },
  {
    name: "Elective PCI",
    category: "Procedure",
    aliases: ["elective angioplasty", "planned pci"],
  },
  {
    name: "Primary PCI",
    category: "Procedure",
    aliases: ["ppci", "stemi pci", "emergent pci", "primary angioplasty"],
  },
  {
    name: "Rescue PCI",
    category: "Procedure",
    aliases: ["rescue angioplasty", "failed thrombolysis pci"],
  },
  {
    name: "Drug-Eluting Stent",
    category: "Procedure",
    aliases: ["des", "drug coated stent", "medicated stent"],
  },
  { name: "Bare Metal Stent", category: "Procedure", aliases: ["bms"] },
  {
    name: "Bioresorbable Stent",
    category: "Procedure",
    aliases: ["brs", "absorb stent", "dissolving stent"],
  },
  {
    name: "Drug-Coated Balloon",
    category: "Procedure",
    aliases: ["dcb", "paclitaxel balloon"],
  },
  {
    name: "Rotational Atherectomy",
    category: "Procedure",
    aliases: ["rotablator", "rota", "coronary atherectomy"],
  },
  {
    name: "Orbital Atherectomy",
    category: "Procedure",
    aliases: ["diamondback", "cso atherectomy"],
  },
  {
    name: "Intravascular Lithotripsy",
    category: "Procedure",
    aliases: ["ivl", "shockwave", "coronary lithotripsy"],
  },
  {
    name: "Laser Atherectomy Coronary",
    category: "Procedure",
    aliases: ["excimer laser", "elca"],
  },
  {
    name: "Chronic Total Occlusion PCI",
    category: "Procedure",
    aliases: [
      "cto pci",
      "cto intervention",
      "chronic total occlusion intervention",
    ],
  },
  {
    name: "Bifurcation PCI",
    category: "Procedure",
    aliases: ["bifurcation stenting", "two stent technique"],
  },
  {
    name: "Left Main PCI",
    category: "Procedure",
    aliases: ["left main stenting", "lmca pci", "unprotected left main"],
  },
  {
    name: "Multi-Vessel PCI",
    category: "Procedure",
    aliases: ["multivessel intervention"],
  },
  {
    name: "Aspiration Thrombectomy Coronary",
    category: "Procedure",
    aliases: ["thrombus aspiration", "coronary thrombectomy"],
  },

  // Coronary Artery Bypass Surgery
  {
    name: "CABG",
    category: "Procedure",
    aliases: [
      "coronary artery bypass",
      "bypass surgery",
      "heart bypass",
      "cabg surgery",
    ],
  },
  {
    name: "On-Pump CABG",
    category: "Procedure",
    aliases: ["conventional cabg", "cabg with cardiopulmonary bypass"],
  },
  {
    name: "Off-Pump CABG",
    category: "Procedure",
    aliases: ["opcab", "beating heart surgery", "opcabg"],
  },
  {
    name: "Minimally Invasive CABG",
    category: "Procedure",
    aliases: ["midcab", "minimally invasive direct coronary artery bypass"],
  },
  {
    name: "Robotic CABG",
    category: "Procedure",
    aliases: ["robotic coronary bypass", "tecab"],
  },
  {
    name: "Hybrid Coronary Revascularization",
    category: "Procedure",
    aliases: ["hybrid cabg pci", "hybrid revascularization"],
  },
  {
    name: "Redo CABG",
    category: "Procedure",
    aliases: ["repeat cabg", "redo coronary bypass"],
  },
  {
    name: "CABG with Valve",
    category: "Procedure",
    aliases: ["cabg avr", "cabg mvr", "combined cabg valve"],
  },

  // Structural Heart - Aortic Valve
  {
    name: "TAVR",
    category: "Procedure",
    aliases: [
      "transcatheter aortic valve replacement",
      "tavi",
      "transcatheter aortic valve implantation",
    ],
  },
  {
    name: "Transfemoral TAVR",
    category: "Procedure",
    aliases: ["tf tavr", "femoral tavr"],
  },
  {
    name: "Transapical TAVR",
    category: "Procedure",
    aliases: ["ta tavr", "apical tavr"],
  },
  {
    name: "Transaxillary TAVR",
    category: "Procedure",
    aliases: ["subclavian tavr", "axillary tavr"],
  },
  {
    name: "Transcarotid TAVR",
    category: "Procedure",
    aliases: ["carotid tavr"],
  },
  { name: "Transcaval TAVR", category: "Procedure", aliases: ["caval tavr"] },
  {
    name: "TAVR in TAVR",
    category: "Procedure",
    aliases: ["valve in valve tavr", "redo tavr"],
  },
  {
    name: "Surgical Aortic Valve Replacement",
    category: "Procedure",
    aliases: ["savr", "avr", "aortic valve replacement"],
  },
  {
    name: "Mechanical Aortic Valve",
    category: "Procedure",
    aliases: ["mechanical avr"],
  },
  {
    name: "Bioprosthetic Aortic Valve",
    category: "Procedure",
    aliases: ["tissue avr", "bioprosthetic avr"],
  },
  {
    name: "Aortic Valve Repair",
    category: "Procedure",
    aliases: ["valve sparing root", "aortic valve reconstruction"],
  },
  {
    name: "Ross Procedure",
    category: "Procedure",
    aliases: ["pulmonary autograft"],
  },
  {
    name: "Balloon Aortic Valvuloplasty",
    category: "Procedure",
    aliases: ["bav", "aortic balloon valvotomy"],
  },
  {
    name: "BASILICA Procedure",
    category: "Procedure",
    aliases: ["bioprosthetic leaflet laceration", "coronary protection tavr"],
  },

  // Structural Heart - Mitral Valve
  {
    name: "MitraClip",
    category: "Procedure",
    aliases: ["transcatheter mitral repair", "teer", "edge to edge repair"],
  },
  {
    name: "PASCAL Repair System",
    category: "Procedure",
    aliases: ["pascal mitral", "mitral pascal"],
  },
  {
    name: "Transcatheter Mitral Valve Replacement",
    category: "Procedure",
    aliases: ["tmvr", "mitral valve in valve"],
  },
  {
    name: "Surgical Mitral Valve Repair",
    category: "Procedure",
    aliases: ["mitral repair", "mitral valve repair", "mitral annuloplasty"],
  },
  {
    name: "Surgical Mitral Valve Replacement",
    category: "Procedure",
    aliases: ["mvr", "mitral valve replacement"],
  },
  {
    name: "Mechanical Mitral Valve",
    category: "Procedure",
    aliases: ["mechanical mvr"],
  },
  {
    name: "Bioprosthetic Mitral Valve",
    category: "Procedure",
    aliases: ["tissue mvr", "bioprosthetic mvr"],
  },
  {
    name: "Mitral Valve in Ring",
    category: "Procedure",
    aliases: ["tmvr in ring", "valve in ring mitral"],
  },
  {
    name: "Mitral Valve in MAC",
    category: "Procedure",
    aliases: ["tmvr in mac", "mitral annular calcification"],
  },
  {
    name: "Balloon Mitral Valvuloplasty",
    category: "Procedure",
    aliases: [
      "bmv",
      "mitral balloon valvotomy",
      "percutaneous mitral commissurotomy",
    ],
  },
  {
    name: "Cardioband Mitral",
    category: "Procedure",
    aliases: ["transcatheter mitral annuloplasty"],
  },

  // Structural Heart - Tricuspid Valve
  {
    name: "TriClip",
    category: "Procedure",
    aliases: ["tricuspid teer", "transcatheter tricuspid repair"],
  },
  {
    name: "Transcatheter Tricuspid Valve Replacement",
    category: "Procedure",
    aliases: ["ttvr", "evoque", "tricuspid replacement transcatheter"],
  },
  {
    name: "Surgical Tricuspid Valve Repair",
    category: "Procedure",
    aliases: ["tricuspid repair", "tricuspid annuloplasty"],
  },
  {
    name: "Surgical Tricuspid Valve Replacement",
    category: "Procedure",
    aliases: ["tvr", "tricuspid replacement"],
  },
  {
    name: "Cardioband Tricuspid",
    category: "Procedure",
    aliases: ["transcatheter tricuspid annuloplasty"],
  },
  {
    name: "FORMA Tricuspid",
    category: "Procedure",
    aliases: ["tricuspid spacer"],
  },

  // Structural Heart - Pulmonic Valve
  {
    name: "Transcatheter Pulmonary Valve",
    category: "Procedure",
    aliases: ["tpvr", "melody valve", "sapien pulmonary"],
  },
  {
    name: "Surgical Pulmonary Valve Replacement",
    category: "Procedure",
    aliases: ["pvr", "pulmonary valve replacement"],
  },
  {
    name: "Balloon Pulmonary Valvuloplasty",
    category: "Procedure",
    aliases: ["pulmonary balloon valvotomy"],
  },

  // Structural Heart - Other
  {
    name: "Watchman Device",
    category: "Procedure",
    aliases: ["left atrial appendage closure", "laac", "watchman implant"],
  },
  {
    name: "Amulet Device",
    category: "Procedure",
    aliases: ["amulet laac", "abbott laac"],
  },
  {
    name: "Lariat Procedure",
    category: "Procedure",
    aliases: ["laa ligation", "lariat laa closure"],
  },
  {
    name: "AtriClip",
    category: "Procedure",
    aliases: ["surgical laa closure", "atriclip laa"],
  },
  {
    name: "ASD Closure Device",
    category: "Procedure",
    aliases: ["atrial septal defect closure", "asd occluder"],
  },
  {
    name: "PFO Closure Device",
    category: "Procedure",
    aliases: ["patent foramen ovale closure", "pfo occluder"],
  },
  {
    name: "VSD Closure Device",
    category: "Procedure",
    aliases: ["ventricular septal defect closure", "vsd occluder"],
  },
  {
    name: "Paravalvular Leak Closure",
    category: "Procedure",
    aliases: ["pvl closure", "paravalvular leak occluder"],
  },
  {
    name: "Alcohol Septal Ablation",
    category: "Procedure",
    aliases: ["asa", "septal ablation hocm", "hocm alcohol ablation"],
  },
  {
    name: "Surgical Myectomy",
    category: "Procedure",
    aliases: ["septal myectomy", "morrow procedure", "hocm myectomy"],
  },

  // Electrophysiology - Diagnostic
  {
    name: "Electrophysiology Study",
    category: "Procedure",
    aliases: ["ep study", "eps", "electrophysiologic study"],
  },
  {
    name: "Diagnostic EP Study",
    category: "Procedure",
    aliases: ["diagnostic electrophysiology"],
  },
  {
    name: "Tilt Table Test",
    category: "Procedure",
    aliases: ["head up tilt test", "syncope evaluation"],
  },
  {
    name: "Loop Recorder Implant",
    category: "Procedure",
    aliases: ["ilr", "implantable loop recorder", "reveal", "linq"],
  },
  {
    name: "Loop Recorder Explant",
    category: "Procedure",
    aliases: ["ilr removal"],
  },
  {
    name: "Cardiac Event Monitor",
    category: "Procedure",
    aliases: ["holter monitor", "zio patch", "extended monitoring"],
  },

  // Electrophysiology - Ablation
  {
    name: "Cardiac Ablation",
    category: "Procedure",
    aliases: ["catheter ablation", "ep ablation"],
  },
  {
    name: "Atrial Fibrillation Ablation",
    category: "Procedure",
    aliases: [
      "afib ablation",
      "af ablation",
      "pulmonary vein isolation",
      "pvi",
    ],
  },
  {
    name: "Cryoballoon Ablation",
    category: "Procedure",
    aliases: ["cryo afib ablation", "arctic front"],
  },
  {
    name: "Radiofrequency Ablation AF",
    category: "Procedure",
    aliases: ["rf ablation afib", "point by point ablation"],
  },
  {
    name: "Pulsed Field Ablation",
    category: "Procedure",
    aliases: ["pfa", "farapulse", "electroporation ablation"],
  },
  {
    name: "Atrial Flutter Ablation",
    category: "Procedure",
    aliases: [
      "flutter ablation",
      "cti ablation",
      "cavotricuspid isthmus ablation",
    ],
  },
  {
    name: "SVT Ablation",
    category: "Procedure",
    aliases: [
      "supraventricular tachycardia ablation",
      "avnrt ablation",
      "avrt ablation",
    ],
  },
  {
    name: "AVNRT Ablation",
    category: "Procedure",
    aliases: [
      "av nodal reentrant tachycardia ablation",
      "slow pathway ablation",
    ],
  },
  {
    name: "Accessory Pathway Ablation",
    category: "Procedure",
    aliases: [
      "wpw ablation",
      "wolff-parkinson-white ablation",
      "bypass tract ablation",
    ],
  },
  {
    name: "Ventricular Tachycardia Ablation",
    category: "Procedure",
    aliases: ["vt ablation", "vtach ablation"],
  },
  {
    name: "VT Ablation Epicardial",
    category: "Procedure",
    aliases: ["epicardial vt ablation", "subxiphoid approach vt"],
  },
  {
    name: "PVC Ablation",
    category: "Procedure",
    aliases: [
      "premature ventricular contraction ablation",
      "pvc ablation rvot",
    ],
  },
  {
    name: "AV Node Ablation",
    category: "Procedure",
    aliases: ["av junction ablation", "ablate and pace"],
  },
  {
    name: "Hybrid AF Ablation",
    category: "Procedure",
    aliases: ["convergent ablation", "surgical hybrid afib"],
  },
  {
    name: "MAZE Procedure",
    category: "Procedure",
    aliases: ["surgical maze", "cox maze", "mini maze"],
  },

  // Pacemakers
  {
    name: "Pacemaker Implantation",
    category: "Procedure",
    aliases: ["pacemaker", "ppm", "permanent pacemaker"],
  },
  {
    name: "Single Chamber Pacemaker",
    category: "Procedure",
    aliases: ["vvi pacemaker", "single lead pacemaker"],
  },
  {
    name: "Dual Chamber Pacemaker",
    category: "Procedure",
    aliases: ["ddd pacemaker", "two lead pacemaker"],
  },
  {
    name: "Leadless Pacemaker",
    category: "Procedure",
    aliases: ["micra", "leadless ppm", "aveir"],
  },
  {
    name: "His Bundle Pacing",
    category: "Procedure",
    aliases: ["hbp", "his pacing", "conduction system pacing"],
  },
  {
    name: "Left Bundle Branch Pacing",
    category: "Procedure",
    aliases: ["lbbp", "lbb pacing", "lbbap"],
  },
  {
    name: "Pacemaker Generator Change",
    category: "Procedure",
    aliases: [
      "pacemaker battery change",
      "generator replacement",
      "pulse generator change",
    ],
  },
  {
    name: "Pacemaker Upgrade",
    category: "Procedure",
    aliases: ["device upgrade", "single to dual upgrade"],
  },
  {
    name: "Pacemaker Revision",
    category: "Procedure",
    aliases: ["lead revision", "pocket revision"],
  },
  {
    name: "Temporary Pacemaker",
    category: "Procedure",
    aliases: [
      "temp pacemaker",
      "transvenous pacing",
      "temporary transvenous pacemaker",
    ],
  },

  // ICDs and CRT
  {
    name: "ICD Implantation",
    category: "Procedure",
    aliases: [
      "implantable cardioverter defibrillator",
      "defibrillator implant",
      "aicd",
    ],
  },
  { name: "Single Chamber ICD", category: "Procedure", aliases: ["vvi icd"] },
  {
    name: "Dual Chamber ICD",
    category: "Procedure",
    aliases: ["ddd icd", "two lead icd"],
  },
  {
    name: "Subcutaneous ICD",
    category: "Procedure",
    aliases: ["s-icd", "sicd", "subcutaneous defibrillator"],
  },
  {
    name: "CRT Implantation",
    category: "Procedure",
    aliases: ["cardiac resynchronization therapy", "biventricular pacemaker"],
  },
  {
    name: "CRT-P Implantation",
    category: "Procedure",
    aliases: ["crt pacemaker", "biventricular pacemaker"],
  },
  {
    name: "CRT-D Implantation",
    category: "Procedure",
    aliases: ["crt defibrillator", "biventricular icd"],
  },
  {
    name: "ICD Generator Change",
    category: "Procedure",
    aliases: ["icd battery change", "icd generator replacement"],
  },
  {
    name: "CRT Upgrade",
    category: "Procedure",
    aliases: ["upgrade to crt", "add lv lead"],
  },
  {
    name: "Coronary Sinus Lead Placement",
    category: "Procedure",
    aliases: ["cs lead", "lv lead placement"],
  },
  {
    name: "Lead Extraction",
    category: "Procedure",
    aliases: [
      "pacemaker lead removal",
      "icd lead extraction",
      "transvenous lead extraction",
    ],
  },
  {
    name: "Laser Lead Extraction",
    category: "Procedure",
    aliases: ["laser assisted lead extraction"],
  },
  {
    name: "Lead Capping",
    category: "Procedure",
    aliases: ["lead abandonment", "lead cap"],
  },

  // Cardioversion & External Devices
  {
    name: "Cardioversion",
    category: "Procedure",
    aliases: ["dccv", "electrical cardioversion", "synchronized cardioversion"],
  },
  {
    name: "External Defibrillation",
    category: "Procedure",
    aliases: ["defibrillation", "external shock"],
  },
  {
    name: "LifeVest",
    category: "Procedure",
    aliases: ["wearable cardioverter defibrillator", "wcd", "zoll lifevest"],
  },

  // Heart Failure - Mechanical Support
  {
    name: "LVAD Implantation",
    category: "Procedure",
    aliases: [
      "left ventricular assist device",
      "heartmate",
      "heartware",
      "hvad",
    ],
  },
  {
    name: "RVAD Implantation",
    category: "Procedure",
    aliases: ["right ventricular assist device"],
  },
  {
    name: "BiVAD Implantation",
    category: "Procedure",
    aliases: ["biventricular assist device"],
  },
  {
    name: "Total Artificial Heart",
    category: "Procedure",
    aliases: ["tah", "syncardia", "artificial heart"],
  },
  {
    name: "LVAD Exchange",
    category: "Procedure",
    aliases: ["vad exchange", "pump exchange"],
  },
  {
    name: "LVAD Explant",
    category: "Procedure",
    aliases: ["vad removal", "device explant"],
  },
  {
    name: "Impella",
    category: "Procedure",
    aliases: ["impella cp", "impella 5.0", "impella support"],
  },
  {
    name: "ECMO Cannulation",
    category: "Procedure",
    aliases: [
      "ecmo",
      "extracorporeal membrane oxygenation",
      "va ecmo",
      "vv ecmo",
    ],
  },
  {
    name: "ECMO Decannulation",
    category: "Procedure",
    aliases: ["ecmo removal", "ecmo wean"],
  },
  {
    name: "Intra-Aortic Balloon Pump",
    category: "Procedure",
    aliases: ["iabp", "balloon pump", "aortic counterpulsation"],
  },
  {
    name: "TandemHeart",
    category: "Procedure",
    aliases: ["tandem heart support", "percutaneous vad"],
  },
  {
    name: "CardioMEMS Implant",
    category: "Procedure",
    aliases: ["pa pressure monitor", "heart failure monitor", "cardiomems"],
  },

  // Heart Transplant
  {
    name: "Heart Transplant",
    category: "Procedure",
    aliases: ["cardiac transplant", "orthotopic heart transplant", "oht"],
  },
  {
    name: "Heart-Lung Transplant",
    category: "Procedure",
    aliases: ["combined heart lung transplant"],
  },
  {
    name: "Transplant Evaluation",
    category: "Procedure",
    aliases: ["heart transplant workup", "transplant listing"],
  },
  {
    name: "Transplant Rejection Biopsy",
    category: "Procedure",
    aliases: ["surveillance biopsy", "rejection monitoring"],
  },

  // Pericardial Procedures
  {
    name: "Pericardiocentesis",
    category: "Procedure",
    aliases: [
      "pericardial tap",
      "pericardial drainage",
      "pericardial aspiration",
    ],
  },
  {
    name: "Pericardial Window",
    category: "Procedure",
    aliases: ["pericardial drainage surgery", "subxiphoid pericardial window"],
  },
  {
    name: "Pericardiectomy",
    category: "Procedure",
    aliases: ["pericardial stripping", "constrictive pericarditis surgery"],
  },
  {
    name: "Pericardial Drain Placement",
    category: "Procedure",
    aliases: ["pericardial catheter", "pigtail pericardial drain"],
  },

  // Aortic Surgery (Cardiac)
  {
    name: "Ascending Aortic Repair",
    category: "Procedure",
    aliases: ["ascending aorta replacement", "supracoronary graft"],
  },
  {
    name: "Aortic Root Replacement",
    category: "Procedure",
    aliases: ["bentall procedure", "aortic root repair"],
  },
  {
    name: "Valve-Sparing Root Replacement",
    category: "Procedure",
    aliases: [
      "david procedure",
      "yacoub procedure",
      "reimplantation technique",
    ],
  },
  {
    name: "Aortic Arch Replacement",
    category: "Procedure",
    aliases: ["arch repair", "total arch replacement", "hemiarch replacement"],
  },
  {
    name: "Elephant Trunk Procedure",
    category: "Procedure",
    aliases: ["frozen elephant trunk", "thoracoabdominal staging"],
  },
  {
    name: "Aortic Dissection Repair",
    category: "Procedure",
    aliases: ["type a dissection repair", "dissection surgery"],
  },

  // Congenital Heart Disease - Adult
  {
    name: "Coarctation Repair",
    category: "Procedure",
    aliases: ["aortic coarctation repair", "coarc repair"],
  },
  {
    name: "Coarctation Stenting",
    category: "Procedure",
    aliases: ["aortic coarctation stent"],
  },
  {
    name: "Tetralogy of Fallot Repair",
    category: "Procedure",
    aliases: ["tof repair", "fallot repair"],
  },
  {
    name: "Fontan Procedure",
    category: "Procedure",
    aliases: ["fontan completion", "fontan revision"],
  },
  {
    name: "Glenn Procedure",
    category: "Procedure",
    aliases: ["bidirectional glenn", "hemi-fontan"],
  },
  {
    name: "Pulmonary Artery Banding",
    category: "Procedure",
    aliases: ["pa band"],
  },
  {
    name: "Congenital Heart Reoperation",
    category: "Procedure",
    aliases: ["redo congenital surgery", "achd reoperation"],
  },

  // Cardiac Imaging Procedures
  {
    name: "Transesophageal Echocardiogram",
    category: "Procedure",
    aliases: ["tee", "transesophageal echo", "tee echo"],
  },
  {
    name: "Intracardiac Echocardiogram",
    category: "Procedure",
    aliases: ["ice", "intracardiac echo"],
  },
  {
    name: "Stress Echocardiogram",
    category: "Procedure",
    aliases: ["stress echo", "dobutamine stress echo", "exercise echo"],
  },
  {
    name: "Cardiac MRI",
    category: "Procedure",
    aliases: ["cmr", "cardiac magnetic resonance", "heart mri"],
  },
  {
    name: "Cardiac CT",
    category: "Procedure",
    aliases: ["coronary cta", "cardiac cta", "calcium score"],
  },
  {
    name: "Nuclear Stress Test",
    category: "Procedure",
    aliases: [
      "myocardial perfusion imaging",
      "mpi",
      "nuclear cardiology",
      "spect",
    ],
  },
  {
    name: "PET Cardiac",
    category: "Procedure",
    aliases: ["cardiac pet", "pet perfusion", "myocardial viability pet"],
  },
  {
    name: "Cardiac Catheterization CT Guidance",
    category: "Procedure",
    aliases: ["ct guided structural"],
  },

  // ============================================
  // PROCEDURES - VASCULAR SURGERY
  // ============================================

  {
    name: "Carotid Endarterectomy",
    category: "Procedure",
    aliases: ["cea", "carotid surgery"],
  },
  {
    name: "Carotid Stenting",
    category: "Procedure",
    aliases: ["cas", "carotid artery stent"],
  },
  {
    name: "AAA Repair",
    category: "Procedure",
    aliases: [
      "abdominal aortic aneurysm repair",
      "aaa surgery",
      "open aaa repair",
    ],
  },
  {
    name: "EVAR",
    category: "Procedure",
    aliases: ["endovascular aneurysm repair", "endovascular aaa repair"],
  },
  {
    name: "TEVAR",
    category: "Procedure",
    aliases: ["thoracic endovascular aortic repair"],
  },
  {
    name: "Peripheral Angioplasty",
    category: "Procedure",
    aliases: [
      "leg angioplasty",
      "peripheral stent",
      "iliac stent",
      "femoral stent",
    ],
  },
  {
    name: "Peripheral Bypass",
    category: "Procedure",
    aliases: ["leg bypass", "femoral popliteal bypass", "fem-pop bypass"],
  },
  {
    name: "Atherectomy",
    category: "Procedure",
    aliases: ["peripheral atherectomy", "plaque removal"],
  },
  {
    name: "Thrombectomy",
    category: "Procedure",
    aliases: ["clot removal", "mechanical thrombectomy"],
  },
  { name: "Embolectomy", category: "Procedure", aliases: ["embolus removal"] },
  {
    name: "Vein Ablation",
    category: "Procedure",
    aliases: [
      "endovenous ablation",
      "evla",
      "vnus closure",
      "laser vein ablation",
      "rfa vein",
    ],
  },
  {
    name: "Sclerotherapy",
    category: "Procedure",
    aliases: ["vein sclerotherapy", "spider vein treatment"],
  },
  {
    name: "Varicose Vein Stripping",
    category: "Procedure",
    aliases: ["vein stripping", "phlebectomy"],
  },
  {
    name: "AV Fistula Creation",
    category: "Procedure",
    aliases: ["avf", "dialysis access", "fistula creation"],
  },
  {
    name: "AV Graft",
    category: "Procedure",
    aliases: ["dialysis graft", "avg"],
  },
  {
    name: "Port Placement",
    category: "Procedure",
    aliases: ["port-a-cath", "mediport", "chemo port", "central line port"],
  },
  {
    name: "Tunneled Catheter",
    category: "Procedure",
    aliases: ["hickman", "permacath", "tunneled dialysis catheter"],
  },
  {
    name: "IVC Filter",
    category: "Procedure",
    aliases: ["vena cava filter", "ivc filter placement", "ivc filter removal"],
  },
  {
    name: "Amputation",
    category: "Procedure",
    aliases: [
      "leg amputation",
      "bka",
      "aka",
      "below knee amputation",
      "above knee amputation",
      "toe amputation",
    ],
  },

  // ============================================
  // PROCEDURES - NEUROSURGERY
  // ============================================

  // Craniotomy - General
  {
    name: "Craniotomy",
    category: "Procedure",
    aliases: ["brain surgery", "open brain surgery"],
  },
  {
    name: "Craniotomy for Tumor",
    category: "Procedure",
    aliases: [
      "brain tumor surgery",
      "tumor resection brain",
      "brain tumor resection",
    ],
  },
  {
    name: "Awake Craniotomy",
    category: "Procedure",
    aliases: ["awake brain surgery", "speech mapping craniotomy"],
  },
  {
    name: "Stereotactic Craniotomy",
    category: "Procedure",
    aliases: ["navigated craniotomy", "image-guided craniotomy"],
  },
  {
    name: "Frontal Craniotomy",
    category: "Procedure",
    aliases: ["bifrontal craniotomy", "frontal lobectomy"],
  },
  {
    name: "Temporal Craniotomy",
    category: "Procedure",
    aliases: ["temporal lobectomy", "pterional craniotomy"],
  },
  {
    name: "Parietal Craniotomy",
    category: "Procedure",
    aliases: ["parietal lobectomy"],
  },
  {
    name: "Occipital Craniotomy",
    category: "Procedure",
    aliases: ["occipital lobectomy"],
  },
  {
    name: "Posterior Fossa Craniotomy",
    category: "Procedure",
    aliases: ["suboccipital craniotomy", "infratentorial craniotomy"],
  },
  {
    name: "Retrosigmoid Craniotomy",
    category: "Procedure",
    aliases: ["retrosigmoid approach"],
  },
  {
    name: "Translabyrinthine Craniotomy",
    category: "Procedure",
    aliases: ["translabyrinthine approach"],
  },
  {
    name: "Decompressive Craniectomy",
    category: "Procedure",
    aliases: ["decompressive hemicraniectomy", "dc", "bone flap removal"],
  },
  {
    name: "Cranioplasty",
    category: "Procedure",
    aliases: ["skull repair", "skull reconstruction", "bone flap replacement"],
  },
  {
    name: "Custom Cranioplasty",
    category: "Procedure",
    aliases: [
      "peek cranioplasty",
      "titanium cranioplasty",
      "3d printed cranioplasty",
    ],
  },

  // Brain Tumors - Specific
  {
    name: "Glioma Resection",
    category: "Procedure",
    aliases: ["glioblastoma surgery", "gbm resection", "astrocytoma surgery"],
  },
  {
    name: "Meningioma Resection",
    category: "Procedure",
    aliases: ["meningioma surgery", "meningioma removal"],
  },
  {
    name: "Acoustic Neuroma Surgery",
    category: "Procedure",
    aliases: ["vestibular schwannoma surgery", "acoustic schwannoma resection"],
  },
  {
    name: "Pituitary Tumor Surgery",
    category: "Procedure",
    aliases: ["pituitary adenoma resection", "transsphenoidal surgery"],
  },
  {
    name: "Transsphenoidal Hypophysectomy",
    category: "Procedure",
    aliases: [
      "endoscopic pituitary surgery",
      "endonasal pituitary",
      "transsphenoidal resection",
    ],
  },
  {
    name: "Metastatic Brain Tumor Resection",
    category: "Procedure",
    aliases: ["brain met resection", "cerebral metastasectomy"],
  },
  {
    name: "Pineal Tumor Surgery",
    category: "Procedure",
    aliases: ["pineal region surgery", "pinealoma resection"],
  },
  {
    name: "Skull Base Tumor Surgery",
    category: "Procedure",
    aliases: ["skull base resection", "anterior skull base surgery"],
  },
  {
    name: "Intraventricular Tumor Surgery",
    category: "Procedure",
    aliases: ["ventricular tumor resection", "colloid cyst removal"],
  },
  {
    name: "Brainstem Tumor Surgery",
    category: "Procedure",
    aliases: ["brainstem glioma surgery"],
  },
  {
    name: "Pediatric Brain Tumor Surgery",
    category: "Procedure",
    aliases: [
      "medulloblastoma surgery",
      "ependymoma surgery",
      "pediatric craniotomy",
    ],
  },

  // Hydrocephalus / CSF
  {
    name: "VP Shunt",
    category: "Procedure",
    aliases: [
      "ventriculoperitoneal shunt",
      "brain shunt",
      "hydrocephalus shunt",
    ],
  },
  {
    name: "VP Shunt Placement",
    category: "Procedure",
    aliases: ["vp shunt insertion", "shunt placement"],
  },
  {
    name: "VP Shunt Revision",
    category: "Procedure",
    aliases: ["shunt revision", "vp shunt repair"],
  },
  {
    name: "Shunt Externalization",
    category: "Procedure",
    aliases: ["external shunt", "shunt tap"],
  },
  {
    name: "VA Shunt",
    category: "Procedure",
    aliases: ["ventriculoatrial shunt", "cardiac shunt"],
  },
  {
    name: "LP Shunt",
    category: "Procedure",
    aliases: ["lumboperitoneal shunt", "lumbar shunt"],
  },
  {
    name: "Endoscopic Third Ventriculostomy",
    category: "Procedure",
    aliases: ["etv", "third ventriculostomy", "endoscopic ventriculostomy"],
  },
  {
    name: "ETV with CPC",
    category: "Procedure",
    aliases: [
      "etv cpc",
      "endoscopic third ventriculostomy with choroid plexus cauterization",
    ],
  },
  {
    name: "Ventricular Catheter Placement",
    category: "Procedure",
    aliases: ["ventricular drain", "ventriculostomy"],
  },
  {
    name: "External Ventricular Drain",
    category: "Procedure",
    aliases: ["evd", "external ventricular drainage", "ivd"],
  },
  {
    name: "Lumbar Drain",
    category: "Procedure",
    aliases: ["lumbar drainage", "csf drain", "continuous lumbar drainage"],
  },
  {
    name: "Ommaya Reservoir Placement",
    category: "Procedure",
    aliases: ["ommaya", "ventricular access device", "intrathecal reservoir"],
  },
  {
    name: "Shunt Infection Treatment",
    category: "Procedure",
    aliases: ["shunt removal for infection", "shunt externalization infection"],
  },
  {
    name: "Arachnoid Cyst Fenestration",
    category: "Procedure",
    aliases: ["cyst fenestration", "arachnoid cyst surgery"],
  },

  // Trauma
  {
    name: "Burr Hole",
    category: "Procedure",
    aliases: ["burr hole drainage", "twist drill", "trephination"],
  },
  {
    name: "Subdural Hematoma Evacuation",
    category: "Procedure",
    aliases: [
      "sdh evacuation",
      "subdural drainage",
      "chronic subdural evacuation",
    ],
  },
  {
    name: "Epidural Hematoma Evacuation",
    category: "Procedure",
    aliases: ["edh evacuation", "epidural drainage"],
  },
  {
    name: "Intracerebral Hemorrhage Evacuation",
    category: "Procedure",
    aliases: [
      "ich evacuation",
      "brain hemorrhage surgery",
      "hematoma evacuation",
    ],
  },
  {
    name: "Depressed Skull Fracture Repair",
    category: "Procedure",
    aliases: ["skull fracture elevation", "depressed fracture surgery"],
  },
  {
    name: "Penetrating Brain Injury Surgery",
    category: "Procedure",
    aliases: ["penetrating trauma craniotomy", "missile injury surgery"],
  },
  {
    name: "ICP Monitor Placement",
    category: "Procedure",
    aliases: ["intracranial pressure monitor", "icp bolt", "camino bolt"],
  },
  {
    name: "Brain Tissue Oxygen Monitor",
    category: "Procedure",
    aliases: ["licox", "pbto2 monitor", "brain oxygen monitor"],
  },

  // Cerebrovascular - Open
  {
    name: "Aneurysm Clipping",
    category: "Procedure",
    aliases: [
      "brain aneurysm surgery",
      "cerebral aneurysm clipping",
      "aneurysm clip",
    ],
  },
  {
    name: "Giant Aneurysm Surgery",
    category: "Procedure",
    aliases: ["complex aneurysm surgery", "giant aneurysm clipping"],
  },
  {
    name: "Bypass for Aneurysm",
    category: "Procedure",
    aliases: ["ec-ic bypass", "sta-mca bypass", "cerebral bypass"],
  },
  {
    name: "AVM Resection",
    category: "Procedure",
    aliases: [
      "arteriovenous malformation surgery",
      "avm surgery",
      "brain avm removal",
    ],
  },
  {
    name: "Cavernous Malformation Resection",
    category: "Procedure",
    aliases: ["cavernoma surgery", "cavernous angioma resection"],
  },
  {
    name: "Moyamoya Surgery",
    category: "Procedure",
    aliases: ["edas", "encephaloduroarteriosynangiosis", "moyamoya bypass"],
  },
  {
    name: "Carotid Endarterectomy Neurosurgery",
    category: "Procedure",
    aliases: ["cea neurosurgery"],
  },
  {
    name: "Carotid-Cavernous Fistula Repair",
    category: "Procedure",
    aliases: ["ccf repair", "carotid cavernous fistula surgery"],
  },
  {
    name: "Dural Arteriovenous Fistula Surgery",
    category: "Procedure",
    aliases: ["davf surgery", "dural avf resection"],
  },

  // Cerebrovascular - Endovascular
  {
    name: "Aneurysm Coiling",
    category: "Procedure",
    aliases: [
      "endovascular coiling",
      "cerebral coiling",
      "aneurysm embolization",
    ],
  },
  {
    name: "Flow Diverter Placement",
    category: "Procedure",
    aliases: ["pipeline embolization", "flow diversion", "pipeline flex"],
  },
  {
    name: "Stent-Assisted Coiling",
    category: "Procedure",
    aliases: ["stent coiling", "intracranial stent"],
  },
  {
    name: "AVM Embolization",
    category: "Procedure",
    aliases: ["avm endovascular", "onyx embolization"],
  },
  {
    name: "Mechanical Thrombectomy",
    category: "Procedure",
    aliases: [
      "stroke thrombectomy",
      "clot retrieval",
      "endovascular stroke treatment",
    ],
  },
  {
    name: "Carotid Stenting Neuro",
    category: "Procedure",
    aliases: ["carotid artery stent", "cas"],
  },
  {
    name: "Intracranial Stenting",
    category: "Procedure",
    aliases: ["intracranial angioplasty", "wingspan stent"],
  },
  {
    name: "Venous Sinus Stenting",
    category: "Procedure",
    aliases: ["transverse sinus stent", "sigmoid sinus stent", "iih stenting"],
  },
  {
    name: "Diagnostic Cerebral Angiogram",
    category: "Procedure",
    aliases: ["cerebral angiography", "brain angiogram", "4 vessel angiogram"],
  },
  {
    name: "Wada Test",
    category: "Procedure",
    aliases: ["intracarotid amobarbital test", "iat"],
  },

  // Functional Neurosurgery
  {
    name: "Deep Brain Stimulation",
    category: "Procedure",
    aliases: ["dbs", "brain stimulator"],
  },
  {
    name: "DBS for Parkinson's",
    category: "Procedure",
    aliases: ["parkinson dbs", "stn dbs", "gpi dbs"],
  },
  {
    name: "DBS for Essential Tremor",
    category: "Procedure",
    aliases: ["vim dbs", "tremor dbs"],
  },
  {
    name: "DBS for Dystonia",
    category: "Procedure",
    aliases: ["dystonia dbs", "gpi dbs dystonia"],
  },
  {
    name: "DBS for OCD",
    category: "Procedure",
    aliases: ["ocd dbs", "psychiatric dbs"],
  },
  {
    name: "DBS Lead Placement",
    category: "Procedure",
    aliases: ["dbs electrode placement"],
  },
  {
    name: "DBS Generator Placement",
    category: "Procedure",
    aliases: ["dbs battery", "ipg placement"],
  },
  {
    name: "DBS Revision",
    category: "Procedure",
    aliases: ["dbs lead revision", "dbs replacement"],
  },
  {
    name: "Responsive Neurostimulation",
    category: "Procedure",
    aliases: ["rns", "neuropace", "responsive neurostimulator"],
  },
  {
    name: "Vagus Nerve Stimulator",
    category: "Procedure",
    aliases: ["vns", "vagal nerve stimulator", "epilepsy stimulator"],
  },
  {
    name: "VNS Revision",
    category: "Procedure",
    aliases: ["vns replacement", "vns generator change"],
  },
  {
    name: "Motor Cortex Stimulation",
    category: "Procedure",
    aliases: ["mcs", "cortical stimulation pain"],
  },
  {
    name: "Thalamotomy",
    category: "Procedure",
    aliases: ["stereotactic thalamotomy"],
  },
  {
    name: "Pallidotomy",
    category: "Procedure",
    aliases: ["stereotactic pallidotomy", "gpi lesion"],
  },
  {
    name: "MRI-Guided Focused Ultrasound",
    category: "Procedure",
    aliases: [
      "mrgfus",
      "hifu brain",
      "focused ultrasound thalamotomy",
      "exablate",
    ],
  },
  {
    name: "Gamma Knife Thalamotomy",
    category: "Procedure",
    aliases: ["radiosurgical thalamotomy"],
  },

  // Epilepsy Surgery
  {
    name: "Epilepsy Surgery",
    category: "Procedure",
    aliases: ["seizure surgery"],
  },
  {
    name: "Temporal Lobectomy",
    category: "Procedure",
    aliases: [
      "anterior temporal lobectomy",
      "atl",
      "mesial temporal resection",
    ],
  },
  {
    name: "Selective Amygdalohippocampectomy",
    category: "Procedure",
    aliases: ["sah", "amygdalohippocampectomy"],
  },
  {
    name: "Lesionectomy",
    category: "Procedure",
    aliases: ["epilepsy lesion resection", "focal resection"],
  },
  {
    name: "Corpus Callosotomy",
    category: "Procedure",
    aliases: ["split brain surgery", "callosotomy"],
  },
  {
    name: "Hemispherectomy",
    category: "Procedure",
    aliases: ["functional hemispherectomy", "hemispherotomy"],
  },
  {
    name: "Multiple Subpial Transections",
    category: "Procedure",
    aliases: ["mst", "subpial transection"],
  },
  {
    name: "Subdural Grid Placement",
    category: "Procedure",
    aliases: ["grid placement", "intracranial monitoring", "invasive eeg"],
  },
  {
    name: "Stereo EEG",
    category: "Procedure",
    aliases: [
      "seeg",
      "depth electrode placement",
      "stereoelectroencephalography",
    ],
  },
  {
    name: "Laser Ablation Epilepsy",
    category: "Procedure",
    aliases: [
      "litt epilepsy",
      "laser interstitial thermal therapy",
      "visualase",
    ],
  },
  {
    name: "Gamma Knife Epilepsy",
    category: "Procedure",
    aliases: ["radiosurgery epilepsy", "srs epilepsy"],
  },
  {
    name: "Thermocoagulation Epilepsy",
    category: "Procedure",
    aliases: ["rf thermocoagulation", "seeg guided thermocoagulation"],
  },

  // Radiosurgery
  {
    name: "Gamma Knife",
    category: "Procedure",
    aliases: ["gamma knife radiosurgery", "gkrs"],
  },
  {
    name: "CyberKnife",
    category: "Procedure",
    aliases: ["cyberknife radiosurgery", "robotic radiosurgery"],
  },
  {
    name: "Stereotactic Radiosurgery",
    category: "Procedure",
    aliases: ["srs", "linac radiosurgery", "stereotactic radiation"],
  },
  {
    name: "Fractionated Stereotactic Radiotherapy",
    category: "Procedure",
    aliases: ["fsrt", "fractionated srs"],
  },
  {
    name: "Gamma Knife for Metastases",
    category: "Procedure",
    aliases: ["brain met radiosurgery", "srs brain mets"],
  },
  {
    name: "Gamma Knife for Trigeminal Neuralgia",
    category: "Procedure",
    aliases: ["trigeminal neuralgia radiosurgery", "gk tn"],
  },
  {
    name: "Gamma Knife for Acoustic Neuroma",
    category: "Procedure",
    aliases: ["acoustic neuroma radiosurgery", "vs srs"],
  },
  {
    name: "Gamma Knife for AVM",
    category: "Procedure",
    aliases: ["avm radiosurgery", "srs avm"],
  },
  {
    name: "Gamma Knife for Meningioma",
    category: "Procedure",
    aliases: ["meningioma radiosurgery"],
  },

  // Pain / Trigeminal Neuralgia
  {
    name: "Microvascular Decompression",
    category: "Procedure",
    aliases: ["mvd", "trigeminal neuralgia surgery", "jannetta procedure"],
  },
  {
    name: "MVD for Trigeminal Neuralgia",
    category: "Procedure",
    aliases: ["mvd tn", "trigeminal mvd"],
  },
  {
    name: "MVD for Hemifacial Spasm",
    category: "Procedure",
    aliases: ["mvd hfs", "hemifacial spasm surgery"],
  },
  {
    name: "MVD for Glossopharyngeal Neuralgia",
    category: "Procedure",
    aliases: ["mvd gn"],
  },
  {
    name: "Percutaneous Rhizotomy",
    category: "Procedure",
    aliases: [
      "trigeminal rhizotomy",
      "glycerol rhizotomy",
      "balloon compression",
    ],
  },
  {
    name: "Radiofrequency Rhizotomy",
    category: "Procedure",
    aliases: ["rf rhizotomy trigeminal", "percutaneous rf lesion"],
  },
  {
    name: "Balloon Compression Trigeminal",
    category: "Procedure",
    aliases: ["percutaneous balloon compression"],
  },
  {
    name: "Glycerol Injection Trigeminal",
    category: "Procedure",
    aliases: ["percutaneous glycerol injection"],
  },
  {
    name: "Motor Cortex Stimulation Pain",
    category: "Procedure",
    aliases: ["mcs for pain", "cortical stimulation"],
  },
  {
    name: "Spinal Cord Stimulator Implant",
    category: "Procedure",
    aliases: ["scs implant", "dorsal column stimulator"],
  },
  {
    name: "Intrathecal Pump Implant",
    category: "Procedure",
    aliases: ["pain pump", "baclofen pump", "morphine pump implant"],
  },
  {
    name: "DREZ Lesion",
    category: "Procedure",
    aliases: ["dorsal root entry zone lesion", "drezotomy"],
  },
  {
    name: "Cordotomy",
    category: "Procedure",
    aliases: ["anterolateral cordotomy", "percutaneous cordotomy"],
  },
  {
    name: "Cingulotomy",
    category: "Procedure",
    aliases: ["stereotactic cingulotomy", "cingulate lesion"],
  },

  // Chiari / Posterior Fossa
  {
    name: "Chiari Decompression",
    category: "Procedure",
    aliases: [
      "chiari malformation surgery",
      "posterior fossa decompression",
      "foramen magnum decompression",
    ],
  },
  {
    name: "Chiari Decompression with Duraplasty",
    category: "Procedure",
    aliases: ["chiari duraplasty", "dural expansion"],
  },
  {
    name: "Syrinx Shunting",
    category: "Procedure",
    aliases: [
      "syringomyelia surgery",
      "syringosubarachnoid shunt",
      "syringopleural shunt",
    ],
  },
  {
    name: "Tethered Cord Release",
    category: "Procedure",
    aliases: ["tethered spinal cord surgery", "filum terminale sectioning"],
  },
  {
    name: "Dandy-Walker Surgery",
    category: "Procedure",
    aliases: ["dandy walker shunt", "posterior fossa cyst surgery"],
  },

  // Peripheral Nerve
  {
    name: "Carpal Tunnel Release Neurosurgery",
    category: "Procedure",
    aliases: ["ctr neurosurgery", "median nerve decompression"],
  },
  {
    name: "Ulnar Nerve Decompression",
    category: "Procedure",
    aliases: ["cubital tunnel release", "ulnar nerve transposition"],
  },
  {
    name: "Ulnar Nerve Transposition",
    category: "Procedure",
    aliases: ["ulnar transposition", "submuscular transposition"],
  },
  {
    name: "Peripheral Nerve Tumor Resection",
    category: "Procedure",
    aliases: [
      "schwannoma resection",
      "neurofibroma resection",
      "nerve sheath tumor",
    ],
  },
  {
    name: "Brachial Plexus Surgery",
    category: "Procedure",
    aliases: ["brachial plexus exploration", "plexus reconstruction"],
  },
  {
    name: "Nerve Transfer",
    category: "Procedure",
    aliases: ["nerve transfer surgery", "oberlin transfer"],
  },
  {
    name: "Nerve Graft",
    category: "Procedure",
    aliases: ["nerve grafting", "sural nerve graft", "cable graft"],
  },
  {
    name: "Nerve Repair",
    category: "Procedure",
    aliases: ["primary nerve repair", "nerve anastomosis", "neurorrhaphy"],
  },
  {
    name: "Neurolysis",
    category: "Procedure",
    aliases: [
      "nerve decompression",
      "external neurolysis",
      "internal neurolysis",
    ],
  },
  {
    name: "Tarsal Tunnel Release",
    category: "Procedure",
    aliases: ["posterior tibial nerve decompression"],
  },
  {
    name: "Meralgia Paresthetica Surgery",
    category: "Procedure",
    aliases: ["lateral femoral cutaneous nerve decompression"],
  },
  {
    name: "Thoracic Outlet Decompression Neuro",
    category: "Procedure",
    aliases: ["tos surgery", "scalenectomy"],
  },
  {
    name: "Piriformis Syndrome Surgery",
    category: "Procedure",
    aliases: ["sciatic nerve decompression piriformis"],
  },

  // Pediatric Neurosurgery
  {
    name: "Myelomeningocele Repair",
    category: "Procedure",
    aliases: ["spina bifida surgery", "neural tube defect repair"],
  },
  {
    name: "Fetal Myelomeningocele Repair",
    category: "Procedure",
    aliases: ["fetal spina bifida surgery", "prenatal myelomeningocele"],
  },
  {
    name: "Encephalocele Repair",
    category: "Procedure",
    aliases: ["encephalocele surgery"],
  },
  {
    name: "Craniosynostosis Surgery",
    category: "Procedure",
    aliases: [
      "craniosynostosis repair",
      "sagittal synostosis",
      "metopic synostosis",
    ],
  },
  {
    name: "Endoscopic Strip Craniectomy",
    category: "Procedure",
    aliases: [
      "endoscopic craniosynostosis",
      "minimally invasive craniosynostosis",
    ],
  },
  {
    name: "Cranial Vault Remodeling",
    category: "Procedure",
    aliases: ["cvr", "cranial reconstruction", "pi plasty"],
  },
  {
    name: "Spring-Assisted Cranioplasty",
    category: "Procedure",
    aliases: ["spring cranioplasty"],
  },
  {
    name: "Distraction Osteogenesis Skull",
    category: "Procedure",
    aliases: ["cranial distraction"],
  },
  {
    name: "Pediatric Hydrocephalus Surgery",
    category: "Procedure",
    aliases: ["pediatric shunt", "infant shunt"],
  },
  {
    name: "Selective Dorsal Rhizotomy",
    category: "Procedure",
    aliases: ["sdr", "dorsal rhizotomy", "spasticity surgery"],
  },
  {
    name: "Intrathecal Baclofen Pump Pediatric",
    category: "Procedure",
    aliases: ["itb pump", "baclofen pump pediatric"],
  },
  {
    name: "Lipomyelomeningocele Repair",
    category: "Procedure",
    aliases: ["lipoma spinal cord", "lipomeningocele surgery"],
  },
  {
    name: "Dermal Sinus Tract Excision",
    category: "Procedure",
    aliases: ["congenital dermal sinus", "pilonidal dimple excision spine"],
  },
  {
    name: "Split Cord Malformation Repair",
    category: "Procedure",
    aliases: ["diastematomyelia surgery", "diplomyelia repair"],
  },

  // ============================================
  // PROCEDURES - ENT
  // ============================================

  // Tonsils & Adenoids
  {
    name: "Tonsillectomy",
    category: "Procedure",
    aliases: ["tonsil removal", "tonsil surgery"],
  },
  {
    name: "Tonsillectomy and Adenoidectomy",
    category: "Procedure",
    aliases: ["t&a", "tonsils and adenoids"],
  },
  {
    name: "Adenoidectomy",
    category: "Procedure",
    aliases: ["adenoid removal", "adenoid surgery"],
  },
  {
    name: "Intracapsular Tonsillectomy",
    category: "Procedure",
    aliases: ["partial tonsillectomy", "tonsillotomy"],
  },
  {
    name: "Lingual Tonsillectomy",
    category: "Procedure",
    aliases: ["lingual tonsil removal", "base of tongue tonsillectomy"],
  },
  {
    name: "Peritonsillar Abscess Drainage",
    category: "Procedure",
    aliases: ["quinsy drainage", "pta drainage", "peritonsillar i&d"],
  },

  // Nose - Septum & Turbinates
  {
    name: "Septoplasty",
    category: "Procedure",
    aliases: [
      "deviated septum surgery",
      "nasal septum repair",
      "septal surgery",
    ],
  },
  {
    name: "Septorhinoplasty",
    category: "Procedure",
    aliases: ["septoplasty with rhinoplasty", "functional rhinoplasty"],
  },
  {
    name: "Submucous Resection",
    category: "Procedure",
    aliases: ["smr", "septal resection"],
  },
  {
    name: "Septal Perforation Repair",
    category: "Procedure",
    aliases: ["septal hole repair", "perforation closure"],
  },
  {
    name: "Septal Button Placement",
    category: "Procedure",
    aliases: ["septal prosthesis"],
  },
  {
    name: "Turbinate Reduction",
    category: "Procedure",
    aliases: [
      "turbinoplasty",
      "inferior turbinate reduction",
      "turbinate surgery",
    ],
  },
  {
    name: "Radiofrequency Turbinate Reduction",
    category: "Procedure",
    aliases: ["rf turbinate", "coblation turbinate", "somnoplasty turbinate"],
  },
  {
    name: "Turbinate Outfracture",
    category: "Procedure",
    aliases: ["inferior turbinate outfracture"],
  },
  {
    name: "Partial Turbinectomy",
    category: "Procedure",
    aliases: ["turbinate excision"],
  },

  // Nose - Rhinoplasty & Reconstruction
  {
    name: "Rhinoplasty",
    category: "Procedure",
    aliases: ["nose surgery", "nose job", "nasal reshaping"],
  },
  {
    name: "Open Rhinoplasty",
    category: "Procedure",
    aliases: ["external rhinoplasty"],
  },
  {
    name: "Closed Rhinoplasty",
    category: "Procedure",
    aliases: ["endonasal rhinoplasty"],
  },
  {
    name: "Revision Rhinoplasty",
    category: "Procedure",
    aliases: ["secondary rhinoplasty", "redo nose surgery"],
  },
  {
    name: "Nasal Valve Repair",
    category: "Procedure",
    aliases: [
      "nasal valve surgery",
      "nasal valve collapse repair",
      "spreader grafts",
    ],
  },
  {
    name: "Alar Batten Grafts",
    category: "Procedure",
    aliases: ["lateral nasal wall grafts"],
  },
  {
    name: "Nasal Fracture Reduction",
    category: "Procedure",
    aliases: ["broken nose repair", "closed nasal reduction"],
  },
  {
    name: "Open Nasal Fracture Repair",
    category: "Procedure",
    aliases: ["nasal bone orif"],
  },
  {
    name: "Nasal Reconstruction",
    category: "Procedure",
    aliases: ["nose reconstruction", "nasal defect repair"],
  },

  // Sinus Surgery
  {
    name: "FESS",
    category: "Procedure",
    aliases: [
      "functional endoscopic sinus surgery",
      "endoscopic sinus surgery",
      "ess",
    ],
  },
  {
    name: "Maxillary Antrostomy",
    category: "Procedure",
    aliases: ["maxillary sinus surgery", "middle meatal antrostomy"],
  },
  {
    name: "Ethmoidectomy",
    category: "Procedure",
    aliases: [
      "ethmoid sinus surgery",
      "anterior ethmoidectomy",
      "posterior ethmoidectomy",
    ],
  },
  {
    name: "Frontal Sinusotomy",
    category: "Procedure",
    aliases: [
      "frontal sinus surgery",
      "draf procedure",
      "frontal recess surgery",
    ],
  },
  {
    name: "Draf III Procedure",
    category: "Procedure",
    aliases: [
      "modified lothrop",
      "frontal drillout",
      "endoscopic modified lothrop",
    ],
  },
  {
    name: "Sphenoidotomy",
    category: "Procedure",
    aliases: ["sphenoid sinus surgery", "sphenoid sinusotomy"],
  },
  {
    name: "Balloon Sinuplasty",
    category: "Procedure",
    aliases: ["sinus balloon", "balloon sinus dilation", "bsd"],
  },
  {
    name: "Image-Guided Sinus Surgery",
    category: "Procedure",
    aliases: ["navigation sinus surgery", "igs sinus"],
  },
  {
    name: "Revision Sinus Surgery",
    category: "Procedure",
    aliases: ["redo sinus surgery", "revision fess"],
  },
  {
    name: "Caldwell-Luc Procedure",
    category: "Procedure",
    aliases: ["maxillary sinus surgery open", "canine fossa approach"],
  },
  {
    name: "Frontal Sinus Obliteration",
    category: "Procedure",
    aliases: ["frontal sinus surgery open", "osteoplastic flap"],
  },
  {
    name: "Endoscopic Sinus Debridement",
    category: "Procedure",
    aliases: ["sinus debridement", "postop sinus cleaning"],
  },
  {
    name: "Nasal Polypectomy",
    category: "Procedure",
    aliases: ["polyp removal nose", "sinus polyp removal", "polypectomy"],
  },
  {
    name: "CSF Leak Repair Endoscopic",
    category: "Procedure",
    aliases: ["csf rhinorrhea repair", "endoscopic skull base repair"],
  },
  {
    name: "Choanal Atresia Repair",
    category: "Procedure",
    aliases: ["choanal atresia surgery"],
  },

  // Ear - General / Otology
  {
    name: "Myringotomy",
    category: "Procedure",
    aliases: [
      "ear tubes",
      "pe tubes",
      "tympanostomy tubes",
      "pressure equalization tubes",
      "grommets",
    ],
  },
  {
    name: "Myringotomy with Tube Placement",
    category: "Procedure",
    aliases: ["tubes bilateral", "bmt", "bilateral myringotomy tubes"],
  },
  {
    name: "Tube Removal",
    category: "Procedure",
    aliases: ["ear tube removal", "tympanostomy tube removal"],
  },
  {
    name: "Tympanoplasty",
    category: "Procedure",
    aliases: ["eardrum repair", "tympanic membrane repair", "drum repair"],
  },
  {
    name: "Type I Tympanoplasty",
    category: "Procedure",
    aliases: ["myringoplasty", "eardrum patch"],
  },
  {
    name: "Cartilage Tympanoplasty",
    category: "Procedure",
    aliases: ["cartilage graft tympanoplasty"],
  },
  {
    name: "Tympanomastoidectomy",
    category: "Procedure",
    aliases: ["tympanoplasty with mastoidectomy"],
  },
  {
    name: "Mastoidectomy",
    category: "Procedure",
    aliases: ["mastoid surgery"],
  },
  {
    name: "Simple Mastoidectomy",
    category: "Procedure",
    aliases: ["cortical mastoidectomy"],
  },
  {
    name: "Modified Radical Mastoidectomy",
    category: "Procedure",
    aliases: ["canal wall down mastoidectomy", "cwd"],
  },
  {
    name: "Canal Wall Up Mastoidectomy",
    category: "Procedure",
    aliases: ["intact canal wall mastoidectomy", "cwu"],
  },
  {
    name: "Radical Mastoidectomy",
    category: "Procedure",
    aliases: ["complete mastoidectomy"],
  },
  {
    name: "Mastoid Obliteration",
    category: "Procedure",
    aliases: ["mastoid cavity obliteration"],
  },
  {
    name: "Ossiculoplasty",
    category: "Procedure",
    aliases: [
      "ossicular chain reconstruction",
      "ocr",
      "hearing bone reconstruction",
    ],
  },
  {
    name: "Stapedectomy",
    category: "Procedure",
    aliases: ["stapes surgery", "otosclerosis surgery"],
  },
  {
    name: "Stapedotomy",
    category: "Procedure",
    aliases: ["laser stapedotomy", "partial stapedectomy"],
  },
  {
    name: "Stapes Revision Surgery",
    category: "Procedure",
    aliases: ["revision stapedectomy"],
  },
  {
    name: "Cholesteatoma Surgery",
    category: "Procedure",
    aliases: ["cholesteatoma removal", "cholesteatoma excision"],
  },
  {
    name: "Second Look Tympanomastoidectomy",
    category: "Procedure",
    aliases: ["second stage ear surgery", "planned second look"],
  },
  {
    name: "Canalplasty",
    category: "Procedure",
    aliases: ["ear canal surgery", "ear canal widening", "exostosis removal"],
  },
  {
    name: "Exostosis Removal",
    category: "Procedure",
    aliases: ["surfer's ear surgery", "ear canal exostectomy"],
  },
  {
    name: "Aural Atresia Repair",
    category: "Procedure",
    aliases: ["ear canal atresia surgery", "atresiaplasty"],
  },

  // Ear - Hearing Devices
  {
    name: "Cochlear Implant",
    category: "Procedure",
    aliases: ["ci", "cochlear implantation", "cochlear implant surgery"],
  },
  {
    name: "Bilateral Cochlear Implants",
    category: "Procedure",
    aliases: ["bilateral ci", "simultaneous cochlear implants"],
  },
  {
    name: "Cochlear Implant Revision",
    category: "Procedure",
    aliases: ["ci revision", "reimplantation"],
  },
  {
    name: "Bone Anchored Hearing Aid",
    category: "Procedure",
    aliases: ["baha", "osseointegrated hearing device", "osia"],
  },
  {
    name: "BAHA Connect",
    category: "Procedure",
    aliases: ["percutaneous baha", "abutment baha"],
  },
  {
    name: "BAHA Attract",
    category: "Procedure",
    aliases: ["magnetic baha", "transcutaneous baha"],
  },
  {
    name: "Osia Implant",
    category: "Procedure",
    aliases: ["osia system", "cochlear osia"],
  },
  {
    name: "Ponto Implant",
    category: "Procedure",
    aliases: ["oticon ponto", "ponto baha"],
  },
  {
    name: "Middle Ear Implant",
    category: "Procedure",
    aliases: ["vibrant soundbridge", "active middle ear implant", "mei"],
  },
  {
    name: "Auditory Brainstem Implant",
    category: "Procedure",
    aliases: ["abi", "brainstem implant"],
  },
  {
    name: "Eustachian Tube Dilation",
    category: "Procedure",
    aliases: ["balloon eustachian tuboplasty", "etd", "aera"],
  },

  // Vestibular / Balance
  {
    name: "Labyrinthectomy",
    category: "Procedure",
    aliases: ["inner ear surgery vertigo", "labyrinth destruction"],
  },
  {
    name: "Vestibular Nerve Section",
    category: "Procedure",
    aliases: ["vestibular neurectomy"],
  },
  {
    name: "Endolymphatic Sac Decompression",
    category: "Procedure",
    aliases: ["endolymphatic sac surgery", "meniere's surgery"],
  },
  {
    name: "Semicircular Canal Plugging",
    category: "Procedure",
    aliases: ["canal plugging", "superior canal dehiscence repair"],
  },
  {
    name: "Superior Canal Dehiscence Repair",
    category: "Procedure",
    aliases: ["scds repair", "scd surgery"],
  },
  {
    name: "Intratympanic Steroid Injection",
    category: "Procedure",
    aliases: ["it steroid", "inner ear steroid"],
  },
  {
    name: "Intratympanic Gentamicin",
    category: "Procedure",
    aliases: ["chemical labyrinthectomy", "gentamicin ablation"],
  },

  // Larynx & Voice
  {
    name: "Laryngoscopy",
    category: "Procedure",
    aliases: ["direct laryngoscopy", "diagnostic laryngoscopy"],
  },
  {
    name: "Microlaryngoscopy",
    category: "Procedure",
    aliases: ["microlaryngeal surgery", "mls", "operative laryngoscopy"],
  },
  {
    name: "Suspension Laryngoscopy",
    category: "Procedure",
    aliases: ["suspension microlaryngoscopy"],
  },
  {
    name: "Vocal Cord Injection",
    category: "Procedure",
    aliases: [
      "vocal fold injection",
      "injection laryngoplasty",
      "voice injection",
    ],
  },
  {
    name: "Vocal Cord Medialization",
    category: "Procedure",
    aliases: [
      "thyroplasty",
      "medialization laryngoplasty",
      "type i thyroplasty",
    ],
  },
  {
    name: "Arytenoid Adduction",
    category: "Procedure",
    aliases: ["arytenoid surgery"],
  },
  {
    name: "Vocal Cord Polyp Removal",
    category: "Procedure",
    aliases: ["vocal fold polyp excision", "polyp microlaryngoscopy"],
  },
  {
    name: "Vocal Cord Nodule Removal",
    category: "Procedure",
    aliases: ["vocal fold nodule excision", "singer's nodule surgery"],
  },
  {
    name: "Vocal Cord Cyst Excision",
    category: "Procedure",
    aliases: ["vocal fold cyst removal"],
  },
  {
    name: "Reinke's Edema Surgery",
    category: "Procedure",
    aliases: ["polypoid corditis surgery"],
  },
  {
    name: "Vocal Cord Paralysis Surgery",
    category: "Procedure",
    aliases: ["vocal fold paralysis treatment"],
  },
  {
    name: "Laryngeal Papilloma Removal",
    category: "Procedure",
    aliases: ["rrp surgery", "papillomatosis surgery", "laser papilloma"],
  },
  {
    name: "Laser Laryngoscopy",
    category: "Procedure",
    aliases: ["co2 laser laryngoscopy", "laryngeal laser surgery"],
  },
  {
    name: "Laryngeal Botox",
    category: "Procedure",
    aliases: ["botox for voice", "spasmodic dysphonia botox"],
  },
  {
    name: "Laryngeal Framework Surgery",
    category: "Procedure",
    aliases: ["laryngeal skeleton surgery"],
  },
  {
    name: "Cricotracheal Resection",
    category: "Procedure",
    aliases: ["ctr", "subglottic stenosis surgery"],
  },
  {
    name: "Laryngotracheal Reconstruction",
    category: "Procedure",
    aliases: ["ltr", "airway reconstruction"],
  },
  {
    name: "Laryngeal Cleft Repair",
    category: "Procedure",
    aliases: ["cleft larynx repair"],
  },
  {
    name: "Supraglottoplasty",
    category: "Procedure",
    aliases: ["laryngomalacia surgery", "epiglottoplasty"],
  },

  // Trachea & Airway
  {
    name: "Tracheostomy",
    category: "Procedure",
    aliases: ["trach", "tracheotomy", "surgical airway"],
  },
  {
    name: "Percutaneous Tracheostomy",
    category: "Procedure",
    aliases: ["pdt", "percutaneous dilational tracheostomy", "bedside trach"],
  },
  {
    name: "Tracheostomy Revision",
    category: "Procedure",
    aliases: ["trach revision", "stomal revision"],
  },
  {
    name: "Tracheostomy Decannulation",
    category: "Procedure",
    aliases: ["trach removal", "decannulation"],
  },
  {
    name: "Tracheal Resection",
    category: "Procedure",
    aliases: ["tracheal stenosis surgery", "tracheal anastomosis"],
  },
  {
    name: "Tracheal Dilation",
    category: "Procedure",
    aliases: ["airway dilation", "tracheal balloon dilation"],
  },
  {
    name: "Tracheal Stent Placement",
    category: "Procedure",
    aliases: ["airway stent"],
  },
  {
    name: "Bronchoscopy ENT",
    category: "Procedure",
    aliases: ["rigid bronchoscopy", "airway bronchoscopy"],
  },

  // Head & Neck Oncology
  {
    name: "Neck Dissection",
    category: "Procedure",
    aliases: ["lymph node dissection neck", "cervical lymphadenectomy"],
  },
  {
    name: "Radical Neck Dissection",
    category: "Procedure",
    aliases: ["rnd", "comprehensive neck dissection"],
  },
  {
    name: "Modified Radical Neck Dissection",
    category: "Procedure",
    aliases: ["mrnd", "functional neck dissection"],
  },
  {
    name: "Selective Neck Dissection",
    category: "Procedure",
    aliases: ["snd", "limited neck dissection"],
  },
  {
    name: "Sentinel Lymph Node Biopsy Head Neck",
    category: "Procedure",
    aliases: ["slnb head neck", "sentinel node head neck"],
  },
  {
    name: "Laryngectomy",
    category: "Procedure",
    aliases: ["voice box removal"],
  },
  {
    name: "Total Laryngectomy",
    category: "Procedure",
    aliases: ["complete laryngectomy"],
  },
  {
    name: "Partial Laryngectomy",
    category: "Procedure",
    aliases: ["conservation laryngeal surgery", "hemilaryngectomy"],
  },
  {
    name: "Supraglottic Laryngectomy",
    category: "Procedure",
    aliases: ["horizontal partial laryngectomy"],
  },
  {
    name: "Transoral Laser Microsurgery",
    category: "Procedure",
    aliases: ["tlm", "laser laryngeal cancer surgery"],
  },
  {
    name: "Transoral Robotic Surgery",
    category: "Procedure",
    aliases: ["tors", "robotic head neck surgery", "davinci tors"],
  },
  {
    name: "Glossectomy",
    category: "Procedure",
    aliases: ["tongue removal", "partial glossectomy", "hemiglossectomy"],
  },
  {
    name: "Base of Tongue Resection",
    category: "Procedure",
    aliases: ["bot resection", "tongue base surgery"],
  },
  {
    name: "Mandibulectomy",
    category: "Procedure",
    aliases: ["jaw resection", "mandible resection"],
  },
  {
    name: "Marginal Mandibulectomy",
    category: "Procedure",
    aliases: ["rim mandibulectomy"],
  },
  {
    name: "Segmental Mandibulectomy",
    category: "Procedure",
    aliases: ["mandible segment removal"],
  },
  {
    name: "Maxillectomy",
    category: "Procedure",
    aliases: ["upper jaw resection"],
  },
  {
    name: "Partial Maxillectomy",
    category: "Procedure",
    aliases: ["infrastructure maxillectomy"],
  },
  {
    name: "Total Maxillectomy",
    category: "Procedure",
    aliases: ["complete maxillectomy"],
  },
  {
    name: "Pharyngectomy",
    category: "Procedure",
    aliases: ["pharynx resection"],
  },
  {
    name: "Oropharyngeal Cancer Surgery",
    category: "Procedure",
    aliases: ["oropharynx resection"],
  },
  {
    name: "Hypopharyngeal Cancer Surgery",
    category: "Procedure",
    aliases: ["hypopharynx resection", "pyriform sinus surgery"],
  },
  {
    name: "Free Flap Reconstruction Head Neck",
    category: "Procedure",
    aliases: ["microvascular reconstruction", "free tissue transfer head neck"],
  },
  {
    name: "Radial Forearm Free Flap",
    category: "Procedure",
    aliases: ["rfff", "radial forearm flap"],
  },
  {
    name: "Fibula Free Flap",
    category: "Procedure",
    aliases: ["fibular flap", "jaw reconstruction"],
  },
  {
    name: "Anterolateral Thigh Flap",
    category: "Procedure",
    aliases: ["alt flap"],
  },
  {
    name: "Pectoralis Major Flap",
    category: "Procedure",
    aliases: ["pec flap", "pmmc flap"],
  },
  {
    name: "Tracheoesophageal Puncture",
    category: "Procedure",
    aliases: ["tep", "voice prosthesis", "voice restoration surgery"],
  },

  // Thyroid & Parathyroid
  {
    name: "Thyroidectomy",
    category: "Procedure",
    aliases: ["thyroid removal", "thyroid surgery"],
  },
  {
    name: "Total Thyroidectomy",
    category: "Procedure",
    aliases: ["complete thyroidectomy"],
  },
  {
    name: "Hemithyroidectomy",
    category: "Procedure",
    aliases: ["thyroid lobectomy", "thyroid lobe removal"],
  },
  {
    name: "Thyroid Lobectomy with Isthmusectomy",
    category: "Procedure",
    aliases: ["lobectomy isthmusectomy"],
  },
  {
    name: "Completion Thyroidectomy",
    category: "Procedure",
    aliases: ["completion thyroid surgery"],
  },
  {
    name: "Subtotal Thyroidectomy",
    category: "Procedure",
    aliases: ["near-total thyroidectomy"],
  },
  {
    name: "Robotic Thyroidectomy",
    category: "Procedure",
    aliases: ["transaxillary thyroidectomy", "robot assisted thyroidectomy"],
  },
  {
    name: "Transoral Thyroidectomy",
    category: "Procedure",
    aliases: ["scarless thyroidectomy", "toetva"],
  },
  {
    name: "Central Neck Dissection",
    category: "Procedure",
    aliases: ["level vi dissection", "central compartment dissection"],
  },
  {
    name: "Thyroid Nodule FNA",
    category: "Procedure",
    aliases: ["thyroid biopsy", "thyroid fine needle aspiration"],
  },
  {
    name: "Parathyroidectomy",
    category: "Procedure",
    aliases: ["parathyroid removal", "parathyroid surgery"],
  },
  {
    name: "Minimally Invasive Parathyroidectomy",
    category: "Procedure",
    aliases: ["mip", "focused parathyroidectomy"],
  },
  {
    name: "Four-Gland Parathyroid Exploration",
    category: "Procedure",
    aliases: ["bilateral neck exploration", "parathyroid exploration"],
  },
  {
    name: "Parathyroid Autotransplantation",
    category: "Procedure",
    aliases: ["parathyroid reimplantation"],
  },
  {
    name: "Radioguided Parathyroidectomy",
    category: "Procedure",
    aliases: ["sestamibi guided parathyroidectomy"],
  },

  // Salivary Glands
  {
    name: "Parotidectomy",
    category: "Procedure",
    aliases: ["parotid gland removal", "parotid surgery"],
  },
  {
    name: "Superficial Parotidectomy",
    category: "Procedure",
    aliases: ["lateral parotidectomy"],
  },
  {
    name: "Total Parotidectomy",
    category: "Procedure",
    aliases: ["complete parotidectomy"],
  },
  {
    name: "Deep Lobe Parotidectomy",
    category: "Procedure",
    aliases: ["deep parotidectomy"],
  },
  {
    name: "Parotidectomy with Facial Nerve Sacrifice",
    category: "Procedure",
    aliases: ["radical parotidectomy"],
  },
  {
    name: "Submandibular Gland Excision",
    category: "Procedure",
    aliases: ["submandibular gland removal", "submandibular sialadenectomy"],
  },
  {
    name: "Sublingual Gland Excision",
    category: "Procedure",
    aliases: ["sublingual gland removal", "ranula surgery"],
  },
  {
    name: "Sialendoscopy",
    category: "Procedure",
    aliases: ["salivary endoscopy", "salivary duct endoscopy"],
  },
  {
    name: "Salivary Duct Stone Removal",
    category: "Procedure",
    aliases: ["sialolithectomy", "salivary stone removal"],
  },
  {
    name: "Salivary Gland Biopsy",
    category: "Procedure",
    aliases: ["minor salivary gland biopsy"],
  },
  {
    name: "Botox for Drooling",
    category: "Procedure",
    aliases: ["salivary gland botox", "sialorrhea treatment"],
  },
  {
    name: "Salivary Duct Ligation",
    category: "Procedure",
    aliases: ["duct ligation"],
  },

  // Sleep Surgery
  {
    name: "Uvulopalatopharyngoplasty",
    category: "Procedure",
    aliases: ["uppp", "sleep apnea surgery", "up3"],
  },
  {
    name: "Expansion Sphincter Pharyngoplasty",
    category: "Procedure",
    aliases: ["esp", "lateral pharyngoplasty"],
  },
  {
    name: "Inspire Implant",
    category: "Procedure",
    aliases: [
      "hypoglossal nerve stimulator",
      "inspire therapy",
      "upper airway stimulation",
    ],
  },
  {
    name: "Genioglossus Advancement",
    category: "Procedure",
    aliases: ["gga", "tongue advancement"],
  },
  {
    name: "Hyoid Suspension",
    category: "Procedure",
    aliases: ["hyoid myotomy suspension"],
  },
  {
    name: "Maxillomandibular Advancement",
    category: "Procedure",
    aliases: ["mma", "jaw advancement sleep apnea"],
  },
  {
    name: "Tongue Base Reduction",
    category: "Procedure",
    aliases: ["tongue reduction", "midline glossectomy"],
  },
  {
    name: "Radiofrequency Tongue Base",
    category: "Procedure",
    aliases: ["tongue base somnoplasty", "rf tongue base"],
  },
  {
    name: "Palatal Implants",
    category: "Procedure",
    aliases: ["pillar procedure", "snoring implants"],
  },
  {
    name: "Nasal Surgery for Sleep Apnea",
    category: "Procedure",
    aliases: ["septoplasty for osa"],
  },
  {
    name: "Drug-Induced Sleep Endoscopy",
    category: "Procedure",
    aliases: ["dise", "sleep endoscopy"],
  },

  // Facial Plastic Surgery
  {
    name: "Facelift",
    category: "Procedure",
    aliases: ["rhytidectomy", "face lift"],
  },
  {
    name: "Neck Lift",
    category: "Procedure",
    aliases: ["cervicoplasty", "platysmaplasty"],
  },
  {
    name: "Brow Lift",
    category: "Procedure",
    aliases: ["forehead lift", "browplasty"],
  },
  {
    name: "Endoscopic Brow Lift",
    category: "Procedure",
    aliases: ["endoscopic forehead lift"],
  },
  {
    name: "Blepharoplasty ENT",
    category: "Procedure",
    aliases: ["eyelid surgery ent", "eyelid lift ent"],
  },
  {
    name: "Otoplasty",
    category: "Procedure",
    aliases: ["ear pinning", "ear reshaping", "prominent ear correction"],
  },
  {
    name: "Ear Reconstruction",
    category: "Procedure",
    aliases: ["microtia repair", "auricular reconstruction"],
  },
  {
    name: "Mentoplasty",
    category: "Procedure",
    aliases: ["chin surgery", "chin implant", "genioplasty"],
  },
  {
    name: "Facial Implants",
    category: "Procedure",
    aliases: ["cheek implants", "malar implants"],
  },
  {
    name: "Fat Transfer Face",
    category: "Procedure",
    aliases: ["facial fat grafting", "fat injection face"],
  },
  {
    name: "Scar Revision Face",
    category: "Procedure",
    aliases: ["facial scar revision"],
  },
  {
    name: "Dermabrasion",
    category: "Procedure",
    aliases: ["facial dermabrasion"],
  },
  {
    name: "Facial Nerve Repair",
    category: "Procedure",
    aliases: ["facial nerve surgery", "nerve grafting face"],
  },
  {
    name: "Facial Reanimation",
    category: "Procedure",
    aliases: ["smile surgery", "facial paralysis surgery"],
  },
  {
    name: "Temporalis Tendon Transfer",
    category: "Procedure",
    aliases: ["smile restoration"],
  },
  {
    name: "Gracilis Free Flap Face",
    category: "Procedure",
    aliases: ["free muscle transfer face"],
  },

  // Skull Base
  {
    name: "Endoscopic Skull Base Surgery",
    category: "Procedure",
    aliases: ["endonasal skull base", "transnasal skull base"],
  },
  {
    name: "Endoscopic Pituitary Surgery",
    category: "Procedure",
    aliases: ["transsphenoidal pituitary", "endonasal pituitary"],
  },
  {
    name: "Acoustic Neuroma Surgery",
    category: "Procedure",
    aliases: [
      "vestibular schwannoma surgery",
      "cerebellopontine angle surgery",
    ],
  },
  {
    name: "Translabyrinthine Approach",
    category: "Procedure",
    aliases: ["translabyrinthine surgery"],
  },
  {
    name: "Middle Fossa Approach",
    category: "Procedure",
    aliases: ["middle cranial fossa approach"],
  },
  {
    name: "Retrosigmoid Approach",
    category: "Procedure",
    aliases: ["retrosigmoid craniotomy"],
  },
  {
    name: "Glomus Tumor Surgery",
    category: "Procedure",
    aliases: ["paraganglioma surgery", "glomus jugulare surgery"],
  },
  {
    name: "Temporal Bone Resection",
    category: "Procedure",
    aliases: [
      "lateral temporal bone resection",
      "temporal bone cancer surgery",
    ],
  },
  {
    name: "Infratemporal Fossa Approach",
    category: "Procedure",
    aliases: ["itf approach", "fisch approach"],
  },
  {
    name: "Anterior Craniofacial Resection",
    category: "Procedure",
    aliases: ["craniofacial surgery", "combined approach skull base"],
  },
  {
    name: "Orbital Decompression ENT",
    category: "Procedure",
    aliases: ["endoscopic orbital decompression"],
  },
  {
    name: "Optic Nerve Decompression",
    category: "Procedure",
    aliases: ["optic canal decompression"],
  },

  // ============================================
  // PROCEDURES - OPHTHALMOLOGY
  // ============================================

  // Cataract surgery
  {
    name: "Cataract Surgery",
    category: "Procedure",
    aliases: ["cataract removal", "cataract extraction"],
  },
  {
    name: "Phacoemulsification",
    category: "Procedure",
    aliases: ["phaco", "ultrasound cataract removal"],
  },
  {
    name: "Extracapsular Cataract Extraction",
    category: "Procedure",
    aliases: ["ecce", "manual cataract extraction"],
  },
  {
    name: "Femtosecond Laser Cataract Surgery",
    category: "Procedure",
    aliases: ["laser cataract", "flacs", "bladeless cataract"],
  },
  {
    name: "IOL Implantation",
    category: "Procedure",
    aliases: ["intraocular lens", "lens implant", "iol"],
  },
  {
    name: "Premium IOL Implantation",
    category: "Procedure",
    aliases: [
      "multifocal iol",
      "toric iol",
      "accommodating iol",
      "extended depth of focus",
      "edof",
    ],
  },
  {
    name: "Secondary IOL Implantation",
    category: "Procedure",
    aliases: ["secondary lens implant", "sulcus iol", "scleral fixated iol"],
  },
  {
    name: "IOL Exchange",
    category: "Procedure",
    aliases: ["lens exchange", "iol replacement"],
  },
  {
    name: "YAG Capsulotomy",
    category: "Procedure",
    aliases: ["yag laser", "posterior capsulotomy", "after cataract treatment"],
  },
  {
    name: "Anterior Vitrectomy",
    category: "Procedure",
    aliases: ["limited vitrectomy", "vitrectomy with cataract"],
  },

  // Refractive surgery
  {
    name: "LASIK",
    category: "Procedure",
    aliases: ["laser eye surgery", "lasik surgery", "laser vision correction"],
  },
  {
    name: "PRK",
    category: "Procedure",
    aliases: ["photorefractive keratectomy", "surface ablation"],
  },
  {
    name: "LASEK",
    category: "Procedure",
    aliases: ["laser epithelial keratomileusis"],
  },
  {
    name: "SMILE",
    category: "Procedure",
    aliases: ["small incision lenticule extraction", "relex smile"],
  },
  {
    name: "ICL Implantation",
    category: "Procedure",
    aliases: [
      "implantable collamer lens",
      "implantable contact lens",
      "phakic iol",
      "visian icl",
    ],
  },
  {
    name: "Refractive Lens Exchange",
    category: "Procedure",
    aliases: [
      "rle",
      "clear lens extraction",
      "cle",
      "presbyopic lens exchange",
    ],
  },
  {
    name: "Limbal Relaxing Incisions",
    category: "Procedure",
    aliases: ["lri", "astigmatic keratotomy", "ak"],
  },
  {
    name: "Conductive Keratoplasty",
    category: "Procedure",
    aliases: ["ck", "nearview ck"],
  },
  {
    name: "Intacs",
    category: "Procedure",
    aliases: ["intracorneal ring segments", "icrs", "corneal implants"],
  },

  // Glaucoma - Laser procedures
  {
    name: "SLT",
    category: "Procedure",
    aliases: ["selective laser trabeculoplasty", "glaucoma laser"],
  },
  {
    name: "ALT",
    category: "Procedure",
    aliases: ["argon laser trabeculoplasty"],
  },
  {
    name: "Laser Peripheral Iridotomy",
    category: "Procedure",
    aliases: ["lpi", "iridotomy", "laser iridotomy", "yag iridotomy"],
  },
  {
    name: "Laser Iridoplasty",
    category: "Procedure",
    aliases: ["gonioplasty", "peripheral iridoplasty"],
  },
  {
    name: "Cyclophotocoagulation",
    category: "Procedure",
    aliases: [
      "cpc",
      "transscleral cyclophotocoagulation",
      "tcp",
      "diode laser cyclo",
    ],
  },
  {
    name: "Micropulse Cyclophotocoagulation",
    category: "Procedure",
    aliases: ["micropulse cpc", "mp3", "micropulse laser"],
  },
  {
    name: "Endocyclophotocoagulation",
    category: "Procedure",
    aliases: ["ecp", "endoscopic cyclophotocoagulation"],
  },

  // Glaucoma - Incisional surgery
  {
    name: "Trabeculectomy",
    category: "Procedure",
    aliases: ["trab", "filtering surgery", "glaucoma filter"],
  },
  {
    name: "Glaucoma Tube Shunt",
    category: "Procedure",
    aliases: [
      "tube shunt",
      "glaucoma drainage device",
      "gdd",
      "ahmed valve",
      "baerveldt",
      "molteno",
    ],
  },
  {
    name: "Tube Shunt Revision",
    category: "Procedure",
    aliases: ["tube revision", "gdd revision"],
  },
  {
    name: "Bleb Revision",
    category: "Procedure",
    aliases: ["bleb needling", "bleb repair"],
  },
  {
    name: "Goniotomy",
    category: "Procedure",
    aliases: ["kahook dual blade", "kdb", "trabectome"],
  },
  {
    name: "Trabeculotomy",
    category: "Procedure",
    aliases: ["gonioscopy-assisted transluminal trabeculotomy", "gatt"],
  },

  // Glaucoma - MIGS
  {
    name: "MIGS",
    category: "Procedure",
    aliases: [
      "minimally invasive glaucoma surgery",
      "microinvasive glaucoma surgery",
    ],
  },
  {
    name: "iStent",
    category: "Procedure",
    aliases: ["istent inject", "trabecular micro-bypass"],
  },
  { name: "Hydrus Microstent", category: "Procedure", aliases: ["hydrus"] },
  {
    name: "Xen Gel Stent",
    category: "Procedure",
    aliases: ["xen", "xen45", "ab interno gelatin stent"],
  },
  { name: "Cypass Micro-Stent", category: "Procedure", aliases: ["cypass"] },
  {
    name: "GATT Procedure",
    category: "Procedure",
    aliases: ["gonioscopy assisted transluminal trabeculotomy"],
  },
  {
    name: "Canaloplasty",
    category: "Procedure",
    aliases: ["ab interno canaloplasty", "abic"],
  },
  {
    name: "Viscocanalostomy",
    category: "Procedure",
    aliases: ["non-penetrating glaucoma surgery"],
  },

  // Retina - Vitreoretinal surgery
  {
    name: "Vitrectomy",
    category: "Procedure",
    aliases: ["vitreous surgery", "pars plana vitrectomy", "ppv"],
  },
  {
    name: "23-Gauge Vitrectomy",
    category: "Procedure",
    aliases: ["23g vitrectomy", "small gauge vitrectomy"],
  },
  {
    name: "25-Gauge Vitrectomy",
    category: "Procedure",
    aliases: ["25g vitrectomy"],
  },
  {
    name: "27-Gauge Vitrectomy",
    category: "Procedure",
    aliases: ["27g vitrectomy"],
  },
  {
    name: "Membrane Peel",
    category: "Procedure",
    aliases: [
      "epiretinal membrane peel",
      "erm peel",
      "macular pucker surgery",
      "ilm peel",
    ],
  },
  {
    name: "Macular Hole Surgery",
    category: "Procedure",
    aliases: ["macular hole repair"],
  },
  {
    name: "Retinal Detachment Repair",
    category: "Procedure",
    aliases: ["retina surgery", "rd repair"],
  },
  {
    name: "Scleral Buckle",
    category: "Procedure",
    aliases: ["scleral buckling", "encircling band"],
  },
  {
    name: "Pneumatic Retinopexy",
    category: "Procedure",
    aliases: ["gas bubble injection", "pneumatic retinal repair"],
  },
  {
    name: "Silicone Oil Injection",
    category: "Procedure",
    aliases: ["silicone oil tamponade", "so injection"],
  },
  {
    name: "Silicone Oil Removal",
    category: "Procedure",
    aliases: ["soor", "oil removal"],
  },
  {
    name: "Vitreous Hemorrhage Surgery",
    category: "Procedure",
    aliases: ["vitreous hemorrhage removal"],
  },
  {
    name: "Proliferative Vitreoretinopathy Surgery",
    category: "Procedure",
    aliases: ["pvr surgery", "pvr repair"],
  },
  {
    name: "Dislocated IOL Repair",
    category: "Procedure",
    aliases: [
      "dislocated lens repair",
      "iol repositioning",
      "dropped iol surgery",
    ],
  },
  {
    name: "Floater Removal",
    category: "Procedure",
    aliases: ["floaterectomy", "vitrectomy for floaters"],
  },

  // Retina - Laser and injections
  {
    name: "Intravitreal Injection",
    category: "Procedure",
    aliases: ["eye injection", "ivt injection", "anti-vegf injection"],
  },
  {
    name: "Avastin Injection",
    category: "Procedure",
    aliases: ["bevacizumab injection", "intravitreal avastin"],
  },
  {
    name: "Eylea Injection",
    category: "Procedure",
    aliases: ["aflibercept injection", "intravitreal eylea"],
  },
  {
    name: "Lucentis Injection",
    category: "Procedure",
    aliases: ["ranibizumab injection", "intravitreal lucentis"],
  },
  {
    name: "Vabysmo Injection",
    category: "Procedure",
    aliases: ["faricimab injection", "intravitreal vabysmo"],
  },
  {
    name: "Ozurdex Injection",
    category: "Procedure",
    aliases: ["dexamethasone implant", "intravitreal steroid implant"],
  },
  {
    name: "Iluvien Injection",
    category: "Procedure",
    aliases: ["fluocinolone implant"],
  },
  {
    name: "Jetrea Injection",
    category: "Procedure",
    aliases: ["ocriplasmin", "enzymatic vitreolysis"],
  },
  {
    name: "Panretinal Photocoagulation",
    category: "Procedure",
    aliases: ["prp", "scatter laser", "prp laser"],
  },
  {
    name: "Focal Laser Photocoagulation",
    category: "Procedure",
    aliases: ["focal laser", "macular laser", "grid laser"],
  },
  {
    name: "Laser Retinopexy",
    category: "Procedure",
    aliases: ["retinal laser", "laser barricade", "prophylactic laser"],
  },
  {
    name: "Photodynamic Therapy",
    category: "Procedure",
    aliases: ["pdt", "visudyne", "verteporfin therapy"],
  },

  // Cornea
  {
    name: "Corneal Transplant",
    category: "Procedure",
    aliases: ["keratoplasty", "cornea transplant"],
  },
  {
    name: "Penetrating Keratoplasty",
    category: "Procedure",
    aliases: ["pk", "pkp", "full thickness corneal transplant"],
  },
  {
    name: "DSAEK",
    category: "Procedure",
    aliases: ["descemet stripping automated endothelial keratoplasty", "dsek"],
  },
  {
    name: "DMEK",
    category: "Procedure",
    aliases: ["descemet membrane endothelial keratoplasty"],
  },
  {
    name: "DALK",
    category: "Procedure",
    aliases: ["deep anterior lamellar keratoplasty"],
  },
  {
    name: "Corneal Collagen Crosslinking",
    category: "Procedure",
    aliases: [
      "cxl",
      "crosslinking",
      "collagen crosslinking",
      "keratoconus treatment",
    ],
  },
  {
    name: "Pterygium Surgery",
    category: "Procedure",
    aliases: ["pterygium removal", "pterygium excision", "surfers eye surgery"],
  },
  {
    name: "Pterygium with Graft",
    category: "Procedure",
    aliases: ["pterygium with conjunctival autograft", "cag"],
  },
  {
    name: "Amniotic Membrane Transplant",
    category: "Procedure",
    aliases: ["amt", "amniotic membrane graft", "prokera"],
  },
  {
    name: "Superficial Keratectomy",
    category: "Procedure",
    aliases: ["phototherapeutic keratectomy", "ptk"],
  },
  {
    name: "Corneal Debridement",
    category: "Procedure",
    aliases: ["epithelial debridement"],
  },
  {
    name: "Corneal Foreign Body Removal",
    category: "Procedure",
    aliases: ["rust ring removal", "fb removal eye"],
  },
  {
    name: "Bandage Contact Lens",
    category: "Procedure",
    aliases: ["bcl", "therapeutic contact lens"],
  },
  {
    name: "Boston Keratoprosthesis",
    category: "Procedure",
    aliases: ["kpro", "artificial cornea", "boston kpro"],
  },

  // Oculoplastics - Eyelid
  {
    name: "Blepharoplasty",
    category: "Procedure",
    aliases: ["eyelid surgery", "eyelid lift"],
  },
  {
    name: "Upper Blepharoplasty",
    category: "Procedure",
    aliases: ["upper eyelid surgery", "upper lid blepharoplasty"],
  },
  {
    name: "Lower Blepharoplasty",
    category: "Procedure",
    aliases: [
      "lower eyelid surgery",
      "lower lid blepharoplasty",
      "eye bag removal",
    ],
  },
  {
    name: "Functional Blepharoplasty",
    category: "Procedure",
    aliases: ["medical blepharoplasty", "insurance blepharoplasty"],
  },
  {
    name: "Ptosis Repair",
    category: "Procedure",
    aliases: [
      "droopy eyelid surgery",
      "eyelid ptosis repair",
      "levator advancement",
    ],
  },
  {
    name: "Frontalis Sling",
    category: "Procedure",
    aliases: ["brow suspension", "frontalis suspension"],
  },
  {
    name: "Brow Lift",
    category: "Procedure",
    aliases: ["browplasty", "forehead lift", "brow ptosis repair"],
  },
  {
    name: "Ectropion Repair",
    category: "Procedure",
    aliases: ["eyelid turning out repair", "lower lid tightening"],
  },
  {
    name: "Entropion Repair",
    category: "Procedure",
    aliases: ["eyelid turning in repair", "trichiasis surgery"],
  },
  {
    name: "Eyelid Reconstruction",
    category: "Procedure",
    aliases: ["eyelid cancer reconstruction", "lid reconstruction"],
  },
  {
    name: "Eyelid Lesion Excision",
    category: "Procedure",
    aliases: [
      "eyelid tumor removal",
      "lid lesion removal",
      "chalazion excision",
    ],
  },
  {
    name: "Chalazion Incision and Curettage",
    category: "Procedure",
    aliases: ["chalazion i&c", "chalazion drainage", "stye surgery"],
  },
  {
    name: "Tarsorrhaphy",
    category: "Procedure",
    aliases: ["eyelid closure", "lid suturing"],
  },
  {
    name: "Canthoplasty",
    category: "Procedure",
    aliases: ["canthal surgery", "lateral canthoplasty"],
  },
  {
    name: "Canthotomy",
    category: "Procedure",
    aliases: ["lateral canthotomy", "cantholysis"],
  },

  // Oculoplastics - Lacrimal
  {
    name: "Dacryocystorhinostomy",
    category: "Procedure",
    aliases: ["dcr", "tear duct surgery", "blocked tear duct"],
  },
  {
    name: "External DCR",
    category: "Procedure",
    aliases: ["external dacryocystorhinostomy"],
  },
  {
    name: "Endoscopic DCR",
    category: "Procedure",
    aliases: ["endonasal dcr", "endoscopic dacryocystorhinostomy"],
  },
  {
    name: "Punctoplasty",
    category: "Procedure",
    aliases: ["punctal dilation", "punctal stenosis repair"],
  },
  {
    name: "Punctal Plug Insertion",
    category: "Procedure",
    aliases: ["punctal occlusion", "tear duct plugs", "dry eye plugs"],
  },
  {
    name: "Punctal Cautery",
    category: "Procedure",
    aliases: ["punctal occlusion permanent"],
  },
  {
    name: "Nasolacrimal Duct Probing",
    category: "Procedure",
    aliases: [
      "tear duct probing",
      "nld probing",
      "pediatric tear duct surgery",
    ],
  },
  {
    name: "Jones Tube Placement",
    category: "Procedure",
    aliases: ["conjunctivodacryocystorhinostomy", "cdcr", "jones tube"],
  },
  {
    name: "Lacrimal Gland Biopsy",
    category: "Procedure",
    aliases: ["tear gland biopsy"],
  },

  // Oculoplastics - Orbit
  {
    name: "Orbital Decompression",
    category: "Procedure",
    aliases: [
      "thyroid eye disease surgery",
      "graves orbitopathy surgery",
      "ted surgery",
    ],
  },
  {
    name: "Orbital Fracture Repair",
    category: "Procedure",
    aliases: [
      "orbital floor repair",
      "blowout fracture repair",
      "orbital wall reconstruction",
    ],
  },
  {
    name: "Orbital Tumor Excision",
    category: "Procedure",
    aliases: ["orbital mass removal", "orbital surgery"],
  },
  { name: "Orbital Biopsy", category: "Procedure", aliases: ["orbit biopsy"] },
  {
    name: "Enucleation",
    category: "Procedure",
    aliases: ["eye removal", "eyeball removal"],
  },
  {
    name: "Evisceration",
    category: "Procedure",
    aliases: ["eye contents removal"],
  },
  {
    name: "Exenteration",
    category: "Procedure",
    aliases: ["orbital exenteration", "orbital contents removal"],
  },
  {
    name: "Orbital Implant",
    category: "Procedure",
    aliases: ["eye implant", "orbital prosthesis"],
  },
  {
    name: "Ocular Prosthesis Fitting",
    category: "Procedure",
    aliases: ["artificial eye", "prosthetic eye", "glass eye"],
  },

  // Strabismus and pediatric
  {
    name: "Strabismus Surgery",
    category: "Procedure",
    aliases: [
      "eye muscle surgery",
      "crossed eyes surgery",
      "eye alignment surgery",
    ],
  },
  {
    name: "Recession Procedure",
    category: "Procedure",
    aliases: ["muscle recession", "extraocular muscle recession"],
  },
  {
    name: "Resection Procedure",
    category: "Procedure",
    aliases: ["muscle resection", "extraocular muscle resection"],
  },
  {
    name: "Adjustable Suture Strabismus Surgery",
    category: "Procedure",
    aliases: ["adjustable sutures"],
  },
  {
    name: "Botox for Strabismus",
    category: "Procedure",
    aliases: ["botulinum toxin eye muscle"],
  },
  {
    name: "Pediatric Eye Exam Under Anesthesia",
    category: "Procedure",
    aliases: ["eua", "exam under anesthesia"],
  },
  {
    name: "Retinopathy of Prematurity Treatment",
    category: "Procedure",
    aliases: ["rop laser", "rop treatment", "rop injection"],
  },

  // Conjunctiva and sclera
  {
    name: "Conjunctival Biopsy",
    category: "Procedure",
    aliases: ["conjunctiva biopsy"],
  },
  {
    name: "Conjunctival Excision",
    category: "Procedure",
    aliases: ["conjunctival lesion removal", "pinguecula excision"],
  },
  {
    name: "Conjunctival Graft",
    category: "Procedure",
    aliases: ["conjunctival autograft", "conjunctival transplant"],
  },
  {
    name: "Symblepharon Lysis",
    category: "Procedure",
    aliases: ["symblepharon release", "conjunctival adhesion lysis"],
  },
  {
    name: "Scleral Patch Graft",
    category: "Procedure",
    aliases: ["scleral reinforcement"],
  },

  // ============================================
  // PROCEDURES - GI/ENDOSCOPY
  // ============================================

  // Upper Endoscopy
  {
    name: "Upper Endoscopy",
    category: "Procedure",
    aliases: [
      "egd",
      "esophagogastroduodenoscopy",
      "upper gi endoscopy",
      "gastroscopy",
    ],
  },
  {
    name: "Diagnostic EGD",
    category: "Procedure",
    aliases: ["diagnostic upper endoscopy", "diagnostic egd"],
  },
  {
    name: "EGD with Biopsy",
    category: "Procedure",
    aliases: [
      "upper endoscopy with biopsy",
      "gastric biopsy",
      "esophageal biopsy",
    ],
  },
  {
    name: "Therapeutic EGD",
    category: "Procedure",
    aliases: ["therapeutic upper endoscopy"],
  },
  {
    name: "Transnasal Endoscopy",
    category: "Procedure",
    aliases: ["tne", "unsedated endoscopy", "transnasal esophagoscopy"],
  },
  {
    name: "Bravo pH Monitoring",
    category: "Procedure",
    aliases: ["bravo capsule", "wireless ph monitoring", "esophageal ph study"],
  },
  {
    name: "Esophageal Manometry",
    category: "Procedure",
    aliases: ["esophageal motility study", "high resolution manometry", "hrm"],
  },
  {
    name: "Impedance pH Testing",
    category: "Procedure",
    aliases: ["multichannel intraluminal impedance", "mii-ph"],
  },

  // Colonoscopy
  {
    name: "Colonoscopy",
    category: "Procedure",
    aliases: ["colon scope", "lower endoscopy"],
  },
  {
    name: "Screening Colonoscopy",
    category: "Procedure",
    aliases: ["screening colon", "preventive colonoscopy"],
  },
  {
    name: "Diagnostic Colonoscopy",
    category: "Procedure",
    aliases: ["diagnostic colon"],
  },
  {
    name: "Surveillance Colonoscopy",
    category: "Procedure",
    aliases: ["surveillance colon", "follow-up colonoscopy"],
  },
  {
    name: "Colonoscopy with Polypectomy",
    category: "Procedure",
    aliases: ["polyp removal colonoscopy", "snare polypectomy"],
  },
  {
    name: "Colonoscopy with Biopsy",
    category: "Procedure",
    aliases: ["colon biopsy"],
  },
  {
    name: "Therapeutic Colonoscopy",
    category: "Procedure",
    aliases: ["interventional colonoscopy"],
  },
  {
    name: "Flexible Sigmoidoscopy",
    category: "Procedure",
    aliases: ["flex sig", "sigmoidoscopy"],
  },
  {
    name: "Fecal Microbiota Transplant",
    category: "Procedure",
    aliases: ["fmt", "stool transplant", "fecal transplant"],
  },
  {
    name: "Colonic Decompression",
    category: "Procedure",
    aliases: ["colon decompression", "colonoscopic decompression"],
  },
  {
    name: "Colonic Stent Placement",
    category: "Procedure",
    aliases: ["colon stent", "colorectal stent"],
  },

  // ERCP & Biliary
  {
    name: "ERCP",
    category: "Procedure",
    aliases: ["endoscopic retrograde cholangiopancreatography"],
  },
  {
    name: "Diagnostic ERCP",
    category: "Procedure",
    aliases: ["diagnostic ercp"],
  },
  {
    name: "Therapeutic ERCP",
    category: "Procedure",
    aliases: ["interventional ercp"],
  },
  {
    name: "ERCP with Sphincterotomy",
    category: "Procedure",
    aliases: [
      "sphincterotomy",
      "biliary sphincterotomy",
      "endoscopic sphincterotomy",
    ],
  },
  {
    name: "ERCP with Stone Extraction",
    category: "Procedure",
    aliases: [
      "bile duct stone removal",
      "cbd stone extraction",
      "choledocholithiasis ercp",
    ],
  },
  {
    name: "ERCP with Stent Placement",
    category: "Procedure",
    aliases: ["biliary stent", "bile duct stent", "plastic stent ercp"],
  },
  {
    name: "Biliary Metal Stent",
    category: "Procedure",
    aliases: [
      "metal biliary stent",
      "sems biliary",
      "self-expanding metal stent",
    ],
  },
  {
    name: "Pancreatic Stent Placement",
    category: "Procedure",
    aliases: ["pancreatic duct stent", "pd stent"],
  },
  {
    name: "Biliary Dilation",
    category: "Procedure",
    aliases: ["bile duct dilation", "stricture dilation ercp"],
  },
  {
    name: "Cholangioscopy",
    category: "Procedure",
    aliases: [
      "spyglass",
      "direct visualization cholangioscopy",
      "single operator cholangioscopy",
    ],
  },
  {
    name: "Electrohydraulic Lithotripsy",
    category: "Procedure",
    aliases: ["ehl", "biliary lithotripsy"],
  },
  {
    name: "Laser Lithotripsy Biliary",
    category: "Procedure",
    aliases: ["holmium laser lithotripsy bile duct"],
  },
  {
    name: "Biliary Brush Cytology",
    category: "Procedure",
    aliases: ["bile duct brushings"],
  },
  {
    name: "Ampullectomy",
    category: "Procedure",
    aliases: ["ampullary resection", "endoscopic ampullectomy", "papillectomy"],
  },
  {
    name: "Precut Sphincterotomy",
    category: "Procedure",
    aliases: ["needle knife sphincterotomy", "precut papillotomy"],
  },
  {
    name: "Rendezvous Procedure",
    category: "Procedure",
    aliases: ["combined percutaneous endoscopic"],
  },

  // EUS (Endoscopic Ultrasound)
  {
    name: "EUS",
    category: "Procedure",
    aliases: ["endoscopic ultrasound", "endosonography"],
  },
  {
    name: "EUS Upper",
    category: "Procedure",
    aliases: ["upper eus", "eus esophagus stomach"],
  },
  {
    name: "EUS Rectal",
    category: "Procedure",
    aliases: ["rectal eus", "endorectal ultrasound", "erus"],
  },
  {
    name: "EUS with FNA",
    category: "Procedure",
    aliases: ["eus fine needle aspiration", "eus-fna"],
  },
  {
    name: "EUS with FNB",
    category: "Procedure",
    aliases: ["eus fine needle biopsy", "eus-fnb", "eus core biopsy"],
  },
  {
    name: "EUS Pancreatic Cyst",
    category: "Procedure",
    aliases: ["pancreatic cyst aspiration", "cyst fluid analysis"],
  },
  {
    name: "EUS-Guided Drainage",
    category: "Procedure",
    aliases: ["eus drainage", "eus-guided transmural drainage"],
  },
  {
    name: "EUS-Guided Pseudocyst Drainage",
    category: "Procedure",
    aliases: ["pseudocyst drainage", "pancreatic pseudocyst eus"],
  },
  {
    name: "EUS-Guided Walled-Off Necrosis Drainage",
    category: "Procedure",
    aliases: [
      "won drainage",
      "necrosectomy eus",
      "direct endoscopic necrosectomy",
    ],
  },
  {
    name: "EUS-Guided Biliary Drainage",
    category: "Procedure",
    aliases: [
      "eus-bd",
      "eus choledochoduodenostomy",
      "eus hepaticogastrostomy",
    ],
  },
  {
    name: "EUS-Guided Gallbladder Drainage",
    category: "Procedure",
    aliases: ["eus cholecystostomy", "lams gallbladder"],
  },
  {
    name: "EUS-Guided Celiac Plexus Block",
    category: "Procedure",
    aliases: ["eus celiac block", "endoscopic celiac block"],
  },
  {
    name: "EUS-Guided Celiac Plexus Neurolysis",
    category: "Procedure",
    aliases: ["eus cpn", "endoscopic celiac neurolysis"],
  },
  {
    name: "EUS-Guided Fiducial Placement",
    category: "Procedure",
    aliases: ["fiducial placement", "eus fiducials"],
  },
  {
    name: "EUS-Guided Tumor Ablation",
    category: "Procedure",
    aliases: ["eus rfa", "eus radiofrequency ablation"],
  },
  {
    name: "Lumen-Apposing Metal Stent",
    category: "Procedure",
    aliases: ["lams", "axios stent", "hot axios"],
  },

  // Small Bowel
  {
    name: "Capsule Endoscopy",
    category: "Procedure",
    aliases: [
      "pill cam",
      "video capsule endoscopy",
      "pillcam",
      "wireless capsule",
    ],
  },
  {
    name: "Small Bowel Capsule",
    category: "Procedure",
    aliases: ["sb capsule", "small bowel video capsule"],
  },
  {
    name: "Esophageal Capsule",
    category: "Procedure",
    aliases: ["pillcam eso", "esophageal video capsule"],
  },
  {
    name: "Colon Capsule",
    category: "Procedure",
    aliases: ["pillcam colon", "capsule colonoscopy"],
  },
  {
    name: "Enteroscopy",
    category: "Procedure",
    aliases: ["small bowel enteroscopy", "deep enteroscopy"],
  },
  {
    name: "Push Enteroscopy",
    category: "Procedure",
    aliases: ["push endoscopy"],
  },
  {
    name: "Single Balloon Enteroscopy",
    category: "Procedure",
    aliases: ["sbe", "single balloon endoscopy"],
  },
  {
    name: "Double Balloon Enteroscopy",
    category: "Procedure",
    aliases: ["dbe", "double balloon endoscopy"],
  },
  {
    name: "Spiral Enteroscopy",
    category: "Procedure",
    aliases: ["motorized spiral enteroscopy", "powerspiral"],
  },
  {
    name: "Intraoperative Enteroscopy",
    category: "Procedure",
    aliases: ["ioe", "surgical enteroscopy"],
  },
  {
    name: "Balloon-Assisted ERCP",
    category: "Procedure",
    aliases: ["ercp altered anatomy", "roux-en-y ercp"],
  },

  // Esophageal Procedures
  {
    name: "Esophageal Dilation",
    category: "Procedure",
    aliases: ["stricture dilation", "esophageal stretching", "bougie dilation"],
  },
  {
    name: "Savary Dilation",
    category: "Procedure",
    aliases: ["savary-gilliard dilation", "wire-guided dilation"],
  },
  {
    name: "Balloon Dilation Esophagus",
    category: "Procedure",
    aliases: [
      "through-the-scope dilation",
      "tts dilation",
      "pneumatic dilation",
    ],
  },
  {
    name: "Achalasia Dilation",
    category: "Procedure",
    aliases: ["pneumatic dilation achalasia", "graded pneumatic dilation"],
  },
  {
    name: "Esophageal Stent Placement",
    category: "Procedure",
    aliases: ["esophageal stent", "esophageal sems"],
  },
  {
    name: "Esophageal Stent Removal",
    category: "Procedure",
    aliases: ["stent removal esophagus"],
  },
  {
    name: "POEM Procedure",
    category: "Procedure",
    aliases: ["peroral endoscopic myotomy", "poem", "endoscopic myotomy"],
  },
  {
    name: "G-POEM",
    category: "Procedure",
    aliases: ["gastric poem", "pyloromyotomy endoscopic", "gastroparesis poem"],
  },
  {
    name: "Z-POEM",
    category: "Procedure",
    aliases: ["zenker poem", "endoscopic zenker diverticulotomy"],
  },
  {
    name: "Zenker Diverticulum Treatment",
    category: "Procedure",
    aliases: ["zenker's diverticulotomy", "cricopharyngeal myotomy endoscopic"],
  },
  {
    name: "Esophageal Variceal Ligation",
    category: "Procedure",
    aliases: ["evl", "variceal banding", "varix ligation"],
  },
  {
    name: "Esophageal Variceal Sclerotherapy",
    category: "Procedure",
    aliases: ["variceal injection", "sclerotherapy varices"],
  },
  {
    name: "Gastric Variceal Treatment",
    category: "Procedure",
    aliases: [
      "gastric varix injection",
      "cyanoacrylate injection",
      "glue injection varices",
    ],
  },
  {
    name: "Endoscopic Fundoplication",
    category: "Procedure",
    aliases: ["tif", "transoral incisionless fundoplication", "esophyx"],
  },
  {
    name: "Stretta Procedure",
    category: "Procedure",
    aliases: ["stretta", "radiofrequency gerd treatment"],
  },
  {
    name: "LINX Placement Endoscopic",
    category: "Procedure",
    aliases: ["magnetic sphincter augmentation"],
  },
  {
    name: "Anti-Reflux Mucosectomy",
    category: "Procedure",
    aliases: ["arms procedure", "endoscopic cardia resection"],
  },

  // Barrett's Esophagus
  {
    name: "Barrett's Surveillance",
    category: "Procedure",
    aliases: ["barrett's screening", "be surveillance"],
  },
  {
    name: "Radiofrequency Ablation Barrett's",
    category: "Procedure",
    aliases: ["rfa barrett's", "barrx", "halo ablation"],
  },
  {
    name: "Cryotherapy Barrett's",
    category: "Procedure",
    aliases: [
      "cryoablation esophagus",
      "spray cryotherapy",
      "cryoballoon ablation",
    ],
  },
  {
    name: "Endoscopic Mucosal Resection",
    category: "Procedure",
    aliases: ["emr", "mucosal resection"],
  },
  {
    name: "EMR Barrett's",
    category: "Procedure",
    aliases: ["emr dysplasia", "barrett's emr"],
  },
  {
    name: "Endoscopic Submucosal Dissection",
    category: "Procedure",
    aliases: ["esd", "en bloc resection"],
  },
  { name: "ESD Esophagus", category: "Procedure", aliases: ["esophageal esd"] },
  { name: "ESD Stomach", category: "Procedure", aliases: ["gastric esd"] },
  {
    name: "ESD Colon",
    category: "Procedure",
    aliases: ["colonic esd", "colorectal esd"],
  },
  {
    name: "Wide Area Transepithelial Sampling",
    category: "Procedure",
    aliases: ["wats", "wats3d"],
  },

  // Gastric Procedures
  {
    name: "PEG Tube Placement",
    category: "Procedure",
    aliases: [
      "feeding tube",
      "gastrostomy tube",
      "g-tube",
      "percutaneous endoscopic gastrostomy",
    ],
  },
  {
    name: "PEG Tube Replacement",
    category: "Procedure",
    aliases: ["g-tube change", "peg exchange"],
  },
  {
    name: "PEG-J Tube Placement",
    category: "Procedure",
    aliases: ["jejunal extension", "peg with jejunal extension"],
  },
  {
    name: "Direct PEJ Placement",
    category: "Procedure",
    aliases: ["percutaneous endoscopic jejunostomy", "dpej"],
  },
  {
    name: "PEG Tube Removal",
    category: "Procedure",
    aliases: ["g-tube removal", "peg removal"],
  },
  {
    name: "Gastric Foreign Body Removal",
    category: "Procedure",
    aliases: ["foreign body retrieval", "fb removal stomach"],
  },
  {
    name: "Esophageal Foreign Body Removal",
    category: "Procedure",
    aliases: ["food impaction", "food bolus removal"],
  },
  {
    name: "Bezoar Removal",
    category: "Procedure",
    aliases: ["bezoar dissolution", "phytobezoar removal"],
  },
  {
    name: "Gastric Outlet Obstruction Stent",
    category: "Procedure",
    aliases: ["duodenal stent", "goo stent", "pyloric stent"],
  },
  {
    name: "EUS-Guided Gastroenterostomy",
    category: "Procedure",
    aliases: ["eus-ge", "endoscopic gastrojejunostomy"],
  },
  {
    name: "Endoscopic Gastric Reduction",
    category: "Procedure",
    aliases: ["endoscopic sleeve gastroplasty", "esg", "apollo esg"],
  },
  {
    name: "Intragastric Balloon",
    category: "Procedure",
    aliases: ["gastric balloon", "orbera balloon", "obalon"],
  },
  {
    name: "Gastric Balloon Removal",
    category: "Procedure",
    aliases: ["balloon removal", "igb removal"],
  },
  {
    name: "Aspire Assist",
    category: "Procedure",
    aliases: ["aspiration therapy", "a-tube"],
  },
  {
    name: "Transpyloric Stent",
    category: "Procedure",
    aliases: ["tps", "full-thickness endoscopic suturing"],
  },

  // Hemostasis
  {
    name: "Endoscopic Hemostasis",
    category: "Procedure",
    aliases: ["gi bleeding treatment", "endoscopic bleeding control"],
  },
  {
    name: "Injection Therapy",
    category: "Procedure",
    aliases: ["epinephrine injection", "adrenaline injection gi"],
  },
  {
    name: "Thermal Coagulation",
    category: "Procedure",
    aliases: ["bipolar coagulation", "heater probe", "gold probe"],
  },
  {
    name: "Argon Plasma Coagulation",
    category: "Procedure",
    aliases: ["apc", "argon beam coagulation"],
  },
  {
    name: "Hemoclip Placement",
    category: "Procedure",
    aliases: ["endoscopic clipping", "hemostatic clips"],
  },
  {
    name: "Over-The-Scope Clip",
    category: "Procedure",
    aliases: ["otsc", "bear claw clip", "ovesco"],
  },
  {
    name: "Hemostatic Powder Application",
    category: "Procedure",
    aliases: ["hemospray", "endoclot", "tc-325"],
  },
  {
    name: "Band Ligation Bleeding",
    category: "Procedure",
    aliases: ["banding for bleeding", "dieulafoy banding"],
  },
  {
    name: "Endoscopic Suturing Hemostasis",
    category: "Procedure",
    aliases: ["overstitch hemostasis"],
  },
  {
    name: "Angiodysplasia Treatment",
    category: "Procedure",
    aliases: ["avm ablation gi", "angioectasia treatment"],
  },
  {
    name: "GAVE Treatment",
    category: "Procedure",
    aliases: [
      "watermelon stomach treatment",
      "gastric antral vascular ectasia ablation",
    ],
  },
  {
    name: "Radiation Proctitis Treatment",
    category: "Procedure",
    aliases: ["radiation proctopathy ablation", "apc proctitis"],
  },

  // Defect Closure
  {
    name: "Endoscopic Perforation Closure",
    category: "Procedure",
    aliases: ["perforation repair endoscopic"],
  },
  {
    name: "Endoscopic Suturing",
    category: "Procedure",
    aliases: ["overstitch", "apollo endosurgery", "full-thickness suturing"],
  },
  {
    name: "Fistula Closure Endoscopic",
    category: "Procedure",
    aliases: ["gi fistula treatment", "endoscopic fistula repair"],
  },
  {
    name: "Leak Closure Endoscopic",
    category: "Procedure",
    aliases: ["anastomotic leak treatment", "stent for leak"],
  },
  {
    name: "Endoscopic Stent for Leak",
    category: "Procedure",
    aliases: ["covered stent leak", "sems for leak"],
  },
  {
    name: "Endoscopic Vacuum Therapy",
    category: "Procedure",
    aliases: ["evac", "endosponge", "endo-vac"],
  },

  // Anorectal
  {
    name: "Anoscopy",
    category: "Procedure",
    aliases: ["anal exam", "anoscopic exam"],
  },
  {
    name: "High-Resolution Anoscopy",
    category: "Procedure",
    aliases: ["hra", "anal dysplasia screening"],
  },
  {
    name: "Hemorrhoid Banding",
    category: "Procedure",
    aliases: ["rubber band ligation", "rbl hemorrhoids"],
  },
  {
    name: "Hemorrhoid Sclerotherapy",
    category: "Procedure",
    aliases: ["hemorrhoid injection"],
  },
  {
    name: "Infrared Coagulation Hemorrhoids",
    category: "Procedure",
    aliases: ["irc hemorrhoids"],
  },
  {
    name: "Hemorrhoidectomy",
    category: "Procedure",
    aliases: [
      "hemorrhoid surgery",
      "hemorrhoid removal",
      "surgical hemorrhoidectomy",
    ],
  },
  {
    name: "Stapled Hemorrhoidopexy",
    category: "Procedure",
    aliases: [
      "pph",
      "procedure for prolapse and hemorrhoids",
      "stapled hemorrhoidectomy",
    ],
  },
  {
    name: "Hemorrhoid Artery Ligation",
    category: "Procedure",
    aliases: ["hal", "thd", "transanal hemorrhoidal dearterialization"],
  },
  {
    name: "Anal Fissure Treatment",
    category: "Procedure",
    aliases: ["lateral internal sphincterotomy", "lis", "fissurectomy"],
  },
  {
    name: "Botox Anal Fissure",
    category: "Procedure",
    aliases: ["botulinum toxin anal sphincter"],
  },
  {
    name: "Anal Fistula Surgery",
    category: "Procedure",
    aliases: ["fistulotomy", "fistulectomy", "fistula repair"],
  },
  {
    name: "LIFT Procedure",
    category: "Procedure",
    aliases: ["ligation of intersphincteric fistula tract"],
  },
  {
    name: "Anal Fistula Plug",
    category: "Procedure",
    aliases: ["bioprosthetic plug", "surgisis plug"],
  },
  {
    name: "VAAFT",
    category: "Procedure",
    aliases: ["video-assisted anal fistula treatment"],
  },
  {
    name: "FiLaC",
    category: "Procedure",
    aliases: ["fistula laser closure", "laser fistula treatment"],
  },
  {
    name: "Seton Placement",
    category: "Procedure",
    aliases: ["draining seton", "cutting seton"],
  },
  {
    name: "Anal Abscess Drainage",
    category: "Procedure",
    aliases: ["perianal abscess i&d", "anorectal abscess drainage"],
  },
  {
    name: "Rectal Prolapse Repair",
    category: "Procedure",
    aliases: ["rectopexy", "prolapse surgery"],
  },
  {
    name: "Delorme Procedure",
    category: "Procedure",
    aliases: ["mucosal sleeve resection"],
  },
  {
    name: "Altemeier Procedure",
    category: "Procedure",
    aliases: ["perineal rectosigmoidectomy"],
  },
  {
    name: "Ventral Mesh Rectopexy",
    category: "Procedure",
    aliases: ["vmr", "laparoscopic ventral rectopexy"],
  },
  {
    name: "Stapled Transanal Rectal Resection",
    category: "Procedure",
    aliases: ["starr procedure", "transtar"],
  },
  {
    name: "Sacral Nerve Stimulation GI",
    category: "Procedure",
    aliases: ["sns fecal incontinence", "interstim fecal"],
  },
  {
    name: "Anal Sphincter Repair",
    category: "Procedure",
    aliases: ["sphincteroplasty", "overlapping sphincter repair"],
  },
  {
    name: "Artificial Bowel Sphincter",
    category: "Procedure",
    aliases: ["abs", "acticon neosphincter"],
  },
  {
    name: "Solesta Injection",
    category: "Procedure",
    aliases: ["anal bulking", "dextranomer injection anal"],
  },
  {
    name: "Rectal Biopsy",
    category: "Procedure",
    aliases: ["suction rectal biopsy", "hirschsprung biopsy"],
  },
  {
    name: "Anorectal Manometry",
    category: "Procedure",
    aliases: ["arm", "anal manometry", "rectal manometry"],
  },
  {
    name: "Defecography",
    category: "Procedure",
    aliases: ["evacuation proctography", "mr defecography"],
  },
  {
    name: "Biofeedback Therapy GI",
    category: "Procedure",
    aliases: ["pelvic floor biofeedback", "dyssynergia biofeedback"],
  },

  // Liver Procedures (Endoscopic/IR related)
  {
    name: "Transjugular Liver Biopsy",
    category: "Procedure",
    aliases: ["tjlb", "transjugular biopsy"],
  },
  {
    name: "TIPS Procedure",
    category: "Procedure",
    aliases: ["transjugular intrahepatic portosystemic shunt", "tips"],
  },
  {
    name: "TIPS Revision",
    category: "Procedure",
    aliases: ["tips dysfunction treatment"],
  },
  {
    name: "Paracentesis",
    category: "Procedure",
    aliases: ["abdominal tap", "ascites drainage", "therapeutic paracentesis"],
  },
  {
    name: "Large Volume Paracentesis",
    category: "Procedure",
    aliases: ["lvp", "therapeutic ascites drainage"],
  },
  {
    name: "PleurX Peritoneal",
    category: "Procedure",
    aliases: ["peritoneal catheter", "indwelling ascites catheter"],
  },
  {
    name: "Percutaneous Liver Biopsy",
    category: "Procedure",
    aliases: ["liver biopsy", "hepatic biopsy"],
  },
  {
    name: "Fibroscan",
    category: "Procedure",
    aliases: ["transient elastography", "liver stiffness measurement"],
  },

  // Motility Procedures
  {
    name: "Gastric Emptying Study",
    category: "Procedure",
    aliases: ["ges", "gastric emptying scintigraphy", "gastric emptying scan"],
  },
  {
    name: "SmartPill",
    category: "Procedure",
    aliases: ["wireless motility capsule", "gi motility capsule"],
  },
  {
    name: "Hydrogen Breath Test",
    category: "Procedure",
    aliases: [
      "lactulose breath test",
      "sibo breath test",
      "lactose breath test",
    ],
  },
  {
    name: "Gastric Electrical Stimulation",
    category: "Procedure",
    aliases: ["ges", "enterra therapy", "gastric pacemaker"],
  },
  {
    name: "Botox for Gastroparesis",
    category: "Procedure",
    aliases: ["pyloric botox", "intrapyloric botox injection"],
  },

  // ============================================
  // PROCEDURES - PULMONOLOGY
  // ============================================

  // Diagnostic Bronchoscopy
  {
    name: "Bronchoscopy",
    category: "Procedure",
    aliases: ["bronch", "flexible bronchoscopy", "fiberoptic bronchoscopy"],
  },
  {
    name: "Diagnostic Bronchoscopy",
    category: "Procedure",
    aliases: ["diagnostic bronch"],
  },
  {
    name: "Bronchoscopy with BAL",
    category: "Procedure",
    aliases: ["bronchoalveolar lavage", "bal", "bronch with lavage"],
  },
  {
    name: "Bronchoscopy with Biopsy",
    category: "Procedure",
    aliases: ["transbronchial biopsy", "tbb", "bronchial biopsy"],
  },
  {
    name: "Bronchoscopy with Brushings",
    category: "Procedure",
    aliases: ["bronchial brushings", "cytology brushings"],
  },
  {
    name: "Protected Brush Specimen",
    category: "Procedure",
    aliases: ["pbs", "protected specimen brush"],
  },
  {
    name: "Rigid Bronchoscopy",
    category: "Procedure",
    aliases: ["rigid bronch", "interventional bronchoscopy"],
  },
  {
    name: "Pediatric Bronchoscopy",
    category: "Procedure",
    aliases: ["pediatric bronch", "peds bronchoscopy"],
  },

  // Advanced Diagnostic Bronchoscopy
  {
    name: "EBUS",
    category: "Procedure",
    aliases: ["endobronchial ultrasound", "ebus bronchoscopy"],
  },
  {
    name: "EBUS-TBNA",
    category: "Procedure",
    aliases: [
      "ebus with needle aspiration",
      "ebus fna",
      "ebus transbronchial needle aspiration",
    ],
  },
  {
    name: "Linear EBUS",
    category: "Procedure",
    aliases: ["convex probe ebus", "cp-ebus"],
  },
  {
    name: "Radial EBUS",
    category: "Procedure",
    aliases: ["radial probe ebus", "rp-ebus", "peripheral ebus"],
  },
  {
    name: "EUS-B",
    category: "Procedure",
    aliases: ["eus via bronchoscope", "transesophageal eus bronchoscopy"],
  },
  {
    name: "Navigational Bronchoscopy",
    category: "Procedure",
    aliases: [
      "electromagnetic navigation bronchoscopy",
      "enb",
      "superDimension",
    ],
  },
  {
    name: "Robotic Bronchoscopy",
    category: "Procedure",
    aliases: [
      "monarch bronchoscopy",
      "ion bronchoscopy",
      "robotic assisted bronchoscopy",
    ],
  },
  {
    name: "Virtual Bronchoscopy",
    category: "Procedure",
    aliases: ["ct virtual bronchoscopy"],
  },
  {
    name: "Autofluorescence Bronchoscopy",
    category: "Procedure",
    aliases: ["afb", "fluorescence bronchoscopy"],
  },
  {
    name: "Narrow Band Imaging Bronchoscopy",
    category: "Procedure",
    aliases: ["nbi bronchoscopy"],
  },
  {
    name: "Confocal Laser Endomicroscopy",
    category: "Procedure",
    aliases: ["cle bronchoscopy", "probe-based cle"],
  },

  // Transbronchial Procedures
  {
    name: "Transbronchial Lung Biopsy",
    category: "Procedure",
    aliases: ["tblb", "transbronchial biopsy"],
  },
  {
    name: "Transbronchial Cryobiopsy",
    category: "Procedure",
    aliases: [
      "cryo tblb",
      "cryoprobe biopsy",
      "transbronchial lung cryobiopsy",
    ],
  },
  {
    name: "Transbronchial Needle Aspiration",
    category: "Procedure",
    aliases: ["tbna", "wang needle", "conventional tbna"],
  },
  {
    name: "Peripheral Lung Biopsy",
    category: "Procedure",
    aliases: ["peripheral nodule biopsy", "peripheral lesion biopsy"],
  },

  // Therapeutic Bronchoscopy - Airway Management
  {
    name: "Therapeutic Bronchoscopy",
    category: "Procedure",
    aliases: ["interventional bronchoscopy"],
  },
  {
    name: "Foreign Body Removal Bronch",
    category: "Procedure",
    aliases: ["airway foreign body removal", "bronchoscopic fb removal"],
  },
  {
    name: "Mucus Plug Removal",
    category: "Procedure",
    aliases: [
      "mucus clearance bronchoscopy",
      "therapeutic bronch for atelectasis",
    ],
  },
  {
    name: "Whole Lung Lavage",
    category: "Procedure",
    aliases: ["pulmonary alveolar proteinosis treatment", "pap lavage"],
  },
  {
    name: "Difficult Intubation Bronchoscopy",
    category: "Procedure",
    aliases: ["bronchoscope assisted intubation", "fiberoptic intubation"],
  },
  {
    name: "Bronchoscopic Intubation",
    category: "Procedure",
    aliases: ["awake fiberoptic intubation"],
  },
  {
    name: "Percutaneous Tracheostomy Bronch Assist",
    category: "Procedure",
    aliases: ["bronchoscopy assisted pdt"],
  },

  // Therapeutic Bronchoscopy - Tumor/Obstruction
  {
    name: "Airway Stent Placement",
    category: "Procedure",
    aliases: ["bronchial stent", "tracheobronchial stent", "silicone stent"],
  },
  {
    name: "Airway Stent Removal",
    category: "Procedure",
    aliases: ["stent removal bronch", "bronchial stent removal"],
  },
  {
    name: "Metallic Airway Stent",
    category: "Procedure",
    aliases: ["sems airway", "self-expanding metal stent airway"],
  },
  {
    name: "Silicone Airway Stent",
    category: "Procedure",
    aliases: ["dumon stent", "silicone y stent"],
  },
  {
    name: "Airway Dilation",
    category: "Procedure",
    aliases: [
      "bronchial dilation",
      "balloon bronchoplasty",
      "tracheal dilation",
    ],
  },
  {
    name: "Tumor Debulking Bronch",
    category: "Procedure",
    aliases: ["endobronchial tumor resection", "mechanical debulking"],
  },
  {
    name: "Electrocautery Bronchoscopy",
    category: "Procedure",
    aliases: ["bronchoscopic electrocautery", "endobronchial electrosurgery"],
  },
  {
    name: "Argon Plasma Coagulation Bronch",
    category: "Procedure",
    aliases: ["apc bronchoscopy", "bronchoscopic apc"],
  },
  {
    name: "Laser Bronchoscopy",
    category: "Procedure",
    aliases: [
      "endobronchial laser",
      "nd:yag laser bronchoscopy",
      "laser tumor ablation",
    ],
  },
  {
    name: "Cryotherapy Bronchoscopy",
    category: "Procedure",
    aliases: [
      "endobronchial cryotherapy",
      "cryoablation bronch",
      "spray cryotherapy airway",
    ],
  },
  {
    name: "Photodynamic Therapy Bronch",
    category: "Procedure",
    aliases: ["pdt bronchoscopy", "endobronchial pdt"],
  },
  {
    name: "Brachytherapy Bronch",
    category: "Procedure",
    aliases: ["endobronchial brachytherapy", "hdr brachytherapy airway"],
  },
  {
    name: "Microwave Ablation Bronch",
    category: "Procedure",
    aliases: ["bronchoscopic microwave ablation"],
  },
  {
    name: "Radiofrequency Ablation Bronch",
    category: "Procedure",
    aliases: ["bronchoscopic rfa", "endobronchial rfa"],
  },

  // Therapeutic Bronchoscopy - Other
  {
    name: "Bronchoscopic Lung Volume Reduction",
    category: "Procedure",
    aliases: ["blvr", "endobronchial valve", "zephyr valve", "spiration valve"],
  },
  {
    name: "Endobronchial Valve Placement",
    category: "Procedure",
    aliases: ["ebv", "one-way valve", "emphysema valve"],
  },
  {
    name: "Endobronchial Valve Removal",
    category: "Procedure",
    aliases: ["valve removal bronch"],
  },
  {
    name: "Bronchial Thermoplasty",
    category: "Procedure",
    aliases: ["bt", "asthma thermoplasty", "alair"],
  },
  {
    name: "Targeted Lung Denervation",
    category: "Procedure",
    aliases: ["tld", "copd denervation"],
  },
  {
    name: "Bronchoscopic Thermal Vapor Ablation",
    category: "Procedure",
    aliases: ["btva", "vapor ablation emphysema"],
  },
  {
    name: "Airway Fistula Closure",
    category: "Procedure",
    aliases: ["bronchopleural fistula closure", "bpf closure bronch"],
  },
  {
    name: "Tracheobronchial Tear Repair",
    category: "Procedure",
    aliases: ["airway laceration repair"],
  },

  // Pleural Procedures
  {
    name: "Thoracentesis",
    category: "Procedure",
    aliases: ["pleural tap", "chest tap", "pleural fluid drainage"],
  },
  {
    name: "Diagnostic Thoracentesis",
    category: "Procedure",
    aliases: ["diagnostic pleural tap"],
  },
  {
    name: "Therapeutic Thoracentesis",
    category: "Procedure",
    aliases: ["large volume thoracentesis", "therapeutic pleural drainage"],
  },
  {
    name: "Ultrasound-Guided Thoracentesis",
    category: "Procedure",
    aliases: ["us guided thoracentesis", "bedside thoracentesis"],
  },
  {
    name: "PleurX Catheter Placement",
    category: "Procedure",
    aliases: [
      "indwelling pleural catheter",
      "ipc",
      "tunneled pleural catheter",
    ],
  },
  {
    name: "PleurX Catheter Removal",
    category: "Procedure",
    aliases: ["ipc removal"],
  },
  {
    name: "Chest Tube Insertion",
    category: "Procedure",
    aliases: ["chest tube placement", "tube thoracostomy", "intercostal drain"],
  },
  {
    name: "Chest Tube Removal",
    category: "Procedure",
    aliases: ["chest drain removal"],
  },
  {
    name: "Pigtail Catheter Placement",
    category: "Procedure",
    aliases: ["small bore chest tube", "pigtail chest drain"],
  },
  {
    name: "Pleurodesis",
    category: "Procedure",
    aliases: ["chemical pleurodesis", "talc pleurodesis"],
  },
  {
    name: "Talc Pleurodesis",
    category: "Procedure",
    aliases: ["talc poudrage", "talc slurry"],
  },
  {
    name: "Medical Thoracoscopy",
    category: "Procedure",
    aliases: ["pleuroscopy", "local anesthetic thoracoscopy"],
  },
  {
    name: "Pleural Biopsy",
    category: "Procedure",
    aliases: [
      "closed pleural biopsy",
      "abrams needle biopsy",
      "thoracoscopic pleural biopsy",
    ],
  },
  {
    name: "Pleural Fluid Analysis",
    category: "Procedure",
    aliases: ["pleural fluid cytology", "pleural fluid studies"],
  },
  {
    name: "Pneumothorax Aspiration",
    category: "Procedure",
    aliases: ["needle aspiration pneumothorax", "manual aspiration ptx"],
  },
  {
    name: "Ambulatory Pneumothorax Device",
    category: "Procedure",
    aliases: ["heimlich valve", "flutter valve", "thoracic vent"],
  },
  {
    name: "Intrapleural Fibrinolytic Therapy",
    category: "Procedure",
    aliases: ["tpa dnase pleural", "intrapleural tpa", "ipft"],
  },

  // Thoracic Surgery
  {
    name: "VATS",
    category: "Procedure",
    aliases: ["video assisted thoracoscopic surgery", "thoracoscopy"],
  },
  {
    name: "VATS Wedge Resection",
    category: "Procedure",
    aliases: ["vats wedge", "thoracoscopic wedge"],
  },
  {
    name: "VATS Lobectomy",
    category: "Procedure",
    aliases: ["thoracoscopic lobectomy", "vats lung resection"],
  },
  {
    name: "VATS Pleurodesis",
    category: "Procedure",
    aliases: ["thoracoscopic pleurodesis", "vats talc"],
  },
  {
    name: "VATS Decortication",
    category: "Procedure",
    aliases: ["thoracoscopic decortication"],
  },
  {
    name: "VATS Sympathectomy",
    category: "Procedure",
    aliases: ["thoracoscopic sympathectomy", "ets"],
  },
  {
    name: "Robotic Thoracic Surgery",
    category: "Procedure",
    aliases: ["rats", "robotic assisted thoracic surgery", "robotic lobectomy"],
  },
  {
    name: "Thoracotomy",
    category: "Procedure",
    aliases: ["open chest surgery", "open thoracotomy"],
  },
  {
    name: "Lobectomy",
    category: "Procedure",
    aliases: ["lung lobectomy", "pulmonary lobectomy"],
  },
  {
    name: "Bilobectomy",
    category: "Procedure",
    aliases: ["two lobe resection"],
  },
  {
    name: "Pneumonectomy",
    category: "Procedure",
    aliases: ["lung removal", "complete pneumonectomy"],
  },
  {
    name: "Extrapleural Pneumonectomy",
    category: "Procedure",
    aliases: ["epp", "mesothelioma surgery"],
  },
  {
    name: "Sleeve Lobectomy",
    category: "Procedure",
    aliases: ["sleeve resection", "bronchoplastic resection"],
  },
  {
    name: "Wedge Resection",
    category: "Procedure",
    aliases: ["lung wedge", "pulmonary wedge resection", "sublobar resection"],
  },
  {
    name: "Segmentectomy",
    category: "Procedure",
    aliases: ["anatomic segmentectomy", "lung segmentectomy"],
  },
  {
    name: "Lung Volume Reduction Surgery",
    category: "Procedure",
    aliases: ["lvrs", "emphysema surgery"],
  },
  {
    name: "Bullectomy",
    category: "Procedure",
    aliases: ["bulla resection", "giant bulla surgery"],
  },
  {
    name: "Decortication",
    category: "Procedure",
    aliases: ["pleural decortication", "empyema decortication"],
  },
  {
    name: "Pleurectomy",
    category: "Procedure",
    aliases: ["pleural stripping"],
  },
  {
    name: "Lung Transplant",
    category: "Procedure",
    aliases: ["pulmonary transplant", "lung transplantation"],
  },
  {
    name: "Single Lung Transplant",
    category: "Procedure",
    aliases: ["unilateral lung transplant"],
  },
  {
    name: "Double Lung Transplant",
    category: "Procedure",
    aliases: ["bilateral lung transplant", "sequential lung transplant"],
  },
  {
    name: "Living Donor Lung Transplant",
    category: "Procedure",
    aliases: ["ldlt lung", "lobar transplant"],
  },
  {
    name: "Ex Vivo Lung Perfusion",
    category: "Procedure",
    aliases: ["evlp", "lung reconditioning"],
  },

  // Lung Biopsy Procedures
  {
    name: "CT-Guided Lung Biopsy",
    category: "Procedure",
    aliases: ["percutaneous lung biopsy", "ct guided needle biopsy lung"],
  },
  {
    name: "CT-Guided Lung Nodule Localization",
    category: "Procedure",
    aliases: ["preoperative nodule marking", "hookwire localization"],
  },
  {
    name: "Surgical Lung Biopsy",
    category: "Procedure",
    aliases: ["open lung biopsy", "vats lung biopsy"],
  },
  {
    name: "Mediastinoscopy",
    category: "Procedure",
    aliases: ["cervical mediastinoscopy", "staging mediastinoscopy"],
  },
  {
    name: "Chamberlain Procedure",
    category: "Procedure",
    aliases: ["anterior mediastinotomy", "parasternal mediastinotomy"],
  },
  {
    name: "Mediastinal Mass Biopsy",
    category: "Procedure",
    aliases: ["mediastinal tumor biopsy"],
  },

  // Pulmonary Hypertension
  {
    name: "Right Heart Catheterization Pulm",
    category: "Procedure",
    aliases: ["rhc pulmonary hypertension", "hemodynamic assessment ph"],
  },
  {
    name: "Vasoreactivity Testing",
    category: "Procedure",
    aliases: ["acute vasodilator testing", "nitric oxide challenge", "avt"],
  },
  {
    name: "Pulmonary Angiography",
    category: "Procedure",
    aliases: ["pulmonary arteriogram", "pa angiogram"],
  },
  {
    name: "Balloon Pulmonary Angioplasty",
    category: "Procedure",
    aliases: ["bpa", "cteph angioplasty"],
  },
  {
    name: "Pulmonary Thromboendarterectomy",
    category: "Procedure",
    aliases: ["pte", "pulmonary endarterectomy", "cteph surgery"],
  },
  {
    name: "Atrial Septostomy",
    category: "Procedure",
    aliases: ["blade septostomy", "balloon atrial septostomy pah"],
  },

  // Sleep & Respiratory Therapy
  {
    name: "Polysomnography",
    category: "Procedure",
    aliases: ["psg", "sleep study", "overnight sleep study"],
  },
  {
    name: "Split Night Study",
    category: "Procedure",
    aliases: ["split night psg", "diagnostic and titration"],
  },
  {
    name: "CPAP Titration",
    category: "Procedure",
    aliases: ["cpap study", "pap titration"],
  },
  {
    name: "BiPAP Titration",
    category: "Procedure",
    aliases: ["bilevel titration", "bipap study"],
  },
  {
    name: "Home Sleep Test",
    category: "Procedure",
    aliases: ["hst", "home sleep apnea test", "hsat"],
  },
  {
    name: "MSLT",
    category: "Procedure",
    aliases: ["multiple sleep latency test", "narcolepsy test"],
  },
  {
    name: "MWT",
    category: "Procedure",
    aliases: ["maintenance of wakefulness test"],
  },
  {
    name: "Overnight Oximetry",
    category: "Procedure",
    aliases: ["nocturnal oximetry", "sleep oximetry"],
  },
  {
    name: "CPAP Setup",
    category: "Procedure",
    aliases: ["cpap initiation", "pap therapy initiation"],
  },
  {
    name: "NIV Setup",
    category: "Procedure",
    aliases: ["noninvasive ventilation initiation", "bipap setup"],
  },
  {
    name: "High Flow Nasal Cannula Setup",
    category: "Procedure",
    aliases: ["hfnc", "optiflow", "high flow oxygen"],
  },

  // Pulmonary Function & Diagnostics
  {
    name: "Pulmonary Function Test",
    category: "Procedure",
    aliases: ["pft", "spirometry", "lung function test"],
  },
  {
    name: "Full Pulmonary Function Test",
    category: "Procedure",
    aliases: ["complete pft", "full pfts"],
  },
  {
    name: "Spirometry",
    category: "Procedure",
    aliases: ["simple spirometry", "office spirometry"],
  },
  {
    name: "Bronchodilator Response Test",
    category: "Procedure",
    aliases: ["pre and post bronchodilator", "reversibility testing"],
  },
  {
    name: "Lung Volumes",
    category: "Procedure",
    aliases: ["plethysmography", "body box", "helium dilution"],
  },
  {
    name: "Diffusing Capacity",
    category: "Procedure",
    aliases: ["dlco", "diffusion capacity", "gas transfer"],
  },
  {
    name: "Methacholine Challenge",
    category: "Procedure",
    aliases: ["bronchoprovocation test", "methacholine bronchoprovocation"],
  },
  {
    name: "Mannitol Challenge",
    category: "Procedure",
    aliases: ["aridol challenge", "mannitol bronchoprovocation"],
  },
  {
    name: "Exercise Challenge Test",
    category: "Procedure",
    aliases: ["exercise induced bronchoconstriction test", "eib test"],
  },
  {
    name: "Cardiopulmonary Exercise Test",
    category: "Procedure",
    aliases: ["cpet", "cpx", "metabolic stress test", "vo2 max test"],
  },
  {
    name: "Six Minute Walk Test",
    category: "Procedure",
    aliases: ["6mwt", "6 minute walk", "walk test"],
  },
  {
    name: "Arterial Blood Gas",
    category: "Procedure",
    aliases: ["abg", "blood gas analysis"],
  },
  {
    name: "Exhaled Nitric Oxide",
    category: "Procedure",
    aliases: ["feno", "eno test", "fractional exhaled nitric oxide"],
  },
  {
    name: "Induced Sputum",
    category: "Procedure",
    aliases: ["sputum induction", "hypertonic saline sputum"],
  },
  {
    name: "Impulse Oscillometry",
    category: "Procedure",
    aliases: ["ios", "oscillometry", "airway resistance testing"],
  },
  {
    name: "Respiratory Muscle Strength",
    category: "Procedure",
    aliases: [
      "mip mep",
      "maximal inspiratory pressure",
      "maximal expiratory pressure",
    ],
  },
  {
    name: "Shunt Study",
    category: "Procedure",
    aliases: ["intrapulmonary shunt calculation", "100% oxygen test"],
  },

  // Pulmonary Rehabilitation
  {
    name: "Pulmonary Rehabilitation",
    category: "Procedure",
    aliases: [
      "pulm rehab",
      "lung rehabilitation",
      "respiratory rehabilitation",
    ],
  },
  {
    name: "Oxygen Titration",
    category: "Procedure",
    aliases: ["oxygen assessment", "ambulatory oxygen titration"],
  },
  {
    name: "Home Oxygen Setup",
    category: "Procedure",
    aliases: [
      "supplemental oxygen prescription",
      "ltot",
      "long term oxygen therapy",
    ],
  },

  // Interventional Radiology - Chest
  {
    name: "CT-Guided Chest Biopsy",
    category: "Procedure",
    aliases: ["percutaneous chest biopsy"],
  },
  {
    name: "CT-Guided Lung Ablation",
    category: "Procedure",
    aliases: [
      "percutaneous lung tumor ablation",
      "lung rfa",
      "lung microwave ablation",
    ],
  },
  {
    name: "Pulmonary Embolism Intervention",
    category: "Procedure",
    aliases: [
      "pe thrombectomy",
      "catheter directed thrombolysis pe",
      "ekos pe",
    ],
  },
  {
    name: "IVC Filter Placement Pulm",
    category: "Procedure",
    aliases: ["ivc filter", "vena cava filter"],
  },
  {
    name: "Bronchial Artery Embolization",
    category: "Procedure",
    aliases: ["bae", "hemoptysis embolization"],
  },

  // ============================================
  // PROCEDURES - RADIATION ONCOLOGY
  // ============================================

  // Simulation & Treatment Planning
  {
    name: "Radiation Simulation",
    category: "Procedure",
    aliases: ["ct simulation", "radiation planning ct", "sim ct"],
  },
  {
    name: "CT Simulation",
    category: "Procedure",
    aliases: ["ct sim", "treatment planning ct"],
  },
  {
    name: "MRI Simulation",
    category: "Procedure",
    aliases: ["mri sim", "mri for radiation planning"],
  },
  {
    name: "PET/CT Simulation",
    category: "Procedure",
    aliases: ["pet sim", "pet ct for radiation planning"],
  },
  {
    name: "4D CT Simulation",
    category: "Procedure",
    aliases: ["4dct", "respiratory gated ct", "motion management ct"],
  },
  {
    name: "Radiation Treatment Planning",
    category: "Procedure",
    aliases: ["dosimetry", "treatment plan", "radiation plan"],
  },
  {
    name: "IMRT Treatment Planning",
    category: "Procedure",
    aliases: ["imrt planning", "intensity modulated planning"],
  },
  {
    name: "SBRT Treatment Planning",
    category: "Procedure",
    aliases: ["sbrt planning", "stereotactic planning"],
  },
  {
    name: "Proton Treatment Planning",
    category: "Procedure",
    aliases: ["proton planning", "proton dosimetry"],
  },
  {
    name: "Brachytherapy Treatment Planning",
    category: "Procedure",
    aliases: ["brachy planning", "brachytherapy dosimetry"],
  },
  {
    name: "Adaptive Radiation Planning",
    category: "Procedure",
    aliases: ["adaptive replanning", "plan adaptation"],
  },

  // External Beam Radiation - Conventional
  {
    name: "External Beam Radiation Therapy",
    category: "Procedure",
    aliases: ["ebrt", "external beam", "radiation therapy", "xrt"],
  },
  {
    name: "3D Conformal Radiation Therapy",
    category: "Procedure",
    aliases: ["3d crt", "3dcrt", "conformal radiation"],
  },
  {
    name: "Intensity Modulated Radiation Therapy",
    category: "Procedure",
    aliases: ["imrt", "intensity modulated rt"],
  },
  {
    name: "Volumetric Modulated Arc Therapy",
    category: "Procedure",
    aliases: ["vmat", "rapidarc", "arc therapy"],
  },
  {
    name: "Image-Guided Radiation Therapy",
    category: "Procedure",
    aliases: ["igrt", "image guided rt", "cbct guided radiation"],
  },
  {
    name: "Respiratory Gated Radiation",
    category: "Procedure",
    aliases: ["gated radiation", "breath hold radiation", "dibh"],
  },
  {
    name: "Deep Inspiration Breath Hold",
    category: "Procedure",
    aliases: ["dibh", "breath hold technique"],
  },
  {
    name: "Surface Guided Radiation Therapy",
    category: "Procedure",
    aliases: ["sgrt", "align rt", "surface tracking radiation"],
  },
  {
    name: "Electron Beam Therapy",
    category: "Procedure",
    aliases: ["electron radiation", "electron beam rt"],
  },
  {
    name: "Total Skin Electron Beam Therapy",
    category: "Procedure",
    aliases: [
      "tsebt",
      "total skin electron",
      "tsei",
      "mycosis fungoides radiation",
    ],
  },
  {
    name: "Orthovoltage Radiation",
    category: "Procedure",
    aliases: ["superficial radiation", "superficial xrt"],
  },

  // Stereotactic Radiation - Brain
  {
    name: "Stereotactic Radiosurgery Brain",
    category: "Procedure",
    aliases: ["srs brain", "brain radiosurgery", "cranial srs"],
  },
  {
    name: "Fractionated Stereotactic Radiation Brain",
    category: "Procedure",
    aliases: [
      "fsrt brain",
      "fractionated srs brain",
      "hypofractionated brain radiation",
    ],
  },
  {
    name: "Gamma Knife Radiosurgery",
    category: "Procedure",
    aliases: ["gamma knife", "gkrs", "leksell gamma knife"],
  },
  {
    name: "CyberKnife Brain",
    category: "Procedure",
    aliases: ["cyberknife srs", "robotic radiosurgery brain"],
  },
  {
    name: "Linear Accelerator Radiosurgery",
    category: "Procedure",
    aliases: ["linac srs", "linac radiosurgery"],
  },
  {
    name: "SRS for Brain Metastases",
    category: "Procedure",
    aliases: ["brain met srs", "radiosurgery brain mets"],
  },
  {
    name: "SRS for Meningioma",
    category: "Procedure",
    aliases: ["meningioma srs", "meningioma radiosurgery"],
  },
  {
    name: "SRS for Acoustic Neuroma",
    category: "Procedure",
    aliases: ["acoustic neuroma srs", "vestibular schwannoma radiosurgery"],
  },
  {
    name: "SRS for Trigeminal Neuralgia",
    category: "Procedure",
    aliases: ["trigeminal neuralgia srs", "gk trigeminal"],
  },
  {
    name: "SRS for AVM",
    category: "Procedure",
    aliases: ["avm radiosurgery", "avm srs"],
  },
  {
    name: "SRS for Pituitary Adenoma",
    category: "Procedure",
    aliases: ["pituitary radiosurgery", "pituitary srs"],
  },
  {
    name: "Whole Brain Radiation",
    category: "Procedure",
    aliases: ["wbrt", "whole brain rt", "whole brain radiotherapy"],
  },
  {
    name: "Hippocampal Avoidance WBRT",
    category: "Procedure",
    aliases: ["ha-wbrt", "hippocampal sparing wbrt"],
  },
  {
    name: "Prophylactic Cranial Irradiation",
    category: "Procedure",
    aliases: ["pci", "prophylactic brain radiation"],
  },

  // Stereotactic Body Radiation (SBRT/SABR)
  {
    name: "Stereotactic Body Radiation Therapy",
    category: "Procedure",
    aliases: ["sbrt", "sabr", "stereotactic ablative radiotherapy"],
  },
  {
    name: "SBRT Lung",
    category: "Procedure",
    aliases: ["lung sbrt", "lung sabr", "stereotactic lung radiation"],
  },
  {
    name: "SBRT Liver",
    category: "Procedure",
    aliases: ["liver sbrt", "liver sabr", "hepatic sbrt"],
  },
  {
    name: "SBRT Spine",
    category: "Procedure",
    aliases: ["spine sbrt", "spinal sbrt", "spine radiosurgery", "ssrs"],
  },
  {
    name: "SBRT Pancreas",
    category: "Procedure",
    aliases: ["pancreas sbrt", "pancreatic sbrt"],
  },
  {
    name: "SBRT Prostate",
    category: "Procedure",
    aliases: [
      "prostate sbrt",
      "prostate sabr",
      "stereotactic prostate radiation",
    ],
  },
  {
    name: "SBRT Kidney",
    category: "Procedure",
    aliases: ["kidney sbrt", "renal sbrt"],
  },
  {
    name: "SBRT Adrenal",
    category: "Procedure",
    aliases: ["adrenal sbrt", "adrenal metastasis sbrt"],
  },
  {
    name: "SBRT Oligometastases",
    category: "Procedure",
    aliases: ["oligomet sbrt", "oligometastatic sbrt"],
  },
  {
    name: "SBRT Lymph Node",
    category: "Procedure",
    aliases: ["lymph node sbrt", "nodal sbrt"],
  },
  {
    name: "CyberKnife Body",
    category: "Procedure",
    aliases: ["cyberknife sbrt", "robotic sbrt"],
  },
  {
    name: "MRI-Guided Radiation",
    category: "Procedure",
    aliases: ["mri linac", "mridian", "viewray", "unity mri"],
  },
  {
    name: "MRI-Guided SBRT",
    category: "Procedure",
    aliases: ["mr-guided sbrt", "mri sbrt"],
  },

  // Proton Therapy
  {
    name: "Proton Therapy",
    category: "Procedure",
    aliases: [
      "proton beam therapy",
      "proton radiation",
      "proton beam radiation",
    ],
  },
  {
    name: "Proton Therapy Brain",
    category: "Procedure",
    aliases: ["proton brain", "brain proton radiation"],
  },
  {
    name: "Proton Therapy Pediatric",
    category: "Procedure",
    aliases: ["pediatric proton", "proton therapy children"],
  },
  {
    name: "Proton Therapy Head and Neck",
    category: "Procedure",
    aliases: ["proton head neck", "h&n proton"],
  },
  {
    name: "Proton Therapy Prostate",
    category: "Procedure",
    aliases: ["proton prostate", "prostate proton radiation"],
  },
  {
    name: "Proton Therapy Lung",
    category: "Procedure",
    aliases: ["proton lung", "lung proton radiation"],
  },
  {
    name: "Proton Therapy Breast",
    category: "Procedure",
    aliases: ["proton breast", "breast proton radiation"],
  },
  {
    name: "Proton Therapy Liver",
    category: "Procedure",
    aliases: ["proton liver", "hepatic proton"],
  },
  {
    name: "Proton Therapy Eye",
    category: "Procedure",
    aliases: ["proton eye", "ocular proton", "uveal melanoma proton"],
  },
  {
    name: "Proton Therapy CSI",
    category: "Procedure",
    aliases: ["proton craniospinal", "proton csi"],
  },
  {
    name: "Proton Therapy Base of Skull",
    category: "Procedure",
    aliases: ["skull base proton", "chordoma proton"],
  },
  {
    name: "Pencil Beam Scanning Proton",
    category: "Procedure",
    aliases: ["pbs proton", "spot scanning proton", "impt"],
  },
  {
    name: "Passive Scatter Proton",
    category: "Procedure",
    aliases: ["double scatter proton", "uniform scanning proton"],
  },
  {
    name: "Intensity Modulated Proton Therapy",
    category: "Procedure",
    aliases: ["impt", "modulated proton"],
  },

  // Heavy Ion Therapy
  {
    name: "Carbon Ion Therapy",
    category: "Procedure",
    aliases: ["carbon ion radiation", "heavy ion therapy", "cirt"],
  },

  // Brachytherapy - Prostate
  {
    name: "Prostate Brachytherapy",
    category: "Procedure",
    aliases: ["prostate seeds", "prostate implant", "prostate brachy"],
  },
  {
    name: "LDR Prostate Brachytherapy",
    category: "Procedure",
    aliases: [
      "low dose rate prostate",
      "prostate seed implant",
      "permanent prostate implant",
    ],
  },
  {
    name: "HDR Prostate Brachytherapy",
    category: "Procedure",
    aliases: ["high dose rate prostate", "temporary prostate implant"],
  },
  {
    name: "Prostate Brachytherapy Boost",
    category: "Procedure",
    aliases: ["brachy boost prostate", "brachytherapy with ebrt prostate"],
  },
  {
    name: "Salvage Prostate Brachytherapy",
    category: "Procedure",
    aliases: ["salvage brachy prostate", "recurrent prostate brachy"],
  },
  {
    name: "Focal Prostate Brachytherapy",
    category: "Procedure",
    aliases: ["focal brachy", "partial prostate brachytherapy"],
  },

  // Brachytherapy - Gynecologic
  {
    name: "Gynecologic Brachytherapy",
    category: "Procedure",
    aliases: ["gyn brachy", "gynecologic implant"],
  },
  {
    name: "Vaginal Cylinder Brachytherapy",
    category: "Procedure",
    aliases: ["vaginal cuff brachy", "vaginal cylinder", "vcb"],
  },
  {
    name: "Tandem and Ovoid Brachytherapy",
    category: "Procedure",
    aliases: ["tandem ovoid", "t&o", "cervix brachytherapy"],
  },
  {
    name: "Tandem and Ring Brachytherapy",
    category: "Procedure",
    aliases: ["tandem ring", "t&r"],
  },
  {
    name: "Interstitial Gynecologic Brachytherapy",
    category: "Procedure",
    aliases: [
      "interstitial gyn brachy",
      "syed-neblett",
      "gyn interstitial implant",
    ],
  },
  {
    name: "HDR Gynecologic Brachytherapy",
    category: "Procedure",
    aliases: ["hdr gyn", "high dose rate gyn brachy"],
  },
  {
    name: "LDR Gynecologic Brachytherapy",
    category: "Procedure",
    aliases: ["ldr gyn", "low dose rate gyn brachy"],
  },
  {
    name: "Image-Guided Brachytherapy Cervix",
    category: "Procedure",
    aliases: [
      "igbt cervix",
      "mri guided brachy cervix",
      "3d image guided brachy",
    ],
  },
  {
    name: "Intracavitary Brachytherapy",
    category: "Procedure",
    aliases: ["intracavitary implant"],
  },
  {
    name: "Combined Intracavitary Interstitial Brachy",
    category: "Procedure",
    aliases: ["hybrid brachy", "ic/is brachytherapy"],
  },

  // Brachytherapy - Breast
  {
    name: "Breast Brachytherapy",
    category: "Procedure",
    aliases: ["breast implant radiation", "breast brachy"],
  },
  {
    name: "Accelerated Partial Breast Irradiation",
    category: "Procedure",
    aliases: ["apbi", "partial breast radiation"],
  },
  {
    name: "MammoSite Brachytherapy",
    category: "Procedure",
    aliases: ["mammosite", "balloon catheter breast brachy"],
  },
  {
    name: "SAVI Brachytherapy",
    category: "Procedure",
    aliases: ["savi device", "strut adjusted volume implant"],
  },
  {
    name: "Interstitial Breast Brachytherapy",
    category: "Procedure",
    aliases: ["breast interstitial implant", "multicatheter breast brachy"],
  },
  {
    name: "Intraoperative Breast Radiation",
    category: "Procedure",
    aliases: ["iort breast", "intrabeam", "targeted iort"],
  },

  // Brachytherapy - Other Sites
  {
    name: "Skin Brachytherapy",
    category: "Procedure",
    aliases: ["skin surface brachy", "hdr skin", "surface applicator brachy"],
  },
  {
    name: "Eye Plaque Brachytherapy",
    category: "Procedure",
    aliases: [
      "eye plaque",
      "ocular brachytherapy",
      "uveal melanoma plaque",
      "iodine eye plaque",
    ],
  },
  {
    name: "Esophageal Brachytherapy",
    category: "Procedure",
    aliases: ["esophagus brachy", "intraluminal esophageal brachy"],
  },
  {
    name: "Lung Brachytherapy",
    category: "Procedure",
    aliases: ["endobronchial brachy", "airway brachytherapy"],
  },
  {
    name: "Bile Duct Brachytherapy",
    category: "Procedure",
    aliases: ["biliary brachy", "cholangiocarcinoma brachy"],
  },
  {
    name: "Rectal Brachytherapy",
    category: "Procedure",
    aliases: ["rectal brachy", "endorectal brachy"],
  },
  {
    name: "Soft Tissue Sarcoma Brachytherapy",
    category: "Procedure",
    aliases: ["sarcoma brachy", "soft tissue brachy"],
  },
  {
    name: "Head and Neck Brachytherapy",
    category: "Procedure",
    aliases: ["h&n brachy", "oral cavity brachy", "lip brachy"],
  },
  {
    name: "Keloid Brachytherapy",
    category: "Procedure",
    aliases: ["keloid radiation", "post-excision keloid brachy"],
  },
  {
    name: "Coronary Brachytherapy",
    category: "Procedure",
    aliases: ["vascular brachytherapy", "intravascular brachy"],
  },

  // Specialized Radiation Techniques
  {
    name: "Total Body Irradiation",
    category: "Procedure",
    aliases: ["tbi", "total body radiation", "conditioning radiation"],
  },
  {
    name: "Total Lymphoid Irradiation",
    category: "Procedure",
    aliases: ["tli", "lymphoid radiation"],
  },
  {
    name: "Total Marrow Irradiation",
    category: "Procedure",
    aliases: ["tmi", "total marrow radiation", "tmi-tmli"],
  },
  {
    name: "Half Body Irradiation",
    category: "Procedure",
    aliases: ["hemibody irradiation", "half body radiation"],
  },
  {
    name: "Craniospinal Irradiation",
    category: "Procedure",
    aliases: ["csi", "craniospinal radiation", "craniospinal axis radiation"],
  },
  {
    name: "Intraoperative Radiation Therapy",
    category: "Procedure",
    aliases: ["iort", "intraoperative rt", "intraoperative electron"],
  },
  {
    name: "Intraoperative Electron Radiation",
    category: "Procedure",
    aliases: ["ioert", "intraoperative electron beam"],
  },
  {
    name: "Targeted Intraoperative Radiation",
    category: "Procedure",
    aliases: ["targeted iort", "intrabeam iort"],
  },
  {
    name: "Hyperthermia with Radiation",
    category: "Procedure",
    aliases: ["hyperthermia", "thermoradiotherapy", "heat plus radiation"],
  },
  {
    name: "Boron Neutron Capture Therapy",
    category: "Procedure",
    aliases: ["bnct", "neutron capture therapy"],
  },

  // Site-Specific Radiation Courses
  {
    name: "Radiation Therapy Breast",
    category: "Procedure",
    aliases: ["breast radiation", "breast rt", "whole breast radiation"],
  },
  {
    name: "Breast Boost Radiation",
    category: "Procedure",
    aliases: ["lumpectomy bed boost", "tumor bed boost breast"],
  },
  {
    name: "Post-Mastectomy Radiation",
    category: "Procedure",
    aliases: ["pmrt", "chest wall radiation", "post-mastectomy rt"],
  },
  {
    name: "Regional Nodal Irradiation Breast",
    category: "Procedure",
    aliases: ["rni breast", "nodal radiation breast", "axillary radiation"],
  },
  {
    name: "Radiation Therapy Prostate",
    category: "Procedure",
    aliases: ["prostate radiation", "prostate ebrt", "prostate rt"],
  },
  {
    name: "Post-Prostatectomy Radiation",
    category: "Procedure",
    aliases: [
      "adjuvant prostate radiation",
      "salvage prostate radiation",
      "post-rp radiation",
    ],
  },
  {
    name: "Pelvic Radiation Prostate",
    category: "Procedure",
    aliases: ["whole pelvis radiation prostate", "elective nodal prostate"],
  },
  {
    name: "Radiation Therapy Lung",
    category: "Procedure",
    aliases: ["lung radiation", "lung cancer rt", "thoracic radiation"],
  },
  {
    name: "Concurrent Chemoradiation Lung",
    category: "Procedure",
    aliases: ["chemoradiation lung", "definitive lung cancer treatment"],
  },
  {
    name: "Radiation Therapy Head and Neck",
    category: "Procedure",
    aliases: ["head neck radiation", "h&n rt", "head and neck rt"],
  },
  {
    name: "Concurrent Chemoradiation Head Neck",
    category: "Procedure",
    aliases: ["chemoradiation h&n", "definitive head neck treatment"],
  },
  {
    name: "Radiation Therapy Esophagus",
    category: "Procedure",
    aliases: ["esophageal radiation", "esophagus rt"],
  },
  {
    name: "Radiation Therapy Rectum",
    category: "Procedure",
    aliases: [
      "rectal radiation",
      "rectal cancer rt",
      "neoadjuvant rectal radiation",
    ],
  },
  {
    name: "Short Course Rectal Radiation",
    category: "Procedure",
    aliases: ["short course rt", "5x5 rectal radiation"],
  },
  {
    name: "Long Course Chemoradiation Rectum",
    category: "Procedure",
    aliases: ["long course rectal", "standard fractionation rectal"],
  },
  {
    name: "Total Neoadjuvant Therapy Rectum",
    category: "Procedure",
    aliases: ["tnt rectal", "total neoadjuvant rectal"],
  },
  {
    name: "Radiation Therapy Cervix",
    category: "Procedure",
    aliases: ["cervical cancer radiation", "cervix rt"],
  },
  {
    name: "Radiation Therapy Endometrium",
    category: "Procedure",
    aliases: ["endometrial cancer radiation", "uterine cancer rt"],
  },
  {
    name: "Radiation Therapy Pancreas",
    category: "Procedure",
    aliases: ["pancreatic radiation", "pancreas rt"],
  },
  {
    name: "Radiation Therapy Stomach",
    category: "Procedure",
    aliases: ["gastric radiation", "stomach cancer rt"],
  },
  {
    name: "Radiation Therapy Bladder",
    category: "Procedure",
    aliases: ["bladder radiation", "bladder preservation radiation"],
  },
  {
    name: "Radiation Therapy Anal Cancer",
    category: "Procedure",
    aliases: [
      "anal cancer radiation",
      "anal cancer chemoradiation",
      "nigro protocol",
    ],
  },
  {
    name: "Radiation Therapy Lymphoma",
    category: "Procedure",
    aliases: ["lymphoma radiation", "involved site radiation", "isrt"],
  },
  {
    name: "Involved Site Radiation Therapy",
    category: "Procedure",
    aliases: ["isrt", "involved node radiation", "inrt"],
  },
  {
    name: "Radiation Therapy Sarcoma",
    category: "Procedure",
    aliases: ["sarcoma radiation", "soft tissue sarcoma rt"],
  },
  {
    name: "Radiation Therapy Bone Metastases",
    category: "Procedure",
    aliases: ["bone met radiation", "palliation bone mets"],
  },
  {
    name: "Single Fraction Bone Radiation",
    category: "Procedure",
    aliases: ["single fraction palliation", "8 gy single fraction"],
  },
  {
    name: "Radiation Therapy Brain Tumor",
    category: "Procedure",
    aliases: ["brain tumor radiation", "glioma radiation", "glioblastoma rt"],
  },
  {
    name: "Radiation Therapy Spinal Cord Compression",
    category: "Procedure",
    aliases: ["mscc radiation", "cord compression radiation"],
  },
  {
    name: "Palliative Radiation Therapy",
    category: "Procedure",
    aliases: ["palliative rt", "palliative radiation"],
  },

  // Radiation Supportive Procedures
  {
    name: "Fiducial Marker Placement Radiation",
    category: "Procedure",
    aliases: [
      "fiducial placement",
      "gold seed placement",
      "radio-opaque marker",
    ],
  },
  {
    name: "SpaceOAR Placement",
    category: "Procedure",
    aliases: ["spaceoar hydrogel", "rectal spacer", "prostate rectal spacer"],
  },
  {
    name: "Rectal Spacer Placement",
    category: "Procedure",
    aliases: ["perirectal spacer", "hydrogel spacer prostate"],
  },
  {
    name: "Tissue Expander for Radiation",
    category: "Procedure",
    aliases: [
      "bowel displacement device",
      "tissue expander radiation planning",
    ],
  },
  {
    name: "Radiation Bolus Placement",
    category: "Procedure",
    aliases: ["bolus application", "tissue equivalent bolus"],
  },
  {
    name: "Custom Immobilization Device",
    category: "Procedure",
    aliases: ["radiation mask", "thermoplastic mask", "body mold"],
  },
  {
    name: "Stereotactic Frame Placement",
    category: "Procedure",
    aliases: ["head frame", "leksell frame", "stereotactic headframe"],
  },
  {
    name: "Frameless Stereotactic Setup",
    category: "Procedure",
    aliases: ["frameless srs", "mask-based srs"],
  },

  // Radiopharmaceutical Therapy (Systemic Radiation)
  {
    name: "Radioactive Iodine Therapy",
    category: "Procedure",
    aliases: [
      "rai",
      "i-131 therapy",
      "radioiodine ablation",
      "thyroid ablation",
    ],
  },
  {
    name: "High Dose Radioactive Iodine",
    category: "Procedure",
    aliases: ["high dose rai", "i-131 for metastatic thyroid"],
  },
  {
    name: "Radium-223 Therapy",
    category: "Procedure",
    aliases: ["xofigo", "radium-223 dichloride", "ra-223 therapy"],
  },
  {
    name: "Lutetium-177 PSMA Therapy",
    category: "Procedure",
    aliases: ["lu-177 psma", "pluvicto", "psma radioligand therapy"],
  },
  {
    name: "Lutetium-177 DOTATATE",
    category: "Procedure",
    aliases: ["lutathera", "lu-177 dotatate", "prrt neuroendocrine"],
  },
  {
    name: "Peptide Receptor Radionuclide Therapy",
    category: "Procedure",
    aliases: ["prrt", "radiopeptide therapy"],
  },
  {
    name: "Iodine-131 MIBG Therapy",
    category: "Procedure",
    aliases: ["i-131 mibg", "azedra", "mibg therapy neuroblastoma"],
  },
  {
    name: "Yttrium-90 Ibritumomab",
    category: "Procedure",
    aliases: ["zevalin", "y-90 ibritumomab", "radioimmunotherapy lymphoma"],
  },
  {
    name: "Strontium-89 Therapy",
    category: "Procedure",
    aliases: ["metastron", "sr-89 therapy", "strontium bone pain"],
  },
  {
    name: "Samarium-153 Therapy",
    category: "Procedure",
    aliases: ["quadramet", "sm-153 therapy", "samarium bone pain"],
  },

  // Re-irradiation
  {
    name: "Re-irradiation",
    category: "Procedure",
    aliases: ["reirradiation", "repeat radiation", "retreatment radiation"],
  },
  {
    name: "Re-irradiation Brain",
    category: "Procedure",
    aliases: ["brain reirradiation", "recurrent brain tumor radiation"],
  },
  {
    name: "Re-irradiation Head and Neck",
    category: "Procedure",
    aliases: ["head neck reirradiation", "recurrent h&n radiation"],
  },
  {
    name: "Re-irradiation Spine",
    category: "Procedure",
    aliases: ["spine reirradiation", "repeat spine radiation"],
  },
  {
    name: "Re-irradiation Pelvis",
    category: "Procedure",
    aliases: ["pelvic reirradiation", "recurrent pelvic radiation"],
  },

  // ============================================
  // PROCEDURES - INTERVENTIONAL RADIOLOGY
  // ============================================

  // Vascular Access - Central Venous
  {
    name: "Central Line Placement",
    category: "Procedure",
    aliases: [
      "central venous catheter",
      "cvc placement",
      "central venous access",
    ],
  },
  {
    name: "Tunneled Central Line",
    category: "Procedure",
    aliases: ["tunneled catheter", "hickman", "broviac", "permcath"],
  },
  {
    name: "Non-Tunneled Central Line",
    category: "Procedure",
    aliases: ["temporary central line", "triple lumen catheter"],
  },
  {
    name: "PICC Line Placement",
    category: "Procedure",
    aliases: ["picc", "peripherally inserted central catheter"],
  },
  {
    name: "Subclavian Line Placement",
    category: "Procedure",
    aliases: ["subclavian catheter", "subclavian cvc"],
  },
  {
    name: "Internal Jugular Line Placement",
    category: "Procedure",
    aliases: ["ij line", "jugular catheter"],
  },
  {
    name: "Femoral Line Placement",
    category: "Procedure",
    aliases: ["femoral catheter", "femoral cvc"],
  },
  {
    name: "Central Line Removal",
    category: "Procedure",
    aliases: ["cvc removal", "line removal"],
  },
  {
    name: "Central Line Exchange",
    category: "Procedure",
    aliases: ["catheter exchange", "line exchange over wire"],
  },
  {
    name: "Catheter Tip Repositioning",
    category: "Procedure",
    aliases: ["line repositioning", "catheter adjustment"],
  },
  {
    name: "TPA Catheter Clearance",
    category: "Procedure",
    aliases: ["catheter declot", "line clearance", "alteplase catheter"],
  },

  // Dialysis Access
  {
    name: "Dialysis Catheter Placement",
    category: "Procedure",
    aliases: ["hemodialysis catheter", "vas cath", "trialysis catheter"],
  },
  {
    name: "Tunneled Dialysis Catheter",
    category: "Procedure",
    aliases: ["permcath", "tunneled hemodialysis catheter"],
  },
  {
    name: "Non-Tunneled Dialysis Catheter",
    category: "Procedure",
    aliases: ["temporary dialysis catheter", "acute dialysis access"],
  },
  {
    name: "Dialysis Catheter Exchange",
    category: "Procedure",
    aliases: ["permcath exchange", "dialysis line exchange"],
  },
  {
    name: "Dialysis Catheter Removal",
    category: "Procedure",
    aliases: ["permcath removal"],
  },
  {
    name: "Peritoneal Dialysis Catheter Placement",
    category: "Procedure",
    aliases: ["pd catheter", "tenckhoff catheter"],
  },
  {
    name: "Peritoneal Dialysis Catheter Removal",
    category: "Procedure",
    aliases: ["pd catheter removal"],
  },
  {
    name: "AV Fistula Angioplasty",
    category: "Procedure",
    aliases: ["fistula angioplasty", "fistuloplasty", "avf angioplasty"],
  },
  {
    name: "AV Fistula Thrombectomy",
    category: "Procedure",
    aliases: ["fistula declot", "avf thrombectomy"],
  },
  {
    name: "AV Graft Angioplasty",
    category: "Procedure",
    aliases: ["graft angioplasty", "avg angioplasty"],
  },
  {
    name: "AV Graft Thrombectomy",
    category: "Procedure",
    aliases: ["graft declot", "avg thrombectomy"],
  },
  {
    name: "Dialysis Access Stent",
    category: "Procedure",
    aliases: ["fistula stent", "graft stent", "venous outflow stent"],
  },
  {
    name: "Dialysis Access Embolization",
    category: "Procedure",
    aliases: [
      "accessory vein embolization",
      "collateral embolization dialysis",
    ],
  },
  {
    name: "Fistulogram",
    category: "Procedure",
    aliases: ["av fistula study", "dialysis access angiogram"],
  },

  // Venous Interventions - DVT/PE
  {
    name: "IVC Filter Placement",
    category: "Procedure",
    aliases: [
      "inferior vena cava filter",
      "vena cava filter",
      "greenfield filter",
    ],
  },
  {
    name: "IVC Filter Retrieval",
    category: "Procedure",
    aliases: ["filter removal", "ivc filter removal"],
  },
  {
    name: "Complex IVC Filter Retrieval",
    category: "Procedure",
    aliases: ["embedded filter removal", "tilted filter retrieval"],
  },
  {
    name: "Catheter-Directed Thrombolysis DVT",
    category: "Procedure",
    aliases: ["cdt dvt", "dvt lysis", "pharmacomechanical thrombolysis"],
  },
  {
    name: "Mechanical Thrombectomy DVT",
    category: "Procedure",
    aliases: ["dvt thrombectomy", "angiojet dvt", "clot retrieval dvt"],
  },
  {
    name: "Pharmacomechanical Thrombectomy",
    category: "Procedure",
    aliases: ["pcdt", "combined lysis thrombectomy"],
  },
  {
    name: "EKOS Thrombolysis",
    category: "Procedure",
    aliases: ["ultrasound accelerated thrombolysis", "ekos catheter"],
  },
  {
    name: "AngioVac Thrombectomy",
    category: "Procedure",
    aliases: ["angiovac", "aspiration thrombectomy large vein"],
  },
  {
    name: "ClotTriever Thrombectomy",
    category: "Procedure",
    aliases: ["clottriever", "mechanical dvt thrombectomy"],
  },
  {
    name: "FlowTriever PE",
    category: "Procedure",
    aliases: ["flowtriever", "pe thrombectomy"],
  },
  {
    name: "PERT Procedure",
    category: "Procedure",
    aliases: ["pulmonary embolism response team", "pe intervention"],
  },

  // Venous Interventions - Chronic Venous Disease
  {
    name: "May-Thurner Stent",
    category: "Procedure",
    aliases: [
      "iliac vein stent",
      "iliocaval stent",
      "may thurner syndrome treatment",
    ],
  },
  {
    name: "Iliac Vein Angioplasty",
    category: "Procedure",
    aliases: ["iliac venoplasty"],
  },
  {
    name: "SVC Stent",
    category: "Procedure",
    aliases: ["superior vena cava stent", "svc syndrome stent"],
  },
  {
    name: "IVC Stent",
    category: "Procedure",
    aliases: ["inferior vena cava stent", "ivc reconstruction"],
  },
  {
    name: "IVC Recanalization",
    category: "Procedure",
    aliases: ["chronic ivc occlusion treatment"],
  },
  {
    name: "Brachiocephalic Vein Stent",
    category: "Procedure",
    aliases: ["innominate vein stent", "central venous stenosis stent"],
  },
  {
    name: "Subclavian Vein Stent",
    category: "Procedure",
    aliases: ["subclavian stenosis stent"],
  },
  {
    name: "Venous Thoracic Outlet Decompression",
    category: "Procedure",
    aliases: ["paget-schroetter treatment", "venous tos intervention"],
  },
  {
    name: "Gonadal Vein Embolization",
    category: "Procedure",
    aliases: [
      "ovarian vein embolization",
      "testicular vein embolization",
      "varicocele embolization",
    ],
  },
  {
    name: "Pelvic Congestion Syndrome Treatment",
    category: "Procedure",
    aliases: ["pelvic vein embolization", "pcs embolization"],
  },
  {
    name: "Venography",
    category: "Procedure",
    aliases: ["venogram", "contrast venography"],
  },
  {
    name: "IVUS Venous",
    category: "Procedure",
    aliases: ["intravascular ultrasound venous", "ivus iliocaval"],
  },

  // Arterial Interventions - Peripheral
  {
    name: "Peripheral Angiogram",
    category: "Procedure",
    aliases: [
      "lower extremity angiogram",
      "leg angiogram",
      "peripheral arteriogram",
    ],
  },
  {
    name: "Peripheral Artery Angioplasty",
    category: "Procedure",
    aliases: ["pta", "leg angioplasty", "peripheral balloon angioplasty"],
  },
  {
    name: "Peripheral Artery Stent",
    category: "Procedure",
    aliases: ["leg stent", "peripheral stent", "iliac stent"],
  },
  {
    name: "Iliac Artery Angioplasty",
    category: "Procedure",
    aliases: ["iliac pta", "iliac balloon"],
  },
  {
    name: "Iliac Artery Stent",
    category: "Procedure",
    aliases: ["iliac stent placement"],
  },
  {
    name: "Femoral Artery Angioplasty",
    category: "Procedure",
    aliases: ["sfa angioplasty", "femoral pta"],
  },
  {
    name: "Femoral Artery Stent",
    category: "Procedure",
    aliases: ["sfa stent", "femoral stent"],
  },
  {
    name: "Popliteal Artery Angioplasty",
    category: "Procedure",
    aliases: ["popliteal pta"],
  },
  {
    name: "Popliteal Artery Stent",
    category: "Procedure",
    aliases: ["popliteal stent"],
  },
  {
    name: "Tibial Artery Angioplasty",
    category: "Procedure",
    aliases: ["btk angioplasty", "below the knee angioplasty", "tibial pta"],
  },
  {
    name: "Pedal Artery Angioplasty",
    category: "Procedure",
    aliases: ["foot angioplasty", "pedal loop angioplasty"],
  },
  {
    name: "Drug-Coated Balloon Peripheral",
    category: "Procedure",
    aliases: ["dcb peripheral", "paclitaxel balloon peripheral"],
  },
  {
    name: "Drug-Eluting Stent Peripheral",
    category: "Procedure",
    aliases: ["des peripheral", "zilver ptx"],
  },
  {
    name: "Covered Stent Peripheral",
    category: "Procedure",
    aliases: ["stent graft peripheral", "viabahn"],
  },
  {
    name: "Peripheral Atherectomy",
    category: "Procedure",
    aliases: [
      "plaque excision",
      "directional atherectomy",
      "rotational atherectomy peripheral",
    ],
  },
  {
    name: "Laser Atherectomy Peripheral",
    category: "Procedure",
    aliases: ["excimer laser peripheral", "spectranetics laser"],
  },
  {
    name: "Orbital Atherectomy Peripheral",
    category: "Procedure",
    aliases: ["cso atherectomy peripheral", "diamondback peripheral"],
  },
  {
    name: "Peripheral Thrombolysis",
    category: "Procedure",
    aliases: ["arterial thrombolysis", "tpa peripheral"],
  },
  {
    name: "Peripheral Thrombectomy",
    category: "Procedure",
    aliases: ["arterial thrombectomy", "mechanical thrombectomy peripheral"],
  },
  {
    name: "Acute Limb Ischemia Intervention",
    category: "Procedure",
    aliases: ["ali treatment", "acute limb intervention"],
  },
  {
    name: "Critical Limb Ischemia Intervention",
    category: "Procedure",
    aliases: ["cli treatment", "chronic limb threatening ischemia"],
  },

  // Arterial Interventions - Aortic
  {
    name: "Aortogram",
    category: "Procedure",
    aliases: ["aortic angiogram", "aortography"],
  },
  {
    name: "Abdominal Aortic Aneurysm Stent Graft",
    category: "Procedure",
    aliases: ["evar", "endovascular aaa repair", "aortic stent graft"],
  },
  {
    name: "Thoracic Aortic Stent Graft",
    category: "Procedure",
    aliases: ["tevar", "thoracic endovascular repair"],
  },
  {
    name: "Fenestrated EVAR",
    category: "Procedure",
    aliases: ["fevar", "fenestrated endograft"],
  },
  {
    name: "Branched EVAR",
    category: "Procedure",
    aliases: ["bevar", "branched endograft"],
  },
  {
    name: "EVAR Endoleak Repair",
    category: "Procedure",
    aliases: ["endoleak embolization", "type 2 endoleak treatment"],
  },
  {
    name: "Aortic Dissection Stent",
    category: "Procedure",
    aliases: ["dissection endograft", "tevar for dissection"],
  },
  {
    name: "Aortic Occlusion Balloon",
    category: "Procedure",
    aliases: ["reboa", "resuscitative balloon occlusion"],
  },

  // Arterial Interventions - Visceral
  {
    name: "Mesenteric Angiogram",
    category: "Procedure",
    aliases: ["visceral angiogram", "celiac angiogram", "sma angiogram"],
  },
  {
    name: "Mesenteric Artery Angioplasty",
    category: "Procedure",
    aliases: ["sma angioplasty", "celiac angioplasty"],
  },
  {
    name: "Mesenteric Artery Stent",
    category: "Procedure",
    aliases: ["sma stent", "celiac stent", "visceral stent"],
  },
  {
    name: "Renal Artery Angioplasty",
    category: "Procedure",
    aliases: ["renal pta", "renal angioplasty"],
  },
  {
    name: "Renal Artery Stent",
    category: "Procedure",
    aliases: ["renal stent", "renal artery stenting"],
  },
  {
    name: "Splenic Artery Aneurysm Treatment",
    category: "Procedure",
    aliases: ["splenic aneurysm embolization", "splenic aneurysm stent"],
  },
  {
    name: "Hepatic Artery Aneurysm Treatment",
    category: "Procedure",
    aliases: ["hepatic aneurysm embolization"],
  },
  {
    name: "Renal Artery Aneurysm Treatment",
    category: "Procedure",
    aliases: ["renal aneurysm embolization"],
  },
  {
    name: "Visceral Artery Aneurysm Repair",
    category: "Procedure",
    aliases: [
      "visceral aneurysm embolization",
      "visceral aneurysm stent graft",
    ],
  },

  // Embolization - Non-Oncologic
  {
    name: "Uterine Fibroid Embolization",
    category: "Procedure",
    aliases: [
      "ufe",
      "uterine artery embolization",
      "uae",
      "fibroid embolization",
    ],
  },
  {
    name: "Pelvic Arteriovenous Malformation Embolization",
    category: "Procedure",
    aliases: ["pelvic avm embo"],
  },
  {
    name: "GI Bleeding Embolization",
    category: "Procedure",
    aliases: ["gi bleed embo", "mesenteric embolization bleeding"],
  },
  {
    name: "Upper GI Bleeding Embolization",
    category: "Procedure",
    aliases: ["gastric artery embolization", "gda embolization"],
  },
  {
    name: "Lower GI Bleeding Embolization",
    category: "Procedure",
    aliases: ["colonic bleeding embolization"],
  },
  {
    name: "Trauma Embolization",
    category: "Procedure",
    aliases: ["trauma embo", "hemorrhage embolization trauma"],
  },
  {
    name: "Splenic Trauma Embolization",
    category: "Procedure",
    aliases: ["splenic embo trauma", "splenic artery embolization injury"],
  },
  {
    name: "Hepatic Trauma Embolization",
    category: "Procedure",
    aliases: ["liver embo trauma", "hepatic artery embolization injury"],
  },
  {
    name: "Renal Trauma Embolization",
    category: "Procedure",
    aliases: ["kidney embo trauma", "renal artery embolization injury"],
  },
  {
    name: "Pelvic Trauma Embolization",
    category: "Procedure",
    aliases: [
      "pelvic fracture embolization",
      "internal iliac embolization trauma",
    ],
  },
  {
    name: "Prostatic Artery Embolization",
    category: "Procedure",
    aliases: ["pae", "prostate embolization", "bph embolization"],
  },
  {
    name: "Hemorrhoid Embolization",
    category: "Procedure",
    aliases: ["hemorrhoidal artery embolization", "emborrhoid"],
  },
  {
    name: "Genicular Artery Embolization",
    category: "Procedure",
    aliases: ["gae", "knee embolization", "osteoarthritis embolization"],
  },
  {
    name: "Shoulder Embolization",
    category: "Procedure",
    aliases: [
      "frozen shoulder embolization",
      "adhesive capsulitis embolization",
    ],
  },
  {
    name: "Epistaxis Embolization",
    category: "Procedure",
    aliases: ["nasal bleeding embolization", "sphenopalatine embolization"],
  },
  {
    name: "Postpartum Hemorrhage Embolization",
    category: "Procedure",
    aliases: ["pph embolization", "uterine embolization postpartum"],
  },
  {
    name: "Aneurysm Embolization Visceral",
    category: "Procedure",
    aliases: ["visceral aneurysm coiling"],
  },
  {
    name: "Pseudoaneurysm Embolization",
    category: "Procedure",
    aliases: ["false aneurysm embolization", "pseudoaneurysm treatment"],
  },
  {
    name: "Splenic Embolization",
    category: "Procedure",
    aliases: [
      "partial splenic embolization",
      "pse",
      "hypersplenism embolization",
    ],
  },

  // Non-Vascular Interventions - Drainage
  {
    name: "Abscess Drainage",
    category: "Procedure",
    aliases: ["percutaneous abscess drainage", "abscess drain placement"],
  },
  {
    name: "Abdominal Abscess Drainage",
    category: "Procedure",
    aliases: ["intraabdominal abscess drainage", "pelvic abscess drainage"],
  },
  {
    name: "Hepatic Abscess Drainage",
    category: "Procedure",
    aliases: ["liver abscess drainage", "pyogenic abscess drain"],
  },
  {
    name: "Renal Abscess Drainage",
    category: "Procedure",
    aliases: ["perinephric abscess drainage", "kidney abscess drain"],
  },
  {
    name: "Lung Abscess Drainage",
    category: "Procedure",
    aliases: ["pulmonary abscess drainage", "empyema drainage"],
  },
  {
    name: "Soft Tissue Abscess Drainage",
    category: "Procedure",
    aliases: ["subcutaneous abscess drainage"],
  },
  {
    name: "Fluid Collection Drainage",
    category: "Procedure",
    aliases: ["seroma drainage", "hematoma drainage", "lymphocele drainage"],
  },
  {
    name: "Percutaneous Cholecystostomy",
    category: "Procedure",
    aliases: ["cholecystostomy tube", "gallbladder drain"],
  },

  // Non-Vascular Interventions - Biliary
  {
    name: "Percutaneous Transhepatic Cholangiogram",
    category: "Procedure",
    aliases: ["ptc", "transhepatic cholangiography"],
  },
  {
    name: "Percutaneous Biliary Drainage",
    category: "Procedure",
    aliases: ["ptbd", "biliary drain", "transhepatic biliary drain"],
  },
  {
    name: "Internal External Biliary Drain",
    category: "Procedure",
    aliases: ["internal external drain", "biliary catheter"],
  },
  {
    name: "External Biliary Drain",
    category: "Procedure",
    aliases: ["external ptbd"],
  },
  {
    name: "Biliary Drain Exchange",
    category: "Procedure",
    aliases: ["ptbd exchange", "biliary catheter change"],
  },
  {
    name: "Biliary Drain Removal",
    category: "Procedure",
    aliases: ["ptbd removal"],
  },
  {
    name: "Percutaneous Biliary Stone Extraction",
    category: "Procedure",
    aliases: ["transhepatic stone removal", "biliary stone removal"],
  },
  {
    name: "Percutaneous Biliary Stent",
    category: "Procedure",
    aliases: ["transhepatic biliary stent", "percutaneous metal stent biliary"],
  },
  {
    name: "Biliary Stricture Dilation",
    category: "Procedure",
    aliases: ["biliary balloon dilation", "anastomotic stricture dilation"],
  },
  {
    name: "Biliary Biopsy Percutaneous",
    category: "Procedure",
    aliases: ["transhepatic biopsy bile duct"],
  },

  // Non-Vascular Interventions - Urinary
  {
    name: "Percutaneous Nephrostomy",
    category: "Procedure",
    aliases: ["pcn", "nephrostomy tube", "kidney drain"],
  },
  {
    name: "Nephrostomy Exchange",
    category: "Procedure",
    aliases: ["pcn exchange", "nephrostomy tube change"],
  },
  {
    name: "Nephrostomy Removal",
    category: "Procedure",
    aliases: ["pcn removal"],
  },
  {
    name: "Nephroureteral Stent",
    category: "Procedure",
    aliases: ["antegrade ureteral stent", "nephroureteral catheter"],
  },
  {
    name: "Antegrade Ureteral Stent",
    category: "Procedure",
    aliases: ["percutaneous ureteral stent"],
  },
  {
    name: "Ureteral Stricture Dilation",
    category: "Procedure",
    aliases: ["ureteral balloon dilation"],
  },
  {
    name: "Percutaneous Cystostomy",
    category: "Procedure",
    aliases: ["suprapubic catheter", "suprapubic tube", "spc placement"],
  },
  {
    name: "Suprapubic Catheter Exchange",
    category: "Procedure",
    aliases: ["spc exchange", "suprapubic tube change"],
  },
  {
    name: "Percutaneous Nephrostolithotomy Access",
    category: "Procedure",
    aliases: ["pcnl access", "renal access for stones"],
  },
  {
    name: "Ileal Conduit Access",
    category: "Procedure",
    aliases: ["loopogram", "conduit access"],
  },

  // Non-Vascular Interventions - GI
  {
    name: "Percutaneous Gastrostomy",
    category: "Procedure",
    aliases: ["prg", "radiologic gastrostomy", "fluoroscopic g-tube"],
  },
  {
    name: "Percutaneous Gastrojejunostomy",
    category: "Procedure",
    aliases: ["prgj", "radiologic gj tube", "gj placement"],
  },
  {
    name: "Gastrostomy Tube Exchange",
    category: "Procedure",
    aliases: ["g-tube exchange", "peg replacement"],
  },
  {
    name: "Gastrojejunostomy Tube Exchange",
    category: "Procedure",
    aliases: ["gj tube exchange", "gj replacement"],
  },
  {
    name: "Gastrostomy Conversion to GJ",
    category: "Procedure",
    aliases: ["g to gj conversion"],
  },
  {
    name: "Cecostomy Tube Placement",
    category: "Procedure",
    aliases: ["percutaneous cecostomy", "c-tube"],
  },
  {
    name: "Colonic Stent Placement IR",
    category: "Procedure",
    aliases: ["colonic stent ir", "colon decompression stent"],
  },
  {
    name: "Esophageal Stent Placement IR",
    category: "Procedure",
    aliases: ["esophageal stent ir", "esophageal sems ir"],
  },
  {
    name: "Gastroduodenal Stent IR",
    category: "Procedure",
    aliases: ["duodenal stent ir", "goo stent ir"],
  },
  {
    name: "Percutaneous Transgastric Jejunostomy",
    category: "Procedure",
    aliases: ["direct pej", "percutaneous jejunostomy"],
  },
  {
    name: "Contrast Enema",
    category: "Procedure",
    aliases: ["barium enema", "water soluble contrast enema"],
  },
  {
    name: "Upper GI Series",
    category: "Procedure",
    aliases: ["ugi", "barium swallow", "esophagram"],
  },
  {
    name: "Small Bowel Follow Through",
    category: "Procedure",
    aliases: ["sbft", "small bowel series"],
  },
  {
    name: "Fluoroscopic Guidance Procedure",
    category: "Procedure",
    aliases: ["fluoro guidance"],
  },

  // Spine Interventions - Non-Oncologic
  {
    name: "Vertebroplasty",
    category: "Procedure",
    aliases: ["vertebral augmentation", "spine cement"],
  },
  {
    name: "Kyphoplasty",
    category: "Procedure",
    aliases: ["balloon kyphoplasty", "spine balloon"],
  },
  {
    name: "Sacroplasty",
    category: "Procedure",
    aliases: ["sacral vertebroplasty", "sacral augmentation"],
  },
  {
    name: "Vertebral Augmentation",
    category: "Procedure",
    aliases: ["percutaneous vertebral augmentation"],
  },
  {
    name: "SpineJack",
    category: "Procedure",
    aliases: ["vertebral body stenting", "spinejack implant"],
  },
  {
    name: "Discography",
    category: "Procedure",
    aliases: ["disc injection diagnostic", "provocative discography"],
  },
  {
    name: "Intradiscal Procedure",
    category: "Procedure",
    aliases: ["intradiscal injection", "disc treatment"],
  },
  {
    name: "Facet Joint Injection IR",
    category: "Procedure",
    aliases: ["facet block ir", "facet injection fluoroscopic"],
  },
  {
    name: "SI Joint Injection IR",
    category: "Procedure",
    aliases: ["sacroiliac injection ir", "si injection fluoroscopic"],
  },
  {
    name: "Epidural Steroid Injection IR",
    category: "Procedure",
    aliases: ["esi ir", "epidural ir"],
  },
  {
    name: "Transforaminal Epidural IR",
    category: "Procedure",
    aliases: ["tfesi ir", "transforaminal injection ir"],
  },
  {
    name: "Nerve Root Block IR",
    category: "Procedure",
    aliases: ["selective nerve root block", "snrb ir"],
  },
  {
    name: "Myelography",
    category: "Procedure",
    aliases: ["myelogram", "intrathecal contrast"],
  },
  {
    name: "CT Myelography",
    category: "Procedure",
    aliases: ["ct myelogram", "post myelogram ct"],
  },
  {
    name: "Lumbar Puncture IR",
    category: "Procedure",
    aliases: ["fluoroscopic lumbar puncture", "difficult lp"],
  },

  // ============================================
  // PROCEDURES - INTERVENTIONAL ONCOLOGY
  // ============================================

  // Liver-Directed Therapy - Embolization
  {
    name: "Transarterial Chemoembolization",
    category: "Procedure",
    aliases: ["tace", "chemoembolization", "hepatic chemoembolization"],
  },
  {
    name: "Conventional TACE",
    category: "Procedure",
    aliases: ["ctace", "lipiodol tace", "conventional chemoembolization"],
  },
  {
    name: "DEB-TACE",
    category: "Procedure",
    aliases: [
      "drug-eluting bead tace",
      "deb chemoembolization",
      "dc bead tace",
      "lc bead tace",
    ],
  },
  {
    name: "TACE for HCC",
    category: "Procedure",
    aliases: ["hcc chemoembolization", "hepatocellular carcinoma tace"],
  },
  {
    name: "TACE for Liver Metastases",
    category: "Procedure",
    aliases: ["metastatic liver tace", "colorectal liver met tace"],
  },
  {
    name: "Bland Embolization Liver",
    category: "Procedure",
    aliases: [
      "tae",
      "transarterial embolization",
      "bland hepatic embolization",
    ],
  },
  {
    name: "Hepatic Artery Embolization",
    category: "Procedure",
    aliases: ["hae", "liver embolization"],
  },

  // Liver-Directed Therapy - Radioembolization
  {
    name: "Y90 Radioembolization",
    category: "Procedure",
    aliases: [
      "y-90",
      "yttrium-90 radioembolization",
      "sirt",
      "selective internal radiation therapy",
    ],
  },
  {
    name: "Y90 TheraSphere",
    category: "Procedure",
    aliases: ["therasphere", "glass microsphere y90"],
  },
  {
    name: "Y90 SIR-Spheres",
    category: "Procedure",
    aliases: ["sir-spheres", "resin microsphere y90"],
  },
  {
    name: "Y90 for HCC",
    category: "Procedure",
    aliases: ["y90 hepatocellular carcinoma", "sirt hcc"],
  },
  {
    name: "Y90 for Liver Metastases",
    category: "Procedure",
    aliases: ["y90 liver mets", "sirt metastases", "y90 colorectal mets"],
  },
  {
    name: "Y90 Radiation Segmentectomy",
    category: "Procedure",
    aliases: ["radiation segmentectomy", "ablative y90"],
  },
  {
    name: "Y90 Radiation Lobectomy",
    category: "Procedure",
    aliases: ["radiation lobectomy", "lobar y90"],
  },
  {
    name: "Y90 Mapping Angiogram",
    category: "Procedure",
    aliases: ["y90 planning angiogram", "tc-99 maa scan", "lung shunt study"],
  },
  {
    name: "Hepatic Artery Infusion Pump",
    category: "Procedure",
    aliases: ["haip", "hepatic artery pump", "floxuridine pump"],
  },

  // Portal Vein Interventions
  {
    name: "Portal Vein Embolization",
    category: "Procedure",
    aliases: ["pve", "preoperative portal embolization"],
  },
  {
    name: "Portal Vein Embolization Right",
    category: "Procedure",
    aliases: ["right pve", "right portal vein embolization"],
  },
  {
    name: "Portal Vein Embolization Extended",
    category: "Procedure",
    aliases: ["extended pve", "segment 4 embolization"],
  },
  {
    name: "ALPPS Stage 1",
    category: "Procedure",
    aliases: ["alpps", "associating liver partition", "in situ liver split"],
  },
  {
    name: "Liver Venous Deprivation",
    category: "Procedure",
    aliases: ["lvd", "combined pve hvd", "hepatic vein embolization"],
  },
  {
    name: "Portal Vein Tumor Thrombosis Treatment",
    category: "Procedure",
    aliases: ["pvtt treatment", "portal vein recanalization"],
  },

  // Ablation - Radiofrequency
  {
    name: "Radiofrequency Ablation Tumor",
    category: "Procedure",
    aliases: ["rfa tumor", "rf ablation tumor"],
  },
  {
    name: "RFA Liver Tumor",
    category: "Procedure",
    aliases: ["liver rfa", "hepatic rfa", "rfa hcc"],
  },
  {
    name: "RFA Liver Metastasis",
    category: "Procedure",
    aliases: ["rfa colorectal met", "rfa liver met"],
  },
  {
    name: "RFA Lung Tumor",
    category: "Procedure",
    aliases: ["lung rfa", "pulmonary rfa"],
  },
  {
    name: "RFA Kidney Tumor",
    category: "Procedure",
    aliases: ["renal rfa", "kidney rfa", "rfa rcc"],
  },
  {
    name: "RFA Bone Tumor",
    category: "Procedure",
    aliases: ["bone rfa", "osseous rfa", "osteoid osteoma rfa"],
  },
  {
    name: "RFA Adrenal Tumor",
    category: "Procedure",
    aliases: ["adrenal rfa"],
  },
  {
    name: "RFA Thyroid Nodule",
    category: "Procedure",
    aliases: ["thyroid rfa", "thyroid ablation"],
  },

  // Ablation - Microwave
  {
    name: "Microwave Ablation Tumor",
    category: "Procedure",
    aliases: ["mwa tumor", "microwave tumor ablation"],
  },
  {
    name: "Microwave Ablation Liver",
    category: "Procedure",
    aliases: ["liver mwa", "hepatic microwave ablation", "mwa hcc"],
  },
  {
    name: "Microwave Ablation Liver Metastasis",
    category: "Procedure",
    aliases: ["mwa liver met", "microwave colorectal met"],
  },
  {
    name: "Microwave Ablation Lung",
    category: "Procedure",
    aliases: ["lung mwa", "pulmonary microwave ablation"],
  },
  {
    name: "Microwave Ablation Kidney",
    category: "Procedure",
    aliases: ["renal mwa", "kidney microwave ablation", "mwa rcc"],
  },
  {
    name: "Microwave Ablation Bone",
    category: "Procedure",
    aliases: ["bone mwa", "osseous microwave ablation"],
  },
  {
    name: "Microwave Ablation Adrenal",
    category: "Procedure",
    aliases: ["adrenal mwa", "adrenal microwave"],
  },

  // Ablation - Cryoablation
  {
    name: "Cryoablation Tumor",
    category: "Procedure",
    aliases: ["cryotherapy tumor", "cryo ablation tumor", "tumor freezing"],
  },
  {
    name: "Cryoablation Liver",
    category: "Procedure",
    aliases: ["liver cryoablation", "hepatic cryotherapy"],
  },
  {
    name: "Cryoablation Lung",
    category: "Procedure",
    aliases: ["lung cryoablation", "pulmonary cryotherapy"],
  },
  {
    name: "Cryoablation Kidney",
    category: "Procedure",
    aliases: ["renal cryoablation", "kidney cryotherapy", "cryo rcc"],
  },
  {
    name: "Cryoablation Bone",
    category: "Procedure",
    aliases: ["bone cryoablation", "osseous cryotherapy"],
  },
  {
    name: "Cryoablation Soft Tissue",
    category: "Procedure",
    aliases: ["soft tissue cryo", "desmoid cryoablation"],
  },
  {
    name: "Cryoablation Prostate",
    category: "Procedure",
    aliases: ["prostate cryotherapy", "cryo prostate", "prostate freezing"],
  },
  {
    name: "Cryoablation Breast",
    category: "Procedure",
    aliases: ["breast cryoablation", "breast tumor freezing"],
  },

  // Ablation - Irreversible Electroporation
  {
    name: "Irreversible Electroporation",
    category: "Procedure",
    aliases: ["ire", "nanoknife", "electroporation ablation"],
  },
  {
    name: "IRE Liver",
    category: "Procedure",
    aliases: ["nanoknife liver", "liver ire", "hepatic electroporation"],
  },
  {
    name: "IRE Pancreas",
    category: "Procedure",
    aliases: [
      "nanoknife pancreas",
      "pancreatic ire",
      "pancreas electroporation",
    ],
  },
  {
    name: "IRE Prostate",
    category: "Procedure",
    aliases: ["nanoknife prostate", "prostate ire"],
  },
  {
    name: "IRE Kidney",
    category: "Procedure",
    aliases: ["nanoknife kidney", "renal ire"],
  },

  // Ablation - Other Modalities
  {
    name: "Laser Ablation Tumor",
    category: "Procedure",
    aliases: [
      "laser interstitial thermal therapy",
      "litt tumor",
      "mri guided laser ablation",
    ],
  },
  {
    name: "LITT Liver",
    category: "Procedure",
    aliases: ["laser ablation liver", "hepatic litt"],
  },
  {
    name: "LITT Brain Tumor",
    category: "Procedure",
    aliases: [
      "laser ablation brain",
      "mri guided laser brain",
      "visualase brain",
    ],
  },
  {
    name: "High Intensity Focused Ultrasound Tumor",
    category: "Procedure",
    aliases: ["hifu tumor", "focused ultrasound ablation"],
  },
  {
    name: "HIFU Liver",
    category: "Procedure",
    aliases: ["hifu hepatic", "focused ultrasound liver"],
  },
  {
    name: "HIFU Prostate",
    category: "Procedure",
    aliases: ["hifu prostate", "focal one", "ablatherm"],
  },
  {
    name: "HIFU Bone Metastasis",
    category: "Procedure",
    aliases: ["hifu bone", "exablate bone", "mrgfus bone met"],
  },
  {
    name: "HIFU Uterine Fibroids",
    category: "Procedure",
    aliases: [
      "mrgfus fibroids",
      "exablate fibroids",
      "focused ultrasound fibroids",
    ],
  },
  {
    name: "Percutaneous Ethanol Injection",
    category: "Procedure",
    aliases: ["pei", "ethanol ablation", "alcohol injection tumor"],
  },
  {
    name: "Percutaneous Acetic Acid Injection",
    category: "Procedure",
    aliases: ["pai", "acetic acid ablation"],
  },
  {
    name: "Histotripsy",
    category: "Procedure",
    aliases: ["mechanical histotripsy", "non-thermal ablation"],
  },

  // Bone Tumor Interventions
  {
    name: "Bone Metastasis Ablation",
    category: "Procedure",
    aliases: ["bone met ablation", "osseous metastasis treatment"],
  },
  {
    name: "Cementoplasty",
    category: "Procedure",
    aliases: ["bone cement injection", "osteoplasty"],
  },
  {
    name: "Acetabuloplasty",
    category: "Procedure",
    aliases: ["acetabular cement", "hip cementoplasty"],
  },
  {
    name: "Sacroplasty",
    category: "Procedure",
    aliases: ["sacral cementoplasty", "sacral insufficiency treatment"],
  },
  {
    name: "Kyphoplasty for Malignancy",
    category: "Procedure",
    aliases: ["kyphoplasty tumor", "kyphoplasty pathologic fracture"],
  },
  {
    name: "Vertebroplasty for Malignancy",
    category: "Procedure",
    aliases: ["vertebroplasty tumor", "vertebroplasty pathologic fracture"],
  },
  {
    name: "Ablation with Cementoplasty",
    category: "Procedure",
    aliases: ["combined ablation cement", "rfa with cement"],
  },
  {
    name: "Bone Tumor Embolization",
    category: "Procedure",
    aliases: ["preoperative bone tumor embo", "bone met embolization"],
  },
  {
    name: "Vertebral Body Tumor Embolization",
    category: "Procedure",
    aliases: ["spine tumor embolization", "preop spine embo"],
  },
  {
    name: "Pelvic Tumor Embolization",
    category: "Procedure",
    aliases: ["pelvic bone embolization"],
  },
  {
    name: "Radiofrequency Ablation Osteoid Osteoma",
    category: "Procedure",
    aliases: ["osteoid osteoma ablation", "oo rfa"],
  },

  // Lung Tumor Interventions
  {
    name: "CT-Guided Lung Tumor Ablation",
    category: "Procedure",
    aliases: ["percutaneous lung ablation", "image guided lung ablation"],
  },
  {
    name: "Bronchial Artery Chemoembolization",
    category: "Procedure",
    aliases: ["bace", "lung chemoembolization"],
  },
  {
    name: "Pulmonary Tumor Embolization",
    category: "Procedure",
    aliases: ["lung tumor embolization"],
  },

  // Kidney/Adrenal Tumor Interventions
  {
    name: "Renal Tumor Ablation",
    category: "Procedure",
    aliases: [
      "kidney tumor ablation",
      "rcc ablation",
      "small renal mass ablation",
    ],
  },
  {
    name: "Renal Artery Embolization Tumor",
    category: "Procedure",
    aliases: ["preoperative renal embolization", "kidney tumor embolization"],
  },
  {
    name: "Adrenal Tumor Ablation",
    category: "Procedure",
    aliases: ["adrenal ablation", "adrenal metastasis ablation"],
  },
  {
    name: "Adrenal Vein Sampling",
    category: "Procedure",
    aliases: ["avs", "adrenal venous sampling"],
  },

  // Soft Tissue & Lymph Node Interventions
  {
    name: "Soft Tissue Tumor Ablation",
    category: "Procedure",
    aliases: ["soft tissue ablation", "sarcoma ablation", "desmoid ablation"],
  },
  {
    name: "Lymph Node Ablation",
    category: "Procedure",
    aliases: ["lymph node rfa", "nodal ablation"],
  },
  {
    name: "Retroperitoneal Tumor Ablation",
    category: "Procedure",
    aliases: ["retroperitoneal ablation"],
  },
  {
    name: "Pelvic Tumor Ablation",
    category: "Procedure",
    aliases: ["pelvic mass ablation", "pelvic recurrence ablation"],
  },

  // Head & Neck Interventional Oncology
  {
    name: "Head and Neck Tumor Embolization",
    category: "Procedure",
    aliases: ["h&n tumor embolization", "preop h&n embolization"],
  },
  {
    name: "Juvenile Nasopharyngeal Angiofibroma Embolization",
    category: "Procedure",
    aliases: ["jna embolization", "angiofibroma embolization"],
  },
  {
    name: "Carotid Blowout Prevention",
    category: "Procedure",
    aliases: ["carotid sacrifice", "carotid occlusion for tumor"],
  },
  {
    name: "Thyroid Ablation Nodule",
    category: "Procedure",
    aliases: ["thyroid rfa", "thyroid microwave", "thyroid laser ablation"],
  },
  {
    name: "Parathyroid Ablation",
    category: "Procedure",
    aliases: ["parathyroid rfa", "parathyroid ethanol ablation"],
  },

  // Tumor Biopsy - Image Guided
  {
    name: "CT-Guided Tumor Biopsy",
    category: "Procedure",
    aliases: ["ct guided biopsy", "percutaneous tumor biopsy"],
  },
  {
    name: "CT-Guided Liver Biopsy",
    category: "Procedure",
    aliases: ["percutaneous liver biopsy ct", "liver mass biopsy"],
  },
  {
    name: "CT-Guided Lung Biopsy",
    category: "Procedure",
    aliases: ["percutaneous lung biopsy", "lung mass biopsy ct"],
  },
  {
    name: "CT-Guided Bone Biopsy",
    category: "Procedure",
    aliases: ["percutaneous bone biopsy", "osseous biopsy ct"],
  },
  {
    name: "CT-Guided Lymph Node Biopsy",
    category: "Procedure",
    aliases: ["percutaneous lymph node biopsy", "deep node biopsy"],
  },
  {
    name: "CT-Guided Soft Tissue Biopsy",
    category: "Procedure",
    aliases: ["soft tissue mass biopsy", "percutaneous mass biopsy"],
  },
  {
    name: "CT-Guided Retroperitoneal Biopsy",
    category: "Procedure",
    aliases: ["retroperitoneal mass biopsy"],
  },
  {
    name: "CT-Guided Adrenal Biopsy",
    category: "Procedure",
    aliases: ["adrenal mass biopsy", "percutaneous adrenal biopsy"],
  },
  {
    name: "CT-Guided Pelvic Biopsy",
    category: "Procedure",
    aliases: ["pelvic mass biopsy", "deep pelvic biopsy"],
  },
  {
    name: "Ultrasound-Guided Tumor Biopsy",
    category: "Procedure",
    aliases: ["us guided biopsy", "ultrasound guided mass biopsy"],
  },
  {
    name: "MRI-Guided Biopsy",
    category: "Procedure",
    aliases: ["mri guided tumor biopsy"],
  },
  {
    name: "PET-CT Guided Biopsy",
    category: "Procedure",
    aliases: ["pet guided biopsy", "metabolically active lesion biopsy"],
  },

  // Oncologic Vascular Access
  {
    name: "Port-a-Cath Placement",
    category: "Procedure",
    aliases: ["port placement", "chemoport", "implanted port", "mediport"],
  },
  {
    name: "Port-a-Cath Removal",
    category: "Procedure",
    aliases: ["port removal", "port explant"],
  },
  {
    name: "Port Revision",
    category: "Procedure",
    aliases: ["port repositioning", "port exchange"],
  },
  {
    name: "Tunneled Catheter Placement Oncology",
    category: "Procedure",
    aliases: ["hickman placement", "broviac placement", "tunneled cvc"],
  },
  {
    name: "Tunneled Catheter Removal",
    category: "Procedure",
    aliases: ["hickman removal", "tunneled line removal"],
  },
  {
    name: "PICC Placement Oncology",
    category: "Procedure",
    aliases: ["picc line", "peripherally inserted central catheter"],
  },
  {
    name: "Dialysis Catheter Oncology",
    category: "Procedure",
    aliases: ["apheresis catheter", "pheresis catheter placement"],
  },

  // Oncologic Drainage & Palliation
  {
    name: "Biliary Drainage Malignant",
    category: "Procedure",
    aliases: ["malignant biliary obstruction drainage", "ptbd malignant"],
  },
  {
    name: "Percutaneous Biliary Drain Malignant",
    category: "Procedure",
    aliases: ["percutaneous transhepatic biliary drain", "ptbd tumor"],
  },
  {
    name: "Biliary Stent Malignant",
    category: "Procedure",
    aliases: ["malignant biliary stent", "metal biliary stent malignant"],
  },
  {
    name: "Nephrostomy Malignant Obstruction",
    category: "Procedure",
    aliases: ["pcn malignant", "nephrostomy tumor obstruction"],
  },
  {
    name: "Ureteral Stent Malignant",
    category: "Procedure",
    aliases: ["malignant ureteral obstruction stent"],
  },
  {
    name: "Gastrostomy Malignant",
    category: "Procedure",
    aliases: ["g-tube for malignancy", "prg malignant obstruction"],
  },
  {
    name: "Gastrojejunostomy Malignant",
    category: "Procedure",
    aliases: ["gj tube malignancy", "feeding access malignancy"],
  },
  {
    name: "Cecostomy Malignant Obstruction",
    category: "Procedure",
    aliases: ["percutaneous cecostomy", "colonic decompression"],
  },
  {
    name: "Malignant Ascites Drainage",
    category: "Procedure",
    aliases: ["paracentesis malignant", "tunneled ascites catheter"],
  },
  {
    name: "PleurX for Malignant Effusion",
    category: "Procedure",
    aliases: [
      "tunneled pleural catheter malignant",
      "malignant pleural effusion ipc",
    ],
  },
  {
    name: "Peritoneal Port Placement",
    category: "Procedure",
    aliases: ["ip port", "intraperitoneal port", "hipec port"],
  },
  {
    name: "Ommaya Reservoir Oncology",
    category: "Procedure",
    aliases: ["intrathecal reservoir", "ventricular access device oncology"],
  },

  // Tumor Markers & Localization
  {
    name: "Tumor Marker Placement",
    category: "Procedure",
    aliases: ["fiducial marker tumor", "clip placement tumor"],
  },
  {
    name: "Preoperative Tumor Localization",
    category: "Procedure",
    aliases: ["wire localization tumor", "preop marking"],
  },
  {
    name: "Radiofrequency Identification Tag",
    category: "Procedure",
    aliases: ["rfid tumor tag", "savi scout placement"],
  },
  {
    name: "Magnetic Seed Localization",
    category: "Procedure",
    aliases: ["magseed", "magnetic marker tumor"],
  },
  {
    name: "Radar Reflector Localization",
    category: "Procedure",
    aliases: ["savi scout", "radar localization tumor"],
  },

  // Embolization for Symptom Control
  {
    name: "Tumor Hemorrhage Embolization",
    category: "Procedure",
    aliases: ["bleeding tumor embolization", "hemorrhagic tumor embo"],
  },
  {
    name: "Renal Tumor Embolization Palliation",
    category: "Procedure",
    aliases: ["palliative renal embolization", "bleeding kidney tumor embo"],
  },
  {
    name: "Hepatic Tumor Embolization Palliation",
    category: "Procedure",
    aliases: ["palliative liver embolization"],
  },
  {
    name: "Pelvic Tumor Embolization Palliation",
    category: "Procedure",
    aliases: ["palliative pelvic embolization", "bleeding pelvic tumor"],
  },
  {
    name: "Splenic Embolization Oncology",
    category: "Procedure",
    aliases: [
      "splenic embo hypersplenism",
      "splenic artery embolization tumor",
    ],
  },

  // Combination & Advanced Techniques
  {
    name: "Combined Ablation Embolization",
    category: "Procedure",
    aliases: ["ablation plus embolization", "combined locoregional therapy"],
  },
  {
    name: "Sequential TACE Ablation",
    category: "Procedure",
    aliases: ["tace then ablation", "staged locoregional therapy"],
  },
  {
    name: "Immunoembolization",
    category: "Procedure",
    aliases: ["immuno-embolization", "checkpoint inhibitor embolization"],
  },
  {
    name: "Drug-Eluting Bead Irinotecan",
    category: "Procedure",
    aliases: ["debiri", "irinotecan bead", "colorectal liver met tace"],
  },
  {
    name: "Pressure-Enabled Drug Delivery",
    category: "Procedure",
    aliases: ["pedd", "surefire infusion", "hepatic pressure directed therapy"],
  },

  // ============================================
  // PROCEDURES - OTHER SPECIALTIES
  // ============================================

  {
    name: "EMG/NCS",
    category: "Procedure",
    aliases: [
      "emg",
      "electromyography",
      "nerve conduction study",
      "ncs",
      "emg/ncv",
      "ncv",
    ],
  },
  {
    name: "Lumbar Puncture",
    category: "Procedure",
    aliases: ["lp", "spinal tap"],
  },
  {
    name: "Bone Marrow Biopsy",
    category: "Procedure",
    aliases: ["bmb", "bone marrow aspiration", "bma"],
  },
  {
    name: "Paracentesis",
    category: "Procedure",
    aliases: ["abdominal tap", "ascites tap"],
  },
  {
    name: "Arthrocentesis",
    category: "Procedure",
    aliases: ["joint aspiration", "joint tap", "knee aspiration"],
  },
  {
    name: "Skin Biopsy",
    category: "Procedure",
    aliases: ["punch biopsy", "shave biopsy", "excisional biopsy"],
  },
  {
    name: "Mohs Surgery",
    category: "Procedure",
    aliases: ["mohs micrographic surgery", "skin cancer surgery"],
  },
  {
    name: "Wide Local Excision",
    category: "Procedure",
    aliases: ["wle", "excision with margins"],
  },
  {
    name: "Lymph Node Biopsy",
    category: "Procedure",
    aliases: ["lymph node excision", "lymphadenectomy"],
  },
  {
    name: "Liver Biopsy",
    category: "Procedure",
    aliases: ["hepatic biopsy", "percutaneous liver biopsy"],
  },
  { name: "Kidney Biopsy", category: "Procedure", aliases: ["renal biopsy"] },
  {
    name: "Sleep Study",
    category: "Procedure",
    aliases: ["polysomnography", "psg", "sleep test", "overnight sleep study"],
  },
  {
    name: "Home Sleep Test",
    category: "Procedure",
    aliases: ["hst", "home sleep apnea test", "hsat"],
  },
  {
    name: "EEG",
    category: "Procedure",
    aliases: ["electroencephalogram", "brain wave test"],
  },
  {
    name: "Video EEG",
    category: "Procedure",
    aliases: ["veeg", "long term eeg monitoring", "epilepsy monitoring"],
  },

  // ============================================
  // ADMISSIONS
  // ============================================

  {
    name: "Inpatient Admission",
    category: "Admission",
    aliases: ["admit", "admission", "inpatient", "hospital admission"],
  },
  {
    name: "Observation Stay",
    category: "Admission",
    aliases: ["obs", "observation", "obs stay", "23 hour"],
  },
  {
    name: "ICU Admission",
    category: "Admission",
    aliases: ["icu", "intensive care", "critical care", "icu admit"],
  },
  {
    name: "Skilled Nursing Facility",
    category: "Admission",
    aliases: ["snf", "skilled nursing", "rehab facility", "nursing home"],
  },
  {
    name: "Inpatient Rehabilitation",
    category: "Admission",
    aliases: ["inpatient rehab", "acute rehab", "irf", "rehab admission"],
  },
  {
    name: "Long-Term Acute Care",
    category: "Admission",
    aliases: ["ltac", "ltach", "long term acute care"],
  },
  {
    name: "Psychiatric Admission",
    category: "Admission",
    aliases: [
      "psych admit",
      "psychiatric inpatient",
      "mental health admission",
      "behavioral health",
    ],
  },
  {
    name: "Extended Stay",
    category: "Admission",
    aliases: ["continued stay", "extended admission", "additional days"],
  },

  // ============================================
  // DME (DURABLE MEDICAL EQUIPMENT)
  // ============================================

  {
    name: "CPAP Machine",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "cpap",
      "sleep apnea machine",
      "continuous positive airway pressure",
    ],
  },
  {
    name: "BiPAP Machine",
    category: "DME (Durable Medical Equipment)",
    aliases: ["bipap", "bilevel", "bpap", "bilevel positive airway pressure"],
  },
  {
    name: "Power Wheelchair",
    category: "DME (Durable Medical Equipment)",
    aliases: ["electric wheelchair", "motorized wheelchair", "pwc"],
  },
  {
    name: "Hospital Bed",
    category: "DME (Durable Medical Equipment)",
    aliases: ["hospital bed", "electric bed"],
  },
  {
    name: "Continuous Glucose Monitor",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "cgm",
      "dexcom",
      "libre",
      "glucose monitor",
      "freestyle libre",
      "dexcom g6",
      "dexcom g7",
    ],
  },
  {
    name: "Insulin Pump",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "pump",
      "omnipod",
      "tandem",
      "medtronic pump",
      "insulin infusion pump",
    ],
  },
  {
    name: "Knee Brace",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "knee orthotic",
      "knee support",
      "knee immobilizer",
      "knee sleeve",
    ],
  },
  {
    name: "Back Brace",
    category: "DME (Durable Medical Equipment)",
    aliases: ["lso", "tlso", "lumbar brace", "lumbar support", "back support"],
  },
  {
    name: "Wheelchair",
    category: "DME (Durable Medical Equipment)",
    aliases: ["manual wheelchair", "wc"],
  },
  {
    name: "Walker",
    category: "DME (Durable Medical Equipment)",
    aliases: ["rollator", "rolling walker"],
  },
  {
    name: "Home Oxygen",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "oxygen concentrator",
      "portable oxygen",
      "o2",
      "supplemental oxygen",
      "home o2",
    ],
  },
  {
    name: "Nebulizer",
    category: "DME (Durable Medical Equipment)",
    aliases: ["neb", "breathing treatment machine"],
  },
  {
    name: "TENS Unit",
    category: "DME (Durable Medical Equipment)",
    aliases: ["tens", "transcutaneous electrical nerve stimulation"],
  },
  {
    name: "Prosthetic Limb",
    category: "DME (Durable Medical Equipment)",
    aliases: ["prosthesis", "prosthetic leg", "prosthetic arm"],
  },
  {
    name: "Hearing Aid",
    category: "DME (Durable Medical Equipment)",
    aliases: ["hearing device", "hearing amplifier"],
  },
  {
    name: "Breast Pump",
    category: "DME (Durable Medical Equipment)",
    aliases: ["electric breast pump"],
  },
  {
    name: "Wound VAC",
    category: "DME (Durable Medical Equipment)",
    aliases: ["negative pressure wound therapy", "npwt", "vac therapy"],
  },

  // ============================================
  // REFERRALS
  // ============================================

  // Primary Specialty Referrals
  {
    name: "Cardiology Referral",
    category: "Referral",
    aliases: ["heart doctor", "cardiologist", "cardiology consult"],
  },
  {
    name: "Dermatology Referral",
    category: "Referral",
    aliases: ["skin doctor", "dermatologist", "derm referral"],
  },
  {
    name: "Endocrinology Referral",
    category: "Referral",
    aliases: [
      "endocrine",
      "endocrinologist",
      "diabetes specialist",
      "thyroid specialist",
    ],
  },
  {
    name: "Gastroenterology Referral",
    category: "Referral",
    aliases: ["gi referral", "gi doctor", "gastroenterologist"],
  },
  {
    name: "Hematology Referral",
    category: "Referral",
    aliases: ["hematologist", "blood doctor", "blood disorder specialist"],
  },
  {
    name: "Hematology/Oncology Referral",
    category: "Referral",
    aliases: ["hem onc", "cancer doctor", "oncologist", "oncology referral"],
  },
  {
    name: "Infectious Disease Referral",
    category: "Referral",
    aliases: ["id referral", "id consult", "infectious disease doctor"],
  },
  {
    name: "Nephrology Referral",
    category: "Referral",
    aliases: ["kidney doctor", "nephrologist", "renal specialist"],
  },
  {
    name: "Neurology Referral",
    category: "Referral",
    aliases: ["neurologist", "brain doctor", "nerve doctor"],
  },
  {
    name: "Neurosurgery Referral",
    category: "Referral",
    aliases: ["neurosurgeon", "brain surgeon", "spine surgeon referral"],
  },
  {
    name: "Ob/Gyn Referral",
    category: "Referral",
    aliases: ["obgyn", "gynecologist", "gynecology referral", "women's health"],
  },
  {
    name: "Ophthalmology Referral",
    category: "Referral",
    aliases: ["eye doctor", "ophthalmologist", "eye specialist"],
  },
  {
    name: "Orthopedic Surgery Referral",
    category: "Referral",
    aliases: [
      "orthopedics",
      "ortho referral",
      "bone doctor",
      "joint specialist",
    ],
  },
  {
    name: "Otolaryngology Referral",
    category: "Referral",
    aliases: ["ent", "ent referral", "ear nose throat", "ent doctor"],
  },
  {
    name: "Pain Management Referral",
    category: "Referral",
    aliases: ["pain doctor", "pain specialist", "pain clinic", "pain medicine"],
  },
  {
    name: "Physical Medicine and Rehabilitation Referral",
    category: "Referral",
    aliases: ["pmr", "physiatrist", "physiatry", "rehab medicine"],
  },
  {
    name: "Psychiatry Referral",
    category: "Referral",
    aliases: ["psychiatrist", "psych referral", "mental health referral"],
  },
  {
    name: "Pulmonology Referral",
    category: "Referral",
    aliases: ["pulmonologist", "lung doctor", "lung specialist"],
  },
  {
    name: "Rheumatology Referral",
    category: "Referral",
    aliases: ["rheumatologist", "arthritis doctor", "autoimmune specialist"],
  },
  {
    name: "Urology Referral",
    category: "Referral",
    aliases: ["urologist", "urology consult"],
  },
  {
    name: "Vascular Surgery Referral",
    category: "Referral",
    aliases: ["vascular surgeon", "vascular specialist"],
  },

  // Surgical Subspecialty Referrals
  {
    name: "General Surgery Referral",
    category: "Referral",
    aliases: ["surgeon", "general surgeon", "surgery consult"],
  },
  {
    name: "Colorectal Surgery Referral",
    category: "Referral",
    aliases: ["colorectal surgeon", "rectal surgeon"],
  },
  {
    name: "Thoracic Surgery Referral",
    category: "Referral",
    aliases: ["thoracic surgeon", "chest surgeon", "lung surgeon"],
  },
  {
    name: "Plastic Surgery Referral",
    category: "Referral",
    aliases: ["plastic surgeon", "reconstructive surgery"],
  },
  {
    name: "Breast Surgery Referral",
    category: "Referral",
    aliases: ["breast surgeon", "breast surgical oncology"],
  },
  {
    name: "Surgical Oncology Referral",
    category: "Referral",
    aliases: ["surgical oncologist", "cancer surgeon"],
  },
  {
    name: "Transplant Surgery Referral",
    category: "Referral",
    aliases: ["transplant surgeon", "transplant evaluation referral"],
  },
  {
    name: "Pediatric Surgery Referral",
    category: "Referral",
    aliases: ["pediatric surgeon", "peds surgery"],
  },
  {
    name: "Hand Surgery Referral",
    category: "Referral",
    aliases: ["hand surgeon", "hand specialist"],
  },
  {
    name: "Foot and Ankle Surgery Referral",
    category: "Referral",
    aliases: ["podiatrist", "podiatry referral", "foot doctor", "foot surgeon"],
  },

  // Non-Surgical Subspecialty Referrals
  {
    name: "Allergy/Immunology Referral",
    category: "Referral",
    aliases: ["allergist", "immunologist", "allergy doctor", "allergy testing"],
  },
  {
    name: "Sleep Medicine Referral",
    category: "Referral",
    aliases: ["sleep doctor", "sleep specialist", "sleep study referral"],
  },
  {
    name: "Geriatrics Referral",
    category: "Referral",
    aliases: ["geriatrician", "geriatric medicine", "elderly care specialist"],
  },
  {
    name: "Palliative Care Referral",
    category: "Referral",
    aliases: ["palliative medicine", "symptom management", "supportive care"],
  },
  {
    name: "Hospice Referral",
    category: "Referral",
    aliases: ["hospice care", "end of life care", "comfort care"],
  },
  {
    name: "Genetics Referral",
    category: "Referral",
    aliases: [
      "genetic counselor",
      "genetic testing referral",
      "genetics consult",
    ],
  },
  {
    name: "Maternal-Fetal Medicine Referral",
    category: "Referral",
    aliases: ["mfm", "perinatologist", "high risk pregnancy", "high risk ob"],
  },
  {
    name: "Reproductive Endocrinology Referral",
    category: "Referral",
    aliases: [
      "rei",
      "fertility specialist",
      "fertility doctor",
      "infertility referral",
    ],
  },
  {
    name: "Gynecologic Oncology Referral",
    category: "Referral",
    aliases: ["gyn onc", "gynecologic oncologist"],
  },
  {
    name: "Urogynecology Referral",
    category: "Referral",
    aliases: ["urogyn", "pelvic floor specialist", "female pelvic medicine"],
  },
  {
    name: "Pediatric Cardiology Referral",
    category: "Referral",
    aliases: ["pediatric cardiologist", "peds cardiology"],
  },
  {
    name: "Pediatric Neurology Referral",
    category: "Referral",
    aliases: ["pediatric neurologist", "peds neuro", "child neurologist"],
  },
  {
    name: "Pediatric GI Referral",
    category: "Referral",
    aliases: ["pediatric gastroenterologist", "peds gi"],
  },
  {
    name: "Pediatric Endocrinology Referral",
    category: "Referral",
    aliases: ["pediatric endocrinologist", "peds endo"],
  },
  {
    name: "Pediatric Pulmonology Referral",
    category: "Referral",
    aliases: ["pediatric pulmonologist", "peds pulm"],
  },
  {
    name: "Developmental Pediatrics Referral",
    category: "Referral",
    aliases: ["developmental specialist", "developmental delay evaluation"],
  },
  {
    name: "Electrophysiology Referral",
    category: "Referral",
    aliases: [
      "ep referral",
      "ep doctor",
      "heart rhythm specialist",
      "arrhythmia specialist",
    ],
  },
  {
    name: "Interventional Cardiology Referral",
    category: "Referral",
    aliases: ["interventional cardiologist", "cath lab referral"],
  },
  {
    name: "Heart Failure Referral",
    category: "Referral",
    aliases: [
      "heart failure specialist",
      "advanced heart failure",
      "hf clinic",
    ],
  },
  {
    name: "Hepatology Referral",
    category: "Referral",
    aliases: ["hepatologist", "liver specialist", "liver doctor"],
  },
  {
    name: "Interventional Pulmonology Referral",
    category: "Referral",
    aliases: ["interventional pulmonologist", "advanced bronchoscopy"],
  },

  // Therapy & Rehab Referrals
  {
    name: "Physical Therapy Referral",
    category: "Referral",
    aliases: [
      "pt referral",
      "physical therapy",
      "physiotherapy",
      "pt prescription",
    ],
  },
  {
    name: "Occupational Therapy Referral",
    category: "Referral",
    aliases: ["ot referral", "occupational therapy", "ot prescription"],
  },
  {
    name: "Speech Therapy Referral",
    category: "Referral",
    aliases: [
      "speech language pathology",
      "slp referral",
      "speech pathologist",
      "swallowing therapy",
    ],
  },
  {
    name: "Cardiac Rehabilitation Referral",
    category: "Referral",
    aliases: ["cardiac rehab", "heart rehab", "cr referral"],
  },
  {
    name: "Pulmonary Rehabilitation Referral",
    category: "Referral",
    aliases: ["pulm rehab", "lung rehab", "pr referral"],
  },
  {
    name: "Vestibular Rehabilitation Referral",
    category: "Referral",
    aliases: ["vestibular pt", "balance therapy", "vertigo rehab"],
  },
  {
    name: "Pelvic Floor Therapy Referral",
    category: "Referral",
    aliases: [
      "pelvic floor pt",
      "pelvic rehab",
      "pelvic floor physical therapy",
    ],
  },
  {
    name: "Lymphedema Therapy Referral",
    category: "Referral",
    aliases: ["lymphedema specialist", "cdt therapy", "lymphatic massage"],
  },
  {
    name: "Hand Therapy Referral",
    category: "Referral",
    aliases: ["hand therapist", "certified hand therapist", "cht"],
  },
  {
    name: "Aquatic Therapy Referral",
    category: "Referral",
    aliases: ["pool therapy", "aquatic pt", "hydrotherapy"],
  },

  // Mental Health & Behavioral Referrals
  {
    name: "Psychology Referral",
    category: "Referral",
    aliases: [
      "psychologist",
      "psychological testing",
      "neuropsychological testing",
    ],
  },
  {
    name: "Neuropsychological Testing Referral",
    category: "Referral",
    aliases: ["neuropsych testing", "cognitive testing", "neuropsych eval"],
  },
  {
    name: "Behavioral Health Referral",
    category: "Referral",
    aliases: [
      "behavioral health",
      "mental health counselor",
      "therapist referral",
    ],
  },
  {
    name: "Substance Abuse Treatment Referral",
    category: "Referral",
    aliases: [
      "addiction referral",
      "substance use treatment",
      "rehab referral",
      "suboxone clinic",
    ],
  },
  {
    name: "Eating Disorder Referral",
    category: "Referral",
    aliases: [
      "eating disorder specialist",
      "anorexia treatment",
      "bulimia treatment",
    ],
  },
  {
    name: "Child Psychology Referral",
    category: "Referral",
    aliases: ["child psychologist", "pediatric psychology", "child therapist"],
  },
  {
    name: "ABA Therapy Referral",
    category: "Referral",
    aliases: ["applied behavior analysis", "aba", "autism therapy referral"],
  },

  // Out of Network / Special
  {
    name: "Out of Network Referral",
    category: "Referral",
    aliases: [
      "oon referral",
      "out of network specialist",
      "oon auth",
      "oon authorization",
    ],
  },
  {
    name: "Second Opinion Referral",
    category: "Referral",
    aliases: ["second opinion", "expert opinion", "specialist second opinion"],
  },
  {
    name: "Tertiary Care Referral",
    category: "Referral",
    aliases: [
      "academic center",
      "university hospital referral",
      "specialty center",
    ],
  },
  {
    name: "Clinical Trial Referral",
    category: "Referral",
    aliases: ["clinical trial", "research study", "trial enrollment"],
  },

  // ============================================
  // PROCEDURES - INFUSION THERAPY
  // ============================================

  // Iron Infusions
  {
    name: "Iron Infusion",
    category: "Procedure",
    aliases: ["iv iron", "iron iv", "iron replacement infusion"],
  },
  {
    name: "Injectafer Infusion",
    category: "Procedure",
    aliases: ["ferric carboxymaltose", "injectafer", "fcm infusion"],
  },
  {
    name: "Venofer Infusion",
    category: "Procedure",
    aliases: ["iron sucrose", "venofer", "iron sucrose infusion"],
  },
  {
    name: "Feraheme Infusion",
    category: "Procedure",
    aliases: ["ferumoxytol", "feraheme"],
  },
  {
    name: "Monoferric Infusion",
    category: "Procedure",
    aliases: ["ferric derisomaltose", "monoferric"],
  },
  {
    name: "INFeD Infusion",
    category: "Procedure",
    aliases: ["iron dextran", "infed", "low molecular weight iron dextran"],
  },

  // IVIG / Immunoglobulin
  {
    name: "IVIG Infusion",
    category: "Procedure",
    aliases: [
      "intravenous immunoglobulin",
      "ivig",
      "gammagard",
      "privigen",
      "gamunex",
    ],
  },
  {
    name: "SCIG Infusion",
    category: "Procedure",
    aliases: ["subcutaneous immunoglobulin", "hizentra", "cuvitru", "cutaquig"],
  },
  {
    name: "IVIG for CIDP",
    category: "Procedure",
    aliases: [
      "ivig cidp",
      "chronic inflammatory demyelinating polyneuropathy treatment",
    ],
  },
  {
    name: "IVIG for Myasthenia Gravis",
    category: "Procedure",
    aliases: ["ivig mg", "myasthenia gravis infusion"],
  },
  {
    name: "IVIG for Immune Deficiency",
    category: "Procedure",
    aliases: ["ivig pid", "immunodeficiency treatment", "ivig replacement"],
  },

  // Biologic Infusions — Rheumatologic
  {
    name: "Remicade Infusion",
    category: "Procedure",
    aliases: ["infliximab infusion", "remicade", "ifx infusion"],
  },
  {
    name: "Inflectra Infusion",
    category: "Procedure",
    aliases: [
      "infliximab-dyyb infusion",
      "inflectra",
      "infliximab biosimilar infusion",
    ],
  },
  {
    name: "Renflexis Infusion",
    category: "Procedure",
    aliases: ["infliximab-abda infusion", "renflexis"],
  },
  {
    name: "Avsola Infusion",
    category: "Procedure",
    aliases: ["infliximab-axxq infusion", "avsola"],
  },
  {
    name: "Orencia Infusion",
    category: "Procedure",
    aliases: ["abatacept infusion", "orencia iv"],
  },
  {
    name: "Actemra Infusion",
    category: "Procedure",
    aliases: ["tocilizumab infusion", "actemra iv"],
  },
  {
    name: "Rituxan Infusion",
    category: "Procedure",
    aliases: ["rituximab infusion", "rituxan", "anti-cd20 infusion"],
  },
  {
    name: "Benlysta Infusion",
    category: "Procedure",
    aliases: ["belimumab infusion", "benlysta iv", "lupus infusion"],
  },
  {
    name: "Saphnelo Infusion",
    category: "Procedure",
    aliases: ["anifrolumab infusion", "saphnelo"],
  },

  // Biologic Infusions — GI
  {
    name: "Entyvio Infusion",
    category: "Procedure",
    aliases: ["vedolizumab infusion", "entyvio", "vedolizumab iv"],
  },
  {
    name: "Stelara Infusion",
    category: "Procedure",
    aliases: ["ustekinumab infusion", "stelara iv", "ustekinumab iv induction"],
  },
  {
    name: "Tysabri Infusion",
    category: "Procedure",
    aliases: ["natalizumab infusion", "tysabri", "crohn's infusion"],
  },
  {
    name: "Inflectra Infusion for IBD",
    category: "Procedure",
    aliases: ["infliximab ibd infusion", "crohn's remicade", "uc remicade"],
  },

  // Biologic Infusions — Neurology
  {
    name: "Ocrevus Infusion",
    category: "Procedure",
    aliases: ["ocrelizumab infusion", "ocrevus", "ms infusion"],
  },
  {
    name: "Tysabri Infusion for MS",
    category: "Procedure",
    aliases: ["natalizumab ms", "ms natalizumab infusion"],
  },
  {
    name: "Lemtrada Infusion",
    category: "Procedure",
    aliases: ["alemtuzumab infusion", "lemtrada"],
  },
  {
    name: "Briumvi Infusion",
    category: "Procedure",
    aliases: ["ublituximab infusion", "briumvi"],
  },
  {
    name: "Soliris Infusion",
    category: "Procedure",
    aliases: [
      "eculizumab infusion",
      "soliris",
      "complement inhibitor infusion",
    ],
  },
  {
    name: "Ultomiris Infusion",
    category: "Procedure",
    aliases: ["ravulizumab infusion", "ultomiris"],
  },
  {
    name: "Vyvgart Infusion",
    category: "Procedure",
    aliases: ["efgartigimod infusion", "vyvgart", "myasthenia gravis infusion"],
  },
  {
    name: "Rystiggo Infusion",
    category: "Procedure",
    aliases: ["rozanolixizumab infusion", "rystiggo"],
  },

  // Biologic Infusions — Oncology
  {
    name: "Keytruda Infusion",
    category: "Procedure",
    aliases: ["pembrolizumab infusion", "keytruda iv"],
  },
  {
    name: "Opdivo Infusion",
    category: "Procedure",
    aliases: ["nivolumab infusion", "opdivo iv"],
  },
  {
    name: "Yervoy Infusion",
    category: "Procedure",
    aliases: ["ipilimumab infusion", "yervoy iv"],
  },
  {
    name: "Herceptin Infusion",
    category: "Procedure",
    aliases: ["trastuzumab infusion", "herceptin iv"],
  },
  {
    name: "Avastin Infusion",
    category: "Procedure",
    aliases: ["bevacizumab infusion", "avastin iv"],
  },
  {
    name: "Erbitux Infusion",
    category: "Procedure",
    aliases: ["cetuximab infusion", "erbitux iv"],
  },
  {
    name: "Darzalex Infusion",
    category: "Procedure",
    aliases: ["daratumumab infusion", "darzalex iv"],
  },
  {
    name: "Perjeta Infusion",
    category: "Procedure",
    aliases: ["pertuzumab infusion", "perjeta iv"],
  },
  {
    name: "Tecentriq Infusion",
    category: "Procedure",
    aliases: ["atezolizumab infusion", "tecentriq iv"],
  },
  {
    name: "Imfinzi Infusion",
    category: "Procedure",
    aliases: ["durvalumab infusion", "imfinzi iv"],
  },
  {
    name: "Bavencio Infusion",
    category: "Procedure",
    aliases: ["avelumab infusion", "bavencio iv"],
  },

  // Biologic Infusions — Asthma / Allergy / Pulm
  {
    name: "Xolair Infusion",
    category: "Procedure",
    aliases: ["omalizumab injection", "xolair", "anti-ige therapy"],
  },
  {
    name: "Nucala Infusion",
    category: "Procedure",
    aliases: ["mepolizumab injection", "nucala", "anti-il5"],
  },
  {
    name: "Fasenra Infusion",
    category: "Procedure",
    aliases: ["benralizumab injection", "fasenra"],
  },
  {
    name: "Cinqair Infusion",
    category: "Procedure",
    aliases: ["reslizumab infusion", "cinqair"],
  },
  {
    name: "Tezspire Injection",
    category: "Procedure",
    aliases: ["tezepelumab injection", "tezspire"],
  },

  // Biologic Infusions — Dermatology
  {
    name: "Skyrizi Infusion",
    category: "Procedure",
    aliases: ["risankizumab infusion", "skyrizi iv"],
  },

  // Biologic Infusions — Osteoporosis / Bone
  {
    name: "Reclast Infusion",
    category: "Procedure",
    aliases: [
      "zoledronic acid infusion",
      "reclast",
      "zometa",
      "iv bisphosphonate",
    ],
  },
  {
    name: "Prolia Injection",
    category: "Procedure",
    aliases: ["denosumab injection", "prolia"],
  },
  {
    name: "Evenity Injection",
    category: "Procedure",
    aliases: ["romosozumab injection", "evenity"],
  },
  {
    name: "Zometa Infusion",
    category: "Procedure",
    aliases: ["zoledronic acid oncology", "zometa", "bone met infusion"],
  },
  {
    name: "Xgeva Injection",
    category: "Procedure",
    aliases: ["denosumab oncology", "xgeva", "bone metastasis injection"],
  },

  // Blood Product / Factor Infusions
  {
    name: "Blood Transfusion",
    category: "Procedure",
    aliases: [
      "prbc transfusion",
      "packed red blood cells",
      "red cell transfusion",
    ],
  },
  {
    name: "Platelet Transfusion",
    category: "Procedure",
    aliases: ["platelet infusion", "plt transfusion"],
  },
  {
    name: "Albumin Infusion",
    category: "Procedure",
    aliases: ["iv albumin", "albumin replacement"],
  },
  {
    name: "Factor Infusion",
    category: "Procedure",
    aliases: [
      "clotting factor",
      "factor viii infusion",
      "factor ix infusion",
      "hemophilia infusion",
    ],
  },

  // Other Infusions
  {
    name: "IV Antibiotic Infusion",
    category: "Procedure",
    aliases: [
      "iv antibiotics",
      "outpatient parenteral antibiotic therapy",
      "opat",
    ],
  },
  {
    name: "TPN",
    category: "Procedure",
    aliases: [
      "total parenteral nutrition",
      "iv nutrition",
      "parenteral nutrition",
    ],
  },
  {
    name: "IV Hydration Therapy",
    category: "Procedure",
    aliases: ["iv fluids", "hydration infusion"],
  },
  {
    name: "Ketamine Infusion",
    category: "Procedure",
    aliases: ["iv ketamine", "ketamine therapy", "ketamine for depression"],
  },
  {
    name: "Lidocaine Infusion",
    category: "Procedure",
    aliases: ["iv lidocaine", "lidocaine for pain"],
  },
  {
    name: "Bisphosphonate Infusion",
    category: "Procedure",
    aliases: ["iv bisphosphonate", "pamidronate infusion"],
  },
  {
    name: "Chelation Therapy",
    category: "Procedure",
    aliases: ["iron chelation", "deferoxamine infusion", "deferasirox"],
  },
  {
    name: "Alpha-1 Antitrypsin Infusion",
    category: "Procedure",
    aliases: [
      "aralast",
      "prolastin",
      "a1at augmentation",
      "alpha-1 augmentation",
    ],
  },
  {
    name: "Enzyme Replacement Therapy",
    category: "Procedure",
    aliases: [
      "ert infusion",
      "fabrazyme",
      "cerezyme",
      "lumizyme",
      "gaucher infusion",
      "fabry infusion",
    ],
  },

  // ============================================
  // PROCEDURES - PHYSICAL / OCCUPATIONAL / SPEECH THERAPY
  // ============================================

  // Physical Therapy
  {
    name: "Physical Therapy",
    category: "Procedure",
    aliases: ["pt", "physiotherapy", "physical therapy sessions"],
  },
  {
    name: "Physical Therapy Evaluation",
    category: "Procedure",
    aliases: [
      "pt eval",
      "pt assessment",
      "physical therapy initial evaluation",
    ],
  },
  {
    name: "Outpatient Physical Therapy",
    category: "Procedure",
    aliases: ["outpatient pt", "clinic-based pt"],
  },
  {
    name: "Home Physical Therapy",
    category: "Procedure",
    aliases: ["home pt", "home-based physical therapy"],
  },
  {
    name: "Post-Surgical Physical Therapy",
    category: "Procedure",
    aliases: [
      "post-op pt",
      "surgical rehab pt",
      "post-operative rehabilitation",
    ],
  },
  {
    name: "Spine Physical Therapy",
    category: "Procedure",
    aliases: ["back pt", "spine rehab", "mckenzie therapy", "lumbar pt"],
  },
  {
    name: "Shoulder Physical Therapy",
    category: "Procedure",
    aliases: ["shoulder pt", "shoulder rehab", "rotator cuff pt"],
  },
  {
    name: "Knee Physical Therapy",
    category: "Procedure",
    aliases: ["knee pt", "knee rehab", "post-tka pt", "acl rehab"],
  },
  {
    name: "Hip Physical Therapy",
    category: "Procedure",
    aliases: ["hip pt", "hip rehab", "post-tha pt"],
  },
  {
    name: "Neck Physical Therapy",
    category: "Procedure",
    aliases: ["cervical pt", "neck rehab", "cervical spine pt"],
  },
  {
    name: "Neurological Physical Therapy",
    category: "Procedure",
    aliases: [
      "neuro pt",
      "stroke rehab pt",
      "parkinson pt",
      "ms physical therapy",
    ],
  },
  {
    name: "Vestibular Physical Therapy",
    category: "Procedure",
    aliases: [
      "vestibular rehab",
      "balance therapy",
      "vertigo pt",
      "bppv treatment",
    ],
  },
  {
    name: "Pelvic Floor Physical Therapy",
    category: "Procedure",
    aliases: [
      "pelvic floor pt",
      "pelvic rehab",
      "incontinence pt",
      "pelvic pain pt",
    ],
  },
  {
    name: "Pediatric Physical Therapy",
    category: "Procedure",
    aliases: [
      "peds pt",
      "pediatric pt",
      "child physical therapy",
      "developmental pt",
    ],
  },
  {
    name: "Aquatic Physical Therapy",
    category: "Procedure",
    aliases: ["aquatic pt", "pool therapy", "hydrotherapy", "water therapy"],
  },
  {
    name: "Manual Therapy",
    category: "Procedure",
    aliases: ["manual pt", "joint mobilization", "soft tissue mobilization"],
  },
  {
    name: "Dry Needling",
    category: "Procedure",
    aliases: ["intramuscular dry needling", "trigger point dry needling"],
  },
  {
    name: "Lymphedema Therapy",
    category: "Procedure",
    aliases: [
      "complete decongestive therapy",
      "cdt",
      "lymphatic drainage",
      "lymphedema management",
    ],
  },
  {
    name: "PT Extension Request",
    category: "Procedure",
    aliases: [
      "pt continuation",
      "additional pt visits",
      "pt reauthorization",
      "pt visit extension",
    ],
  },
  {
    name: "Sports Physical Therapy",
    category: "Procedure",
    aliases: ["sports rehab", "athletic rehabilitation", "return to sport pt"],
  },
  {
    name: "TMJ Physical Therapy",
    category: "Procedure",
    aliases: ["tmj pt", "jaw physical therapy", "temporomandibular therapy"],
  },
  {
    name: "Wound Care Physical Therapy",
    category: "Procedure",
    aliases: ["wound pt", "wound management pt"],
  },
  {
    name: "Work Hardening",
    category: "Procedure",
    aliases: [
      "work conditioning",
      "return to work program",
      "functional capacity evaluation",
    ],
  },
  {
    name: "Functional Capacity Evaluation",
    category: "Procedure",
    aliases: ["fce", "functional assessment", "work capacity evaluation"],
  },

  // Occupational Therapy
  {
    name: "Occupational Therapy",
    category: "Procedure",
    aliases: ["ot", "occupational therapy sessions"],
  },
  {
    name: "Occupational Therapy Evaluation",
    category: "Procedure",
    aliases: [
      "ot eval",
      "ot assessment",
      "occupational therapy initial evaluation",
    ],
  },
  {
    name: "Hand Therapy",
    category: "Procedure",
    aliases: [
      "hand ot",
      "certified hand therapist",
      "cht",
      "hand rehabilitation",
    ],
  },
  {
    name: "Upper Extremity Rehabilitation",
    category: "Procedure",
    aliases: ["arm rehab", "upper limb rehab", "shoulder ot"],
  },
  {
    name: "ADL Training",
    category: "Procedure",
    aliases: [
      "activities of daily living training",
      "self-care training",
      "adl retraining",
    ],
  },
  {
    name: "Cognitive Rehabilitation",
    category: "Procedure",
    aliases: ["cognitive rehab", "brain injury rehab", "cognitive therapy ot"],
  },
  {
    name: "Pediatric Occupational Therapy",
    category: "Procedure",
    aliases: [
      "peds ot",
      "pediatric ot",
      "child occupational therapy",
      "sensory integration therapy",
    ],
  },
  {
    name: "Splint Fabrication",
    category: "Procedure",
    aliases: ["orthotic fabrication", "custom splint", "hand splint"],
  },
  {
    name: "Home Safety Evaluation",
    category: "Procedure",
    aliases: [
      "home assessment",
      "home modification eval",
      "fall risk assessment",
    ],
  },
  {
    name: "Driver Rehabilitation",
    category: "Procedure",
    aliases: ["driving evaluation", "driver rehab", "driving assessment"],
  },
  {
    name: "OT Extension Request",
    category: "Procedure",
    aliases: ["ot continuation", "additional ot visits", "ot reauthorization"],
  },
  {
    name: "Stroke Occupational Therapy",
    category: "Procedure",
    aliases: ["stroke ot", "cva rehab ot", "stroke upper extremity"],
  },
  {
    name: "Burn Rehabilitation OT",
    category: "Procedure",
    aliases: ["burn rehab", "burn scar management", "burn ot"],
  },
  {
    name: "Low Vision Rehabilitation",
    category: "Procedure",
    aliases: ["low vision therapy", "vision rehab ot"],
  },

  // Speech-Language Pathology
  {
    name: "Speech Therapy",
    category: "Procedure",
    aliases: ["speech language pathology", "slp", "speech therapy sessions"],
  },
  {
    name: "Speech Therapy Evaluation",
    category: "Procedure",
    aliases: ["slp eval", "speech evaluation", "communication assessment"],
  },
  {
    name: "Swallowing Therapy",
    category: "Procedure",
    aliases: [
      "dysphagia therapy",
      "swallow therapy",
      "swallowing rehabilitation",
    ],
  },
  {
    name: "Modified Barium Swallow Study",
    category: "Procedure",
    aliases: [
      "mbss",
      "videofluoroscopic swallow study",
      "vfss",
      "barium swallow study",
    ],
  },
  {
    name: "FEES",
    category: "Procedure",
    aliases: [
      "fiberoptic endoscopic evaluation of swallowing",
      "endoscopic swallow study",
    ],
  },
  {
    name: "Voice Therapy",
    category: "Procedure",
    aliases: ["vocal therapy", "voice rehabilitation", "dysphonia therapy"],
  },
  {
    name: "Aphasia Therapy",
    category: "Procedure",
    aliases: [
      "language therapy",
      "aphasia rehabilitation",
      "stroke speech therapy",
    ],
  },
  {
    name: "Cognitive-Communication Therapy",
    category: "Procedure",
    aliases: [
      "cognitive communication",
      "tbi speech therapy",
      "brain injury slp",
    ],
  },
  {
    name: "Pediatric Speech Therapy",
    category: "Procedure",
    aliases: [
      "peds slp",
      "child speech therapy",
      "pediatric language therapy",
      "speech delay therapy",
    ],
  },
  {
    name: "Articulation Therapy",
    category: "Procedure",
    aliases: ["speech sound therapy", "pronunciation therapy"],
  },
  {
    name: "Fluency Therapy",
    category: "Procedure",
    aliases: ["stuttering therapy", "fluency treatment"],
  },
  {
    name: "Augmentative Communication",
    category: "Procedure",
    aliases: [
      "aac",
      "augmentative and alternative communication",
      "communication device",
    ],
  },
  {
    name: "Laryngectomy Speech Rehabilitation",
    category: "Procedure",
    aliases: [
      "tep speech therapy",
      "alaryngeal speech",
      "electrolarynx training",
    ],
  },
  {
    name: "SLP Extension Request",
    category: "Procedure",
    aliases: [
      "speech therapy continuation",
      "additional slp visits",
      "slp reauthorization",
    ],
  },
  {
    name: "Lee Silverman Voice Treatment",
    category: "Procedure",
    aliases: ["lsvt", "lsvt loud", "parkinson speech therapy"],
  },
  {
    name: "VitalStim Therapy",
    category: "Procedure",
    aliases: [
      "neuromuscular electrical stimulation swallowing",
      "nmes swallowing",
    ],
  },

  // ============================================
  // PROCEDURES - GENETIC TESTING
  // ============================================

  {
    name: "BRCA1/BRCA2 Testing",
    category: "Procedure",
    aliases: [
      "brca testing",
      "brca genetic test",
      "hereditary breast cancer testing",
    ],
  },
  {
    name: "Hereditary Cancer Panel",
    category: "Procedure",
    aliases: [
      "multi-gene cancer panel",
      "hereditary cancer screening",
      "cancer gene panel",
    ],
  },
  {
    name: "Lynch Syndrome Testing",
    category: "Procedure",
    aliases: [
      "msi testing",
      "hnpcc testing",
      "microsatellite instability testing",
      "mmr testing",
    ],
  },
  {
    name: "Pharmacogenomic Testing",
    category: "Procedure",
    aliases: [
      "pgx testing",
      "pharmacogenomics",
      "drug metabolism testing",
      "cyp450 testing",
    ],
  },
  {
    name: "Carrier Screening",
    category: "Procedure",
    aliases: [
      "genetic carrier testing",
      "reproductive carrier screen",
      "preconception genetic testing",
    ],
  },
  {
    name: "Prenatal Genetic Testing",
    category: "Procedure",
    aliases: [
      "nipt",
      "cell-free dna testing",
      "noninvasive prenatal testing",
      "first trimester screening",
    ],
  },
  {
    name: "Whole Exome Sequencing",
    category: "Procedure",
    aliases: ["wes", "exome sequencing", "diagnostic exome"],
  },
  {
    name: "Whole Genome Sequencing",
    category: "Procedure",
    aliases: ["wgs", "genome sequencing"],
  },
  {
    name: "Chromosomal Microarray",
    category: "Procedure",
    aliases: ["cma", "snp microarray", "array cgh"],
  },
  {
    name: "Tumor Genomic Profiling",
    category: "Procedure",
    aliases: [
      "next gen sequencing tumor",
      "ngs tumor",
      "foundation one",
      "tempus",
      "guardant360",
    ],
  },
  {
    name: "Liquid Biopsy",
    category: "Procedure",
    aliases: [
      "circulating tumor dna",
      "ctdna",
      "cell-free dna tumor",
      "guardant health",
    ],
  },
  {
    name: "Oncotype DX",
    category: "Procedure",
    aliases: ["oncotype", "breast cancer recurrence score", "21-gene assay"],
  },
  {
    name: "MammaPrint",
    category: "Procedure",
    aliases: ["70-gene panel", "breast cancer genomic test"],
  },
  {
    name: "Prolaris Test",
    category: "Procedure",
    aliases: ["prostate cancer genomic test", "cell cycle progression score"],
  },
  {
    name: "Decipher Test",
    category: "Procedure",
    aliases: ["prostate cancer decipher", "genomic classifier prostate"],
  },
  {
    name: "Cologuard",
    category: "Procedure",
    aliases: [
      "stool dna test",
      "colon cancer screening stool",
      "multitarget stool dna",
    ],
  },
  {
    name: "Shield Blood Test",
    category: "Procedure",
    aliases: ["guardant shield", "blood-based colon cancer screening"],
  },

  // ============================================
  // PROCEDURES - ALLERGY & IMMUNOLOGY
  // ============================================

  {
    name: "Allergy Testing",
    category: "Procedure",
    aliases: ["allergy skin testing", "skin prick testing", "allergy panel"],
  },
  {
    name: "Allergy Skin Prick Testing",
    category: "Procedure",
    aliases: ["spt", "percutaneous allergy testing", "scratch test"],
  },
  {
    name: "Intradermal Allergy Testing",
    category: "Procedure",
    aliases: ["intradermal skin test", "idt"],
  },
  {
    name: "Patch Testing",
    category: "Procedure",
    aliases: [
      "contact dermatitis testing",
      "allergen patch test",
      "t.r.u.e. test",
    ],
  },
  {
    name: "Allergen Immunotherapy",
    category: "Procedure",
    aliases: [
      "allergy shots",
      "scit",
      "subcutaneous immunotherapy",
      "desensitization",
    ],
  },
  {
    name: "Sublingual Immunotherapy",
    category: "Procedure",
    aliases: [
      "slit",
      "allergy drops",
      "allergy tablets",
      "grastek",
      "ragwitek",
      "odactra",
    ],
  },
  {
    name: "Venom Immunotherapy",
    category: "Procedure",
    aliases: [
      "bee sting allergy shots",
      "venom desensitization",
      "hymenoptera immunotherapy",
    ],
  },
  {
    name: "Drug Desensitization",
    category: "Procedure",
    aliases: [
      "drug allergy desensitization",
      "antibiotic desensitization",
      "aspirin desensitization",
    ],
  },
  {
    name: "Food Challenge",
    category: "Procedure",
    aliases: [
      "oral food challenge",
      "ofc",
      "graded food challenge",
      "food allergy testing",
    ],
  },
  {
    name: "Drug Challenge",
    category: "Procedure",
    aliases: ["graded drug challenge", "drug provocation test"],
  },
  {
    name: "Penicillin Allergy Testing",
    category: "Procedure",
    aliases: [
      "pcn allergy test",
      "penicillin skin test",
      "penicillin delabeling",
    ],
  },
  {
    name: "Spirometry with Bronchoprovocation",
    category: "Procedure",
    aliases: ["methacholine challenge allergy", "exercise challenge allergy"],
  },
  {
    name: "Component Resolved Diagnostics",
    category: "Procedure",
    aliases: ["crd", "specific ige components", "molecular allergy testing"],
  },
  {
    name: "Total IgE and Specific IgE Panel",
    category: "Procedure",
    aliases: ["rast test", "immunocap", "specific ige blood test"],
  },

  // ============================================
  // PROCEDURES - DERMATOLOGY
  // ============================================

  {
    name: "Phototherapy",
    category: "Procedure",
    aliases: ["light therapy", "narrowband uvb", "nbuvb", "uv light treatment"],
  },
  {
    name: "PUVA Therapy",
    category: "Procedure",
    aliases: ["psoralen plus uva", "puva", "photochemotherapy"],
  },
  {
    name: "Excimer Laser Treatment",
    category: "Procedure",
    aliases: ["excimer laser", "xtrac laser", "targeted phototherapy"],
  },
  {
    name: "Photodynamic Therapy Skin",
    category: "Procedure",
    aliases: [
      "pdt skin",
      "blue light therapy",
      "levulan",
      "ameluz",
      "actinic keratosis treatment",
    ],
  },
  {
    name: "Laser Hair Removal",
    category: "Procedure",
    aliases: ["hair removal laser", "laser epilation"],
  },
  {
    name: "Laser Treatment Vascular",
    category: "Procedure",
    aliases: ["pulsed dye laser", "pdl", "vbeam", "vascular laser"],
  },
  {
    name: "Laser Treatment Pigmented Lesions",
    category: "Procedure",
    aliases: ["q-switched laser", "picosecond laser", "pigment laser"],
  },
  {
    name: "CO2 Laser Resurfacing",
    category: "Procedure",
    aliases: [
      "fractional laser",
      "ablative laser resurfacing",
      "laser skin resurfacing",
    ],
  },
  {
    name: "Cryosurgery Skin Lesion",
    category: "Procedure",
    aliases: [
      "cryotherapy skin",
      "liquid nitrogen treatment",
      "wart removal",
      "wart freezing",
      "actinic keratosis cryotherapy",
      "cryodestruction",
    ],
  },
  {
    name: "Intralesional Steroid Injection",
    category: "Procedure",
    aliases: [
      "intralesional kenalog",
      "keloid injection",
      "cystic acne injection",
      "steroid injection skin",
      "intralesional triamcinolone",
      "alopecia areata injection",
    ],
  },
  {
    name: "Electrodesiccation and Curettage",
    category: "Procedure",
    aliases: [
      "ed&c",
      "edc",
      "electrodesiccation curettage",
      "curettage skin cancer",
      "ed and c skin",
    ],
  },
  {
    name: "Sentinel Lymph Node Biopsy Melanoma",
    category: "Procedure",
    aliases: [
      "slnb melanoma",
      "sentinel node biopsy skin",
      "melanoma staging biopsy",
      "lymphoscintigraphy melanoma",
    ],
  },
  {
    name: "Skin Lesion Excision",
    category: "Procedure",
    aliases: [
      "mole removal",
      "nevus excision",
      "lesion removal",
      "skin growth removal",
      "benign lesion excision",
    ],
  },
  {
    name: "Wound Care Debridement Skin",
    category: "Procedure",
    aliases: [
      "skin debridement",
      "ulcer debridement",
      "wound debridement dermatology",
      "chronic wound care",
    ],
  },
  {
    name: "Chemical Peel",
    category: "Procedure",
    aliases: [
      "glycolic peel",
      "tca peel",
      "chemical peel acne",
      "jessner peel",
      "medical chemical peel",
    ],
  },
  {
    name: "Biologic Injection Training Derm",
    category: "Procedure",
    aliases: [
      "self-injection training",
      "biologic injection teaching",
      "dupixent injection training",
    ],
  },
  {
    name: "Nail Avulsion",
    category: "Procedure",
    aliases: [
      "nail removal",
      "matrixectomy",
      "ingrown toenail surgery",
      "nail plate avulsion",
      "chemical matrixectomy",
      "phenol matrixectomy",
    ],
  },
  {
    name: "Sclerotherapy",
    category: "Procedure",
    aliases: [
      "spider vein injection",
      "varicose vein injection",
      "vein sclerotherapy",
      "leg vein treatment",
    ],
  },
  {
    name: "Botox Injection Hyperhidrosis",
    category: "Procedure",
    aliases: [
      "botulinum toxin hyperhidrosis",
      "excessive sweating treatment",
      "axillary botox",
      "botox sweating",
    ],
  },
  {
    name: "Patch Testing Contact Dermatitis",
    category: "Procedure",
    aliases: [
      "contact allergy testing",
      "true test",
      "t.r.u.e. test",
      "epicutaneous patch test",
      "contact dermatitis workup",
      "allergen patch testing derm",
    ],
  },
  {
    name: "Phototherapy Vitiligo",
    category: "Procedure",
    aliases: [
      "narrowband uvb vitiligo",
      "nbuvb vitiligo",
      "targeted phototherapy vitiligo",
      "excimer laser vitiligo",
      "light therapy vitiligo",
    ],
  },
  {
    name: "Incision and Drainage Skin",
    category: "Procedure",
    aliases: [
      "i&d abscess",
      "abscess drainage",
      "skin abscess incision",
      "cyst drainage",
      "i and d skin",
    ],
  },
  {
    name: "Skin Tag Removal",
    category: "Procedure",
    aliases: [
      "acrochordon removal",
      "skin tag excision",
      "skin tag snip",
    ],
  },
  {
    name: "Keloid Treatment",
    category: "Procedure",
    aliases: [
      "keloid injection",
      "keloid excision",
      "keloid cryotherapy",
      "hypertrophic scar treatment",
    ],
  },

  // ============================================
  // PROCEDURES - HOME HEALTH
  // ============================================

  {
    name: "Home Health Nursing",
    category: "Procedure",
    aliases: ["home nursing", "skilled nursing visits", "home rn visits"],
  },
  {
    name: "Home Infusion Therapy",
    category: "Procedure",
    aliases: ["home iv therapy", "home antibiotic infusion", "home tpn"],
  },
  {
    name: "Home Physical Therapy",
    category: "Procedure",
    aliases: ["home pt", "home-based pt", "home physical therapy visits"],
  },
  {
    name: "Home Occupational Therapy",
    category: "Procedure",
    aliases: ["home ot", "home-based ot", "home occupational therapy visits"],
  },
  {
    name: "Home Speech Therapy",
    category: "Procedure",
    aliases: ["home slp", "home speech language pathology"],
  },
  {
    name: "Home Health Aide",
    category: "Procedure",
    aliases: ["hha", "home aide", "personal care aide"],
  },
  {
    name: "Home Wound Care",
    category: "Procedure",
    aliases: ["wound care nurse", "home wound management", "wound vac home"],
  },
  {
    name: "Home Dialysis Training",
    category: "Procedure",
    aliases: ["peritoneal dialysis training", "home hemo training"],
  },

  // ============================================
  // MEDICATIONS — MIGRAINE / CGRP
  // ============================================

  {
    name: "Aimovig (erenumab)",
    category: "Medication",
    aliases: ["erenumab", "aimovig", "cgrp antibody"],
  },
  {
    name: "Ajovy (fremanezumab)",
    category: "Medication",
    aliases: ["fremanezumab", "ajovy"],
  },
  {
    name: "Emgality (galcanezumab)",
    category: "Medication",
    aliases: ["galcanezumab", "emgality"],
  },
  {
    name: "Vyepti (eptinezumab)",
    category: "Medication",
    aliases: ["eptinezumab", "vyepti", "cgrp infusion migraine"],
  },
  {
    name: "Nurtec ODT (rimegepant)",
    category: "Medication",
    aliases: ["rimegepant", "nurtec", "gepant"],
  },
  {
    name: "Ubrelvy (ubrogepant)",
    category: "Medication",
    aliases: ["ubrogepant", "ubrelvy"],
  },
  {
    name: "Qulipta (atogepant)",
    category: "Medication",
    aliases: ["atogepant", "qulipta", "oral cgrp preventive"],
  },
  {
    name: "Zavzpret (zavegepant)",
    category: "Medication",
    aliases: ["zavegepant", "zavzpret", "nasal gepant"],
  },

  // ============================================
  // MEDICATIONS — ADHD / STIMULANTS
  // ============================================

  {
    name: "Adderall XR (amphetamine/dextroamphetamine)",
    category: "Medication",
    aliases: [
      "adderall",
      "adderall xr",
      "mixed amphetamine salts",
      "amphetamine",
    ],
  },
  {
    name: "Concerta (methylphenidate ER)",
    category: "Medication",
    aliases: [
      "concerta",
      "methylphenidate er",
      "methylphenidate extended release",
    ],
  },
  {
    name: "Ritalin (methylphenidate)",
    category: "Medication",
    aliases: ["ritalin", "methylphenidate", "ritalin la"],
  },
  {
    name: "Focalin XR (dexmethylphenidate)",
    category: "Medication",
    aliases: ["focalin", "dexmethylphenidate", "focalin xr"],
  },
  {
    name: "Strattera (atomoxetine)",
    category: "Medication",
    aliases: ["atomoxetine", "strattera", "non-stimulant adhd"],
  },
  {
    name: "Mydayis (amphetamine ER)",
    category: "Medication",
    aliases: ["mydayis", "triple bead amphetamine"],
  },
  {
    name: "Jornay PM (methylphenidate)",
    category: "Medication",
    aliases: ["jornay", "jornay pm", "evening-dosed methylphenidate"],
  },
  {
    name: "Azstarys (serdexmethylphenidate)",
    category: "Medication",
    aliases: ["azstarys", "serdexmethylphenidate"],
  },
  {
    name: "Dyanavel XR (amphetamine)",
    category: "Medication",
    aliases: ["dyanavel", "liquid amphetamine"],
  },
  {
    name: "Adhansia XR (methylphenidate)",
    category: "Medication",
    aliases: ["adhansia", "adhansia xr"],
  },
  {
    name: "Aptensio XR (methylphenidate)",
    category: "Medication",
    aliases: ["aptensio", "aptensio xr"],
  },
  {
    name: "Daytrana (methylphenidate patch)",
    category: "Medication",
    aliases: ["daytrana", "methylphenidate patch", "adhd patch"],
  },
  {
    name: "Intuniv (guanfacine ER)",
    category: "Medication",
    aliases: ["guanfacine er", "intuniv", "guanfacine extended release"],
  },
  {
    name: "Kapvay (clonidine ER)",
    category: "Medication",
    aliases: ["clonidine er", "kapvay", "clonidine extended release adhd"],
  },

  // ============================================
  // MEDICATIONS — CYSTIC FIBROSIS
  // ============================================

  {
    name: "Trikafta (elexacaftor/tezacaftor/ivacaftor)",
    category: "Medication",
    aliases: ["trikafta", "elexacaftor", "cftr modulator", "cf modulator"],
  },
  {
    name: "Symdeko (tezacaftor/ivacaftor)",
    category: "Medication",
    aliases: ["symdeko", "tezacaftor", "symkevi"],
  },
  {
    name: "Orkambi (lumacaftor/ivacaftor)",
    category: "Medication",
    aliases: ["orkambi", "lumacaftor"],
  },
  {
    name: "Kalydeco (ivacaftor)",
    category: "Medication",
    aliases: ["kalydeco", "ivacaftor"],
  },
  {
    name: "Pulmozyme (dornase alfa)",
    category: "Medication",
    aliases: ["pulmozyme", "dornase alfa", "dnase"],
  },
  {
    name: "TOBI (tobramycin inhaled)",
    category: "Medication",
    aliases: [
      "tobi",
      "tobramycin inhalation",
      "tobi podhaler",
      "inhaled tobramycin",
    ],
  },
  {
    name: "Cayston (aztreonam inhaled)",
    category: "Medication",
    aliases: ["cayston", "aztreonam lysine", "inhaled aztreonam"],
  },

  // ============================================
  // MEDICATIONS — PULMONARY HYPERTENSION
  // ============================================

  {
    name: "Opsumit (macitentan)",
    category: "Medication",
    aliases: ["macitentan", "opsumit", "endothelin receptor antagonist"],
  },
  {
    name: "Letairis (ambrisentan)",
    category: "Medication",
    aliases: ["ambrisentan", "letairis"],
  },
  {
    name: "Tracleer (bosentan)",
    category: "Medication",
    aliases: ["bosentan", "tracleer"],
  },
  {
    name: "Uptravi (selexipag)",
    category: "Medication",
    aliases: ["selexipag", "uptravi", "ip receptor agonist"],
  },
  {
    name: "Adempas (riociguat)",
    category: "Medication",
    aliases: ["riociguat", "adempas", "sgc stimulator"],
  },
  {
    name: "Tyvaso (treprostinil)",
    category: "Medication",
    aliases: ["treprostinil", "tyvaso", "inhaled treprostinil", "tyvaso dpi"],
  },
  {
    name: "Remodulin (treprostinil)",
    category: "Medication",
    aliases: ["remodulin", "subcutaneous treprostinil", "iv treprostinil"],
  },
  {
    name: "Flolan (epoprostenol)",
    category: "Medication",
    aliases: ["epoprostenol", "flolan", "iv prostacyclin"],
  },
  {
    name: "Veletri (epoprostenol)",
    category: "Medication",
    aliases: ["veletri"],
  },
  {
    name: "Ventavis (iloprost)",
    category: "Medication",
    aliases: ["iloprost", "ventavis", "inhaled iloprost"],
  },
  {
    name: "Orenitram (treprostinil oral)",
    category: "Medication",
    aliases: ["orenitram", "oral treprostinil"],
  },
  {
    name: "Winrevair (sotatercept)",
    category: "Medication",
    aliases: ["sotatercept", "winrevair", "activin signaling inhibitor"],
  },

  // ============================================
  // MEDICATIONS — HIV / PrEP
  // ============================================

  {
    name: "Descovy for PrEP (emtricitabine/tenofovir)",
    category: "Medication",
    aliases: ["descovy", "descovy prep", "ftaf", "prep pill"],
  },
  {
    name: "Truvada for PrEP (emtricitabine/tenofovir)",
    category: "Medication",
    aliases: ["truvada", "truvada prep", "ftdf"],
  },
  {
    name: "Apretude (cabotegravir)",
    category: "Medication",
    aliases: [
      "apretude",
      "cabotegravir injection",
      "injectable prep",
      "long-acting prep",
    ],
  },
  {
    name: "Biktarvy (bictegravir/emtricitabine/tenofovir)",
    category: "Medication",
    aliases: ["biktarvy", "bictegravir"],
  },
  {
    name: "Dovato (dolutegravir/lamivudine)",
    category: "Medication",
    aliases: ["dovato", "two drug regimen hiv"],
  },
  {
    name: "Cabenuva (cabotegravir/rilpivirine)",
    category: "Medication",
    aliases: ["cabenuva", "long-acting hiv injection", "injectable art"],
  },
  {
    name: "Triumeq (dolutegravir/abacavir/lamivudine)",
    category: "Medication",
    aliases: ["triumeq"],
  },
  {
    name: "Symtuza (darunavir/cobicistat/emtricitabine/tenofovir)",
    category: "Medication",
    aliases: ["symtuza"],
  },
  {
    name: "Genvoya (elvitegravir/cobicistat/emtricitabine/tenofovir)",
    category: "Medication",
    aliases: ["genvoya"],
  },
  {
    name: "Sunlenca (lenacapavir)",
    category: "Medication",
    aliases: ["lenacapavir", "sunlenca", "capsid inhibitor"],
  },

  // ============================================
  // MEDICATIONS — HEPATITIS C
  // ============================================

  {
    name: "Epclusa (sofosbuvir/velpatasvir)",
    category: "Medication",
    aliases: [
      "epclusa",
      "sofosbuvir velpatasvir",
      "hcv treatment",
      "hep c cure",
    ],
  },
  {
    name: "Mavyret (glecaprevir/pibrentasvir)",
    category: "Medication",
    aliases: ["mavyret", "glecaprevir", "pibrentasvir"],
  },
  {
    name: "Harvoni (ledipasvir/sofosbuvir)",
    category: "Medication",
    aliases: ["harvoni", "ledipasvir"],
  },
  {
    name: "Vosevi (sofosbuvir/velpatasvir/voxilaprevir)",
    category: "Medication",
    aliases: ["vosevi"],
  },
  {
    name: "Zepatier (elbasvir/grazoprevir)",
    category: "Medication",
    aliases: ["zepatier", "elbasvir"],
  },

  // ============================================
  // MEDICATIONS — OPHTHALMOLOGY / EYE DROPS
  // ============================================

  {
    name: "Restasis (cyclosporine ophthalmic)",
    category: "Medication",
    aliases: ["restasis", "cyclosporine eye drops", "dry eye drops"],
  },
  {
    name: "Xiidra (lifitegrast)",
    category: "Medication",
    aliases: ["xiidra", "lifitegrast", "dry eye treatment"],
  },
  {
    name: "Cequa (cyclosporine 0.09%)",
    category: "Medication",
    aliases: ["cequa", "cyclosporine nanomicellar"],
  },
  {
    name: "Eysuvis (loteprednol)",
    category: "Medication",
    aliases: ["eysuvis", "loteprednol", "dry eye flare"],
  },
  {
    name: "Tyrvaya (varenicline nasal)",
    category: "Medication",
    aliases: ["tyrvaya", "varenicline nasal spray", "dry eye nasal spray"],
  },
  {
    name: "Miebo (perfluorohexyloctane)",
    category: "Medication",
    aliases: ["miebo", "novaliq", "perfluorohexyloctane"],
  },
  {
    name: "Vuity (pilocarpine ophthalmic)",
    category: "Medication",
    aliases: ["vuity", "pilocarpine for presbyopia"],
  },
  {
    name: "Vyzulta (latanoprostene bunod)",
    category: "Medication",
    aliases: ["vyzulta", "latanoprostene"],
  },
  {
    name: "Rhopressa (netarsudil)",
    category: "Medication",
    aliases: ["rhopressa", "netarsudil"],
  },
  {
    name: "Rocklatan (netarsudil/latanoprost)",
    category: "Medication",
    aliases: ["rocklatan"],
  },
  {
    name: "Durysta (bimatoprost implant)",
    category: "Medication",
    aliases: ["durysta", "bimatoprost implant", "glaucoma implant"],
  },
  {
    name: "Ikervis (cyclosporine 0.1%)",
    category: "Medication",
    aliases: ["ikervis"],
  },

  // ============================================
  // MEDICATIONS — ANTICONVULSANTS / EPILEPSY
  // ============================================

  {
    name: "Briviact (brivaracetam)",
    category: "Medication",
    aliases: ["brivaracetam", "briviact"],
  },
  {
    name: "Xcopri (cenobamate)",
    category: "Medication",
    aliases: ["cenobamate", "xcopri"],
  },
  {
    name: "Fycompa (perampanel)",
    category: "Medication",
    aliases: ["perampanel", "fycompa"],
  },
  {
    name: "Epidiolex (cannabidiol)",
    category: "Medication",
    aliases: [
      "epidiolex",
      "cannabidiol",
      "cbd prescription",
      "dravet syndrome treatment",
      "lennox-gastaut treatment",
    ],
  },
  {
    name: "Fintepla (fenfluramine)",
    category: "Medication",
    aliases: ["fenfluramine", "fintepla", "dravet treatment"],
  },
  {
    name: "Banzel (rufinamide)",
    category: "Medication",
    aliases: ["rufinamide", "banzel"],
  },
  {
    name: "Onfi (clobazam)",
    category: "Medication",
    aliases: ["clobazam", "onfi"],
  },
  {
    name: "Vigabatrin (Sabril)",
    category: "Medication",
    aliases: ["vigabatrin", "sabril", "infantile spasms treatment"],
  },
  {
    name: "Diacomit (stiripentol)",
    category: "Medication",
    aliases: ["stiripentol", "diacomit"],
  },
  {
    name: "Oxtellar XR (oxcarbazepine ER)",
    category: "Medication",
    aliases: ["oxtellar", "oxcarbazepine extended release"],
  },
  {
    name: "Aptiom (eslicarbazepine)",
    category: "Medication",
    aliases: ["eslicarbazepine", "aptiom"],
  },

  // ============================================
  // MEDICATIONS — JAK INHIBITORS
  // ============================================

  {
    name: "Olumiant (baricitinib)",
    category: "Medication",
    aliases: ["baricitinib", "olumiant"],
  },
  {
    name: "Sotyktu (deucravacitinib)",
    category: "Medication",
    aliases: ["deucravacitinib", "sotyktu", "tyk2 inhibitor"],
  },
  {
    name: "Cibinqo (abrocitinib)",
    category: "Medication",
    aliases: ["abrocitinib", "cibinqo", "jak1 inhibitor eczema"],
  },
  {
    name: "Litfulo (ritlecitinib)",
    category: "Medication",
    aliases: ["ritlecitinib", "litfulo", "alopecia areata treatment"],
  },
  {
    name: "Opzelura (ruxolitinib topical)",
    category: "Medication",
    aliases: ["ruxolitinib cream", "opzelura", "topical jak inhibitor"],
  },

  // ============================================
  // MEDICATIONS — TESTOSTERONE / HORMONE REPLACEMENT
  // ============================================

  {
    name: "Testosterone Cypionate Injection",
    category: "Medication",
    aliases: ["testosterone injection", "depo-testosterone", "test cyp", "trt"],
  },
  {
    name: "AndroGel (testosterone gel)",
    category: "Medication",
    aliases: [
      "androgel",
      "testosterone gel",
      "testogel",
      "testosterone topical",
    ],
  },
  {
    name: "Testopel (testosterone pellets)",
    category: "Medication",
    aliases: ["testopel", "testosterone pellet implant"],
  },
  {
    name: "Natesto (testosterone nasal)",
    category: "Medication",
    aliases: ["natesto", "testosterone nasal gel"],
  },
  {
    name: "Xyosted (testosterone enanthate auto-injector)",
    category: "Medication",
    aliases: ["xyosted", "testosterone auto-injector"],
  },
  {
    name: "Jatenzo (testosterone undecanoate oral)",
    category: "Medication",
    aliases: ["jatenzo", "oral testosterone"],
  },
  {
    name: "Tlando (testosterone undecanoate oral)",
    category: "Medication",
    aliases: ["tlando"],
  },
  {
    name: "Clomiphene for Hypogonadism",
    category: "Medication",
    aliases: [
      "clomid off-label",
      "clomiphene testosterone",
      "clomid for low t",
    ],
  },
  {
    name: "Enclomiphene",
    category: "Medication",
    aliases: ["enclomiphene citrate"],
  },

  // ============================================
  // MEDICATIONS — BIOSIMILARS
  // ============================================

  {
    name: "Hadlima (adalimumab-bwwd)",
    category: "Medication",
    aliases: ["hadlima", "adalimumab biosimilar", "humira biosimilar"],
  },
  {
    name: "Hyrimoz (adalimumab-adaz)",
    category: "Medication",
    aliases: ["hyrimoz", "adalimumab biosimilar"],
  },
  {
    name: "Cyltezo (adalimumab-adbm)",
    category: "Medication",
    aliases: ["cyltezo", "interchangeable adalimumab"],
  },
  {
    name: "Yusimry (adalimumab-aqvh)",
    category: "Medication",
    aliases: ["yusimry"],
  },
  {
    name: "Simlandi (adalimumab-ryvk)",
    category: "Medication",
    aliases: ["simlandi"],
  },
  {
    name: "Erelzi (etanercept-szzs)",
    category: "Medication",
    aliases: ["erelzi", "etanercept biosimilar", "enbrel biosimilar"],
  },
  {
    name: "Avsola (infliximab-axxq)",
    category: "Medication",
    aliases: ["avsola", "infliximab biosimilar"],
  },
  {
    name: "Mvasi (bevacizumab-awwb)",
    category: "Medication",
    aliases: ["mvasi", "bevacizumab biosimilar", "avastin biosimilar"],
  },
  {
    name: "Kanjinti (trastuzumab-anns)",
    category: "Medication",
    aliases: ["kanjinti", "trastuzumab biosimilar", "herceptin biosimilar"],
  },
  {
    name: "Truxima (rituximab-abbs)",
    category: "Medication",
    aliases: ["truxima", "rituximab biosimilar", "rituxan biosimilar"],
  },
  {
    name: "Riabni (rituximab-arrx)",
    category: "Medication",
    aliases: ["riabni"],
  },
  {
    name: "Ruxience (rituximab-pvvr)",
    category: "Medication",
    aliases: ["ruxience"],
  },
  {
    name: "Zirabev (bevacizumab-bvzr)",
    category: "Medication",
    aliases: ["zirabev"],
  },
  {
    name: "Herzuma (trastuzumab-pkrb)",
    category: "Medication",
    aliases: ["herzuma"],
  },
  {
    name: "Ogivri (trastuzumab-dkst)",
    category: "Medication",
    aliases: ["ogivri"],
  },
  {
    name: "Ontruzant (trastuzumab-dttb)",
    category: "Medication",
    aliases: ["ontruzant"],
  },
  {
    name: "Vegzelma (bevacizumab-adcd)",
    category: "Medication",
    aliases: ["vegzelma"],
  },
  {
    name: "Yuflyma (adalimumab-aaty)",
    category: "Medication",
    aliases: ["yuflyma"],
  },

  // ============================================
  // MEDICATIONS — DERMATOLOGY (SPECIALTY)
  // ============================================

  {
    name: "Adbry (tralokinumab)",
    category: "Medication",
    aliases: ["tralokinumab", "adbry", "il-13 inhibitor"],
  },
  {
    name: "Vtama (tapinarof)",
    category: "Medication",
    aliases: ["tapinarof", "vtama", "aryl hydrocarbon receptor agonist"],
  },
  {
    name: "Bimzelx (bimekizumab)",
    category: "Medication",
    aliases: ["bimekizumab", "bimzelx", "il-17a il-17f inhibitor"],
  },
  {
    name: "Arcalyst (rilonacept)",
    category: "Medication",
    aliases: ["rilonacept", "arcalyst", "il-1 trap"],
  },
  {
    name: "Ilumya (tildrakizumab)",
    category: "Medication",
    aliases: ["tildrakizumab", "ilumya", "il-23 inhibitor"],
  },
  {
    name: "Siliq (brodalumab)",
    category: "Medication",
    aliases: ["brodalumab", "siliq", "il-17ra antagonist"],
  },
  {
    name: "Eucrisa (crisaborole)",
    category: "Medication",
    aliases: ["crisaborole", "eucrisa", "pde4 inhibitor topical"],
  },
  {
    name: "Elidel (pimecrolimus)",
    category: "Medication",
    aliases: ["pimecrolimus", "elidel", "topical calcineurin inhibitor"],
  },
  {
    name: "Protopic (tacrolimus topical)",
    category: "Medication",
    aliases: ["tacrolimus ointment", "protopic", "topical calcineurin"],
  },
  {
    name: "Soolantra (ivermectin topical)",
    category: "Medication",
    aliases: ["ivermectin cream", "soolantra", "rosacea cream"],
  },
  {
    name: "Rhofade (oxymetazoline topical)",
    category: "Medication",
    aliases: ["oxymetazoline cream", "rhofade", "rosacea redness"],
  },
  {
    name: "Zilxi (minocycline topical)",
    category: "Medication",
    aliases: ["minocycline foam", "zilxi", "topical rosacea"],
  },
  {
    name: "Arazlo (tazarotene lotion)",
    category: "Medication",
    aliases: ["tazarotene", "arazlo", "topical retinoid acne"],
  },
  {
    name: "Aklief (trifarotene)",
    category: "Medication",
    aliases: ["trifarotene", "aklief", "retinoid cream"],
  },
  {
    name: "Winlevi (clascoterone)",
    category: "Medication",
    aliases: ["clascoterone", "winlevi", "topical antiandrogen acne"],
  },
  {
    name: "Cabtreo (adapalene/benzoyl peroxide/clindamycin)",
    category: "Medication",
    aliases: ["cabtreo", "triple combination acne", "adapalene bpo clindamycin"],
  },
  {
    name: "Finacea (azelaic acid)",
    category: "Medication",
    aliases: ["azelaic acid", "finacea", "azelaic acid gel rosacea"],
  },
  {
    name: "Metronidazole Topical",
    category: "Medication",
    aliases: ["metrogel", "metrocream", "topical metronidazole rosacea"],
  },
  {
    name: "Spironolactone (hormonal acne)",
    category: "Medication",
    aliases: ["spironolactone acne", "aldactone acne", "hormonal acne treatment"],
  },
  {
    name: "Accutane (isotretinoin)",
    category: "Medication",
    aliases: ["isotretinoin", "accutane", "claravis", "absorica", "zenatane"],
  },
  {
    name: "Soriatane (acitretin)",
    category: "Medication",
    aliases: ["acitretin", "soriatane", "oral retinoid psoriasis"],
  },
  {
    name: "Dapsone Topical",
    category: "Medication",
    aliases: ["aczone", "dapsone gel", "topical dapsone acne"],
  },
  {
    name: "Imiquimod Topical",
    category: "Medication",
    aliases: ["aldara", "zyclara", "imiquimod cream", "actinic keratosis cream", "bcc cream"],
  },
  {
    name: "5-Fluorouracil Topical",
    category: "Medication",
    aliases: ["efudex", "5-fu cream", "fluorouracil cream", "actinic keratosis topical"],
  },
  {
    name: "Tirbanibulin (Klisyri)",
    category: "Medication",
    aliases: ["klisyri", "tirbanibulin", "actinic keratosis ointment"],
  },
  {
    name: "Diclofenac Topical (Solaraze)",
    category: "Medication",
    aliases: ["solaraze", "diclofenac gel", "topical nsaid actinic keratosis"],
  },
  {
    name: "Qbrexza (glycopyrronium)",
    category: "Medication",
    aliases: ["glycopyrronium cloth", "qbrexza", "hyperhidrosis wipe", "anticholinergic topical sweating"],
  },
  {
    name: "Ketoconazole Topical",
    category: "Medication",
    aliases: ["nizoral cream", "nizoral shampoo", "ketoconazole shampoo", "seborrheic dermatitis treatment"],
  },
  {
    name: "Ciclopirox",
    category: "Medication",
    aliases: ["loprox", "penlac", "ciclopirox shampoo", "ciclopirox nail lacquer", "onychomycosis topical"],
  },
  {
    name: "Hydroxychloroquine (Dermatologic)",
    category: "Medication",
    aliases: ["plaquenil derm", "hydroxychloroquine lupus skin", "hydroxychloroquine dermatomyositis"],
  },
  {
    name: "Mycophenolate (Dermatologic)",
    category: "Medication",
    aliases: ["cellcept derm", "mycophenolate pemphigus", "mycophenolate dermatology"],
  },
  {
    name: "Apremilast (Otezla)",
    category: "Medication",
    aliases: ["otezla", "apremilast", "pde4 inhibitor oral", "oral psoriasis"],
  },
  {
    name: "Doxycycline (Dermatologic)",
    category: "Medication",
    aliases: ["doxycycline acne", "doxycycline rosacea", "oracea", "low dose doxycycline"],
  },
  {
    name: "Minocycline (Dermatologic)",
    category: "Medication",
    aliases: ["minocycline acne", "solodyn", "amzeeq", "minocycline foam"],
  },

  // ============================================
  // MEDICATIONS — MULTIPLE SCLEROSIS
  // ============================================

  {
    name: "Kesimpta (ofatumumab)",
    category: "Medication",
    aliases: ["ofatumumab", "kesimpta", "anti-cd20 subcutaneous ms"],
  },
  {
    name: "Mavenclad (cladribine)",
    category: "Medication",
    aliases: ["cladribine oral", "mavenclad"],
  },
  {
    name: "Zeposia (ozanimod)",
    category: "Medication",
    aliases: ["ozanimod", "zeposia", "s1p receptor modulator"],
  },
  {
    name: "Ponvory (ponesimod)",
    category: "Medication",
    aliases: ["ponesimod", "ponvory"],
  },
  {
    name: "Mayzent (siponimod)",
    category: "Medication",
    aliases: ["siponimod", "mayzent"],
  },
  {
    name: "Gilenya (fingolimod)",
    category: "Medication",
    aliases: ["fingolimod", "gilenya"],
  },
  {
    name: "Vumerity (diroximel fumarate)",
    category: "Medication",
    aliases: ["diroximel fumarate", "vumerity"],
  },
  {
    name: "Bafiertam (monomethyl fumarate)",
    category: "Medication",
    aliases: ["monomethyl fumarate", "bafiertam"],
  },
  {
    name: "Aubagio (teriflunomide)",
    category: "Medication",
    aliases: ["teriflunomide", "aubagio"],
  },
  {
    name: "Copaxone (glatiramer acetate)",
    category: "Medication",
    aliases: ["glatiramer", "copaxone"],
  },
  {
    name: "Briumvi (ublituximab)",
    category: "Medication",
    aliases: ["ublituximab", "briumvi"],
  },

  // ============================================
  // MEDICATIONS — RESPIRATORY / ASTHMA / COPD
  // ============================================

  {
    name: "Trelegy Ellipta (fluticasone/umeclidinium/vilanterol)",
    category: "Medication",
    aliases: ["trelegy", "triple inhaler", "trelegy ellipta"],
  },
  {
    name: "Breztri Aerosphere (budesonide/glycopyrrolate/formoterol)",
    category: "Medication",
    aliases: ["breztri", "triple therapy copd"],
  },
  {
    name: "Breo Ellipta (fluticasone/vilanterol)",
    category: "Medication",
    aliases: ["breo", "breo ellipta"],
  },
  {
    name: "Symbicort (budesonide/formoterol)",
    category: "Medication",
    aliases: ["symbicort", "budesonide formoterol"],
  },
  {
    name: "Advair (fluticasone/salmeterol)",
    category: "Medication",
    aliases: ["advair", "advair diskus", "advair hfa"],
  },
  {
    name: "Dulera (mometasone/formoterol)",
    category: "Medication",
    aliases: ["dulera"],
  },
  {
    name: "Spiriva (tiotropium)",
    category: "Medication",
    aliases: [
      "spiriva",
      "tiotropium",
      "spiriva respimat",
      "spiriva handihaler",
    ],
  },
  {
    name: "Incruse Ellipta (umeclidinium)",
    category: "Medication",
    aliases: ["incruse", "umeclidinium"],
  },
  {
    name: "Stiolto Respimat (tiotropium/olodaterol)",
    category: "Medication",
    aliases: ["stiolto", "tiotropium olodaterol"],
  },
  {
    name: "Anoro Ellipta (umeclidinium/vilanterol)",
    category: "Medication",
    aliases: ["anoro", "umeclidinium vilanterol"],
  },
  {
    name: "Serevent (salmeterol)",
    category: "Medication",
    aliases: ["serevent", "salmeterol diskus"],
  },
  {
    name: "Pulmicort (budesonide)",
    category: "Medication",
    aliases: ["pulmicort", "budesonide respules", "budesonide nebulizer"],
  },
  {
    name: "Arnuity Ellipta (fluticasone furoate)",
    category: "Medication",
    aliases: ["arnuity", "fluticasone furoate inhaler"],
  },
  {
    name: "Singulair (montelukast)",
    category: "Medication",
    aliases: ["montelukast", "singulair"],
  },

  // ============================================
  // MEDICATIONS — DIABETES (BEYOND GLP-1)
  // ============================================

  {
    name: "Invokana (canagliflozin)",
    category: "Medication",
    aliases: ["canagliflozin", "invokana", "sglt2 inhibitor"],
  },
  {
    name: "Steglatro (ertugliflozin)",
    category: "Medication",
    aliases: ["ertugliflozin", "steglatro"],
  },
  {
    name: "Brenzavvy (bexagliflozin)",
    category: "Medication",
    aliases: ["bexagliflozin", "brenzavvy"],
  },
  {
    name: "Tradjenta (linagliptin)",
    category: "Medication",
    aliases: ["linagliptin", "tradjenta", "dpp-4 inhibitor"],
  },
  {
    name: "Januvia (sitagliptin)",
    category: "Medication",
    aliases: ["sitagliptin", "januvia"],
  },
  {
    name: "Onglyza (saxagliptin)",
    category: "Medication",
    aliases: ["saxagliptin", "onglyza"],
  },
  {
    name: "Nesina (alogliptin)",
    category: "Medication",
    aliases: ["alogliptin", "nesina"],
  },
  {
    name: "Afrezza (insulin inhaled)",
    category: "Medication",
    aliases: ["afrezza", "inhaled insulin", "technosphere insulin"],
  },
  {
    name: "Tzield (teplizumab)",
    category: "Medication",
    aliases: ["teplizumab", "tzield", "type 1 diabetes delay"],
  },
  {
    name: "Tresiba (insulin degludec)",
    category: "Medication",
    aliases: ["tresiba", "insulin degludec", "ultra-long acting insulin"],
  },
  {
    name: "Toujeo (insulin glargine U-300)",
    category: "Medication",
    aliases: ["toujeo", "insulin glargine 300", "concentrated glargine"],
  },
  {
    name: "Lyumjev (insulin lispro-aabc)",
    category: "Medication",
    aliases: ["lyumjev", "ultra rapid lispro"],
  },
  {
    name: "Fiasp (insulin aspart fast-acting)",
    category: "Medication",
    aliases: ["fiasp", "faster-acting aspart"],
  },

  // ============================================
  // MEDICATIONS — CARDIOLOGY (SPECIALTY)
  // ============================================

  {
    name: "Verquvo (vericiguat)",
    category: "Medication",
    aliases: ["vericiguat", "verquvo", "sgc stimulator heart failure"],
  },
  {
    name: "Camzyos (mavacamten)",
    category: "Medication",
    aliases: [
      "mavacamten",
      "camzyos",
      "hocm treatment",
      "cardiac myosin inhibitor",
    ],
  },
  {
    name: "Praluent (alirocumab)",
    category: "Medication",
    aliases: ["alirocumab", "praluent", "pcsk9 inhibitor"],
  },
  {
    name: "Repatha (evolocumab)",
    category: "Medication",
    aliases: ["evolocumab", "repatha", "pcsk9 inhibitor"],
  },
  {
    name: "Leqvio (inclisiran)",
    category: "Medication",
    aliases: ["inclisiran", "leqvio", "sirna cholesterol"],
  },
  {
    name: "Nexletol (bempedoic acid)",
    category: "Medication",
    aliases: ["bempedoic acid", "nexletol"],
  },
  {
    name: "Nexlizet (bempedoic acid/ezetimibe)",
    category: "Medication",
    aliases: ["nexlizet", "bempedoic ezetimibe"],
  },
  {
    name: "Vascepa (icosapent ethyl)",
    category: "Medication",
    aliases: ["icosapent ethyl", "vascepa", "epa", "prescription fish oil"],
  },
  {
    name: "Corlanor (ivabradine)",
    category: "Medication",
    aliases: ["ivabradine", "corlanor", "funny channel blocker"],
  },
  {
    name: "Multaq (dronedarone)",
    category: "Medication",
    aliases: ["dronedarone", "multaq", "atrial fibrillation rhythm control"],
  },
  {
    name: "Tikosyn (dofetilide)",
    category: "Medication",
    aliases: ["dofetilide", "tikosyn"],
  },
  {
    name: "Savaysa (edoxaban)",
    category: "Medication",
    aliases: ["edoxaban", "savaysa", "doac"],
  },

  // ============================================
  // MEDICATIONS — PAIN / OPIOID MANAGEMENT
  // ============================================

  {
    name: "Sublocade (buprenorphine extended-release)",
    category: "Medication",
    aliases: ["sublocade", "buprenorphine injection", "monthly buprenorphine"],
  },
  {
    name: "Suboxone (buprenorphine/naloxone)",
    category: "Medication",
    aliases: ["suboxone", "buprenorphine naloxone", "bupe"],
  },
  {
    name: "Vivitrol (naltrexone extended-release)",
    category: "Medication",
    aliases: ["vivitrol", "naltrexone injection", "monthly naltrexone"],
  },
  {
    name: "Nucynta ER (tapentadol ER)",
    category: "Medication",
    aliases: ["tapentadol", "nucynta", "nucynta er"],
  },
  {
    name: "Xtampza ER (oxycodone ER abuse-deterrent)",
    category: "Medication",
    aliases: ["xtampza", "abuse deterrent oxycodone"],
  },
  {
    name: "Butrans Patch (buprenorphine patch)",
    category: "Medication",
    aliases: ["butrans", "buprenorphine patch", "bup patch"],
  },
  {
    name: "Arymo ER (morphine ER abuse-deterrent)",
    category: "Medication",
    aliases: ["arymo", "abuse deterrent morphine"],
  },
  {
    name: "Lyrica (pregabalin)",
    category: "Medication",
    aliases: ["pregabalin", "lyrica"],
  },
  {
    name: "Gralise (gabapentin ER)",
    category: "Medication",
    aliases: ["gralise", "gabapentin extended release"],
  },
  {
    name: "Qutenza (capsaicin 8% patch)",
    category: "Medication",
    aliases: ["qutenza", "capsaicin patch", "high dose capsaicin"],
  },
  {
    name: "Zorvolex (diclofenac submicron)",
    category: "Medication",
    aliases: ["zorvolex"],
  },
  {
    name: "Savella (milnacipran)",
    category: "Medication",
    aliases: ["milnacipran", "savella", "fibromyalgia snri"],
  },
  {
    name: "Zilretta (triamcinolone ER)",
    category: "Medication",
    aliases: ["zilretta", "extended release steroid injection", "er triamcinolone knee"],
  },
  {
    name: "Exparel (liposomal bupivacaine)",
    category: "Medication",
    aliases: ["liposomal bupivacaine", "exparel", "long acting local anesthetic"],
  },
  {
    name: "Movantik (naloxegol)",
    category: "Medication",
    aliases: ["naloxegol", "movantik", "opioid induced constipation", "oic"],
  },
  {
    name: "Symproic (naldemedine)",
    category: "Medication",
    aliases: ["naldemedine", "symproic", "opioid induced constipation"],
  },
  {
    name: "Relistor (methylnaltrexone)",
    category: "Medication",
    aliases: ["methylnaltrexone", "relistor", "oic injection"],
  },
  {
    name: "Pennsaid (diclofenac topical solution)",
    category: "Medication",
    aliases: ["pennsaid", "diclofenac solution", "topical nsaid knee"],
  },
  {
    name: "Flector Patch (diclofenac patch)",
    category: "Medication",
    aliases: ["flector", "diclofenac patch", "topical nsaid patch"],
  },
  {
    name: "Zanaflex (tizanidine)",
    category: "Medication",
    aliases: ["tizanidine", "zanaflex", "muscle relaxant"],
  },
  {
    name: "Baclofen",
    category: "Medication",
    aliases: ["lioresal", "baclofen oral", "baclofen intrathecal", "muscle relaxant"],
  },
  {
    name: "Duloxetine (Pain)",
    category: "Medication",
    aliases: ["cymbalta pain", "duloxetine neuropathy", "duloxetine chronic pain", "snri pain"],
  },
  {
    name: "Gabapentin",
    category: "Medication",
    aliases: ["neurontin", "gabapentin pain", "gabapentin neuropathy"],
  },
  {
    name: "Pregabalin (Lyrica)",
    category: "Medication",
    aliases: ["lyrica", "pregabalin pain", "pregabalin neuropathy"],
  },
  {
    name: "Low Dose Naltrexone",
    category: "Medication",
    aliases: ["ldn", "low dose naltrexone pain", "naltrexone fibromyalgia"],
  },
  {
    name: "Ketamine Nasal Spray",
    category: "Medication",
    aliases: ["ketamine spray", "compounded ketamine nasal", "intranasal ketamine pain"],
  },
  {
    name: "Compounded Pain Cream",
    category: "Medication",
    aliases: [
      "compounded topical",
      "compounded cream",
      "ketamine gabapentin cream",
      "compound pain cream",
      "topical compound",
    ],
  },

  // ============================================
  // MEDICATIONS — PSYCHIATRIC (SPECIALTY)
  // ============================================

  {
    name: "Rexulti (brexpiprazole)",
    category: "Medication",
    aliases: ["brexpiprazole", "rexulti"],
  },
  {
    name: "Vraylar (cariprazine)",
    category: "Medication",
    aliases: ["cariprazine", "vraylar"],
  },
  {
    name: "Caplyta (lumateperone)",
    category: "Medication",
    aliases: ["lumateperone", "caplyta"],
  },
  {
    name: "Lybalvi (olanzapine/samidorphan)",
    category: "Medication",
    aliases: ["lybalvi", "olanzapine samidorphan"],
  },
  {
    name: "Abilify Maintena (aripiprazole ER)",
    category: "Medication",
    aliases: [
      "abilify maintena",
      "aripiprazole injection",
      "long-acting aripiprazole",
    ],
  },
  {
    name: "Aristada (aripiprazole lauroxil)",
    category: "Medication",
    aliases: ["aristada", "aripiprazole lauroxil"],
  },
  {
    name: "Invega Sustenna (paliperidone palmitate)",
    category: "Medication",
    aliases: ["invega sustenna", "paliperidone monthly"],
  },
  {
    name: "Invega Trinza (paliperidone palmitate 3-month)",
    category: "Medication",
    aliases: ["invega trinza", "paliperidone quarterly"],
  },
  {
    name: "Trintellix (vortioxetine)",
    category: "Medication",
    aliases: ["vortioxetine", "trintellix"],
  },
  {
    name: "Pristiq (desvenlafaxine)",
    category: "Medication",
    aliases: ["desvenlafaxine", "pristiq"],
  },
  {
    name: "Fetzima (levomilnacipran)",
    category: "Medication",
    aliases: ["levomilnacipran", "fetzima"],
  },
  {
    name: "Zurzuvae (zuranolone)",
    category: "Medication",
    aliases: ["zuranolone", "zurzuvae", "postpartum depression treatment"],
  },
  {
    name: "Zulresso (brexanolone)",
    category: "Medication",
    aliases: ["brexanolone", "zulresso", "ppd infusion"],
  },

  // ============================================
  // MEDICATIONS — ONCOLOGY (ORAL TARGETED)
  // ============================================

  {
    name: "Tagrisso (osimertinib)",
    category: "Medication",
    aliases: ["osimertinib", "tagrisso", "egfr inhibitor"],
  },
  {
    name: "Verzenio (abemaciclib)",
    category: "Medication",
    aliases: ["abemaciclib", "verzenio", "cdk4/6 inhibitor"],
  },
  {
    name: "Kisqali (ribociclib)",
    category: "Medication",
    aliases: ["ribociclib", "kisqali"],
  },
  {
    name: "Lynparza (olaparib)",
    category: "Medication",
    aliases: ["olaparib", "lynparza", "parp inhibitor"],
  },
  {
    name: "Zejula (niraparib)",
    category: "Medication",
    aliases: ["niraparib", "zejula"],
  },
  {
    name: "Rubraca (rucaparib)",
    category: "Medication",
    aliases: ["rucaparib", "rubraca"],
  },
  {
    name: "Calquence (acalabrutinib)",
    category: "Medication",
    aliases: ["acalabrutinib", "calquence", "btk inhibitor"],
  },
  {
    name: "Imbruvica (ibrutinib)",
    category: "Medication",
    aliases: ["ibrutinib", "imbruvica"],
  },
  {
    name: "Venclexta (venetoclax)",
    category: "Medication",
    aliases: ["venetoclax", "venclexta", "bcl-2 inhibitor"],
  },
  {
    name: "Revlimid (lenalidomide)",
    category: "Medication",
    aliases: ["lenalidomide", "revlimid"],
  },
  {
    name: "Pomalyst (pomalidomide)",
    category: "Medication",
    aliases: ["pomalidomide", "pomalyst"],
  },
  {
    name: "Jakafi (ruxolitinib)",
    category: "Medication",
    aliases: ["ruxolitinib", "jakafi", "myelofibrosis treatment"],
  },
  {
    name: "Gleevec (imatinib)",
    category: "Medication",
    aliases: ["imatinib", "gleevec", "gist treatment"],
  },
  {
    name: "Alecensa (alectinib)",
    category: "Medication",
    aliases: ["alectinib", "alecensa", "alk inhibitor"],
  },
  {
    name: "Lorbrena (lorlatinib)",
    category: "Medication",
    aliases: ["lorlatinib", "lorbrena"],
  },
  {
    name: "Tukysa (tucatinib)",
    category: "Medication",
    aliases: ["tucatinib", "tukysa", "her2 oral"],
  },
  {
    name: "Enhertu (trastuzumab deruxtecan)",
    category: "Medication",
    aliases: ["trastuzumab deruxtecan", "enhertu", "t-dxd"],
  },
  {
    name: "Padcev (enfortumab vedotin)",
    category: "Medication",
    aliases: ["enfortumab", "padcev", "bladder cancer adc"],
  },
  {
    name: "Trodelvy (sacituzumab govitecan)",
    category: "Medication",
    aliases: ["sacituzumab", "trodelvy", "trop-2 adc"],
  },
  {
    name: "Krazati (adagrasib)",
    category: "Medication",
    aliases: ["adagrasib", "krazati", "kras g12c inhibitor"],
  },
  {
    name: "Lumakras (sotorasib)",
    category: "Medication",
    aliases: ["sotorasib", "lumakras"],
  },

  // ============================================
  // DME — EXPANDED
  // ============================================

  // Orthotics
  {
    name: "Custom AFO",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "ankle foot orthosis",
      "afo",
      "custom ankle brace",
      "foot drop brace",
    ],
  },
  {
    name: "Custom KAFO",
    category: "DME (Durable Medical Equipment)",
    aliases: ["knee ankle foot orthosis", "kafo"],
  },
  {
    name: "Custom Foot Orthotic",
    category: "DME (Durable Medical Equipment)",
    aliases: ["custom insole", "custom orthotic insert", "foot orthosis"],
  },
  {
    name: "Cervical Collar",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "c-collar",
      "neck brace",
      "cervical orthosis",
      "aspen collar",
      "miami j collar",
    ],
  },
  {
    name: "Wrist Splint",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "wrist brace",
      "wrist orthosis",
      "cock-up splint",
      "carpal tunnel brace",
    ],
  },
  {
    name: "Shoulder Brace",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "shoulder sling",
      "shoulder immobilizer",
      "shoulder abduction brace",
    ],
  },
  {
    name: "Hip Brace",
    category: "DME (Durable Medical Equipment)",
    aliases: ["hip abduction brace", "hip orthosis"],
  },
  {
    name: "Ankle Brace",
    category: "DME (Durable Medical Equipment)",
    aliases: ["ankle support", "ankle orthosis", "cam boot", "walking boot"],
  },
  {
    name: "Custom Cranial Helmet",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "cranial remolding orthosis",
      "plagiocephaly helmet",
      "doc band",
      "starband",
    ],
  },
  {
    name: "Scoliosis Brace",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "boston brace",
      "charleston brace",
      "milwaukee brace",
      "thoracolumbosacral orthosis",
    ],
  },

  // Prosthetics
  {
    name: "Below Knee Prosthesis",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "bk prosthesis",
      "transtibial prosthesis",
      "below knee prosthetic leg",
    ],
  },
  {
    name: "Above Knee Prosthesis",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "ak prosthesis",
      "transfemoral prosthesis",
      "above knee prosthetic leg",
    ],
  },
  {
    name: "Microprocessor Knee",
    category: "DME (Durable Medical Equipment)",
    aliases: ["c-leg", "genium", "computerized knee", "mpc knee"],
  },
  {
    name: "Running Prosthesis",
    category: "DME (Durable Medical Equipment)",
    aliases: ["sport prosthesis", "blade prosthesis", "running blade"],
  },
  {
    name: "Upper Extremity Prosthesis",
    category: "DME (Durable Medical Equipment)",
    aliases: ["arm prosthesis", "hand prosthesis", "upper limb prosthetic"],
  },
  {
    name: "Myoelectric Prosthesis",
    category: "DME (Durable Medical Equipment)",
    aliases: ["myoelectric arm", "bionic arm", "myoelectric hand"],
  },
  {
    name: "Partial Foot Prosthesis",
    category: "DME (Durable Medical Equipment)",
    aliases: ["toe prosthesis", "partial foot prosthetic"],
  },
  {
    name: "Prosthetic Socket Replacement",
    category: "DME (Durable Medical Equipment)",
    aliases: ["socket replacement", "prosthetic socket", "new socket"],
  },
  {
    name: "Prosthetic Liner",
    category: "DME (Durable Medical Equipment)",
    aliases: ["gel liner", "silicone liner", "prosthetic suspension liner"],
  },

  // Mobility
  {
    name: "Power Scooter",
    category: "DME (Durable Medical Equipment)",
    aliases: ["mobility scooter", "electric scooter"],
  },
  {
    name: "Standing Wheelchair",
    category: "DME (Durable Medical Equipment)",
    aliases: ["stand up wheelchair", "standing power wheelchair", "permobil"],
  },
  {
    name: "Tilt-in-Space Wheelchair",
    category: "DME (Durable Medical Equipment)",
    aliases: ["tilt wheelchair", "reclining wheelchair"],
  },
  {
    name: "Lightweight Wheelchair",
    category: "DME (Durable Medical Equipment)",
    aliases: ["ultralight wheelchair", "rigid frame wheelchair"],
  },
  {
    name: "Pediatric Wheelchair",
    category: "DME (Durable Medical Equipment)",
    aliases: ["peds wheelchair", "child wheelchair"],
  },
  {
    name: "Wheelchair Cushion",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "pressure relief cushion",
      "roho cushion",
      "gel cushion",
      "wheelchair seat cushion",
    ],
  },
  {
    name: "Wheelchair Ramp",
    category: "DME (Durable Medical Equipment)",
    aliases: ["portable ramp", "threshold ramp"],
  },
  {
    name: "Stair Lift",
    category: "DME (Durable Medical Equipment)",
    aliases: ["stairlift", "chair lift stairs"],
  },
  {
    name: "Patient Lift",
    category: "DME (Durable Medical Equipment)",
    aliases: ["hoyer lift", "patient hoist", "ceiling lift"],
  },
  {
    name: "Crutches",
    category: "DME (Durable Medical Equipment)",
    aliases: ["forearm crutches", "axillary crutches", "lofstrand crutches"],
  },
  {
    name: "Cane",
    category: "DME (Durable Medical Equipment)",
    aliases: ["quad cane", "single point cane", "offset cane"],
  },
  {
    name: "Knee Scooter",
    category: "DME (Durable Medical Equipment)",
    aliases: ["knee walker", "roll about"],
  },
  {
    name: "Gait Trainer",
    category: "DME (Durable Medical Equipment)",
    aliases: ["pediatric gait trainer", "posterior walker", "anterior walker"],
  },
  {
    name: "Standing Frame",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "stander",
      "standing device",
      "supine stander",
      "prone stander",
      "easystand",
    ],
  },
  {
    name: "Adaptive Stroller",
    category: "DME (Durable Medical Equipment)",
    aliases: ["special needs stroller", "medical stroller", "convaid stroller"],
  },

  // Respiratory DME
  {
    name: "Portable Oxygen Concentrator",
    category: "DME (Durable Medical Equipment)",
    aliases: ["poc", "travel oxygen", "portable o2"],
  },
  {
    name: "Oxygen Tank",
    category: "DME (Durable Medical Equipment)",
    aliases: ["oxygen cylinder", "compressed oxygen", "liquid oxygen"],
  },
  {
    name: "Pulse Oximeter",
    category: "DME (Durable Medical Equipment)",
    aliases: ["pulse ox", "finger oximeter", "spo2 monitor"],
  },
  {
    name: "Suction Machine",
    category: "DME (Durable Medical Equipment)",
    aliases: ["portable suction", "suction device", "airway suction"],
  },
  {
    name: "Cough Assist Device",
    category: "DME (Durable Medical Equipment)",
    aliases: ["mechanical insufflator-exsufflator", "cough machine", "mi-e"],
  },
  {
    name: "Ventilator",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "home ventilator",
      "portable ventilator",
      "life support ventilator",
    ],
  },
  {
    name: "Trach Supplies",
    category: "DME (Durable Medical Equipment)",
    aliases: ["tracheostomy supplies", "trach tubes", "trach care kit"],
  },
  {
    name: "Airvo High Flow",
    category: "DME (Durable Medical Equipment)",
    aliases: ["home high flow", "high flow nasal cannula home", "myairvo"],
  },

  // Diabetes DME
  {
    name: "Insulin Pen",
    category: "DME (Durable Medical Equipment)",
    aliases: ["insulin pen needles", "pen needles", "reusable insulin pen"],
  },
  {
    name: "Glucose Test Strips",
    category: "DME (Durable Medical Equipment)",
    aliases: ["blood sugar test strips", "bgm strips", "glucometer strips"],
  },
  {
    name: "Tandem Insulin Pump",
    category: "DME (Durable Medical Equipment)",
    aliases: ["tandem t:slim", "t:slim x2", "control-iq"],
  },
  {
    name: "Omnipod Insulin Pump",
    category: "DME (Durable Medical Equipment)",
    aliases: ["omnipod", "omnipod 5", "tubeless pump", "omnipod dash"],
  },
  {
    name: "Medtronic Insulin Pump",
    category: "DME (Durable Medical Equipment)",
    aliases: ["medtronic 780g", "minimed", "guardian sensor"],
  },
  {
    name: "Dexcom CGM",
    category: "DME (Durable Medical Equipment)",
    aliases: ["dexcom g7", "dexcom g6", "dexcom sensor"],
  },
  {
    name: "Libre CGM",
    category: "DME (Durable Medical Equipment)",
    aliases: ["freestyle libre", "libre 3", "libre 2", "abbott cgm"],
  },
  {
    name: "Eversense CGM",
    category: "DME (Durable Medical Equipment)",
    aliases: ["eversense", "implantable cgm", "senseonics"],
  },

  // Other DME
  {
    name: "Specialty Mattress",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "pressure relief mattress",
      "alternating pressure mattress",
      "low air loss mattress",
      "wound care mattress",
    ],
  },
  {
    name: "Compression Pump",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "lymphedema pump",
      "pneumatic compression device",
      "sequential compression device",
    ],
  },
  {
    name: "Compression Stockings",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "compression socks",
      "ted hose",
      "graduated compression",
      "support stockings",
    ],
  },
  {
    name: "Shower Chair",
    category: "DME (Durable Medical Equipment)",
    aliases: ["bath bench", "shower bench", "bath seat", "transfer bench"],
  },
  {
    name: "Commode",
    category: "DME (Durable Medical Equipment)",
    aliases: ["bedside commode", "raised toilet seat", "3-in-1 commode"],
  },
  {
    name: "Hospital Bed Mattress",
    category: "DME (Durable Medical Equipment)",
    aliases: ["foam mattress", "innerspring mattress", "bariatric mattress"],
  },
  {
    name: "Trapeze Bar",
    category: "DME (Durable Medical Equipment)",
    aliases: ["bed trapeze", "overhead trapeze"],
  },
  {
    name: "Enteral Feeding Pump",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "feeding pump",
      "kangaroo pump",
      "infinity pump",
      "tube feeding pump",
    ],
  },
  {
    name: "Enteral Formula",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "tube feeding formula",
      "ensure",
      "jevity",
      "osmolite",
      "peptamen",
    ],
  },
  {
    name: "Bone Growth Stimulator",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "bone stimulator",
      "osteogenesis stimulator",
      "fracture healing device",
    ],
  },
  {
    name: "Cold Therapy Unit",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "ice machine",
      "game ready",
      "cold therapy machine",
      "cryotherapy unit",
    ],
  },
  {
    name: "CPM Machine",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "continuous passive motion",
      "knee cpm",
      "post-surgical motion machine",
    ],
  },
  {
    name: "Home Blood Pressure Monitor",
    category: "DME (Durable Medical Equipment)",
    aliases: ["bp monitor", "blood pressure cuff", "home bp cuff"],
  },
  {
    name: "Home ECG Monitor",
    category: "DME (Durable Medical Equipment)",
    aliases: ["kardia", "alivecor", "personal ecg", "home ekg"],
  },

  // ============================================
  // ADMISSIONS — EXPANDED
  // ============================================

  {
    name: "Telemetry Admission",
    category: "Admission",
    aliases: ["telemetry", "monitored bed", "cardiac monitoring admission"],
  },
  {
    name: "Step-Down Unit Admission",
    category: "Admission",
    aliases: [
      "step-down",
      "imc",
      "pcu",
      "intermediate care",
      "progressive care unit",
    ],
  },
  {
    name: "Detox Admission",
    category: "Admission",
    aliases: [
      "detoxification",
      "medical detox",
      "alcohol detox",
      "drug detox",
      "withdrawal management",
    ],
  },
  {
    name: "Pediatric Admission",
    category: "Admission",
    aliases: ["pediatric inpatient", "peds admission", "picu admission"],
  },
  {
    name: "Neonatal ICU Admission",
    category: "Admission",
    aliases: ["nicu", "nicu admission", "neonatal intensive care"],
  },
  {
    name: "Burn Unit Admission",
    category: "Admission",
    aliases: ["burn center", "burn unit", "burn icu"],
  },
  {
    name: "Stroke Unit Admission",
    category: "Admission",
    aliases: ["stroke admission", "neuroscience icu", "neuro icu"],
  },
  {
    name: "Cardiac ICU Admission",
    category: "Admission",
    aliases: ["cicu", "cardiac intensive care", "coronary care unit", "ccu"],
  },
  {
    name: "Surgical ICU Admission",
    category: "Admission",
    aliases: ["sicu", "surgical intensive care"],
  },
  {
    name: "Medical ICU Admission",
    category: "Admission",
    aliases: ["micu", "medical intensive care"],
  },
  {
    name: "Transplant Admission",
    category: "Admission",
    aliases: ["transplant inpatient", "transplant evaluation admission"],
  },
  {
    name: "Respite Care Admission",
    category: "Admission",
    aliases: ["respite care", "respite stay", "caregiver relief"],
  },

  // ============================================
  // PROCEDURES — BOTOX / BOTULINUM TOXIN (INDICATION-SPECIFIC)
  // ============================================

  {
    name: "Botox for Chronic Migraine",
    category: "Procedure",
    aliases: [
      "botox migraine",
      "onabotulinumtoxinA migraine",
      "chronic migraine botox",
      "migraine injections",
      "botox headache",
    ],
  },
  {
    name: "Botox for Spasticity",
    category: "Procedure",
    aliases: [
      "botox spasticity",
      "onabotulinumtoxinA spasticity",
      "botox for tone",
      "spasticity injection",
      "botox cerebral palsy",
      "botox stroke spasticity",
    ],
  },
  {
    name: "Botox for Hyperhidrosis",
    category: "Procedure",
    aliases: [
      "botox sweating",
      "onabotulinumtoxinA hyperhidrosis",
      "excessive sweating botox",
      "axillary botox",
    ],
  },
  {
    name: "Botox for Cervical Dystonia",
    category: "Procedure",
    aliases: [
      "botox dystonia",
      "botox torticollis",
      "cervical dystonia injection",
    ],
  },
  {
    name: "Botox for Overactive Bladder",
    category: "Procedure",
    aliases: [
      "botox oab",
      "bladder botox",
      "onabotulinumtoxinA bladder",
      "detrusor botox injection",
    ],
  },
  {
    name: "Botox for TMJ/Bruxism",
    category: "Procedure",
    aliases: ["botox tmj", "botox jaw", "masseter botox", "bruxism botox"],
  },
  {
    name: "Dysport for Spasticity",
    category: "Procedure",
    aliases: ["abobotulinumtoxinA", "dysport", "dysport spasticity"],
  },
  {
    name: "Myobloc for Cervical Dystonia",
    category: "Procedure",
    aliases: ["rimabotulinumtoxinB", "myobloc", "type b botulinum"],
  },

  // ============================================
  // "OTHER" CATCH-ALL OPTIONS — ONE PER CATEGORY
  // ============================================

  {
    name: "Other Medication",
    category: "Medication",
    aliases: [
      "custom medication",
      "unlisted medication",
      "other drug",
      "medication not listed",
    ],
  },
  {
    name: "Other Imaging Study",
    category: "Imaging",
    aliases: [
      "custom imaging",
      "unlisted imaging",
      "other scan",
      "imaging not listed",
    ],
  },
  {
    name: "Other Procedure",
    category: "Procedure",
    aliases: [
      "custom procedure",
      "unlisted procedure",
      "other surgery",
      "procedure not listed",
    ],
  },
  {
    name: "Other Referral",
    category: "Referral",
    aliases: [
      "custom referral",
      "unlisted referral",
      "other specialist",
      "referral not listed",
    ],
  },
  {
    name: "Other Admission",
    category: "Admission",
    aliases: [
      "custom admission",
      "unlisted admission",
      "other inpatient",
      "admission not listed",
    ],
  },
  {
    name: "Other DME",
    category: "DME (Durable Medical Equipment)",
    aliases: [
      "custom dme",
      "unlisted equipment",
      "other device",
      "dme not listed",
      "other durable medical equipment",
    ],
  },
];

// Category-specific request types for filtering
export const REQUEST_CATEGORIES = [
  "Medication",
  "Imaging",
  "Procedure",
  "Referral",
  "Admission",
  "DME (Durable Medical Equipment)",
  "Other",
] as const;

export type RequestCategory = (typeof REQUEST_CATEGORIES)[number];

// Helper to get requests by category
export function getRequestsByCategory(category: string): CommonRequest[] {
  return COMMON_REQUESTS.filter((r) => r.category === category);
}

// Helper to search requests
export function searchRequests(
  query: string,
  limit: number = 10
): CommonRequest[] {
  const queryLower = query.toLowerCase().trim();
  if (!queryLower) return [];

  const results: { request: CommonRequest; score: number }[] = [];

  for (const request of COMMON_REQUESTS) {
    let score = 0;

    // Exact name match (highest priority)
    if (request.name.toLowerCase() === queryLower) {
      score = 100;
    }
    // Name starts with query
    else if (request.name.toLowerCase().startsWith(queryLower)) {
      score = 80;
    }
    // Name contains query
    else if (request.name.toLowerCase().includes(queryLower)) {
      score = 60;
    }
    // Alias exact match
    else if (request.aliases?.some((a) => a.toLowerCase() === queryLower)) {
      score = 90;
    }
    // Alias starts with query
    else if (
      request.aliases?.some((a) => a.toLowerCase().startsWith(queryLower))
    ) {
      score = 70;
    }
    // Alias contains query
    else if (
      request.aliases?.some((a) => a.toLowerCase().includes(queryLower))
    ) {
      score = 50;
    }

    if (score > 0) {
      results.push({ request, score });
    }
  }

  // Sort by score descending, then by name
  results.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.request.name.localeCompare(b.request.name);
  });

  return results.slice(0, limit).map((r) => r.request);
}